import Phaser from 'phaser';
import { GAME, ASSETS } from '../config';
import { HEnemy, type HEnemyVariant, type HEnemyMovePattern } from '../entities/h-enemy';
import { HBullet } from '../entities/h-bullet';
import {
  Boss, BossAttack, stepHomingBullet, MAX_HOMING_BULLETS,
  shockwaveHitsPlayer, makeTeleportGimmickState,
  bossTeleportDue, pickBossTeleportTarget,
  type TeleportGimmickState,
} from '../entities/boss';
import { pickRandomBoss, pickBossById, computeRingShots } from '../entities/boss-pool';
import {
  FACTION_MECHANIC,
  makeFactionMechanicState,
  applyElementalDeathRespawn,
  type FactionMechanicState,
} from '../entities/FactionBossAI';
import { BOSS_TUTORIAL_POOL, TUTORIAL_BOSS_ID } from '../ai/states/tutorial-boss';
import { getBalanceSync, DEFAULT_BALANCE } from '../data/balance-loader';
import type { BalanceConfig } from '../data/balance-types';
import {
  effectiveDifficulty,
  waveCompositionFor,
  pickVariantFromComposition,
  type EffectiveDifficulty,
} from '../data/chapter-difficulty';
import { getChaptersSync } from '../data/chapter-difficulty-loader';
import type { FactionId } from '../../shared/types';
import { showPauseOverlay } from '../ui/pause-overlay';
import { MerchantOverlay, type OverlayScene } from './merchant-overlay';
import { showAffinityTutorial } from '../ui/affinity-tutorial-overlay';
import { drawChargeRing } from '../systems/charge-ring';
import { computeTelegraphPlan, drawBossTelegraph } from '../systems/boss-telegraph';
import { RunXp, XP_PER_KILL } from '../systems/run-xp';
import { loadProgress, saveProgress, isDailyLimitReached, recordPlayMinutes } from '../../shared/progress';
import { snapshotSkillLevels } from '../fx/emblem-reveal';
import {
  enemyFrameLoadsForFaction, pickEnemyForWave, baseHpFor, vxFor, movePatternForDef,
  variantForDef, enemyFrameKey, type EnemyDef, type EnemyFrame,
} from '../entities/enemy-registry';
import {
  PLAYER_TANK_SPRITE_URLS,
  ENEMY_TANK_SPRITE_URLS,
  ENEMY_FACTION_TANK_SPRITE_KEYS,
  ENEMY_FACTION_TANK_SPRITE_URLS,
  BOSS_SPRITE_URLS,
  BOSS_SPRITE_KEYS,
  MINIBOSS_SPRITE_KEYS,
  MINIBOSS_SPRITE_URLS,
  MINIBOSS_SPRITE_IDS,
  miniBossSkinKey,
  BOSS_PHASE2_SPRITE_KEYS,
  BOSS_PHASE2_SPRITE_URLS,
  HERO_PORTRAIT_KEYS,
  HERO_PORTRAIT_URLS,
  HERO_CUTIN_KEYS,
  HERO_CUTIN_URLS,
  HERO_CUTIN2_KEYS,
  HERO_CUTIN2_URLS,
  playerTankKeyFor,
  enemyTankKeyForFaction,
  bossSpriteKey,
  bossPhase2SpriteKey,
  shouldSwapToPhase2,
  pickDisplayFaction,
  BULLET_SPRITE_KEYS,
  BULLET_SPRITE_URLS,
  bulletKeyFor,
  DRONE_SPRITE_KEY,
  DRONE_SPRITE_URL,
  SKILL_FX_SPRITE_KEYS,
  SKILL_FX_SPRITE_URLS,
  skillFxKeyForFaction,
  type EnemyTankType,
} from '../entities/sprite-keys';
import { resolveTankRender } from '../entities/tank-atlas';
import { registerSceneAssets, unloadSceneAssets } from '../scene-asset-lifecycle';
import { SoundPlayer, KillComboPitch, type SoundKind } from '../audio/sounds';
import { BgmPlayer } from '../audio/bgm-player';
import { AudioBus } from '../audio/AudioBus';
import { spawnDamageNumber } from '../fx/damage-numbers';
import { BACKGROUND_READABILITY, PLAYER_GLOW, HUD_TEXT } from '../fx/readability';
import { skillCastBurst } from '../fx/skill-burst';
import { detectJustReady } from '../fx/skill-ready';
import {
  preloadExplosionSheets,
  ensureExplosionAnims,
  playEnemyExplosion,
  playBossExplosion,
  playExplosion,
  RING_EXPLOSION,
} from '../fx/explosion-vfx';
import {
  preloadSkillParticles,
  playCelestialStars,
  playRunicRunes,
  playTechnoBarrage,
  playElementalFlames,
  type SkillFxHooks,
} from '../fx/skill-particles';
import { flashWhite, explosionParticles, JuiceSystem } from '../systems/juice';
import { computeEvolutionDamageBuff } from '../systems/evolution-buff';
import { setReduceMotion, isReduceMotion } from '../systems/motion-prefs';
import { JuiceLayer } from './JuiceLayer';
import { FACTION_BURST_COLOR, FACTION_BURST_FALLBACK_COLOR } from './factionColors';
import { ParallaxSystem } from './ParallaxSystem';
import { BattleParallax, preloadBattleBackdrops } from './BattleParallax';
import { Pathfinder } from '../ai/pathfinder';
import {
  computeBossObstacles,
  shouldRepath,
  repathEnemiesToward,
  clearAllEnemyPaths,
  BATTLE_PATHFINDER_CELL_SIZE,
  BATTLE_REPATH_INTERVAL_MS,
} from './pathfinding-wire';
import { loadMapObjects, type MapObjects } from './tilemap-loader';
import { Player } from '../entities/player';
import { Destructible, makeDestructibleLayout, DESTRUCTIBLE_XP_MULT, COVER_TEXTURE_KEYS } from '../entities/destructible';
import { ChargingState, factionMultiplier } from '../skills/skill-instance';
import {
  CooldownSlot,
  BURST,
  ULTIMATE,
  CELESTIAL_PURPLE,
  CELESTIAL_L2_SKILL_ID,
  masteryBoxLevelForMath,
  masteryDamageMultiplier,
} from '../skills/celestial-active';
import { SkillHud, type SkillSlotConfig } from './skill-hud';
import {
  FactionPlaystyleState,
  SLOT0_CONFIG,
  RUNIC_SHIELD,
  RUNIC_BLAST,
  TECHNO_SCAN,
  TECHNO_BARRAGE,
  ELEMENTAL_FIRE_PILLAR,
  DOMINION_BOMBARD,
  CELESTIAL_STARFALL,
} from '../skills/faction-playstyles';
import { getSelectedHeroId } from '../../shared/progress';
import { applyMetaBonuses, type MetaBonuses, NEUTRAL_META_BONUSES } from '../progression/meta-shop';
import { waveStrengthHpMult } from '../progression/difficulty-model';
import { RunModifiers, SPREAD_DAMAGE_FALLOFF } from '../systems/run-modifiers';
import { stepToward } from '../systems/touch-move';
import { pickThree, createUpgradePicker, flavorPool, shopOffer, UPGRADE_POOL, weaponCardsFor, dilateByBuildArchetype, filterExclusiveForHero } from '../systems/upgrade-pool';
import type { Upgrade, UpgradePicker } from '../systems/upgrade-pool';
import { computeFactionAffinity, type FactionAffinity } from '../systems/faction-affinity';
import { showShopOverlay } from '../ui/shop-overlay';
import {
  nearestOtherEnemyIndex,
  redirectVelocityToward,
  reflectVerticalWall,
  enemiesInBlastRadius,
  explodeDamageForRadius,
  ricochetOffBoss,
} from '../systems/mutation-fx';
import { detectSynergies, chainBlastRadiusMult, type SynergyDef } from '../systems/synergies';
import { detectWeaponEvolutions, evolutionPreviewForCard, type EvolutionRecipe } from '../systems/weapon-evolution';
import { showUpgradePicker } from '../ui/upgrade-picker';
import { initialRerollCharges } from '../systems/reroll';
import { resolveWeaponBehavior, weaponMetaById } from '../weapons/registry';
import { rollWeaponBoxDrop, pickWeaponBoxContents, weaponRarity } from '../weapons/weapon-loot';
import type { WeaponRarity } from '../weapons/weapon-loot';
import { enemyKillXpMult } from '../systems/enemy-xp';
import type { WeaponBehavior, WeaponTickContext, WeaponTargetInfo } from '../weapons/types';
import { pierceDamageFalloff, PIERCE_DAMAGE_FALLOFF } from '../weapons/types';
import type { RapidCannonContext } from '../weapons/rapid-cannon';
import { RailgunBehavior } from '../weapons/railgun';
import { HomingMissileBehavior } from '../weapons/homing-missile';
import { SpinBladeBehavior, SPIN_BLADE_ORBIT_RADIUS } from '../weapons/spin-blade';
import { LaserBeamBehavior } from '../weapons/laser-beam';
import { MortarBehavior } from '../weapons/mortar';

/** JUICE (task #19): screen-shake presets. Centralised so balance tweaks live in one place. */
const SHAKE_PLAYER_HIT_MS = 50;
const SHAKE_PLAYER_HIT_INTENSITY = 0.008;
const SHAKE_BOSS_FAN_MS = 70;
const SHAKE_BOSS_FAN_INTENSITY = 0.012;

/** Map boss faction → h-stage asset key. Falls back to celestial if key absent. */
const FACTION_STAGE_KEY: Record<string, string> = {
  celestial: ASSETS.hStageCelestial,
  runic:     ASSETS.hStageRunic,
  techno:    ASSETS.hStageTechno,
  elemental: ASSETS.hStageElemental,
  dominion:  ASSETS.hStageDominion,
};

/**
 * C05-wire (2026-05-23): module-level flag set when the home banner fires
 * `ef:start-tutorial-boss` BEFORE the scene is mounted (banner lives on home
 * page; the kid is not in battle yet). main.ts captures the event, calls
 * `requestTutorialBossSpawn()` to set this flag, then routes to 'h-battle'.
 * The scene reads + clears the flag in create() so the very first boss
 * spawn uses the BOSS_TUTORIAL_POOL def instead of pickRandomBoss().
 *
 * Why a module flag (not a constructor arg / scene-data param):
 *   - Phaser scene starts are fire-and-forget via `game.scene.start(key)` and
 *     this codebase does not use init(data) — adding data plumbing here would
 *     touch every existing scene-start call site for no benefit.
 *   - The flag is consumed-on-read so stale requests can't bleed into the
 *     next regular battle (kid taps 教學魔王 → loses → starts a normal battle
 *     would otherwise still spawn tutorial boss).
 *
 * The scene ALSO adds a window-listener so an in-flight battle (rare —
 * banner only renders on home) can also react.
 */
let _pendingTutorialBossSpawn: boolean = false;

/**
 * Set the module flag so the NEXT HorizontalBattleScene.create() spawns the
 * tutorial boss instead of running the normal wave-then-boss flow. Idempotent.
 * Exported for main.ts wiring.
 */
export function requestTutorialBossSpawn(): void {
  _pendingTutorialBossSpawn = true;
}

/**
 * Read-and-clear the module flag. Used by the scene in create().
 * Exported for test inspection ONLY — production code should never call this
 * directly outside the scene.
 */
export function consumeTutorialBossRequest(): boolean {
  const v = _pendingTutorialBossSpawn;
  _pendingTutorialBossSpawn = false;
  return v;
}

/**
 * boss-reachability (2026-05-31, boss「魔王太爛、要測試太久、不一定看得到」):
 * one-tap「直接跳關底 boss 戰」entry. When the page URL carries ?boss / ?boss=1
 * (or #boss hash) the scene SKIPS every wave and spawns the 關底 boss now.
 * Pure + headless-safe (typeof guard + try). Optional loc for testing.
 */
export function shouldSkipToBossFromUrl(loc?: { search?: string; hash?: string }): boolean {
  try {
    const l = loc ?? (typeof window !== 'undefined' ? window.location : undefined);
    if (!l) return false;
    const search = (l.search ?? '').toLowerCase();
    const hash = (l.hash ?? '').toLowerCase();
    if (/[?&]boss\b/.test(search)) return true;
    if (/(^|#|&)boss\b/.test(hash)) return true;
    return false;
  } catch {
    return false;
  }
}

/**
 * boss-reachability: 從 URL 解析「指定哪一隻關底 boss」。`?boss=shu-mo` /
 * `#boss=fu-hun` → 回傳 'shu-mo';`?boss`(無值) / 解析失敗 → null(走隨機)。
 * 用途:老闆要測特定魔王(例如可學習 AI 的 SHU_MO)能直接指定,不必隨機碰運氣。
 */
export function bossIdFromUrl(loc?: { search?: string; hash?: string }): string | null {
  try {
    const l = loc ?? (typeof window !== 'undefined' ? window.location : undefined);
    if (!l) return null;
    const str = `${l.search ?? ''}&${l.hash ?? ''}`.toLowerCase();
    const m = str.match(/[?&#]boss=([a-z-]+)/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/**
 * P02. Opt-in TileMap mode — when this flag is set, the next
 * HorizontalBattleScene.create() will:
 *   1. Load the demo-map.json via load.tilemapTiledJSON (if not already cached)
 *   2. Parse the object layers (spawns / obstacles / items) via tilemap-loader
 *   3. Feed obstacles directly into the Pathfinder (boss bbox still appended
 *      every repath cycle)
 *   4. Pre-spawn enemies at the map's spawn points instead of the random
 *      wave-based spawn loop
 *
 * Default OFF — the existing wave + boss flow is fully preserved so we don't
 * regress any of the shipped horizontal-battle-scene-*.test.ts coverage. Set
 * via `requestTileMapMode('demo-map')` from a debug menu / boss-designer tool.
 *
 * Why a module flag: same rationale as `_pendingTutorialBossSpawn` — the
 * codebase doesn't pipe scene-data through `init(data)` and we don't want to
 * touch every scene-start call site for a debug feature.
 */
let _pendingTileMapKey: string | null = null;

/**
 * Request that the NEXT HorizontalBattleScene.create() use the given Tiled
 * map (cache key) as the source of truth for spawn points + obstacles. Pass
 * `null` to clear a pending request. Idempotent.
 */
export function requestTileMapMode(mapKey: string | null): void {
  _pendingTileMapKey = mapKey;
}

/**
 * Read-and-clear the TileMap key. Used by the scene in create().
 * Exported for test inspection ONLY.
 */
export function consumeTileMapRequest(): string | null {
  const v = _pendingTileMapKey;
  _pendingTileMapKey = null;
  return v;
}

/** Boss enters from right at this x-offset beyond screen */
const BOSS_SPAWN_X = GAME.width + 96;
/** Boss rests at this x once it flies in */
const BOSS_TARGET_X = GAME.width - 120;
/** boss 2026-06-16:瞬移落點預警提前量(ms)。先在目的地顯示記號這麼久,才真的瞬移過去。 */
const BOSS_TELEPORT_TELEGRAPH_MS = 1500;
/** Boss fly-in speed px/sec */
const BOSS_FLY_SPEED = 200;
/** Boss bob amplitude px */
const BOSS_BOB_AMP = 20;
/** Boss bob period seconds */
const BOSS_BOB_PERIOD_SEC = 2;
/**
 * boss-behavior: 追蹤彈導引持續時間 (ms). 發射後最多追蹤這麼久,之後停止轉向
 * 改直線飛出畫面 (老闆「追蹤不能追那麼久」). 2.2s 夠給壓力但玩家甩得開.
 */
const HOMING_DURATION_MS = 2200;

const Y_MIN = 40;
// 2026-06-05 老闆「輪子不見了」:坦克 ~90px 高、origin 置中,半高 ~45。舊 Y_MAX
// = height-40 時開到最底,履帶會被切出畫面外。留半台車高 + 餘裕,讓履帶永遠在畫內。
const Y_MAX = GAME.height - 56;
// game-feel: player may now move left/right too. X_MIN keeps the tank off the
// far-left edge; X_MAX caps forward travel at 60% width so it can dodge/advance
// without colliding into the boss arena on the right.
const X_MIN = 60;
const X_MAX = Math.round(GAME.width * 0.6);
const ENEMY_Y_MIN = 80;
const ENEMY_Y_MAX = GAME.height - 80;

// tank sprite 顯示高度(px,世界座標)。boss 2026-06-13「放太大了」:加入主畫面
// 全螢幕後,坦克相對戰場偏大、戰場顯得擁擠。視覺尺寸與碰撞「完全無關」(碰撞框
// 固定 36px,見 _checkEnemyCollisions 的 size=36),純美觀 → 72 降 56。player 與
// enemy 同步降,維持「敵我同框比例」。boss 仍維持原本大尺寸(壓迫感)。改這個常數
// 即可調整,勿再散落硬編 72。
export const TANK_SPRITE_H = 56;

/**
 * touch-drag: pointer-follow deadzone (px). If the finger is within this radius
 * of the tank we don't move — kills jitter when the kid presses right on the
 * tank. ~12px is small enough to feel responsive, big enough to stop shimmer.
 */
const TOUCH_FOLLOW_DEADZONE = 12;
/**
 * touch-drag: top-right square (px) reserved for the pause button so a
 * pointer-down there toggles pause instead of yanking the tank to the corner.
 * Pause button is a 56px rect centred at (width-36, 36); 64 covers it + margin.
 */
const TOUCH_PAUSE_CORNER = 64;
/**
 * touch-drag + 搖桿並存排除區 (boss 2026-06-17「兩個都同時支援」)。座標為 GAME 空間
 * (800×600);DOM 搖桿/技能鈕本身會吃掉自己的觸控,這些區只是雙保險,避免多指時
 * Phaser activePointer 的 downX/downY 落在角落被誤判成 pointer-follow。
 *   - 搖桿:左下角 120px 圓底座 + margin → 取 200×200 角落。
 *   - 技能鈕:右下角直排小圓鈕(每顆 60px,最多疊到 4 顆)→ 取 130(寬)×200(高)。
 */
const TOUCH_JOYSTICK_ZONE = 200;
const TOUCH_SKILL_ZONE_W = 130;
const TOUCH_SKILL_ZONE_H = 200;

interface EnemyEntry {
  entity: HEnemy;
  /** Graphics fallback (always present so existing draw/move code keeps working). */
  sprite: Phaser.GameObjects.Graphics;
  /**
   * art-gen-1: when the per-variant tank PNG is loaded we add an Image on top
   * of the Graphics and hide the Graphics. Null when running with the Graphics
   * fallback only.
   */
  image?: Phaser.GameObjects.Image | null;
  /**
   * HENEMY-AFFINITY (2026-05-21): dedicated Graphics layer for the affinity
   * halo, drawn beneath the sprite/image (setDepth(0)). Null when the enemy
   * has no affinity (no halo to render — saves a Graphics object).
   */
  halo?: Phaser.GameObjects.Graphics | null;
  /**
   * enemy-toughness (2026-05-31): floating HP bar above tougher enemies
   * (maxHp > 2 — tanks + mini-boss). Null for fragile 2-HP grunts.
   */
  hpBar?: Phaser.GameObjects.Graphics | null;
  /** mini-boss (2026-05-31): true when this entry is a 小魔王. */
  miniBoss?: boolean;
  /**
   * miniboss-roster-wire (2026-06-13): true when this 小魔王 is rendered with a
   * bespoke full-colour cartoon skin PNG (vs the tinted faction-chassis fallback).
   * Skinned mini-bosses skip the per-frame magenta MINI_BOSS_TINT so the full
   * colour isn't muddied.
   */
  miniBossSkinned?: boolean;
  /** mini-boss attack scheduling: wall-clock ms of the next attack volley. */
  nextAttackMs?: number;
  /**
   * bestiary-enemy-wire (2026-06-17): the 100-enemy registry def driving this
   * enemy's art / behaviour / loot. Null for legacy 3-variant / mini-boss spawns.
   */
  def?: EnemyDef | null;
  /** move/attack/damaged texture keys (when the bestiary art loaded). */
  moveKey?: string;
  attackKey?: string;
  damagedKey?: string;
  /** currently displayed frame — avoids redundant setTexture each tick. */
  curFrame?: EnemyFrame;
  /** wall-clock ms to revert from the attack frame back to the move frame. */
  attackFrameUntilMs?: number;
}

/**
 * HENEMY-AFFINITY: weighted probabilities for halo assignment in _spawnEnemy.
 * Roughly half of grunts have NO affinity (keeps the "haloed = important" cue
 * meaningful — if every enemy glowed, kids would tune out the signal). The
 * other half is split evenly across the 5 factions.
 *
 * Total: 0.5 (null) + 5 × 0.1 = 1.0. Adjust HALO_NONE_WEIGHT to dial halo
 * density up/down without touching the per-faction weights.
 */
const HALO_NONE_WEIGHT = 0.5;
const ALL_FACTIONS_FOR_HALO: FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];

// W5: per-faction sky tint for the bg parallax layer. Falls back to dark navy
// when the boss faction is unknown / scene is enemy-only.
const FACTION_BG_COLOR: Record<string, number> = {
  celestial: 0x1a1a2e,  // deep navy
  runic:     0x2e1a2e,  // deep purple
  techno:    0x1a2e2e,  // deep teal
  elemental: 0x1a2e1a,  // deep green
  dominion:  0x2e2a1a,  // deep amber
};
// extras-wire:玩家主砲彈可用門派色 sprite Image(texture 存在時)或退回現有
// 發光彈體 Graphics。兩者都有 x/y/rotation/destroy/setScale,sync 迴圈共用。
interface BulletEntry { entity: HBullet; sprite: Phaser.GameObjects.Graphics | Phaser.GameObjects.Image; }
interface BBulletEntry {
  x: number; y: number; vx: number; vy: number;
  damage: number; alive: boolean;
  sprite: Phaser.GameObjects.Graphics;
  // boss-behavior: 追蹤彈 (homing). 普通 boss 彈這三欄 undefined (直線飛行).
  // homing=true 時 _updateBossBullets 每幀以 stepHomingBullet 朝玩家限速轉向 (可閃),
  // 仍受 _enemyFreezeUntilMs 凍結.
  homing?: boolean;
  speedPxPerSec?: number;
  turnRatePerSec?: number;
  /**
   * boss-behavior: 追蹤彈停止導引的時間戳 (this.time.now ms). 過了這個時間
   * homing 彈不再轉向、改直線飛出畫面 → 玩家甩開後它就飛走,不會無限追 (老闆
   * 「追蹤不能追那麼久」). undefined 表示沒有導引期限 (向後相容).
   */
  homingUntilMs?: number;
  /**
   * boss-movesets (老闆「多加魔法元素」): 波浪 (sine) 前進的魔法彈。magic=true 時
   * _updateBossBullets 沿「行進方向的垂直軸」加 sine 擺動 → 視覺波浪、跟直線彈
   * 明顯不同。baseVx/baseVy 是主行進向量;magicPhase0/magicAmp/magicPeriodMs/
   * magicBornMs 控制波形。純遊戲機制 (學習與遊戲分離)。
   */
  magic?: boolean;
  baseVx?: number;
  baseVy?: number;
  magicAmp?: number;
  magicPeriodMs?: number;
  magicBornMs?: number;
}
/**
 * boss-movesets (老闆「多加雷射光」): 持續貫穿雷射光束。先 warn (細線預警) 再
 * fire (粗光束、命中判定)。角度在 spawn 當下鎖定 (不追),K-3 看預警就能閃 →
 * 老闆鐵則「看得出、躲得掉」。純遊戲機制 (學習與遊戲分離)。
 */
interface BLaserEntry {
  /** 光束起點 (boss 中心) */
  x: number; y: number;
  /** 光束方向 (rad, 標準座標 0=右) — spawn 當下鎖定 */
  angleRad: number;
  /** 開火時的光束粗細 (px, 全寬) */
  thickness: number;
  damage: number;
  /** 預警結束 (= 開火開始) 的時間戳 (ms) */
  warnEndMs: number;
  /** 開火結束 (= 整條消失) 的時間戳 (ms) */
  fireEndMs: number;
  alive: boolean;
  sprite: Phaser.GameObjects.Graphics;
}
/**
 * boss-aoe (老闆「大面積傷害」+「要有地方可以躲」): 大面積衝擊波 AoE。
 * 圓心 (x,y) 在 spawn 當下鎖定 (不追玩家)。先「預警」(warn) 期間畫一個擴張到
 * radius 的危險圈 → 玩家走出圈外即安全;預警結束「轟」一下,**只在圈內**判定傷害。
 * warnMs 夠長 (≥ SHOCKWAVE_MIN_SAFE_WARN_MS) → K-3 一定來得及走出去 (可躲鐵則)。
 * 純遊戲機制 (學習與遊戲分離)。
 */
interface BShockwaveEntry {
  /** 衝擊波圓心 (spawn 當下鎖定,不追玩家) */
  x: number; y: number;
  /** 判定/預警圈最大半徑 (px) */
  radiusPx: number;
  damage: number;
  /** 預警結束 (= 引爆判定) 的時間戳 (ms) */
  detonateMs: number;
  /** 整體消失 (引爆後短暫殘留特效) 的時間戳 (ms) */
  expireMs: number;
  /** 已引爆 → 命中只判一次 (避免每幀重扣) */
  detonated: boolean;
  alive: boolean;
  sprite: Phaser.GameObjects.Graphics;
}
/**
 * ENEMY-BULLETS (task #2): regular-enemy projectile. Same shape as BBulletEntry
 * but rendered as a tiny red filled circle (6×6) with a 1-frame trail. Kept
 * separate so collision rules differ (smaller hitbox, lower damage).
 */
interface EBulletEntry {
  x: number; y: number; vx: number; vy: number;
  damage: number; alive: boolean;
  sprite: Phaser.GameObjects.Graphics;
  /** trail Graphics drawn one frame behind, for visual feedback */
  trail: Phaser.GameObjects.Graphics;
}

export class HorizontalBattleScene extends Phaser.Scene {
  // ── Balance config (loaded from public/data/balance/*.json) ─────────────
  private _balance: BalanceConfig = DEFAULT_BALANCE;
  /** Cached player values derived from balance config */
  private _playerSpeed: number = DEFAULT_BALANCE.player.move_speed;
  private _bulletVx: number = DEFAULT_BALANCE.player.bullet_vx;
  private _bulletDamage: number = DEFAULT_BALANCE.player.bullet_damage;
  private _fireIntervalMs: number = DEFAULT_BALANCE.player.fire_interval_ms;
  private _enemyVx: number = DEFAULT_BALANCE.enemies.types.find(t => t.id === 'basic')?.speedVx ?? -150;
  private _spawnIntervalMs: number = DEFAULT_BALANCE.spawning.spawn_interval_ms;
  private _waveGapMs: number = DEFAULT_BALANCE.spawning.wave_gap_ms;
  private _waveSizes: number[] = DEFAULT_BALANCE.spawning.wave_sizes;
  private _totalWaves: number = DEFAULT_BALANCE.spawning.wave_count;

  // game-feel: current stage number (1-based). Set from init(data).stage; drives
  // a runtime difficulty multiplier (waves/enemy HP/boss HP) WITHOUT mutating the
  // balance JSON defaults. Defaults to 1 for all legacy entry points.
  private _stage: number = 1;
  /**
   * R-004 D2/D3:本場「有效難度」(章節配方 × 章內梯度),由 _stage 推導。
   * _applyBalanceConfig 用它把波量(密度)/波間隔(節奏)放大;_pickVariant 用它
   * 對應章的波次組成挑敵種;結算用 rewardMult 放大指揮官 XP(D5)。
   * 純難度資料 — 不寫任何戰力(學習/遊戲分離鐵則)。
   */
  private _difficulty: EffectiveDifficulty = effectiveDifficulty(1, getChaptersSync().chapters);
  private playerX: number = 80;
  private playerY: number = GAME.height / 2;
  private playerHp: number = DEFAULT_BALANCE.player.initial_hp;
  /**
   * W2: Player FSM (idle/moving/firing/hit/dead). Lazily created in create()
   * — scene-side playerX/Y/Hp remain source-of-truth; FSM mirrors them each
   * frame so W3 hurt-flash and W4 roll-dash can hook lifecycle events without
   * scraping scene god-state.
   */
  private playerFSM: Player | null = null;
  /**
   * W3 — Scene-level juice façade (flashSprite/screenShake/hitStop/scalePunch).
   * Additive on top of existing `_juice` (G04 presets) — provides per-hit
   * granular control wired to 4 hook sites: enemy hit / player hit / kill /
   * boss die.
   */
  private _juiceLayer!: JuiceLayer;
  /**
   * W5: three-layer parallax helper (bg + mid Rectangles behind game objects).
   * Null on test harness / setup failure — update()/destroy() are null-safe.
   */
  private _parallax: ParallaxSystem | null = null;
  /** 多層程序化景物背景(天空/星燼/遠中近建築),疊在 _parallax 底色之上做景深。 */
  private _battleParallax: BattleParallax | null = null;
  private _firedThisFrame: boolean = false;
  private playerSprite!: Phaser.GameObjects.Graphics;
  /**
   * art-gen-1: AI-generated player tank sprite (per-faction). Null when the
   * texture failed to load — _drawPlayer keeps using the Graphics fallback.
   * Created in create() after the texture cache is known to be populated.
   */
  private playerImage: Phaser.GameObjects.Image | null = null;
  /** P1 overlay graphics(保留供 P3 僚機/近戰);進化形態靠換整台圖,不畫程式配件。 */
  private tankAccessories: Phaser.GameObjects.Graphics | null = null;
  /** P1 進化:本場已選升級次數(保底用,避免純通用卡玩家永不進化)。 */
  private _upgradeCount = 0;
  /** P2 進化:本場「符合英雄主軸方向」的升級數(chosen.faction===heroId);進化主要看這個。 */
  private _spineCount = 0;
  /** 該英雄門派 factionXP(create 從 progress 讀一次);進化單場 buff 強度綁它(鐵則)。 */
  private _heroFactionXp = 0;
  /** 已套用單場 buff 的進化階段(防重複套);跨場由 create 歸零。 */
  private _evoBuffStage = 0;
  /**
   * 基礎坦克的螢幕顯示寬/高(create 時記一次)。進化換圖時用它把進化坦克「夾」在
   * 同一個顯示框內(寬不超過基礎寬、高不超過基礎高),避免進化圖長寬比較寬導致坦克
   * 在畫面上變大(老闆 2026-06-06「裝甲進階會變大 不太對」)。0 = 尚未量到。
   */
  private _basePlayerDisplayW = 0;
  private _basePlayerDisplayH = 0;
  /** P3 僚機(companion):繞玩家飛、自動射最近敵。由 autoWeapons 含 'drone' 啟用。 */
  private _companions: { sprite: Phaser.GameObjects.Image | null; nextFireMs: number; hero?: boolean; mounted?: boolean }[] = [];
  /** P3 近戰旋刃(weapon):玩家前方旋轉、近距判傷。由 autoWeapons 含 'scythe' 啟用。 */
  private _scythe: Phaser.GameObjects.Image | null = null;
  private _scytheNextTickMs = 0;
  /** 已同步的 autoWeapons 簽章(變動才重建僚機/旋刃)。 */
  private _autoWeaponsKey = '';
  /** Scrolling stage background tileSprite. Null until create(). */
  private stageBg: Phaser.GameObjects.TileSprite | null = null;
  /**
   * v1.1 battle-flow P0: solid faction-tinted backdrop rectangle drawn under the
   * tileSprite. Guarantees the screen is never a bare grey ParallaxSystem rect
   * when the stage PNG fails to decode. Non-null after create() (except headless).
   */
  private _stageBackdrop: Phaser.GameObjects.Rectangle | null = null;

  /**
   * W1 戰場可讀性:玩家戰車底光(外圈暖白 + 內圈亮白 ellipse)。
   * 純視覺 — 不參與碰撞/戰力;headless 測試環境為 null。
   */
  private _playerGlow: Phaser.GameObjects.Ellipse | null = null;
  private _playerGlowInner: Phaser.GameObjects.Ellipse | null = null;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private bullets: BulletEntry[] = [];
  private lastFireMs: number = 0;
  private enemies: EnemyEntry[] = [];

  /**
   * cover-rework (老闆「建築很醜、沒效果」→ 重做成戰術掩體): 場上「擋彈牆」。
   * 魔王彈/敵彈打中掩體被擋下 (不傷玩家) + 掩體扣血;歸零爆掉給少量 XP。玩家彈
   * 穿過 (不被自己掩體擋)。純遊戲機制 (不彈題、不寫回修煉值)。
   * sprite:卡通掩體 Image (cover-*.png);headless / 無貼圖時 = Graphics fallback;
   * 完全 headless (test) = null。命中/破損視覺用 tint+alpha (Image) 或重畫 (Graphics)。
   */
  private destructibles: {
    entity: Destructible;
    sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Graphics | null;
  }[] = [];
  /** cover-rework: 一次性 spawn 守衛 (掩體只生成一次)。 */
  private _destructiblesSpawned = false;
  private lastSpawnMs: number = 0;
  private spawnedThisWave: number = 0;
  private killedThisWave: number = 0;
  private totalKilled: number = 0;
  private currentWave: number = 0;
  private waveState: 'spawning' | 'waiting' | 'done' | 'boss' = 'spawning';
  private waveGapStartMs: number = 0;
  private hpBar!: Phaser.GameObjects.Graphics;
  private hpText!: Phaser.GameObjects.Text;
  private waveText!: Phaser.GameObjects.Text;
  /**
   * roguelite-xp (2026-05-31): per-run XP/level state. Kills feed it; on level-up
   * the 三選一 picker fires (Vampire-Survivors loop). PURE in-run — created fresh
   * here, reset on battle start, NEVER written back to 修煉值/progress (鐵則:
   * 學習與遊戲分離). The bottom XP bar HUD reads _runXp.progress01.
   */
  private _runXp: RunXp = new RunXp();
  /** Bottom-of-screen XP bar fill + frame Graphics. Null until create(). */
  private _xpBar: Phaser.GameObjects.Graphics | null = null;
  /** 'Lv N' label drawn at the left of the XP bar. */
  private _xpText: Phaser.GameObjects.Text | null = null;
  private gameOver: boolean = false;
  private startTimeMs: number = 0;

  // ── Pause state ─────────────────────────────────────────────────────────
  private paused: boolean = false;
  private pauseOverlayContainer: Phaser.GameObjects.Container | null = null;
  private keyP!: Phaser.Input.Keyboard.Key;
  private keyEsc!: Phaser.Input.Keyboard.Key;
  private pauseBtn!: Phaser.GameObjects.Rectangle;

  // ── Boss state ──────────────────────────────────────────────────────────
  private boss: Boss | null = null;
  private bossSprite: Phaser.GameObjects.Graphics | null = null;
  /**
   * boss-sprite-wire: AI-generated boss PNG overlay (per boss id). Null when
   * the texture is missing (stripped deployment / unknown boss id) — in that
   * case the existing Graphics box/halo from _drawBossSprite remains visible.
   * Created in _spawnBoss() after the texture cache is known to be populated.
   */
  private bossImage: Phaser.GameObjects.Image | null = null;
  /**
   * boss-roster-wire (2026-06-13, boss「二階段照型要變」): one-shot guard so the
   * boss only swaps to its phase-2 暴怒變身 art (+ 變身演出) ONCE per spawn.
   * Reset false in _spawnBoss; flipped true in _updateBoss when the boss first
   * crosses into phase ≥ 2 / berserk. 鐵則:純美術旗標,不參與任何傷害/HP 計算。
   */
  private _bossPhase2Swapped = false;
  /** Display height (px) used for the boss image — captured at spawn so the
   *  phase-2 swap can re-size the (different-aspect) 變身 PNG consistently. */
  private _bossImageDisplayH = 96;
  private bossHpBar: Phaser.GameObjects.Graphics | null = null;
  private bossHpText: Phaser.GameObjects.Text | null = null;
  private bossBullets: BBulletEntry[] = [];
  /** boss-movesets: 進行中的雷射光束 (預警→開火→消失)。SHU_MO 數魔專用。 */
  private bossLasers: BLaserEntry[] = [];
  /** boss-aoe: 進行中的衝擊波 (預警圈→引爆判定→殘留消失)。大面積大招,圈外可躲。 */
  private bossShockwaves: BShockwaveEntry[] = [];
  /**
   * ENEMY-BULLETS (task #2): regular-enemy projectiles. Each frame we tick
   * every alive HEnemy.tryFire(nowMs) and push spec → enemyBullets if non-null.
   * Update / cull / collision symmetric with bossBullets, except hitbox is
   * smaller (6×6 circle vs 12×12 square) and damage = 5 (vs boss ≥10).
   */
  private enemyBullets: EBulletEntry[] = [];
  /** Boss Y bob base (y when fly-in ends) */
  private bossBobBaseY: number = GAME.height / 2;
  /** Timestamp when boss reached BOSS_TARGET_X (for bob calculation) */
  private bossArrivalTimeMs: number = 0;
  /** Whether boss has reached its resting x */
  private bossArrived: boolean = false;
  /** Silence / freeze timers */
  private playerSilencedUntilMs: number = 0;
  private playerFrozenUntilMs: number = 0;
  /**
   * boss-movesets (老闆「多加緩速元素」): 玩家被緩速到此 wall-clock 時間 (ms)。
   * 生效時 _playerSpeedNow() 乘 playerSlowMult (非定身,仍能移動)。FU_HUN 符魂專用。
   * 純遊戲機制 (學習與遊戲分離:不寫回修煉值)。
   */
  private playerSlowedUntilMs: number = 0;
  /** 緩速生效時的速度倍率 (例 0.5 = 半速)。被 slow-player 招式設定。 */
  private playerSlowMult: number = 1;

  /** Time-limit tracking: last minute boundary recorded */
  private _lastTimeLimitCheckMs: number = 0;
  /** Whether we are blocked by time limit */
  private _timeLimitBlocked: boolean = false;

  private _merchantOverlay: MerchantOverlay | null = null;
  private _merchantPaused: boolean = false;
  private _warningRingGfx: Phaser.GameObjects.Graphics | null = null;
  /**
   * learnable-boss-proto (老闆 2026-06「魔王要可學習」): graphics for the
   * SHU_MO move-distinct telegraph (laser aim-line / sniper muzzle charge) and
   * the RECOVER breach cue. Only driven for bosses whose tickAttacks populates
   * `patternTelegraph` (currently SHU_MO); harmless no-op for others.
   */
  private _telegraphGfx: Phaser.GameObjects.Graphics | null = null;
  /**
   * W8 PR-1 — Per-spawn state for the active boss's faction mechanic. Reset
   * on each `_spawnBoss()`; consumed by `_updateBoss()` (per-frame tick) and
   * `_bossDefeated()` (one-shot elemental respawn). Null between bosses.
   */
  private _factionMechanicState: FactionMechanicState | null = null;
  /**
   * boss-teleport-gimmick — per-spawn 狀態,讓帶 uniqueGimmick:'teleport' 的 boss
   * (任意門派) 在 HP<50% 後每 5s 真的瞬移 (文件定義的行為,先前僅 techno 透過
   * FACTION_MECHANIC 生效)。每次 `_spawnBoss()` 重建;`_updateBoss()` 每幀推進。
   */
  private _teleportGimmickState: TeleportGimmickState | null = null;
  /** boss 2026-06-16 瞬移預警:已決定但尚未抵達的落點 x(null=無待瞬移);到 _teleportFireAtMs 才真的移動。 */
  private _teleportPendingX: number | null = null;
  private _teleportFireAtMs = 0;
  /** 落點預警記號(graphics/含子物件),瞬移完成或 boss 換場時清掉。 */
  private _teleportMarker: Phaser.GameObjects.GameObject[] | null = null;
  /**
   * W8 PR-1 — runic boss castedShield: while active the boss takes 0 damage.
   * `_bossShieldEndMs` is wall-clock (ms); when `time.now > end` the shield
   * lapses and `_bossShieldActive` flips off in `_updateBoss`. Public so the
   * defeat-flow renderer / tests can observe the protected window.
   */
  public _bossShieldActive: boolean = false;
  public _bossShieldEndMs: number = 0;
  /**
   * Task #31: rising-edge detector for boss attack-B warning ring.
   * `boss.warningRing.active` flips false → true once per attack-B; we play
   * boss-warning SFX only on that transition (not every frame the ring is up).
   */
  private _warningRingWasActive: boolean = false;
  /**
   * Task #31: handle for window-level ef:audio-volume listener so we can
   * remove it on SHUTDOWN — otherwise repeated scene restarts leak handlers
   * that keep hitting a destroyed _sfx.
   */
  private _audioVolumeHandler: ((ev: Event) => void) | null = null;
  private _affinityTutorialSeen: boolean = false;
  /**
   * HENEMY-AFFINITY (2026-05-21): the player's "current faction" — used as the
   * `skillFaction` argument to `HEnemy.effectiveDamageFor()` so matching
   * affinities deal 1.5×. Chosen at create() time from progress.factionXP
   * (whichever faction has the most XP, falls back to celestial).
   *
   * This is the same value the player tank sprite already uses (see
   * `pickDisplayFaction` call below) — we keep it on a dedicated field so
   * tests can override it without round-tripping through `loadProgress()`.
   */
  private _playerFaction: FactionId = 'celestial';
  /**
   * HENEMY-AFFINITY: one-shot gate so the affinity tutorial fires AT MOST once
   * per run — either when the boss arrives (existing path) or when the first
   * haloed grunt spawns (new path), whichever comes first. `_affinityTutorialSeen`
   * still drives persistence via parentSettings; this scene-local flag avoids
   * double-firing within the same run before the persistence flush completes.
   */
  private _firstHaloedEnemySeen: boolean = false;

  /**
   * C05-wire: true when this scene run was triggered by the home banner's
   * 「教學魔王」 button (via the module-level `_pendingTutorialBossSpawn` flag
   * OR a runtime `ef:start-tutorial-boss` event). When true:
   *   1. create() skips the normal wave flow and immediately calls _spawnBoss()
   *   2. _spawnBoss() picks from BOSS_TUTORIAL_POOL[0] (NOT pickRandomBoss())
   *   3. _bossDefeated() dispatches `ef:tutorial-boss-completed` so main.ts
   *      can call dayone-hook's completeTutorialBoss(progress) → toggles
   *      tutorialBossCompleted in PlayerProgress.dailyHook.
   *
   * Public for tests; mutated by `_startTutorialBoss()` and reset on each
   * scene create().
   */
  public _tutorialBossMode: boolean = false;
  /**
   * C05-wire: handle to the window-level `ef:start-tutorial-boss` listener so
   * we can detach it on SHUTDOWN. Without this, repeated scene restarts
   * (kid plays tutorial → result → another tutorial) would leak handlers
   * that fire against destroyed scenes.
   */
  private _startTutorialBossHandler: ((ev: Event) => void) | null = null;

  /**
   * MERCHANT-BUFF FIX: persistent buff fields read back from MerchantOverlay.
   *
   * Prior to this fix, the merchant let the player pick 「充能 +30%」 or
   * 「XP ×2」 but only `ps.hp` was read on `onPick` — `ps.skillCharge` and
   * `ps.waveXpMultiplier` were mutated then discarded (empty promise bug,
   * shipped in WIRE-UP-1 commit 3b71e07 / d53134b).
   *
   * - `_skillChargeBonus` (0–1): fractional charge added to the next skill
   *   that fires. Skill firing isn't wired into this scene yet (W2 work), so
   *   this field is exposed via a test hook (`__getSkillChargeBonus`) and a
   *   `_consumeSkillChargeBonus()` method that future skill code can call.
   * - `_waveXpMult` (≥1): multiplier applied to the freeXP grant when the
   *   wave / boss completes successfully. Capped at 5× to prevent abuse via
   *   stacked merchant rolls (3 merchants × 2× would otherwise hit 8×).
   */
  private _skillChargeBonus: number = 0;
  private _waveXpMult: number = 1;
  /** Cap for skill-charge bonus (matches MerchantOverlay's per-pick cap). */
  private static readonly MAX_SKILL_CHARGE_BONUS = 1.0;
  /** Cap for wave XP multiplier (prevents stacked-merchant runaway). */
  private static readonly MAX_WAVE_XP_MULT = 5;

  /**
   * R3 FIX (2026-05-21): SFX player for death/hit sounds. Lazily instantiated
   * in create() inside try/catch — test harness has no AudioContext.
   */
  private _sfx: SoundPlayer | null = null;
  /**
   * W1 音效變化包: 擊殺連擊升調 — 短窗口連殺時擊殺音逐次 +3%(封頂 +15%,
   * 窗口斷了歸零)。Pure logic;與音量/戰力完全無關(只動 playbackRate)。
   */
  private _killCombo = new KillComboPitch();
  /**
   * W4 AudioBus — Web Audio GainNode chain for bus-level ducking +
   * consecutive-hit pitch ladder. Additive to BgmPlayer/SoundPlayer;
   * lives only as long as the scene. Null in test harness (no AudioContext).
   */
  private _audioBus: AudioBus | null = null;
  /**
   * BGM player for battle scene. IDEA-014 — faction variant set on create().
   * Null in test harness (no AudioContext) or when construction throws.
   */
  private _bgm: BgmPlayer | null = null;
  /** R3 FIX: number of explosion particles spawned (test-visible counter). */
  public _particleCount: number = 0;
  /**
   * G01 + G04 (Wiki) — Trauma-based screen shake + feedback preset system.
   * Lazily instantiated in create(); ticked in update() so trauma decays.
   * Public so unit tests can inspect `_juice.trauma`.
   */
  public _juice: JuiceSystem | null = null;
  /**
   * BOSS-END FIX (2026-05-21, 2nd complaint): one-shot guard so
   * `_bossDefeated()` fires EXACTLY ONCE. Required because the previous
   * implementation relied on `boss.alive` to gate the check, but `takeDamage`
   * sets `alive=false` synchronously when hp hits 0 — and `_updateBoss`
   * early-returned on `!alive` BEFORE reaching the hp<=0 branch, so the
   * transition never fired. Fix: gate `_bossDefeated()` with this flag
   * instead, and let it run regardless of `alive`.
   */
  public _bossDefeatedFired: boolean = false;
  /** R4 FIX: number of player trail particles spawned (test-visible). */
  public _trailParticleCount: number = 0;
  /** R4 FIX: last time a trail particle was emitted (throttle gate). */
  private _lastTrailEmitMs: number = 0;
  /** R4 FIX: throttle for trail particle emission (ms). */
  private static readonly TRAIL_EMIT_INTERVAL_MS = 60;

  /**
   * G06-WIRE: shared A* pathfinder for enemy-vs-boss-obstacle routing.
   * Lazily constructed in create() so test harnesses that bypass create()
   * (synthetic state injection in horizontal-battle-scene-*.test.ts) don't
   * hit "no pathfinder" failures — _maybeRepath bails when null.
   *
   * Public for tests so they can assert the grid was rebuilt with boss
   * obstacles. Use `getPathfinder()` if you need read-only access from
   * production code.
   */
  public _pathfinder: Pathfinder | null = null;
  /**
   * P02: when TileMap mode is active, this holds the parsed object layers
   * (spawns / obstacles / items) from the demo Tiled map. Null when the scene
   * is running the legacy random-wave flow (default). When non-null,
   * `_maybeRepath` appends `_tileMapObjects.obstacles` to the boss bbox so
   * pathfinder routes around BOTH the boss AND the static map obstacles.
   */
  public _tileMapObjects: MapObjects | null = null;
  /** G06-WIRE: timestamp (ms) of the last repath cycle. Drives the 0.5s cadence. */
  public _lastRepathMs: number = 0;
  /**
   * G06-WIRE: count of findPath calls issued so far this scene. Test-visible so
   * the perf assertion (≤1 cycle per 500ms × ≤20 enemies = ≤40/sec) is easy
   * to verify without timing benchmarks.
   */
  public _repathCallCount: number = 0;

  /**
   * W7 hub→battle wiring — bossId override picked from HubScene.onBuildingClick
   * via `scene.start('HorizontalBattleScene', { bossId })`. When non-null,
   * `_spawnBoss()` looks up BOSS_POOL by id INSTEAD of `pickRandomBoss()`.
   * Resets to null on each `init(data)` so a follow-up legacy entry (no init
   * data) reverts to random behaviour. Public for test inspection.
   */
  public _initBossIdOverride: string | null = null;
  /**
   * W7 hub→battle wiring — original hub faction id, passed through to
   * `_bossDefeated()` so the result-scene path can `markFactionConquered`
   * the correct hub building when the kid returns. Public for tests.
   */
  public _initHubFactionId: FactionId | null = null;

  // ── v1.1 星算門 active-skill pilot ───────────────────────────────────────
  /** Cooldown timers for the 4 skill slots. Slot 0 (key 1) = burst fire. */
  private _skillSlots: CooldownSlot[] = [];
  /** Which slots are unlocked this run (slot 0 always, 1-3 future). */
  private _skillSlotUnlocked: boolean[] = [true, false, false, false];
  /** Ultimate「時空爆裂」charge (reuses ChargingState — kill/time/damage). */
  private _ultimateCharge: ChargingState = new ChargingState();
  /** Skill-charge faction multiplier (factionXP-derived). Default 1. */
  private _skillChargeMult: number = 1;
  /** mastery box→damage multiplier applied to burst + auto-fire this run. */
  private _masteryDamageMult: number = 1;
  /** Math mastery box level (1-5) shown in the pre-battle banner. */
  private _masteryBoxLevel: 1 | 2 | 3 | 4 | 5 = 1;
  /** Bottom skill HUD (4 cooldown slots) + top 3-cell power gauge. */
  private _skillHud: SkillHud | null = null;
  /** Rising-edge state for keys 1-4 + space (so hold ≠ repeat-fire). */
  private _skillKeyWasDown: boolean[] = [false, false, false, false];
  private _ultimateKeyWasDown: boolean = false;
  /**
   * Juice Batch 1 — 上一幀各槽 ready 狀態,用 detectJustReady 偵測「剛變 ready」
   * 觸發 glow 脈衝 + 輕「ready」音。初值全 true 避免開場首幀誤觸發。
   */
  private _skillReadyPrev: boolean[] = [true, true, true, true];
  /** Phaser keyboard handles for slots 1-4 (desktop). */
  private _slotKeys: Phaser.Input.Keyboard.Key[] = [];
  private _ultimateKey: Phaser.Input.Keyboard.Key | null = null;
  /** When time.now < this, all enemies + their bullets are frozen (ultimate). */
  private _enemyFreezeUntilMs: number = 0;
  /** Burst-fire in-flight scheduling: remaining shots + next-shot timestamp. */
  private _burstShotsLeft: number = 0;
  private _burstNextShotMs: number = 0;
  /** BUG FIX (boss「空格按不了」): throttle for the「戰力未滿」deny feedback. */
  private _lastPowerDenyMs: number = 0;
  /**
   * BUG FIX (boss「放完大招畫面放大回不去」2026-05-30): guard so the zoom-punch
   * zoom-back fires exactly once — whether triggered by the tween onComplete
   * callback or the wall-clock setTimeout safety net (whichever wins). Without
   * it the two paths could queue competing zoomTo tweens. Public so tests can
   * assert the camera returns to zoom 1.0 after a punch.
   */
  public _zoomReturning: boolean = false;

  // ── v1.2 主人翁 / 英雄玩法 (docs/spec/HERO_SYSTEM_V1.md §1.2) ───────────────
  /**
   * Selected hero門派 (progress.selectedHeroId, fallback 'celestial'). Drives
   * the slot-0 主玩法 + 戰車外觀 + HUD label/color. DISTINCT from
   * `_initHubFactionId` (stage 背景/boss) and `_playerFaction` (strongest
   * factionXP, still used for affinity / BGM). 學習與遊戲分離鐵則:純遊戲身份。
   */
  private _heroId: FactionId = 'celestial';
  /**
   * 永久 meta 商店(PowerUp / meta-shop)在 run 起手算好的乘數(傷害/HP/移速/幸運)。
   * spec: docs/spec/LONG_TERM_PROGRESSION.md §5(戰鬥套用)。每項已在 meta-shop
   * applyMetaBonuses 內硬封頂(傷害 ≤ +25% / HP ≤ +33%…)。run 起手讀一次,
   * 與單場 RunModifiers 相乘但各自封頂。鐵則:meta = 玩遊戲軌底盤,絕不碰學習軌
   * (applyMetaBonuses 只讀 metaShop,不讀 factionXP/skillLevels)。預設中性全 1。
   */
  private _metaBonuses: MetaBonuses = { ...NEUTRAL_META_BONUSES };
  /**
   * Per-run faction-playstyle state machine (shield charges / scan window /
   * autofire buff / damage buff / fire-pillar fields). Pure logic — see
   * faction-playstyles.ts. Public for test inspection.
   *
   * Accessed via `_playstyleState()` so tests that mount the scene via
   * `Object.create(prototype)` (field initializers don't run) still get a live
   * instance lazily instead of crashing on undefined.
   */
  public _playstyle: FactionPlaystyleState = new FactionPlaystyleState();

  /** Lazy-safe accessor: returns the playstyle state, creating it if a test
   *  harness bypassed the field initializer. */
  private _playstyleState(): FactionPlaystyleState {
    if (!this._playstyle) this._playstyle = new FactionPlaystyleState();
    return this._playstyle;
  }

  // ── Roguelike 三選一強化 (restored after v1.1 horizontal battle rewrite) ────
  /**
   * Per-run upgrade modifiers (speed / fire-rate / damage / HP / crit / shield
   * / lifesteal …). Reset to a fresh instance at the start of every battle in
   * create(). Consumed throughout the combat loop (see _playerSpeedNow /
   * _handleAutoFire / _effectiveBulletDamage / _maxHp / _checkCollisions).
   *
   * 學習與遊戲分離鐵則:三選一是純遊戲機制(時間冷卻/擊殺充能/波次清完觸發),
   * 絕不彈出題目。修煉值只決定技能解鎖與強度,不參與這裡的計算。
   */
  public _runMods: RunModifiers = new RunModifiers();
  /**
   * R-005 主武器系統:本場裝備的武器 behavior。create() 依 progress.selectedWeapon
   * (在 unlockedWeapons 內才用,否則回退 rapid-cannon)解析並 reset()。auto-fire tick
   * 委派給 `_equippedWeapon.tick(ctx)`。預設 rapid-cannon = 完全等價現有自動直射
   * (其 tick 委派回 _handleDefaultAutoFire,保真)。鐵則:武器威力讀武器等級,不讀修煉值。
   */
  private _equippedWeapon: WeaponBehavior = resolveWeaponBehavior('rapid-cannon', []);
  /** railgun 蓄力視覺進度(0..1),由武器 ctx.setChargeRatio 更新;_drawXxx 讀來畫蓄力條。 */
  private _weaponChargeRatio: number = 0;
  /** spin-blade 刀刃角度(弧度),由武器 ctx.setBladeAngle 更新;sync 時畫旋刃。 */
  private _bladeAngleRad: number = 0;
  /** laser-beam 過熱比(0..1),由武器 ctx.setHeatRatio 更新;_drawWeaponFx 讀來畫過熱條。 */
  private _weaponHeatRatio: number = 0;
  /** laser-beam 是否過熱冷卻中,由武器 ctx.setHeatRatio 更新;過熱時光束變暗/變紅提示。 */
  private _weaponOverheated: boolean = false;
  /** 本幀 dtSec(update 每幀寫入),武器引擎 ctx.dt 用。預設 0 → 不經 update 的測試安全。 */
  private _lastDtSec: number = 0;
  /** 主武器視覺 Graphics(飛彈/旋刃/蓄力光點),每幀清空重畫。Null until create()。 */
  private _weaponFxGfx: Phaser.GameObjects.Graphics | null = null;
  /**
   * R-005 P3 (C) 戰場武器箱 pickup(單場)。擊殺機率掉,玩家碰到 → 當場立即換武器
   * (只改 _equippedWeapon,**不**寫 progress.selectedWeapon)。run 結束隨 scene 銷毀。
   * 鐵則:純局內、玩遊戲軌,0 戰力寫入。每筆記 id(箱內武器)+ 視覺物件 + 落點。
   */
  private _weaponBoxes: Array<{
    x: number;
    y: number;
    weaponId: string;
    rarity: WeaponRarity;
    sprite: Phaser.GameObjects.GameObject | null;
    label: Phaser.GameObjects.GameObject | null;
  }> = [];
  /**
   * Stateful 三選一 picker — built once per battle (same lifecycle as _runMods /
   * _runXp) so its 保底 (pity) + 稀有度錯開 state actually accumulates across the
   * run instead of resetting on every level-up. Re-created on each scene
   * (re)start in the reset block alongside _runMods/_runXp.
   * 鐵則:純遊戲機制,不碰 progress / 修煉值。
   */
  private _runPicker: UpgradePicker = createUpgradePicker();
  /**
   * True while the DOM upgrade-picker overlay is showing. Mirrors the
   * `_merchantPaused` hard-stop pattern: `update()` early-returns so enemies /
   * bullets / input all freeze (wall-clock based — no timeScale=0 deadlock).
   * Cleared in the onPick callback which then resumes the wave flow.
   */
  public _pickPaused: boolean = false;
  /**
   * 本場剩餘重抽(reroll)次數(ROGUELITE_ROADMAP P0)。`null` = 本場尚未 init;首次開三選一時
   * 從 `qol.rerollCharges` 載入(封頂 2),之後跨升級保留、用完歸零。scene 重啟(新一場)→ 欄位
   * 重置為 null → 重新載入(跨場歸零)。鐵則:只讀 qol(玩遊戲軌 QoL),從不回寫 progress。
   */
  public _rerollLeft: number | null = null;
  /**
   * Lifesteal counter — total kills this run, used by the「吸血」upgrade to heal
   * 1 HP every `1/lifestealPerKill` kills (e.g. every 5 kills at 0.2/upgrade).
   */
  private _runKillCount: number = 0;

  // ── Phase 2:門派風味快照 (§4-0) ────────────────────────────────────────────
  /**
   * 進場算一次的門派親和度唯讀快照 (computeFactionAffinity)。整場不變。
   * 唯一用途:flavorPool() 加權三選一池 (哪張卡更常出現)。
   * 鐵則:affinity 絕不進傷害/攻速/HP 公式;同卡效果與門派無關。
   */
  private _factionAffinity: FactionAffinity | null = null;

  // ── Phase 2:局內金幣商店 (§4-1) ────────────────────────────────────────────
  /** 每隻雜兵死亡掉落的基礎金幣 (× mods.coinDropMult)。與修煉值無關。 */
  static readonly COIN_PER_KILL = 1;
  /** 小魔王 / 精英額外金幣倍率。 */
  static readonly COIN_ELITE_MULT = 3;
  /** True while the coin shop overlay is showing (hard-pause, 同 _pickPaused 模式)。 */
  public _shopPaused: boolean = false;
  /** 魔王前金幣商店一次性守衛 (每場最多彈一次)。 */
  private _preBossShopShown: boolean = false;
  /** 金幣 HUD 文字。 */
  private _coinText: Phaser.GameObjects.Text | null = null;

  // enemy-diversity (2026-05-31): cluster '成群結隊' spawn tuning. Public so the
  // combat-overhaul test can assert the values without reflection.
  /** Probability a single _spawnEnemy tick emits a cluster instead of 1 grunt. */
  static readonly CLUSTER_CHANCE = 0.28;
  /** Min / max enemies in a cluster (inclusive). */
  static readonly CLUSTER_MIN = 2;
  static readonly CLUSTER_MAX = 4;
  /** Vertical gap (px) between stacked cluster members. */
  static readonly CLUSTER_GAP_PX = 46;

  /**
   * 進化坦克「夾尺寸」(contain):把進化圖等比縮放到不超過基礎坦克顯示框
   * (boxW×boxH)。回傳套用後的顯示寬高。純函式、headless 安全、可單測。
   * 鐵則無關(純視覺)。老闆 2026-06-06「裝甲進階會變大 不太對」修正。
   */
  static evoContainSize(srcW: number, srcH: number, boxW: number, boxH: number): { w: number; h: number } {
    if (!(srcW > 0) || !(srcH > 0) || !(boxW > 0) || !(boxH > 0)) {
      return { w: boxW > 0 ? boxW : 72, h: boxH > 0 ? boxH : 72 };
    }
    const scale = Math.min(boxW / srcW, boxH / srcH);
    return { w: srcW * scale, h: srcH * scale };
  }

  // ── mini-boss (2026-05-31, boss「應該也要有小魔王,血多、攻擊招式不同」) ────────
  // miniboss-tougher (boss 2026-06「小王要多一點也要更強」):出現更頻繁 (每 3→2 波)、
  // 更肉 (HP 40→75)、更兇 (攻擊間隔 2200→1700ms)、略大 (120→132)。仍是「血多扇形彈、
  // 中央可閃」的可躲設計;速度/扇形角度不動 → K-3 鐵則「看得出、躲得掉」維持。
  /** 小魔王 HP — 遠高於雜兵 (basic/fast 2, tank 6);調強 40→75. */
  static readonly MINI_BOSS_HP = 75;
  /** 小魔王 出現節奏:每 N 波的壓軸 spawn 一隻 (1-based wave index);3→2 更常出. */
  static readonly MINI_BOSS_EVERY_N_WAVES = 2;
  /** 小魔王 display size (px) — visibly bigger than the 72px grunt sprite;120→132. */
  static readonly MINI_BOSS_SIZE = 132;
  /** 小魔王 tint (deep magenta) so it reads as 'special'. */
  static readonly MINI_BOSS_TINT = 0xab47bc;
  /** 小魔王 attack cadence (ms between volleys);2200→1700 更兇 (仍可閃). */
  static readonly MINI_BOSS_ATTACK_INTERVAL_MS = 1700;
  /** 小魔王 fan-shot bullet count + speed + damage. */
  static readonly MINI_BOSS_FAN_COUNT = 5;
  static readonly MINI_BOSS_BULLET_SPEED = 220;
  static readonly MINI_BOSS_BULLET_DAMAGE = 8;
  /** 小魔王 horizontal entry speed (slower than grunts). */
  static readonly MINI_BOSS_VX = -70;
  /** 小魔王 擊殺額外 XP 倍率;更肉 (HP 40→75) → 獎勵 5→6. */
  static readonly MINI_BOSS_XP_MULT = 6;

  /**
   * 冰光環 (slow-aura) upgrade state. When slowAuraEnabled is picked, every 5th
   * kill triggers a 5s aura that slows all on-screen enemies to 70% move speed
   * (−30%). `_slowAuraUntilMs` is the wall-clock end time; `_slowAuraKillCount`
   * is the streak counter that fires the aura on each 5-kill boundary.
   */
  private _slowAuraUntilMs: number = 0;
  private _slowAuraKillCount: number = 0;
  /** mini-boss: one-shot guard so AT MOST one 小魔王 spawns per qualifying wave. */
  public _miniBossSpawnedThisWave: boolean = false;
  /**
   * mini-boss: monotonic spawn counter (0-based) driving the deterministic skin
   * rotation in _spawnMiniBoss — each 小魔王 cycles to the next of the 6 cartoon
   * skins. Pure presentation; never touches HP/傷害/招式.
   */
  public _miniBossSpawnIndex: number = 0;
  /** Speed factor enemies move at while the aura is active (−30%). */
  private static readonly SLOW_AURA_FACTOR = 0.7;
  /** Kills needed to (re)trigger the aura. */
  private static readonly SLOW_AURA_KILL_THRESHOLD = 5;
  /** Aura duration in ms. */
  private static readonly SLOW_AURA_DURATION_MS = 5000;

  /** True when the 冰光環 aura is currently slowing enemies. */
  private _slowAuraActive(nowMs: number): boolean {
    return this._runModifiers().slowAuraEnabled && nowMs < this._slowAuraUntilMs;
  }

  /**
   * 冰光環:called on every enemy kill when the upgrade is active. Advances the
   * 5-kill streak and (re)starts the 5s aura on each boundary. Returns true if
   * the aura was (re)triggered this call (used to spawn the visible ring fx).
   */
  private _slowAuraOnKill(nowMs: number): boolean {
    if (!this._runModifiers().slowAuraEnabled) return false;
    this._slowAuraKillCount++;
    if (this._slowAuraKillCount % HorizontalBattleScene.SLOW_AURA_KILL_THRESHOLD === 0) {
      this._slowAuraUntilMs = nowMs + HorizontalBattleScene.SLOW_AURA_DURATION_MS;
      return true;
    }
    return false;
  }

  /** Lazy-safe accessor for RunModifiers (test harnesses that bypass field
   *  initializers via Object.create still get a live instance). */
  private _runModifiers(): RunModifiers {
    if (!this._runMods) this._runMods = new RunModifiers();
    return this._runMods;
  }

  /** Lazy-safe accessor for the stateful 三選一 picker (same guard idiom as
   *  _runModifiers, for test harnesses that bypass field initializers). */
  private _picker(): UpgradePicker {
    if (!this._runPicker) this._runPicker = createUpgradePicker();
    return this._runPicker;
  }

  /**
   * Per-run effective max HP = (balance initial_hp × 永久 meta HP 乘數) + upgrade hpBonus.
   * 永久 meta(PowerUp 商店)只乘到 base initial_hp(底盤,已在 applyMetaBonuses 內封頂
   * ≤ +33%);單場升級 hpBonus 在 meta 之外另加(各自封頂)。鐵則:meta = 玩遊戲軌,
   * 不碰學習軌(見 _metaBonuses 註解)。
   */
  private _maxHp(): number {
    const metaHp = Math.round(this._balance.player.initial_hp * (this._metaBonuses?.hpMult ?? 1));
    return metaHp + this._runModifiers().hpBonus;
  }

  /**
   * Per-run effective ultimate-charge multiplier = base factionXP mult ×
   * (1 + 快充 chargeMultBonus). The「快充」upgrade (chargeMultBonus += 0.5)
   * therefore makes the 戰力槽 fill 50% faster from BOTH time and kills.
   * Read fresh each charge event so a mid-run pick takes effect immediately.
   */
  private _effectiveChargeMult(): number {
    return this._skillChargeMult * (1 + this._runModifiers().chargeMultBonus);
  }

  /**
   * Per-run effective move speed = balance speed × 永久 meta 移速乘數 × upgrade speedMult × 緩速倍率。
   * boss-movesets: 被 FU_HUN 緩速時乘 playerSlowMult (非定身,只是變慢)。
   * 永久 meta 移速乘數(_metaBonuses.moveSpeedMult)是玩遊戲軌底盤,已在 applyMetaBonuses
   * 內封頂(≤ +15%);與單場 speedMult 相乘但各自封頂。鐵則:不碰學習軌。
   */
  private _playerSpeedNow(): number {
    const slowMult = this.time && this.time.now < this.playerSlowedUntilMs ? this.playerSlowMult : 1;
    const metaSpeed = this._metaBonuses?.moveSpeedMult ?? 1;
    return this._playerSpeed * metaSpeed * this._runModifiers().speedMult * slowMult;
  }

  /**
   * Show the DOM 三選一強化 picker (純遊戲機制, no 題目). Freezes the battle via
   * `_pickPaused`, applies the chosen upgrade to RunModifiers, runs any instant
   * effects (HP bump / shield), then resumes. Idempotent: a second call while a
   * pick is already in progress is a no-op so a double wave-clear can't stack
   * two overlays.
   *
   * `onResume` lets the caller advance the wave AFTER the pick completes (mirrors
   * how _resumeFromMerchant restarts spawning).
   */
  _showUpgradePicker(onResume: () => void): void {
    // boss 2026-06-16「立繪出現的時候不要跳三選一,等結束才跳」:大招 cut-in 立繪還在
    // 播(_heroCutin 非 null)時,先記下 onResume 延後,等 cut-in finish() 補開。避免
    // 三選一蓋在過場立繪上。
    if (this._heroCutin) {
      this._pendingUpgradeResume = onResume;
      return;
    }
    if (this._pickPaused) return;
    this._pickPaused = true;
    // Stateful picker: 保底 (pity) + 稀有度錯開 now actually accumulate across the
    // whole battle instead of resetting on every level-up (pickThree was stateless).
    // Phase 2 §4-0:若有門派風味快照,把 flavorPool(...) 當 source 注入 picker
    // (加權「哪張卡更常出現」)。affinity 為 null (legacy/headless) 時退回預設池,
    // 行為同今日。鐵則:flavorPool 只改 weight,不改 apply 數值。
    const aff = this._factionAffinity;
    // P3 配件 gating:未解鎖的僚機/近戰卡不進三選一池(解鎖來源=過關獎勵,寫 unlockedAccessories)。
    // 卡 id → autoWeapons key 映射(companion-drone→'drone'、weapon-scythe→'scythe')。
    let unlockedAcc: string[] = [];
    let unlockedWeapons: string[] = [];
    try {
      const prog = loadProgress();
      unlockedAcc = prog?.unlockedAccessories ?? [];
      unlockedWeapons = prog?.unlockedWeapons ?? [];
    } catch { /* headless */ }
    const gated = UPGRADE_POOL.filter((u) => {
      const key = u.id === 'companion-drone' ? 'drone'
        : u.id === 'weapon-scythe' ? 'scythe' : null;
      return key === null || unlockedAcc.includes(key);
    });
    // 打贏魔王解鎖的武器類型 → 進場內牌池(未解鎖不在 unlockedWeapons → weaponCardsFor
    // 不產卡 → 不進池)。武器卡效果走單場 RunModifiers,跨場歸零(鐵則:廣度非戰力)。
    const weaponCards = weaponCardsFor(unlockedWeapons);
    const poolWithWeapons = weaponCards.length > 0 ? [...gated, ...weaponCards] : gated;
    const flavored = aff ? flavorPool(poolWithWeapons, aff) : poolWithWeapons;
    // 廣/高傷二擇一(boss 2026-06-14「不能打得又廣傷害又高」):若本場已重押某原型
    // (廣 or 高傷),稀釋對立原型卡的出現機率,讓單一 build 不易拿滿「最大寬度+最大單發傷」。
    // 純改 weight,不改 apply / 數值。pickCounts=空(開場)→ 不動,行為同今日。
    const dilated = dilateByBuildArchetype(flavored, this._runModifiers().pickCounts);
    // 英雄獨有卡 (boss 2026-06-17):exclusiveTo 硬篩 —— 其他英雄的獨有卡永不出現;
    // 自己門派的獨有卡(召喚主角/招牌強化)加權,否則 legendary 太稀有幾乎抽不到。
    const heroId = this._heroId;
    const source = filterExclusiveForHero(dilated, heroId)
      .map((u) => (u.exclusiveTo === heroId ? { ...u, weight: u.weight ?? 18 } : u));
    const choices = this._picker().pick(undefined, source);
    // C2「可進化!」標記:對每張候選卡,問「再選這張會觸發哪條進化」(純讀 mods,
    // 不寫)。回進化名 → picker 在卡上加徽章引導 K-3。純視覺,不碰戰力/progress。
    const mods = this._runModifiers();
    const evolveHint = (u: Upgrade): string | null =>
      evolutionPreviewForCard(u.id, mods)?.displayName ?? null;
    // reroll(ROGUELITE_ROADMAP P0):本場首次開三選一時,從 qol.rerollCharges 載入重抽預算
    // (封頂 2),之後跨升級保留。鐵則:只讀 qol(玩遊戲軌 QoL),getChoices 只重洗同一池
    // (不增加總拿牌數、不寫戰力)。剩餘 0 → 不傳 config → 無重抽鈕。
    if (this._rerollLeft === null) {
      let qol: { rerollCharges?: number | null } | null = null;
      try { qol = loadProgress()?.qol ?? null; } catch { /* headless */ }
      this._rerollLeft = initialRerollCharges(qol);
    }
    const rerollCfg = this._rerollLeft > 0
      ? {
          charges: this._rerollLeft,
          getChoices: () => this._picker().pick(undefined, source),
          onChargesChanged: (n: number) => { this._rerollLeft = n; },
        }
      : undefined;
    showUpgradePicker(choices, (chosen) => {
      this.applyUpgradeInScene(chosen);
      this._pickPaused = false;
      onResume();
    }, evolveHint, rerollCfg);
  }

  /**
   * Apply a picked upgrade. §4-6 dispatch skeleton: split by `u.kind`, but in
   * Phase 1 EVERY card — 'stat' and 'mutation' alike — works the same way:
   * `u.apply(mods)` writes PURE DATA into RunModifiers, and the scene reads those
   * flags each frame / collision (bounceCount, explodeOnHitRadius, slowAuraEnabled…)
   * to render the effect. So the only kind-specific work here is the handful of
   * INSTANT pick-time effects (HP bump / shield) that act once at pick time.
   * 鐵則:apply 只動純資料,scene 讀旗標施工。不碰 progress / 修煉值。
   */
  applyUpgradeInScene(chosen: Upgrade): void {
    const mods = this._runModifiers();
    mods.applyUpgrade(chosen);
    // Juice Batch 1 — 升級兌現:在戰車附近浮出「升級!」彈跳,讓三選一立刻有
    // 可見回報(原本選完毫無視覺兌現,kid 反映「看不出效果」)。
    try { this._showUpgradeCommit(chosen); } catch { /* headless */ }
    // 英雄獨有卡 (boss 2026-06-17):套用時放該門派專屬特效爆光,讓招牌/召喚卡有儀式感。
    if (chosen.exclusiveTo) {
      try { skillCastBurst(this, this.playerX, this.playerY - 20, chosen.exclusiveTo); } catch { /* headless */ }
    }
    // Instant-effect upgrades that act at pick time (not per-frame). These are
    // all 'stat' cards; 'mutation' cards (bounce/explode) need no instant work —
    // their flags are read live in _checkCollisions.
    if (chosen.id === 'tank-up') {
      // HP 上限 +50 already folded into _maxHp(); also top up current HP by the
      // same +50 so the kid feels the full heal immediately (instant heal ==
      // maxHp increment, kept in sync with upgrade-pool hpBonus += 50).
      this.playerHp = Math.min(this._maxHp(), this.playerHp + 50);
    }
    if (chosen.id === 'starter-shield' && mods.startShieldHits > 0) {
      // Grant the shield charge via the existing 符文盾 playstyle system so
      // _absorbPlayerHit() consumes it like any other shield. Feature-detected
      // at runtime so we don't hard-depend on a specific playstyle method name
      // (keeps tsc green even if the API differs).
      try {
        const ps = this._playstyleState() as unknown as {
          addShieldHits?: (n: number) => void;
          grantShield?: (n: number) => void;
        };
        // Grant +2 per pick to match upgrade-pool starter-shield (+= 2).
        if (typeof ps.addShieldHits === 'function') ps.addShieldHits(2);
        else if (typeof ps.grantShield === 'function') ps.grantShield(2);
      } catch { /* no-op if API absent */ }
    }
    // build synergy:套完這張卡後,偵測有沒有湊出新的「卡片組合」。detectSynergies
    // 只讀/寫 RunModifiers 的 synergy 旗標 (跨場歸零,不碰 progress/修煉值),回傳
    // 「本次新成立」的 combo。對每個新 combo 跳一次純 FX 提示 (K-3 看得到 combo!)。
    try {
      const newly = detectSynergies(mods);
      for (const s of newly) {
        try { this._showSynergyFx(s); } catch { /* headless */ }
      }
    } catch { /* headless / defensive */ }
    // 場內武器進化 (R-004 包 C):套完這張卡後,偵測升級卡是否疊到門檻 / 集滿組合而
    // 「武器進化」。detectWeaponEvolutions 只讀/寫 RunModifiers 的進化旗標 (跨場歸零,
    // 不碰 progress/修煉值),從 balance config 取進化後數值,回傳「本次新進化」的配方。
    // 對每條新進化跳一次明顯演出 (C2:juice playFeedback + COMBO 級大字)。
    try {
      const evolved = detectWeaponEvolutions(mods, this._balance.weaponEvolution);
      for (const e of evolved) {
        try { this._showWeaponEvolutionFx(e); } catch { /* headless */ }
      }
    } catch { /* headless / defensive */ }
    // P1 進化形態:每選一次升級累積 → 達門檻換整台進化圖。
    this._upgradeCount += 1;
    // P2 主軸計數:選到「符合本英雄門派方向」的卡才 +1(進化主要看這個,因果清楚)。
    if (chosen.faction === this._heroId) this._spineCount += 1;
    try { this._refreshTankAccessories(); } catch { /* headless */ }
  }

  /**
   * P1 視覺化升級 — 依當局 RunModifiers 在玩家戰車上疊「配件」overlay。
   * 每次升級套用後(applyUpgradeInScene)重畫;每幀跟 playerImage 移動。
   * 第一個配件:散彈強化(spreadBulletBonus>0)→ 砲口上下長出額外砲管。
   * 鐵則:只讀 mods 旗標畫圖,不碰 progress/修煉值/戰力數值(配件純視覺)。
   */
  private _refreshTankAccessories(): void {
    // P1 進化形態:升級累積 → 換整台進化圖(取代假的程式砲管)。
    this._applyEvolution();
    // overlay graphics 暫不畫(P3 僚機/近戰再用);清掉殘留。
    if (this.tankAccessories) this.tankAccessories.clear();
  }

  /**
   * P1 進化形態(全 5 門派)。依本場已選升級次數(_upgradeCount)切換整台進化圖:
   *   0~1 次 = 基礎(atlas frame,不動);2~3 次 = evo2;4+ 次 = evo3(調低門檻讓一場玩得到)。
   * 缺該門派進化圖時維持基礎。換圖後重套 displaySize(新圖長寬比)。
   * 鐵則:純外觀切換,不碰戰力/修煉值;戰力仍由 mods 決定。
   */
  private _applyEvolution(): void {
    if (!this.playerImage) return;
    const fac = this._heroId;
    // 進化階段:主軸計數為主(選符合英雄方向的卡)、總次數保底(純通用卡玩家也終會進化)。
    const stage = (this._spineCount >= 4 || this._upgradeCount >= 7) ? 3
      : (this._spineCount >= 2 || this._upgradeCount >= 4) ? 2 : 1;
    if (stage < 2) return; // 階段1 維持基礎(atlas frame)
    const key = `tank-player-${fac}-evo${stage}`;
    // 換整台進化圖(缺圖則維持現有外觀,但下方數值里程碑仍生效)。
    if (this.textures.exists(key)
      && !(this.playerImage.texture && this.playerImage.texture.key === key)) {
      this.playerImage.setTexture(key);
      try {
        const src = this.textures.get(key).getSourceImage() as { width?: number; height?: number };
        if (src.width && src.height) {
          // 進化圖的長寬比常比基礎寬,單純固定高度 72 會讓坦克在畫面上變寬變大
          //(老闆 2026-06-06「裝甲進階會變大 不太對」)。改用 contain:把進化圖
          // 等比縮放夾進「基礎坦克的顯示框」內,寬不超過基礎寬、高不超過基礎高 →
          // 進化只會等大或更精實,絕不變大。
          const { w, h } = HorizontalBattleScene.evoContainSize(
            src.width, src.height,
            this._basePlayerDisplayW || 72, this._basePlayerDisplayH || 72,
          );
          this.playerImage.setDisplaySize(w, h);
        }
      } catch { /* headless */ }
      this._playEvolutionFx(); // D2:變身爽感 FX,只在真的換圖時放。
    }
    // D3 單場微 buff(鐵則紅線 §4.2):每階段套一次增量,強度綁 factionXP、寫「當局」mods
    //(RunModifiers 跨場 new 歸零)。computeEvolutionDamageBuff 是純函式、不碰 progress。
    if (stage > this._evoBuffStage) {
      const mods = this._runModifiers();
      let added = 0;
      for (let s = this._evoBuffStage + 1; s <= stage; s++) {
        added += computeEvolutionDamageBuff(s, this._heroFactionXp);
      }
      if (added > 0) mods.bulletDamageBonus += added;
      this._evoBuffStage = stage;
    }
  }

  /** D2 進化變身 FX:坦克放大彈跳 + 震屏(純視覺,headless 安全)。 */
  private _playEvolutionFx(): void {
    if (!this.playerImage) return;
    try {
      this._juiceLayer?.scalePunch(this.playerImage, 1.4, 420);
      this._juiceLayer?.screenShake(0.012, 200);
    } catch { /* headless / no juice */ }
  }

  /**
   * P3 配件(獨立物件)每幀更新。依 autoWeapons 維護僚機/旋刃,各自射 HBullet 進
   * this.bullets → 走現有碰撞(自動處理傷害/掉落/敵死),不繞過敵死清理流程。
   * 鐵則:僚機/旋刃傷害是「當局」效果(RunModifiers.autoWeapons,跨場歸零),
   * 同升級卡性質;不碰 progress/factionXP。
   */
  private _updateCompanions(nowMs: number): void {
    this._syncAutoWeapons();
    const t = nowMs / 1000;
    // 僚機:繞玩家飛(橢圓軌道)+ 每 700ms 射最近敵。
    for (let i = 0; i < this._companions.length; i++) {
      const c = this._companions[i];
      let cx: number; let cy: number;
      if (c.mounted) {
        // 騎乘 (boss 2026-06-17):英雄坐在戰車上方、貼身隨車移動(不繞圈),被動已在卡 apply 套用。
        cx = this.playerX; cy = this.playerY - 30;
      } else {
        // 主角僚機:軌道更外、傷害更高、射速更快(它是主角本人,不是小無人機)。
        const orbit = c.hero ? 92 : 70;
        const ang = t * 2.2 + (i * Math.PI * 2) / Math.max(1, this._companions.length);
        cx = this.playerX + Math.cos(ang) * orbit;
        cy = this.playerY + Math.sin(ang) * (c.hero ? 56 : 42);
      }
      if (c.sprite) { c.sprite.x = cx; c.sprite.y = cy; }
      if (nowMs >= c.nextFireMs) {
        const e = this._nearestAliveEnemy(cx, cy);
        if (e) {
          const dmg = c.hero ? 16 : 8;
          // 騎乘的招牌射擊比召喚僚機更密(它強化的是戰車本體火力)。
          const cd = c.mounted ? 420 : (c.hero ? 480 : 700);
          this._spawnAutoShot(cx, cy, e.x, e.y, dmg, 560);
          c.nextFireMs = nowMs + cd;
        }
      }
    }
    // 近戰旋刃:玩家前方旋轉 + 每 350ms 對近距(78px)敵各射一發近距彈。
    if (this._scythe) {
      const sx = this.playerX + 46;
      const sy = this.playerY;
      this._scythe.x = sx;
      this._scythe.y = sy;
      try { this._scythe.rotation = t * 7; } catch { /* headless */ }
      if (nowMs >= this._scytheNextTickMs) {
        let hit = false;
        for (const en of this.enemies) {
          if (!en.entity.alive) continue;
          const dx = en.entity.x - sx;
          const dy = en.entity.y - sy;
          if (dx * dx + dy * dy <= 78 * 78) {
            this._spawnAutoShot(sx, sy, en.entity.x, en.entity.y, 6, 900);
            hit = true;
          }
        }
        if (hit) this._scytheNextTickMs = nowMs + 350;
      }
    }
  }

  /** 找最近的存活敵人(僚機瞄準用)。無敵人回 null。 */
  private _nearestAliveEnemy(x: number, y: number): HEnemy | null {
    let best: HEnemy | null = null;
    let bd = Infinity;
    for (const e of this.enemies) {
      if (!e.entity.alive) continue;
      const dx = e.entity.x - x;
      const dy = e.entity.y - y;
      const d = dx * dx + dy * dy;
      if (d < bd) { bd = d; best = e.entity; }
    }
    return best;
  }

  /** 從 (fromX,fromY) 朝 (tx,ty) 射一發自動彈(僚機/旋刃共用),走現有碰撞系統。 */
  private _spawnAutoShot(fromX: number, fromY: number, tx: number, ty: number, dmg: number, speed: number): void {
    const dx = tx - fromX;
    const dy = ty - fromY;
    const len = Math.hypot(dx, dy) || 1;
    const entity = new HBullet(
      { x: fromX, y: fromY },
      { vx: (dx / len) * speed, vy: (dy / len) * speed },
      { damage: dmg, sizeMult: 1, bouncesLeft: 0 },
    );
    const sprite = this.add.graphics();
    try {
      sprite.fillStyle(0x80d8ff, 0.85); sprite.fillCircle(0, 0, 4);
      sprite.fillStyle(0xffffff, 1); sprite.fillCircle(0, 0, 1.6);
      sprite.setDepth(4);
      try { sprite.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
    } catch { /* headless graphics stub */ }
    this.bullets.push({ entity, sprite });
  }

  /** 依 mods.autoWeapons 建立僚機/旋刃 sprite(只建一次,變動才重建)。 */
  private _syncAutoWeapons(): void {
    const mods = this._runModifiers();
    const key = mods.autoWeapons.slice().sort().join(',');
    if (key === this._autoWeaponsKey) return;
    this._autoWeaponsKey = key;
    if (mods.autoWeapons.includes('drone') && this._companions.length === 0) {
      let sprite: Phaser.GameObjects.Image | null = null;
      // extras-wire:優先用新「圓眼支援機」(tank-drone-orb);texture 缺(stripped /
      // 測試 harness)才退回舊 companion-drone 圖。僚機跟玩家朝右,新圖原生朝右無需 flip。
      const droneKey = this.textures.exists(DRONE_SPRITE_KEY) ? DRONE_SPRITE_KEY : 'companion-drone';
      try {
        sprite = this.add.image(this.playerX, this.playerY, droneKey).setDepth(5);
        const s = this.textures.get(droneKey).getSourceImage() as { width?: number; height?: number };
        if (s.width && s.height) sprite.setDisplaySize(40 * (s.width / s.height), 40);
      } catch { sprite = null; }
      this._companions.push({ sprite, nextFireMs: 0 });
    }
    // 召喚主角上場 (boss 2026-06-17):用該英雄立繪當僚機,繞飛 + 自動強力射擊(門派色)。
    if (mods.autoWeapons.includes('hero') && !this._companions.some((c) => c.hero)) {
      let sprite: Phaser.GameObjects.Image | null = null;
      const heroKey = HERO_PORTRAIT_KEYS[this._heroId];
      try {
        if (this.textures.exists(heroKey)) {
          sprite = this.add.image(this.playerX, this.playerY, heroKey).setDepth(6);
          const s = this.textures.get(heroKey).getSourceImage() as { width?: number; height?: number };
          if (s.width && s.height) sprite.setDisplaySize(64 * (s.width / s.height), 64);
        }
      } catch { sprite = null; }
      this._companions.push({ sprite, nextFireMs: 0, hero: true });
      // 登場特效:門派色爆光(skill-burst,複用招式特效庫)。
      try { skillCastBurst(this, this.playerX, this.playerY - 30, this._heroId); } catch { /* headless */ }
    }
    // 騎乘主角上場 (boss 2026-06-17):英雄立繪坐上戰車(貼身,不繞圈),門派被動在卡 apply
    // 已套;這裡只建貼身立繪 + 登場爆光。週期招牌射擊由 _updateCompanions(mounted 分支)驅動。
    if (mods.autoWeapons.includes('hero-mount') && !this._companions.some((c) => c.mounted)) {
      let sprite: Phaser.GameObjects.Image | null = null;
      const heroKey = HERO_PORTRAIT_KEYS[this._heroId];
      try {
        if (this.textures.exists(heroKey)) {
          sprite = this.add.image(this.playerX, this.playerY - 30, heroKey).setDepth(7);
          const s = this.textures.get(heroKey).getSourceImage() as { width?: number; height?: number };
          if (s.width && s.height) sprite.setDisplaySize(52 * (s.width / s.height), 52);
        }
      } catch { sprite = null; }
      this._companions.push({ sprite, nextFireMs: 0, hero: true, mounted: true });
      try { skillCastBurst(this, this.playerX, this.playerY - 30, this._heroId); } catch { /* headless */ }
    }
    if (mods.autoWeapons.includes('scythe') && !this._scythe) {
      try {
        this._scythe = this.add.image(this.playerX, this.playerY, 'weapon-scythe').setDepth(5);
        const s = this.textures.get('weapon-scythe').getSourceImage() as { width?: number; height?: number };
        if (s.width && s.height) this._scythe.setDisplaySize(80 * (s.width / s.height), 80);
      } catch { this._scythe = null; }
    }
  }

  /**
   * Phase 2 §4-1:彈出局內金幣商店 (魔王前 / 關卡間)。HARD pause (同 _merchantPaused)。
   * 商品 = shopOffer() 從 SHOP_CATALOG 挑 4 件;買 = mods.spendCoins → applyUpgradeInScene
   * (沿用同一條 apply,效果與三選一一致)。買得起才亮、買不起灰、不刷新/議價。
   *
   * 鐵則(學習與遊戲分離):coins/商店全局內,打完即丟。本流程絕不 saveProgress、
   * 不碰修煉值;affinity 不參與商品定價/效果。
   *
   * @param onClose 玩家離開後的續流 (例:_spawnBoss)。
   */
  _showCoinShop(onClose: () => void): void {
    if (this._shopPaused) return;
    this._shopPaused = true;
    const offers = shopOffer(4);
    const finish = (): void => {
      this._shopPaused = false;
      onClose();
    };
    try {
      showShopOverlay(offers, {
        getCoins: () => this._runModifiers().coins,
        onBuy: (u: Upgrade): boolean => {
          const mods = this._runModifiers();
          const price = typeof u.price === 'number' ? u.price : 0;
          if (!mods.spendCoins(price)) return false; // 買不起,不動。
          this.applyUpgradeInScene(u);               // 扣款後套用 (同三選一 apply)。
          return true;
        },
        onClose: finish,
      });
    } catch {
      // headless / no-DOM:直接續流,不卡關。
      finish();
    }
  }

  constructor() { super('HorizontalBattleScene'); }

  /**
   * Phaser scene `init(data)` hook — runs BEFORE preload+create. We use it
   * only to capture the optional `bossId` / `hubFactionId` passed by
   * `HubScene.onBuildingClick`. Legacy entry points (TitleScene / debug menu
   * / banner) start the scene without data — fields stay null and the scene
   * keeps its existing random-boss behaviour.
   *
   * 學習與遊戲分離鐵則:init data 只能含遊戲端參數(bossId / hubFactionId),
   * 不准傳 chapterId / kpId 等學習端 reference。
   */
  init(data?: { bossId?: string; hubFactionId?: FactionId; stage?: number }): void {
    this._initBossIdOverride = (data && typeof data.bossId === 'string' && data.bossId.length > 0)
      ? data.bossId : null;
    this._initHubFactionId = (data && typeof data.hubFactionId === 'string')
      ? (data.hubFactionId as FactionId) : null;
    // game-feel: stage progression. Clamp to >=1 so a bad payload never makes
    // the run trivially easy (mult < 1) or NaN.
    this._stage = (data && typeof data.stage === 'number' && data.stage >= 1)
      ? Math.floor(data.stage) : 1;
    // R-004 D2:由 stage 推導本場有效難度(章節配方 × 章內梯度,資料驅動)。
    this._difficulty = effectiveDifficulty(this._stage, getChaptersSync().chapters);
  }

  preload(): void {
    this.load.image(ASSETS.hStageCelestial, 'assets/backgrounds/h-stages/h-stage-celestial.png');
    this.load.image(ASSETS.hStageRunic,     'assets/backgrounds/h-stages/h-stage-runic.png');
    this.load.image(ASSETS.hStageTechno,    'assets/backgrounds/h-stages/h-stage-techno.png');
    this.load.image(ASSETS.hStageElemental, 'assets/backgrounds/h-stages/h-stage-elemental.png');
    this.load.image(ASSETS.hStageDominion,  'assets/backgrounds/h-stages/h-stage-dominion.png');

    // P01: packed tank atlas — 8 tank sprites in a single texture so WebGL
    // can batch the player + N enemies into 1 draw call (vs. N+1 with
    // per-file textures). Built by `node scripts/build-atlas.cjs`.
    // The per-file `load.image` calls below remain as fallbacks for the
    // unit-test harness and stripped-deployment scenarios; `resolveTankRender`
    // picks atlas-frame mode whenever both are present.
    this.load.atlas(ASSETS.tanksAtlas, 'assets/atlas/tanks.png', 'assets/atlas/tanks.json');

    // art-gen-1: AI-generated tank PNGs (5 player factions + 3 enemy variants).
    // Kept as the per-file fallback (test harness uses these directly, and a
    // stripped deployment without the atlas falls back to these one-by-one).
    // If a file is missing on disk Phaser emits a load error but does not abort
    // the scene; rendering code below checks textures.exists() before swapping
    // in the sprite, otherwise falls back to existing Graphics shapes.
    this.load.image(ASSETS.tankPlayerCelestial, PLAYER_TANK_SPRITE_URLS.celestial);
    this.load.image(ASSETS.tankPlayerRunic,     PLAYER_TANK_SPRITE_URLS.runic);
    this.load.image(ASSETS.tankPlayerTechno,    PLAYER_TANK_SPRITE_URLS.techno);
    this.load.image(ASSETS.tankPlayerElemental, PLAYER_TANK_SPRITE_URLS.elemental);
    this.load.image(ASSETS.tankPlayerDominion,  PLAYER_TANK_SPRITE_URLS.dominion);
    // P1 進化形態(全 5 門派):升級累積到門檻換整台進化圖。缺檔 Phaser 略過、該門派維持基礎。
    for (const f of ['celestial', 'runic', 'techno', 'elemental', 'dominion'] as const) {
      this.load.image(`tank-player-${f}-evo2`, `/assets/sprites/tank-player-${f}-evo2.png`);
      this.load.image(`tank-player-${f}-evo3`, `/assets/sprites/tank-player-${f}-evo3.png`);
    }
    // P3 配件(獨立物件):僚機(機械斥)/ 近戰旋刃(自然御)。由 autoWeapons 升級卡啟用。
    this.load.image('companion-drone', '/assets/sprites/companion-drone.png');
    this.load.image('weapon-scythe', '/assets/sprites/weapon-scythe.png');
    // extras-wire (2026-06-13):僚機新圖(圓眼支援機)+ 5 門派子彈色 + 5 技能特效。
    // 全部 standalone load.image,缺檔走 loaderror handler → 各自 texture-exists gate
    // 退回現有 Graphics / 舊圖。純美術 0 戰力。
    this.load.image(DRONE_SPRITE_KEY, `/${DRONE_SPRITE_URL}`);
    for (const key of Object.keys(BULLET_SPRITE_KEYS) as (keyof typeof BULLET_SPRITE_KEYS)[]) {
      this.load.image(BULLET_SPRITE_KEYS[key], `/${BULLET_SPRITE_URLS[key]}`);
    }
    for (const key of Object.values(SKILL_FX_SPRITE_KEYS)) {
      this.load.image(key, `/${SKILL_FX_SPRITE_URLS[key]}`);
    }
    this.load.image(ASSETS.tankEnemyBasic, ENEMY_TANK_SPRITE_URLS.basic);
    this.load.image(ASSETS.tankEnemyFast,  ENEMY_TANK_SPRITE_URLS.fast);
    this.load.image(ASSETS.tankEnemyTank,  ENEMY_TANK_SPRITE_URLS.tank);
    // roster-wire (2026-06-13): per-faction cartoon enemy chassis. Preloaded as
    // the per-file fallback for the unit-test harness / stripped deployments;
    // the packed atlas (above) carries the same frame names, and
    // resolveTankRender prefers the atlas frame when it exists.
    for (const key of Object.keys(ENEMY_FACTION_TANK_SPRITE_KEYS)) {
      this.load.image(ENEMY_FACTION_TANK_SPRITE_KEYS[key], ENEMY_FACTION_TANK_SPRITE_URLS[key]);
    }

    // bestiary-enemy-wire (2026-06-17, boss「把 100 種新敵人接進遊戲」): preload the
    // current stage faction's 20 bestiary enemies × (move/attack/damaged) frames —
    // faction-scoped (~60 PNGs) since each stage has one faction. _initHubFactionId
    // is set in init() before preload. Missing file → loaderror → spawn falls back
    // to the existing 3-variant roster (texture-exists gated).
    {
      // 門派來源:stage 的 hubFactionId 優先;沒傳(DOM h-battle 入口 main.ts:359 不傳 data)
      // 時 fallback 到玩家選的英雄門派(getSelectedHeroId),確保任何入口都載得到新敵人圖。
      // 必須與 _spawnEnemy 的 faction 一致(否則載 A 門派、生 B 門派 → 圖缺 → 退回舊敵人)。
      let bf: FactionId | null = this._initHubFactionId;
      if (!bf) {
        try { const p = loadProgress(); bf = (p ? getSelectedHeroId(p) : null) ?? this._playerFaction; }
        catch { bf = this._playerFaction; }
      }
      if (bf) {
        for (const { key, url } of enemyFrameLoadsForFaction(bf)) {
          this.load.image(key, `/${url}`);
        }
      }
    }

    // miniboss-roster-wire (2026-06-13, boss「那個魔王很棒,多產生一些類似的」):
    // preload the 6 cartoon mini-boss skins so _spawnMiniBoss can rotate through
    // them. Standalone load.image (not in atlas — varied 256px sources). Missing
    // file → loaderror → _spawnMiniBoss texture-exists gate keeps the Graphics box.
    for (const id of MINIBOSS_SPRITE_IDS) {
      this.load.image(MINIBOSS_SPRITE_KEYS[id], MINIBOSS_SPRITE_URLS[id]);
    }

    // boss-sprite-wire: boss PNG 全表掃描 preload(R-004 hotfix:原本手列 5 隻,
    // tier2 四隻漏掉 → ch2 boss 變純色方塊;改掃 BOSS_SPRITE_KEYS,登記即生效)。
    // If a file is absent, Phaser emits a load error and we keep the
    // Graphics-based boss render in _drawBossSprite (textures.exists gates it).
    for (const id of Object.keys(BOSS_SPRITE_KEYS)) {
      this.load.image(BOSS_SPRITE_KEYS[id], BOSS_SPRITE_URLS[id]);
    }
    // boss-roster-wire (2026-06-13, boss「二階段照型要變」): also preload each
    // boss's phase-2 暴怒變身 art so the scene can `setTexture()` swap to it
    // the instant the boss enters phase ≥ 2 / berserk (no mid-fight load hitch).
    // Same fail-soft contract: a missing file just leaves the swap as a no-op.
    for (const id of Object.keys(BOSS_PHASE2_SPRITE_KEYS)) {
      this.load.image(BOSS_PHASE2_SPRITE_KEYS[id], BOSS_PHASE2_SPRITE_URLS[id]);
    }

    // hero-cutin-wire (boss 2026-06-16「放技能時叫出主角立牌增氣勢」): preload the
    // 5 門派 hero portraits so _playHeroCutin can flash the主角立牌 on slot-0 cast.
    // Standalone load.image; missing file → cut-in no-op (texture-exists gated).
    for (const [faction, key] of Object.entries(HERO_PORTRAIT_KEYS)) {
      if (!this.textures.exists(key)) {
        this.load.image(key, HERO_PORTRAIT_URLS[faction as FactionId]);
      }
    }
    // hero-skill-cutin:per-門派動態大招立繪(放技能 cut-in 用,優先於 portrait)。
    for (const [faction, key] of Object.entries(HERO_CUTIN_KEYS)) {
      if (!this.textures.exists(key)) {
        this.load.image(key, HERO_CUTIN_URLS[faction as FactionId]);
      }
    }
    // 2-frame cut-in 的第二格「發力」frame(只有部分門派有)。
    for (const [faction, key] of Object.entries(HERO_CUTIN2_KEYS)) {
      if (!this.textures.exists(key)) {
        this.load.image(key, HERO_CUTIN2_URLS[faction]);
      }
    }

    // cover-rework: 卡通戰術掩體 sprite (沙包/能量盾/石牆,Pollinations 生)。
    // 若檔案缺失 Phaser 只發 loaderror 不中斷;_spawnDestructibles 用
    // textures.exists 判定,缺貼圖時 fallback 到改良 Graphics 卡通畫法。
    this.load.image(COVER_TEXTURE_KEYS.sandbag, 'assets/covers/cover-sandbag.png');
    this.load.image(COVER_TEXTURE_KEYS.shield,  'assets/covers/cover-shield.png');
    this.load.image(COVER_TEXTURE_KEYS.stone,   'assets/covers/cover-stone.png');

    // P02: pre-load the demo TileMap JSON (boss-designer hot-editable). ~3KB,
    // shipped under public/assets/maps/. The scene only USES it when the
    // module-level flag is set via requestTileMapMode('demo-map') — default
    // off, so this is a no-op for the shipping random-wave flow. We load it
    // unconditionally because Phaser's loader has no late-mount option and
    // the cache key has to be populated before create() reads it.
    this.load.tilemapTiledJSON('demo-map', 'assets/maps/demo-map.json');

    // Asset Pipeline B: CC0 explosion spritesheets (small pixel + large ring).
    // Played on enemy/boss death via fx/explosion-vfx. Missing files (fresh
    // checkout pre-fetch / stripped build) just emit a loaderror — the
    // texture.exists gate in playExplosion keeps death FX a no-op.
    preloadExplosionSheets(this);

    // boss 2026-06-16「太多一樣了」:每門派大招專屬粒子貼圖(星/符文/雷射/火焰),
    // 取代「同一張爆炸換色」。缺檔 no-op,同上。
    preloadSkillParticles(this);

    // boss 2026-06-16「背景精緻一點 + 每關同風格多張隨機接續」:載入本關門派的全景變體
    // (bg-scroll-<faction>-1..10)。缺檔 → BattleParallax 回退程序剪影。
    preloadBattleBackdrops(this, this._initHubFactionId ?? this._playerFaction);

    // Quietly swallow asset 404s so a stripped deployment still boots.
    this.load.on('loaderror', (_file: unknown) => { /* fallback to Graphics */ });

    // P05: register every key we *attempt* to load so shutdown() can release
    // them. The registration is statically-known (matches the load.atlas /
    // load.image calls above 1:1) so we don't have to wait for load completion.
    // unloadSceneAssets is defensive — it checks textures.exists() before
    // calling remove, so failed loads are safely no-op'd at shutdown time.
    registerSceneAssets(this, [
      ASSETS.hStageCelestial,
      ASSETS.hStageRunic,
      ASSETS.hStageTechno,
      ASSETS.hStageElemental,
      ASSETS.hStageDominion,
      ASSETS.tanksAtlas,
      ASSETS.tankPlayerCelestial,
      ASSETS.tankPlayerRunic,
      ASSETS.tankPlayerTechno,
      ASSETS.tankPlayerElemental,
      ASSETS.tankPlayerDominion,
      ASSETS.tankEnemyBasic,
      ASSETS.tankEnemyFast,
      ASSETS.tankEnemyTank,
      BOSS_SPRITE_KEYS['shu-mo'],
      BOSS_SPRITE_KEYS['fu-hun'],
      BOSS_SPRITE_KEYS['yuan-su'],
      BOSS_SPRITE_KEYS['tian-ming'],
      BOSS_SPRITE_KEYS['shi-zhong'],
      COVER_TEXTURE_KEYS.sandbag,
      COVER_TEXTURE_KEYS.shield,
      COVER_TEXTURE_KEYS.stone,
      // extras-wire:僚機新圖 + 5 門派子彈 + 5 技能特效,一併在 shutdown 釋放記憶體。
      DRONE_SPRITE_KEY,
      ...Object.values(BULLET_SPRITE_KEYS),
      ...Object.values(SKILL_FX_SPRITE_KEYS),
    ]);
  }

  create(): void {
    // ── Daily time-limit check ───────────────────────────────────────────────
    const progress = loadProgress();
    if (progress && isDailyLimitReached(progress)) {
      this._showTimeLimitScreen();
      return;
    }
    // 雙軌分離 bug fix(2026-06-07 老闆「每次過關都說解鎖 Lv2 技能,那到底是啥」):
    // ResultScene 用「戰前 vs 戰後 skillLevels 差異」決定要不要跳技能升級浮字,
    // 但 snapshotSkillLevels 的設計(見其 doc:Caller responsibility: invoke at
    // battle start)從沒被任何人呼叫 → 戰後 diff 的 prev 永遠是 null → 把玩家
    // 現有的每個技能都誤判成「Lv.0→Lv.1 新解鎖」每場狂跳。鐵則上戰鬥本就不會
    // 改 skillLevels(技能=學習軌),所以在戰鬥開始拍一次快照,戰後 diff 必為空,
    // 誤報消失;真正的技能升級慶祝由學習 app 負責。
    try { snapshotSkillLevels(progress?.skillLevels ?? {}); } catch { /* headless / no storage */ }

    // Read from cache — loadBalance() was called in LoadingScene before this scene starts.
    // getBalanceSync() returns the JSON-loaded config if cache is ready, else DEFAULT_BALANCE.
    this._applyBalanceConfig(getBalanceSync());

    this.cameras.main.setBackgroundColor('#000000');
    // G01: trauma-based screen shake — accumulator + decay. Replaces flat
    // cameras.main.shake() calls so big hits have a tail and small hits
    // stack naturally. See docs/GAME_TECHNIQUES_WIKI.md G01.
    try { this._juice = new JuiceSystem(this); } catch { this._juice = null; }
    // W3: scene-level juice façade — granular flash/shake/hitStop/scalePunch
    // wired into the 4 hit-feedback sites below. Constructor is total (no
    // try needed) but defensive instantiation keeps test harness safe.
    try { this._juiceLayer = new JuiceLayer(this); } catch { /* test harness */ }
    // W5: three-layer parallax background (placeholder Rectangles, no asset).
    // factionColor falls back to dark navy when boss is not yet spawned.
    try {
      this._parallax = new ParallaxSystem(this);
      // v1.1 P0: boss isn't spawned yet in create(), so derive the floor color
      // from the incoming hub faction (or the player's faction) instead of the
      // generic navy — keeps the backdrop on-theme from the first frame.
      const initFaction = this._initHubFactionId ?? this._playerFaction;
      const factionColor = FACTION_BG_COLOR[initFaction] ?? 0x1a1330;
      this._parallax.create(factionColor);
    } catch { this._parallax = null; }

    // boss 2026-06-16「背景重畫好看一點、搭建築物/場景物體」:在純色底之上疊多層程序化
    // 景物(天空漸層 + 星/燼 + 遠中近建築剪影 + 窗光),不同速度捲動 = 真景深。純程序化、
    // 不吃外部圖檔(CI/缺檔安全),全在實體之下且壓暗 → 不傷戰場可讀性。
    try {
      this._battleParallax = new BattleParallax(this);
      const pxFaction = this._initHubFactionId ?? this._playerFaction;
      this._battleParallax.create(pxFaction);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        try { this._battleParallax?.destroy(); } catch { /* noop */ }
        this._battleParallax = null;
      });
    } catch { this._battleParallax = null; }

    // Asset Pipeline B: build the play-once explosion animations from the CC0
    // spritesheets loaded in preload(). No-op when the textures are absent
    // (fresh checkout / stripped build) — death FX falls back to the existing
    // circle/rectangle particle bursts.
    try { ensureExplosionAnims(this); } catch { /* headless harness */ }

    // Asset Pipeline B: kick off CC0 OGG sample loading for SFX. Best-effort +
    // async — until a sample is decoded the SoundPlayer plays the synth
    // fallback, so the first few shots/hits may be synth then upgrade silently.
    // import.meta.env.BASE_URL keeps it correct under a sub-path deployment.
    if (this._sfx) {
      const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '';
      try { void this._sfx.loadSamples(base); } catch { /* fetch unavailable */ }
    }

    this.gameOver = false;
    this.playerHp = this._balance.player.initial_hp;
    this.playerX = 80;
    this.playerY = GAME.height / 2;
    // W2: fresh Player FSM per scene boot. Mirrors scene HP so takeDamage
    // landed via _onPlayerHit drives sm.transitionTo('hit').
    this.playerFSM = new Player({ x: this.playerX, y: this.playerY });
    // W3 hook #5 — additional juice flash + hitStop on FSM transition into
    // 'hit' state. Per spec §4.5. Spec references `onStateChange` but W2
    // ships `onTransition` (callback signature identical: (from, to)) —
    // wired against the real W2 API. `'hit'` is lowercase to match W2.
    try {
      this.playerFSM.onTransition?.((_from, to) => {
        if (to !== 'hit') return;
        if (!this._juiceLayer) return;
        const tgt = (this.playerImage ?? this.playerSprite) as Phaser.GameObjects.GameObject;
        this._juiceLayer.flashSprite(tgt, 0xff0000, 120);
        this._juiceLayer.hitStop(40);
      });
    } catch { /* test harness — playerFSM may be stub */ }
    this._firedThisFrame = false;
    this.bullets = [];
    this.enemies = [];
    this.bossBullets = [];
    this.bossLasers = [];
    this.bossShockwaves = [];
    this.enemyBullets = [];
    this.currentWave = 0;
    this.waveState = 'spawning';
    this.spawnedThisWave = 0;
    this.killedThisWave = 0;
    this.totalKilled = 0;
    this.lastFireMs = 0;
    this.lastSpawnMs = 0;
    this.startTimeMs = this.time.now;
    this.boss = null;
    this.bossSprite = null;
    this.bossImage = null;
    this._telegraphGfx = null;
    this.bossArrived = false;
    this.stageBg = null;
    this.playerSilencedUntilMs = 0;
    this.playerFrozenUntilMs = 0;
    this.playerSlowedUntilMs = 0;
    this.playerSlowMult = 1;
    this._lastTimeLimitCheckMs = this.time.now;
    this._timeLimitBlocked = false;
    this._merchantOverlay = null;
    this._merchantPaused = false;
    this._warningRingGfx = null;
    // MERCHANT-BUFF FIX: reset persistent buff state on scene (re)start.
    this._skillChargeBonus = 0;
    this._waveXpMult = 1;
    // Roguelike 三選一強化:fresh per-run modifiers + cleared pick state.
    this._runMods = new RunModifiers();
    // R-005 主武器系統:依 progress.selectedWeapon 裝主武器(在 unlockedWeapons 內才
    // 用,否則回退 rapid-cannon)。每場 new 一個 behavior + reset(),跨場不殘留狀態。
    // 鐵則放寬:武器威力讀武器等級曲線(weaponLevels),不讀修煉值/不需學習解鎖。
    this._equippedWeapon = resolveWeaponBehavior(
      progress?.selectedWeapon ?? 'rapid-cannon',
      progress?.unlockedWeapons ?? [],
    );
    this._equippedWeapon.reset();
    this._weaponChargeRatio = 0;
    this._bladeAngleRad = 0;
    this._onWeaponEquipped();
    this._weaponFxGfx = null; // 上一場的 Graphics 已隨 scene shutdown 銷毀,清舊 ref。
    // R-005 P3 (C):清空上一場殘留的武器箱 pickup(視覺已隨 scene shutdown 銷毀,清 ref)。
    this._weaponBoxes = [];
    // 雙軌進度(spec §3.2.3 / §3.4):玩遊戲軌 QoL — 起手金幣由 commanderLevel
    // 解鎖的 qol.startingCoins 注入「既有局內 coins」。鐵則:coins 純局內、打完
    // 即丟、不寫回 progress;這裡只是把起手值灌進本場 RunModifiers,不碰
    // factionXP/freeXP/skillLevels/傷害/HP。讀檔失敗 → 0(不卡關)。
    try {
      const _p = loadProgress();
      const _startCoins = _p?.qol?.startingCoins ?? 0;
      if (_startCoins > 0) this._runMods.addCoins(_startCoins);
    } catch { /* headless / no localStorage — 起手 0 金幣 */ }
    // Fresh stateful picker each battle so 保底 (pity) + 稀有度錯開 state starts
    // clean (re-create, matching the _runMods/_runXp idiom).
    this._runPicker = createUpgradePicker();
    this._pickPaused = false;
    this._runKillCount = 0;
    // Phase 2:fresh per-run shop state (金幣在新 _runMods 已歸零)。
    this._shopPaused = false;
    this._preBossShopShown = false;
    // roguelite-xp: fresh XP/level bar every battle (鐵則: never persisted).
    // Re-create (not .reset()) to match the _runMods idiom above and to be
    // robust even if the class-field initializer hasn't run on this instance.
    this._runXp = new RunXp();
    // reroll(ROGUELITE_ROADMAP P0):每場重置為 null → 首次開三選一時從 qol.rerollCharges
    // 重新載入(跨場歸零;Phaser scene 重用實例,欄位初始值不會自動重置,故顯式 reset)。
    this._rerollLeft = null;
    // 冰光環 aura state resets per run.
    this._slowAuraUntilMs = 0;
    this._slowAuraKillCount = 0;
    // mini-boss: fresh per-wave 小魔王 guard on each scene (re)start.
    this._miniBossSpawnedThisWave = false;
    // R3/R4 FIX: reset particle counters + (re)init SFX. Try/catch because
    // the test harness (vitest + Phaser HEADLESS) has no AudioContext.
    this._particleCount = 0;
    this._trailParticleCount = 0;
    this._lastTrailEmitMs = 0;
    // G06-WIRE: build the scene-wide pathfinder. cellSize 32 → 25×19 grid for
    // 800×600 world. Sync mode (default) keeps callbacks inline so we don't
    // have to drain `update()` between fire-and-forget findPath calls.
    // Try/catch is defensive — easystarjs construction has never thrown in
    // testing, but the rest of create() is already try-guarded so we stay
    // consistent. _maybeRepath early-returns when null.
    try {
      this._pathfinder = new Pathfinder({
        cellSize: BATTLE_PATHFINDER_CELL_SIZE,
        gridWidth: Math.ceil(GAME.width / BATTLE_PATHFINDER_CELL_SIZE),
        gridHeight: Math.ceil(GAME.height / BATTLE_PATHFINDER_CELL_SIZE),
      });
    } catch {
      this._pathfinder = null;
    }

    // P02: consume the TileMap-mode flag (default off). When set, parse the
    // requested map's object layers and stash them so _maybeRepath picks up
    // the static obstacles AND so debug helpers can pre-spawn from the map.
    // We do NOT yet replace the wave-spawn loop — that's an explicit follow-up
    // (the existing horizontal-battle-scene tests cover the wave flow exactly
    // and the dispatch says "default 維持既有").
    this._tileMapObjects = null;
    const tileMapKey = consumeTileMapRequest();
    if (tileMapKey) {
      try {
        const parsed = loadMapObjects(this as unknown as Parameters<typeof loadMapObjects>[0], tileMapKey);
        if (parsed) {
          this._tileMapObjects = parsed;
          // Apply static obstacles immediately so the very first repath cycle
          // (which runs at time.now == _lastRepathMs, but a later frame's
          // shouldRepath will fire) already routes around the map walls.
          if (this._pathfinder) {
            this._pathfinder.setObstacles(GAME.width, GAME.height, parsed.obstacles);
          }
        }
      } catch (err) {
        // Don't kill the scene over a bad map — log and fall back to legacy.
        // eslint-disable-next-line no-console
        console.warn('[HorizontalBattleScene] TileMap load failed:', err);
        this._tileMapObjects = null;
      }
    }
    this._lastRepathMs = this.time.now;
    this._repathCallCount = 0;
    // BOSS-END FIX: reset one-shot guard on each scene start.
    this._bossDefeatedFired = false;
    try {
      this._sfx = new SoundPlayer();
      if (progress) this._sfx.setVolume(progress.audio?.sfxVolume ?? 1);
    } catch {
      this._sfx = null;
    }
    // W4: AudioBus is fully lazy (ensureChain on first use), so construction
    // itself is cheap and can't throw — but we keep the try/catch for symmetry
    // with the SoundPlayer/BgmPlayer block above.
    try {
      this._audioBus = new AudioBus();
    } catch {
      this._audioBus = null;
    }
    // Task #31: rising-edge state reset on each scene start.
    this._warningRingWasActive = false;
    // Task #31: live SFX volume update when player drags settings slider
    // mid-battle. Without this, scene init was the only chance to apply
    // sfxVolume — kid had to re-enter battle for changes to take effect.
    if (typeof window !== 'undefined' && window.addEventListener) {
      this._audioVolumeHandler = (ev: Event) => {
        const detail = (ev as CustomEvent<{ bgm: number; sfx: number }>).detail;
        if (this._sfx && typeof detail?.sfx === 'number') {
          try { this._sfx.setVolume(detail.sfx); } catch { /* noop */ }
        }
      };
      try { window.addEventListener('ef:audio-volume', this._audioVolumeHandler); } catch { /* test harness */ }
    }

    // C05-wire (2026-05-23): subscribe to `ef:start-tutorial-boss` so an
    // in-flight battle can also flip into tutorial mode (rare — banner is on
    // home page — but cheap and symmetric with the module-flag path).
    // Handler is captured on the instance so the SHUTDOWN listener below can
    // detach it; otherwise repeated scene restarts leak listeners.
    this._tutorialBossMode = false;
    if (typeof window !== 'undefined' && window.addEventListener) {
      this._startTutorialBossHandler = (_ev: Event) => {
        this._startTutorialBoss();
      };
      try { window.addEventListener('ef:start-tutorial-boss', this._startTutorialBossHandler); } catch { /* test harness */ }
    }
    this._affinityTutorialSeen = !!(progress?.parentSettings as unknown as Record<string, unknown> | undefined)?.["seenAffinityTutorial"];
    // HENEMY-AFFINITY: capture player's strongest faction so HEnemy affinity
    // matches deal 1.5×. Also reset the per-run "first haloed grunt" flag.
    this._playerFaction = pickDisplayFaction(progress?.factionXP ?? null);
    // v1.2 主人翁:玩家所選英雄門派 → slot-0 玩法 + 戰車外觀。Fallback
    // 'celestial' for legacy / undefined saves (getSelectedHeroId guards).
    this._heroId = progress ? getSelectedHeroId(progress) : 'celestial';
    // 永久 meta 商店(PowerUp / meta-shop)起手套用(spec LONG_TERM_PROGRESSION §5)。
    // applyMetaBonuses 只讀玩遊戲軌 metaShop、每項硬封頂(傷害 ≤ +25%、HP ≤ +33%…),
    // 絕不讀 factionXP/skillLevels。把永久傷害乘數套到 base bullet damage(_applyBalanceConfig
    // 已先把 _bulletDamage 設成 config 值);HP/移速乘數存起來給 _maxHp()/_playerSpeedNow 用。
    this._metaBonuses = progress ? applyMetaBonuses(progress, this._heroId) : { ...NEUTRAL_META_BONUSES };
    this._bulletDamage = Math.max(1, Math.round(this._bulletDamage * this._metaBonuses.damageMult));
    // playerHp 在上面(line ~1695)以 base initial_hp 起手,早於 meta 算好;這裡把當前 HP
    // 補到永久 meta 加成後的滿血(_maxHp() 含 hpMult),玩家開局即享底盤 HP。
    this.playerHp = this._maxHp();
    // P2 進化:讀該英雄 factionXP(單場 buff 綁它)、本場進化計數歸零(鐵則:跨場歸零)。
    this._heroFactionXp = progress?.factionXP?.[this._heroId] ?? 0;
    // 減少晃動(K-3 易暈):把家長設定 push 進 motion-prefs seam,JuiceLayer 的 screen-shake
    // / hit-stop 會據此減弱(screen shake agent)。try-guard headless 安全。
    try { setReduceMotion(progress?.parentSettings?.reduceMotion === true); } catch { /* test harness */ }
    this._spineCount = 0;
    this._evoBuffStage = 0;
    this._upgradeCount = 0;
    this._basePlayerDisplayW = 0; // 進化夾尺寸用,playerImage 重建時會重新量到
    this._basePlayerDisplayH = 0;
    // P3:本場僚機/旋刃歸零(跨場不殘留;sprite 由 scene 重啟清,只重置 field ref)。
    this._companions = [];
    this._scythe = null;
    this._scytheNextTickMs = 0;
    this._autoWeaponsKey = '';
    // Phase 2 §4-0:進場算一次門派風味快照 (唯讀,整場不變)。flavorPool 用它加權
    // 三選一池。鐵則:affinity 只進「出現機率」,絕不進傷害/攻速/HP 公式。
    this._factionAffinity = progress ? computeFactionAffinity(progress) : null;
    this._playstyleState().reset();
    this._firstHaloedEnemySeen = false;

    // IDEA-014: start battle BGM with player's strongest-faction variant.
    // Wrapped in try/catch — tests have no AudioContext / DOM Audio.
    try {
      this._bgm = new BgmPlayer();
      this._bgm.setFactionVariant(this._playerFaction);
      this._bgm.start();
    } catch {
      this._bgm = null;
    }
    // Stop BGM when scene unloads so we don't leak loops between scene switches.
    // Guarded — some test harnesses stub `this.events` to undefined.
    try {
      (this.events as Phaser.Events.EventEmitter | undefined)?.once?.(
        Phaser.Scenes.Events.SHUTDOWN,
        () => {
          try { this._bgm?.stop(); } catch { /* noop */ }
          this._bgm = null;
          // W4: tear down AudioBus alongside BGM so the GainNode chain doesn't
          // leak between scene restarts.
          try { this._audioBus?.destroy(); } catch { /* noop */ }
          this._audioBus = null;
          // Task #31: detach the ef:audio-volume listener so it doesn't
          // leak between scene restarts (each create() adds a fresh one).
          if (this._audioVolumeHandler && typeof window !== 'undefined') {
            try { window.removeEventListener('ef:audio-volume', this._audioVolumeHandler); } catch { /* noop */ }
            this._audioVolumeHandler = null;
          }
          // C05-wire: same cleanup pattern for the start-tutorial-boss listener.
          if (this._startTutorialBossHandler && typeof window !== 'undefined') {
            try { window.removeEventListener('ef:start-tutorial-boss', this._startTutorialBossHandler); } catch { /* noop */ }
            this._startTutorialBossHandler = null;
          }
          // P05: release boss / atlas / stage textures so 學習端 ↔ 戰鬥端
          // switches don't leak GPU memory on iPad / 低階機. unloadSceneAssets
          // is idempotent + refcounted, so a follow-up `ef:mode-switch`
          // handler that double-unloads the same keys is a safe no-op.
          try {
            unloadSceneAssets(this, [
              ASSETS.hStageCelestial,
              ASSETS.hStageRunic,
              ASSETS.hStageTechno,
              ASSETS.hStageElemental,
              ASSETS.hStageDominion,
              ASSETS.tanksAtlas,
              ASSETS.tankPlayerCelestial,
              ASSETS.tankPlayerRunic,
              ASSETS.tankPlayerTechno,
              ASSETS.tankPlayerElemental,
              ASSETS.tankPlayerDominion,
              ASSETS.tankEnemyBasic,
              ASSETS.tankEnemyFast,
              ASSETS.tankEnemyTank,
              BOSS_SPRITE_KEYS['shu-mo'],
              BOSS_SPRITE_KEYS['fu-hun'],
              BOSS_SPRITE_KEYS['yuan-su'],
              BOSS_SPRITE_KEYS['tian-ming'],
              BOSS_SPRITE_KEYS['shi-zhong'],
            ]);
          } catch { /* defensive: never block scene shutdown */ }
        },
      );
    } catch { /* noop */ }

    // v1.1 battle-flow P0 (boss:「空場 / 中間灰色長方形」): always draw a filled
    // faction-tinted backdrop so the screen is NEVER blank, even when the stage
    // PNG fails to load/decode (observed in prod: "Failed to process file image
    // h-stage-*"). The tileSprite (textured) layers on top when available;
    // otherwise the solid fill is the visible background (depth -11, above the
    // ParallaxSystem floor at -12, below entities).
    {
      const initFaction = this._initHubFactionId ?? this._playerFaction;
      const bgColor = FACTION_BG_COLOR[initFaction] ?? 0x1a1330;
      this._stageBackdrop = null;
      try {
        this._stageBackdrop = this.add.rectangle(0, 0, GAME.width, GAME.height, bgColor)
          .setOrigin(0, 0).setDepth(-11);
      } catch { /* headless */ }
      const stageKey = FACTION_STAGE_KEY[initFaction] ?? ASSETS.hStageCelestial;
      const bgKey = this.textures.exists(stageKey) ? stageKey
        : (this.textures.exists(ASSETS.hStageCelestial) ? ASSETS.hStageCelestial : null);
      if (bgKey) {
        try {
          this.stageBg = this.add.tileSprite(0, 0, GAME.width, GAME.height, bgKey)
            .setOrigin(0, 0).setDepth(-10);
          // W1 戰場可讀性(體檢根因 #6):h-stage PNG 烘了數十顆白星,亮度與
          // 子彈/敵人打平。乘法 tint 把整層壓暗 ~58%,星星退回「背景」。
          this.stageBg.setTint?.(BACKGROUND_READABILITY.dimTint);
        } catch { this.stageBg = null; }
      }
    }

    // W1 戰場可讀性:玩家戰車底光 — 柔和雙層 ellipse 墊在本體(depth 1)下,
    // 讓主角在滿場敵人/彈幕裡第一眼可辨(視覺優先級:玩家 > 一切)。
    // 位置每幀在 _syncSprites 跟車(含 idle bob)。headless 無 add.ellipse → null。
    this._playerGlow = null;
    this._playerGlowInner = null;
    try {
      this._playerGlow = this.add.ellipse(
        this.playerX, this.playerY, PLAYER_GLOW.widthPx, PLAYER_GLOW.heightPx,
        PLAYER_GLOW.color, PLAYER_GLOW.alpha,
      ).setDepth(PLAYER_GLOW.depth);
      this._playerGlowInner = this.add.ellipse(
        this.playerX, this.playerY, PLAYER_GLOW.innerWidthPx, PLAYER_GLOW.innerHeightPx,
        PLAYER_GLOW.innerColor, PLAYER_GLOW.innerAlpha,
      ).setDepth(PLAYER_GLOW.depth);
    } catch { this._playerGlow = null; this._playerGlowInner = null; }

    this.playerSprite = this.add.graphics();
    this.playerSprite.setRotation(Math.PI / 2);
    this._drawPlayer();

    // art-gen-1: attach AI-gen player tank sprite on top of the Graphics
    // fallback. Pick the player's strongest faction (or celestial when none).
    // If the texture failed to load (file missing in a stripped deployment),
    // playerImage stays null and the Graphics rectangle remains visible.
    this.playerImage = null;
    // P1:重建戰車時清掉舊配件 overlay(場景重啟/換英雄),避免指向已銷毀 graphics。
    try { this.tankAccessories?.destroy(); } catch { /* headless */ }
    this.tankAccessories = null;
    try {
      // v1.2 主人翁:玩家戰車 sprite 隨「選的英雄」(selectedHeroId) 切換,
      // 不再用 _playerFaction(strongest XP)。背景/boss 仍走 hubFactionId。
      const faction = this._heroId;
      const key = playerTankKeyFor(faction);
      // P01: prefer atlas frame so multiple tanks batch into 1 draw call.
      const render = resolveTankRender(this.textures, key);
      if (render) {
        // 側視渲染 (tank-gerand): the new Gerand cartoon tanks are SIDE-VIEW art
        // already drawn facing RIGHT (hull/cannon point right, matching the
        // player bullet vector vx > 0 and the rightward horizontal scroll). So
        // the body must NOT be rotated — rotation 0 keeps it upright facing
        // right. (Older WoT top-down PNGs faced UP and needed +PI/2; that no
        // longer applies.) The player image rotation is set once here and never
        // changed per-frame, so the side-view tank never spins to aim.
        this.playerImage = (render.mode === 'atlas'
          ? this.add.image(this.playerX, this.playerY, render.atlasKey, render.frame)
          : this.add.image(this.playerX, this.playerY, render.key))
          .setRotation(0)
          // tank-size: target TANK_SPRITE_H tall (boss「太小」2026-05-30 升到 72,
          // 「放太大了」2026-06-13 降到 56)。enemies scale 同步,player 不會比敵人大。
          .setDisplaySize(TANK_SPRITE_H, TANK_SPRITE_H)
          .setDepth(1);
        // ansimuz-sprite-skin: keep the temporary ansimuz tank's aspect ratio
        // so it isn't squashed into a square (read real frame/source dims).
        try {
          let w = 0;
          let h = 0;
          if (render.mode === 'atlas') {
            const fr = this.textures.get(render.atlasKey).get(render.frame) as
              { realWidth?: number; realHeight?: number; width?: number; height?: number } | undefined;
            w = fr?.realWidth ?? fr?.width ?? 0;
            h = fr?.realHeight ?? fr?.height ?? 0;
          } else {
            const src = this.textures.get(render.key).getSourceImage() as { width?: number; height?: number };
            w = src?.width ?? 0;
            h = src?.height ?? 0;
          }
          if (w > 0 && h > 0) this.playerImage.setDisplaySize(TANK_SPRITE_H * (w / h), TANK_SPRITE_H);
        } catch { /* headless: keep the square set above */ }
        // 記下基礎坦克的顯示框,進化換圖時用來夾住尺寸(見 _applyEvolution)。
        try {
          this._basePlayerDisplayW = this.playerImage.displayWidth || TANK_SPRITE_H;
          this._basePlayerDisplayH = this.playerImage.displayHeight || TANK_SPRITE_H;
        } catch { this._basePlayerDisplayW = TANK_SPRITE_H; this._basePlayerDisplayH = TANK_SPRITE_H; }
        this.playerSprite.setVisible(false);
        // 履帶不再程式生成:Aralepixel Sherman 側視圖自帶完整實心履帶+路輪
        // (2026-06-05 換素材),舊的 graphics 履帶補丁已移除。
      }
    } catch { /* defensive: keep Graphics fallback if anything goes wrong */ }

    // W5: smooth follow with lerp (0.1 x/y = slow, responsive feel without jitter).
    // lerpX / lerpY 0.1 means camera catches up ~10% of the gap each frame.
    // roundPixels = true prevents sub-pixel blur on pixel art.
    try {
      this.cameras.main.startFollow(
        this.playerSprite,
        true,           // roundPixels
        0.1,            // lerpX
        0.1,            // lerpY
      );
      // Constrain camera to world bounds so edges don't show black.
      // Pinned when world == viewport (800x600); lerp still active but clamped.
      this.cameras.main.setBounds(0, 0, GAME.width, GAME.height);
    } catch { /* headless test harness */ }

    this.cursors = this.input.keyboard!.createCursorKeys();
    // BUG FIX (boss「空格按不了」2026-05-30): on desktop the browser eats the
    // spacebar as page-scroll before Phaser sees it. addCapture preventDefaults
    // these keys so the ultimate (space) + movement actually register. Listing
    // arrows too keeps the page from scrolling during play. Try-guarded for the
    // headless test harness where keyboard may be a stub.
    try { this.input.keyboard!.addCapture('SPACE,UP,DOWN,LEFT,RIGHT'); } catch { /* headless */ }

    // W1 戰場可讀性:HUD 文字字級/對比調升 — 800px 畫布 FIT 進手機後縮 ~0.5,
    // 原 14-18px 變 7-9px 等效看不見。常數集中在 readability.ts 的 HUD_TEXT。
    this.hpBar = this.add.graphics().setDepth(10);
    this.hpText = this.add.text(148, GAME.height - 42, `HP: ${this._maxHp()}`, {
      fontSize: HUD_TEXT.fontPxHp + 'px', color: '#ffffff', fontFamily: 'sans-serif',
      fontStyle: 'bold', stroke: HUD_TEXT.strokeColor, strokeThickness: HUD_TEXT.strokePx,
    }).setDepth(10);

    this.waveText = this.add.text(GAME.width - 10, 64, `Wave 1 / ${this._totalWaves}`, {
      fontSize: HUD_TEXT.fontPxTop + 'px', color: '#ffd54f', fontFamily: 'sans-serif',
      fontStyle: 'bold', stroke: HUD_TEXT.strokeColor, strokeThickness: HUD_TEXT.strokePx,
    }).setOrigin(1, 0).setDepth(10);

    // Phase 2 §4-1:局內金幣 HUD (左上角)。鐵則:純局內顯示,不持久化。
    try {
      this._coinText = this.add.text(10, 10, '💰 0', {
        fontSize: HUD_TEXT.fontPxTop + 'px', color: '#ffe082', fontFamily: 'sans-serif',
        fontStyle: 'bold',
      }).setDepth(10);
      this._coinText.setStroke?.('#5d3a00', HUD_TEXT.strokePx);
    } catch { /* headless */ }

    // roguelite-xp:底部經驗條 HUD。_xpBar / _xpText 從前一直只宣告未建立 →
    // _drawXpBar() 的 `if (!this._xpBar) return` 永遠 return,經驗條 + 「⭐ 等級 N」
    // 從來沒畫出來(真 bug)。在此真正建立(座標同 _drawXpBar:底邊 x=4 /
    // y=GAME.height-16 / w=GAME.width-8 / h=14)。depth 10 = HUD 層(背景之上)。
    // scene 重建時跟其他 HUD 一樣在 create() 重新建立。鐵則:純單場視覺,0 修煉值寫入。
    this._xpBar = this.add.graphics().setDepth(10);
    // 條:y=GAME.height-16、h=14 → 垂直中心 ≈ GAME.height-9。label origin(0,0.5) 對齊。
    this._xpText = this.add.text(8, GAME.height - 9, '⭐ 等級 1', {
      fontSize: '15px', color: '#fff8e1', fontFamily: 'sans-serif', fontStyle: 'bold',
      stroke: '#5d3a00', strokeThickness: 4,
    }).setOrigin(0, 0.5).setDepth(11);

    this._drawHpBar();

    // ── Pause button (top-right, ≥48px tap target) ─────────────────────────
    const btnX = GAME.width - 36;
    const btnY = 36;
    this.pauseBtn = this.add.rectangle(btnX, btnY, 56, 56, 0x000000, 0.6)
      .setDepth(20)
      .setInteractive({ useHandCursor: true });
    this.add.text(btnX, btnY, '⏸', {
      fontSize: '28px', fontFamily: 'sans-serif',
    }).setOrigin(0.5).setDepth(21);
    this.pauseBtn.on('pointerup', () => { this._togglePause(); });

    this.keyP   = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.P);
    this.keyEsc = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);

    this._initSkillSystem(progress);

    this._exposeTestHooks();

    // C05-wire: if the home banner fired `ef:start-tutorial-boss` BEFORE this
    // scene mounted (the common path — banner is on home page), main.ts has
    // already called `requestTutorialBossSpawn()` to set the module flag.
    // Consume-and-clear it here so the very first boss spawn uses the
    // tutorial-boss def. Must run AFTER _exposeTestHooks so tests can swap
    // the flag mid-init if needed.
    if (consumeTutorialBossRequest()) {
      this._startTutorialBoss();
    }

    // boss-reachability (2026-05-31): ?boss / #boss in the page URL → skip every
    // wave and drop into the 關底 boss 戰 in one tap. Runs AFTER the tutorial
    // consume; _skipToBoss is a no-op once a boss exists. headless-safe.
    if (!this.boss && shouldSkipToBossFromUrl()) {
      // boss-reachability: ?boss=<id> 指定魔王(老闆要測特定 boss,如可學習 SHU_MO),
      // 無 id 則沿用隨機。設 override 後 _spawnBoss 會用 pickBossById 查 BOSS_POOL。
      const urlBossId = bossIdFromUrl();
      if (urlBossId) this._initBossIdOverride = urlBossId;
      this._skipToBoss();
    }
  }

  /**
   * boss-reachability: production「直接跳關底 boss」(used by the ?boss URL entry).
   * Clears in-flight grunts, fast-forwards past the last wave, spawns the boss.
   * Idempotent. NOT tutorial mode — spawns the normal 關底 boss. 鐵則:純遊戲機制。
   */
  _skipToBoss(): void {
    if (this.boss) return;
    if (this.gameOver) return;
    for (const e of this.enemies) { e.entity.alive = false; }
    this.enemies = [];
    this.currentWave = this._totalWaves;
    this.waveState = 'boss';
    this._spawnBoss();
  }

  /**
   * C05-wire: flip the scene into tutorial-boss mode AND short-circuit the
   * normal wave-then-boss loop by triggering an immediate boss spawn. Idempotent
   * (no-op if already in tutorial mode or boss already alive).
   *
   * Mirrors the __skipToBoss() test hook but is production-facing: clears
   * any in-flight enemies, sets the flag (so _spawnBoss picks BOSS_TUTORIAL_POOL),
   * then calls _spawnBoss() directly.
   */
  _startTutorialBoss(): void {
    if (this._tutorialBossMode) return;
    if (this.boss) return; // boss already spawned (regular run) — don't clobber
    if (this.gameOver) return;
    this._tutorialBossMode = true;
    // Wipe any leftover enemies/state so the kid drops straight into the boss
    // arena (no straggler grunts from a paused regular run).
    for (const e of this.enemies) { e.entity.alive = false; }
    this.enemies = [];
    // Fast-forward wave bookkeeping so update()'s _handleWaveSpawning is a no-op.
    this.currentWave = this._totalWaves;
    this.waveState = 'boss';
    this._spawnBoss();
  }

  private _togglePause(): void {
    if (this.gameOver) return;
    if (this.paused) { this._resumeGame(); } else { this._pauseGame(); }
  }

  _pauseGame(): void {
    if (this.paused || this.gameOver) return;
    this.paused = true;
    this.pauseOverlayContainer = showPauseOverlay(
      this,
      () => this._resumeGame(),
      () => {
        this.paused = false;
        this.pauseOverlayContainer = null;
        this.gameOver = true;
        // bug fix (boss 2026-06-16「中途離開回標題無法操作」):
        // 1) 先移除戰鬥觸控 HUD(左下搖桿 + 右下 技/⚡),否則 DOM overlay 殘留會攔截觸控。
        // 2) 不再 scene.start('TitleScene') —— TitleScene 已是孤兒(DOM 封面取代它,
        //    「開始冒險」dispatch 的 ef:start-game 沒人接 → 死路)。改走 ef:return-hub,
        //    經 main.ts setMode('hub') 正常落地 CampaignMapScene(= 戰鬥結束回 hub 的同路徑,
        //    setMode 內也會 unmountTouch 再清一次觸控、並修正 currentMode 記帳)。
        try { document.querySelector('.touch-controls')?.remove(); } catch { /* noop */ }
        try { window.dispatchEvent(new CustomEvent('ef:return-hub')); } catch { /* noop */ }
      },
    );
  }

  _resumeGame(): void {
    if (!this.paused) return;
    this.paused = false;
    if (this.pauseOverlayContainer) {
      this.pauseOverlayContainer.destroy();
      this.pauseOverlayContainer = null;
    }
  }

  /** Apply a BalanceConfig to all derived instance values. */
  private _applyBalanceConfig(config: BalanceConfig): void {
    this._balance         = config;
    this._playerSpeed     = config.player.move_speed;
    this._bulletVx        = config.player.bullet_vx;
    this._bulletDamage    = config.player.bullet_damage;
    this._fireIntervalMs  = config.player.fire_interval_ms;
    this._enemyVx         = config.enemies.types.find(t => t.id === 'basic')?.speedVx ?? -150;
    this._spawnIntervalMs = config.spawning.spawn_interval_ms;
    // R-004 D2:章節難度曲線 — 生成密度(每波量 × spawnDensityMult,向上取整)+
    //   波次節奏(波間隔 × waveTempoMult,越後越緊湊)。資料驅動,不 inline 倍率;
    //   倍率組由 _difficulty(章配方 × 章內梯度)提供。波數不變,只放大每波密度。
    //   防呆:某些測試以 Object.create 略過建構式 → _difficulty 可能 undefined,
    //   退回 _ensureDifficulty()(第 1 關基準,倍率全 1)。
    const diff = this._ensureDifficulty();
    const dens = diff.spawnDensityMult;
    this._waveGapMs       = Math.max(600, Math.round(config.spawning.wave_gap_ms * diff.waveTempoMult));
    this._waveSizes       = config.spawning.wave_sizes.map((n) => Math.max(1, Math.ceil(n * dens)));
    this._totalWaves      = config.spawning.wave_count;
  }

  /**
   * R-004:回傳本場有效難度;若 _difficulty 尚未設定(測試 Object.create 略過
   * 建構式 / init),以 _stage(預設 1)即時推導一份,保證永不 undefined。
   */
  private _ensureDifficulty(): EffectiveDifficulty {
    if (!this._difficulty) {
      const stage = (typeof this._stage === 'number' && this._stage >= 1) ? this._stage : 1;
      this._difficulty = effectiveDifficulty(stage, getChaptersSync().chapters);
    }
    return this._difficulty;
  }

  update(_time: number, delta: number): void {
    if (this.gameOver || this._timeLimitBlocked) return;

    if (Phaser.Input.Keyboard.JustDown(this.keyP) || Phaser.Input.Keyboard.JustDown(this.keyEsc)) {
      this._togglePause();
      return;
    }
    if (this.paused) return;
    // MERCHANT-PAUSE FIX (kid feedback 2026-05-21 01:10, 🔥🔥):
    // Prior to this fix `_merchantPaused` only short-circuited
    // `_handleWaveSpawning`, so the merchant overlay was a "fake pause" —
    // boss kept attacking, bullets kept flying, collisions kept resolving.
    // The kid got bombed mid-decision. Treat merchant overlay as a HARD pause
    // (same level as `this.paused`) so the only thing ticking is the overlay
    // itself (its own delayedCall timer survives because Phaser scene time
    // is independent of update()).
    if (this._merchantPaused) return;
    // Phase 2 §4-1:金幣商店也是 HARD pause (同 _merchantPaused),敵人/子彈/碰撞全凍。
    if (this._shopPaused) return;


    const dtSec = delta / 1000;
    const nowMs = this.time.now;

    // ── Per-minute time tracking ────────────────────────────────────────────
    const MS_PER_MINUTE = 60_000;
    if (nowMs - this._lastTimeLimitCheckMs >= MS_PER_MINUTE) {
      const minutesElapsed = Math.floor((nowMs - this._lastTimeLimitCheckMs) / MS_PER_MINUTE);
      this._lastTimeLimitCheckMs += minutesElapsed * MS_PER_MINUTE;
      const p = loadProgress();
      if (p) {
        const exceeded = recordPlayMinutes(p, minutesElapsed);
        saveProgress(p);
        if (exceeded) {
          this._timeLimitBlocked = true;
          this._showTimeLimitScreen();
          return;
        }
      }
    }

    // Roguelike 三選一強化:hard-stop the whole battle while the DOM picker is
    // up (mirrors the _merchantPaused guard). Wall-clock freeze — no timeScale=0
    // deadlock — so enemies / bullets / input all halt until the kid picks.
    if (this._pickPaused) return;

    // Allow test hook to trigger game-over by setting playerHp ≤ 0 directly
    if (this.playerHp <= 0) { this._gameOver(nowMs); return; }
    if (this.stageBg) { this.stageBg.tilePositionX += 0.5; }
    const prevY = this.playerY;
    this._firedThisFrame = false;
    // R-005:本幀 dt 給武器引擎(蓄力/追蹤/旋轉用)。rapid-cannon 不讀 dt,既有
    // 直接呼叫 _handleAutoFire 的測試(不經 update)維持預設 0,行為不變。
    this._lastDtSec = dtSec;
    this._handleInput(dtSec, nowMs);
    this._handleAutoFire(nowMs);
    this._handleSkills(dtSec, nowMs);
    // W2: tick Player FSM with this-frame input snapshot. dy derived from
    // playerY delta (handleInput already clamped within Y_MIN/MAX); firing
    // flag stamped by _spawnBullet via _firedThisFrame.
    if (this.playerFSM) {
      this.playerFSM.update(delta, {
        dx: 0,
        dy: this.playerY - prevY,
        firing: this._firedThisFrame,
      });
    }
    this._updateBullets(dtSec);
    this._handleWaveSpawning(nowMs);
    // G06-WIRE: drain async A* work (no-op in sync mode but cheap + safe) AND
    // run the 0.5s repath cycle. _maybeRepath internally throttles via
    // shouldRepath() — calling it every frame is fine. MUST happen BEFORE
    // _updateEnemies so the freshly-assigned path is followed this same frame
    // (avoids one stale frame of straight-line motion through boss bbox).
    try { this._pathfinder?.update(); } catch { /* defensive */ }
    this._maybeRepath(nowMs);
    this._updateEnemies(dtSec, nowMs);
    // v1.2 elemental 火柱:在敵人移動後 tick DoT,讓場上敵人吃持續傷害。
    this._tickFirePillars(nowMs);
    this._updateBoss(dtSec, nowMs);
    this._updateBossBullets(dtSec);
    this._tickEnemyFire(nowMs);
    this._tickMiniBossAttacks(nowMs);
    this._updateEnemyBullets(dtSec);
    this._checkCollisions(nowMs);
    // R-005 P3 (C):玩家碰到武器箱 → 當場換裝(純局內,不寫 progress)。
    this._updateWeaponBoxes();
    this._syncSprites(nowMs);
    // R-005:主武器視覺(追蹤飛彈軌跡 / 旋刃 / 軌道炮蓄力光點)。每幀重畫一張
    // Graphics(headless 安全),不影響子彈池 / 碰撞;rapid-cannon 無 FX 時近乎 no-op。
    this._drawWeaponFx(nowMs);
    this._drawHpBar();
    this._drawBossHpBar();
    // roguelite-xp: bottom XP/level bar, refreshed every frame.
    this._drawXpBar();
    // G01: tick trauma decay + apply noise-driven camera offset. Safe to call
    // every frame even when trauma=0 (early-returns + restores base scroll).
    try { this._juice?.update(dtSec, this.cameras?.main); } catch { /* test harness lacks cameras */ }
    // W5: tick parallax — offset bg + mid layers by camera scrollX fraction.
    try { this._parallax?.update(this.cameras?.main?.scrollX ?? 0); } catch { /* headless */ }
    try { this._battleParallax?.update(); } catch { /* headless */ }
  }

  private _handleInput(dtSec: number, nowMs: number): void {
    if (nowMs < this.playerFrozenUntilMs) return; // frozen
    // 2026-05-29 mobile-touch fix: synthetic KeyboardEvent doesn't reliably
    // reach Phaser KeyboardManager on real mobile (event order + pointerType
    // filters). Touch HUD writes window.__touchInput directly; OR with cursor
    // state so desktop keyboard still works.
    const touch = (window as unknown as { __touchInput?: { up: boolean; down: boolean; left: boolean; right: boolean; moveX?: number; moveY?: number; usingJoystick?: boolean } }).__touchInput;
    const up = (this.cursors.up?.isDown ?? false) || (touch?.up ?? false);
    const down = (this.cursors.down?.isDown ?? false) || (touch?.down ?? false);
    const left = (this.cursors.left?.isDown ?? false) || (touch?.left ?? false);
    const right = (this.cursors.right?.isDown ?? false) || (touch?.right ?? false);
    // Roguelike「加速」upgrade folds into _playerSpeedNow() (speedMult).
    const spd = this._playerSpeedNow();

    // 2026-06-14 老闆「左邊虛擬把手」: analog joystick. When the virtual stick is
    // being driven (usingJoystick) we move by its unit vector × speed × dt so
    // 拖曳力度 = 速度;這條優先於逐軸布林,放開搖桿(usingJoystick=false)就回到鍵盤分支。
    let joystickMoved = false;
    if (touch?.usingJoystick && ((touch.moveX ?? 0) !== 0 || (touch.moveY ?? 0) !== 0)) {
      const mvx = touch.moveX ?? 0;
      const mvy = touch.moveY ?? 0;
      this.playerX = Math.min(X_MAX, Math.max(X_MIN, this.playerX + mvx * spd * dtSec));
      this.playerY = Math.min(Y_MAX, Math.max(Y_MIN, this.playerY + mvy * spd * dtSec));
      joystickMoved = true;
    } else {
      if (up)   this.playerY = Math.max(Y_MIN, this.playerY - spd * dtSec);
      if (down) this.playerY = Math.min(Y_MAX, this.playerY + spd * dtSec);
      if (left)  this.playerX = Math.max(X_MIN, this.playerX - spd * dtSec);
      if (right) this.playerX = Math.min(X_MAX, this.playerX + spd * dtSec);
    }

    // touch-drag + 搖桿並存 (boss 2026-06-17「兩個都同時支援」):搖桿照掛,同時「按搖桿
    // 外的畫面 → 戰車朝手指跑」也生效。只要這幀沒在操搖桿(joystickMoved)且無鍵盤輸入就
    // 跑 pointer-follow;搖桿區(左下)/技能鈕區(右下)/暫停角(右上)已在 _applyPointerFollow
    // 內排除,所以操搖桿、點技能不會誤觸發移動。桌面滑鼠拖曳 fallback 同樣走這條。
    let pointerMoved = false;
    if (!up && !down && !left && !right && !joystickMoved) {
      pointerMoved = this._applyPointerFollow(dtSec, spd);
    }

    // R4 FIX: emit a small cyan thruster trail behind the player when moving.
    // Throttled so we don't flood the display list.
    if ((up || down || left || right || joystickMoved || pointerMoved) && nowMs - this._lastTrailEmitMs >= HorizontalBattleScene.TRAIL_EMIT_INTERVAL_MS) {
      this._lastTrailEmitMs = nowMs;
      this._emitTrailParticle(this.playerX - 16, this.playerY);
    }
  }

  /**
   * touch-drag: while the active pointer is held down, move the tank toward the
   * finger/cursor at the normal move speed (NOT a teleport). Returns true if the
   * tank actually moved this frame (so the caller can emit a thruster trail).
   *
   * Guards:
   *  - frozen / _pickPaused are already handled before _handleInput runs, but we
   *    re-check pointer existence defensively for the headless test harness.
   *  - A press inside the top-right pause-button corner is ignored so it doesn't
   *    steal the pause tap (the pauseBtn's own pointerup still fires).
   *  - worldX/worldY map through the camera; fall back to x/y if unavailable.
   *  - Result is clamped to the arena bounds (X_MIN/X_MAX/Y_MIN/Y_MAX).
   * headless-safe: optional chaining + try/catch, never throws in tests.
   */
  private _applyPointerFollow(dtSec: number, spd: number): boolean {
    try {
      const pointer = this.input?.activePointer;
      if (!pointer || !pointer.isDown) return false;

      // Don't steal the pause button: if the press STARTED inside the top-right
      // corner it's a pause tap, not a move (the pauseBtn's own pointerup fires).
      // downX/downY is the press origin in screen space; if it began in the
      // corner we skip move for the whole gesture so a drag that wanders through
      // the corner mid-move doesn't snap-cancel.
      const downX = Number.isFinite(pointer.downX) ? pointer.downX : pointer.x;
      const downY = Number.isFinite(pointer.downY) ? pointer.downY : pointer.y;
      if (
        typeof downX === 'number' && typeof downY === 'number' &&
        downX > GAME.width - TOUCH_PAUSE_CORNER && downY < TOUCH_PAUSE_CORNER
      ) {
        return false;
      }
      // boss 2026-06-17 搖桿+觸碰並存:排除左下搖桿區與右下技能鈕區,免得「操搖桿/點技能」
      // 的觸控也被當成 pointer-follow 把戰車拉過去。手勢從這些角落開始就整段不移動。
      if (typeof downX === 'number' && typeof downY === 'number') {
        const inJoystickZone = downX < TOUCH_JOYSTICK_ZONE && downY > GAME.height - TOUCH_JOYSTICK_ZONE;
        const inSkillZone = downX > GAME.width - TOUCH_SKILL_ZONE_W && downY > GAME.height - TOUCH_SKILL_ZONE_H;
        if (inJoystickZone || inSkillZone) return false;
      }

      // worldX/worldY account for camera scroll/zoom; fall back to raw x/y.
      const tx = (typeof pointer.worldX === 'number' && Number.isFinite(pointer.worldX))
        ? pointer.worldX : pointer.x;
      const ty = (typeof pointer.worldY === 'number' && Number.isFinite(pointer.worldY))
        ? pointer.worldY : pointer.y;
      if (typeof tx !== 'number' || typeof ty !== 'number') return false;

      const maxStep = spd * dtSec;
      const next = stepToward(
        this.playerX, this.playerY, tx, ty, maxStep, TOUCH_FOLLOW_DEADZONE,
      );
      const nx = Math.min(X_MAX, Math.max(X_MIN, next.x));
      const ny = Math.min(Y_MAX, Math.max(Y_MIN, next.y));
      const moved = nx !== this.playerX || ny !== this.playerY;
      this.playerX = nx;
      this.playerY = ny;
      return moved;
    } catch {
      return false; // headless / mock — never throw from input handling
    }
  }

  /**
   * R4 FIX: spawn a single 3px cyan trail particle behind the player that
   * fades to alpha 0 over 300ms then auto-destroys.
   */
  private _emitTrailParticle(x: number, y: number): void {
    this._trailParticleCount++;
    try {
      const dot = this.add.circle(x, y, 3, 0x80cbc4, 0.7).setDepth(0);
      this.tweens.add({
        targets: dot,
        alpha: 0,
        scale: 0.3,
        duration: 300,
        onComplete: () => { dot.destroy(); },
      });
    } catch { /* test harness: no add.circle / tweens */ }
  }

  /**
   * R3 FIX: when an enemy dies, play 'explode' SFX and burst 6 orange
   * particles that fade out over 350ms. Try/catch keeps tests safe.
   */
  private _emitEnemyDeathFx(x: number, y: number): void {
    this._particleCount += 6;
    if (this._sfx) {
      // W1 連擊升調: 2 秒窗口內連續擊殺 → 擊殺音逐次 +3%、封頂 +15%,
      // 窗口斷了回 1.0。只動 playbackRate,音量/掉落/XP 完全不變。
      let rate = 1;
      try { rate = this._killCombo.onKill(this.time?.now ?? Date.now()); } catch { rate = 1; }
      try { this._sfx.play('explode', 0.5, { rate }); } catch { /* AudioContext busy */ }
    }
    // Asset Pipeline B: real CC0 pixel-explosion spritesheet, faction-tinted to
    // the player's 門派 color for a unified palette. No-op (returns false) when
    // the sheet texture isn't loaded — the circle burst below still plays.
    try { playEnemyExplosion(this, x, y, this._playerHitColor()); } catch { /* headless */ }
    try {
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI * 2 * i) / 6 + Math.random() * 0.4;
        const dist = 18 + Math.random() * 14;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        const p = this.add.circle(x, y, 3, 0xffb74d, 0.9).setDepth(5);
        this.tweens.add({
          targets: p,
          x: x + dx,
          y: y + dy,
          alpha: 0,
          scale: 0.4,
          duration: 350,
          ease: 'Cubic.easeOut',
          onComplete: () => { p.destroy(); },
        });
      }
    } catch { /* test harness: no add.circle / tweens */ }
  }

  /**
   * R-005 主武器系統:auto-fire 委派層。每幀把 scene 能力包成 WeaponTickContext
   * 傳給裝備武器的 `tick()`,由武器自己決定「何時 spawn 什麼 / 怎麼打」。
   *
   * 保真鐵則(最高風險):預設 rapid-cannon 的 tick 直接呼叫 `ctx.runDefaultAutoFire`
   * → `_handleDefaultAutoFire`(下方原封不動的固定間隔自動直射),所以預設武器行為
   * **byte-identical**,既有戰鬥測試與手感零變動。silenced 守衛維持在最外層
   * (與今日一致:被沉默時任何武器都不開火)。
   */
  private _handleAutoFire(nowMs: number): void {
    if (nowMs < this.playerSilencedUntilMs) return; // silenced — 任何武器都不開火
    const ctx = this._buildWeaponTickContext(nowMs);
    this._equippedWeapon.tick(ctx);
  }

  /**
   * 既有「固定間隔自動直射」邏輯,原封不動搬進獨立方法供 rapid-cannon 委派回呼。
   * 這段就是 rapid-cannon 的本體 —— 拆出來只是讓 behavior 能呼叫它而不改任何行為。
   */
  private _handleDefaultAutoFire(nowMs: number): void {
    // v1.2 techno 提速:偵察 buff 期間 auto-fire 間隔縮短。
    // Roguelike「連射」upgrade: autoFireMult < 1 shortens the interval further.
    // synergy 疾風連打 (連射+加速):synergyFireMult<1 時間隔再縮短 (跑起來射更快)。
    // 預設 1 = 無 synergy,行為同今日。
    // 平衡 (boss 2026-06-13):射速倍率走 effectiveAutoFireMult() 下限 clamp,
    // 杜絕連射卡乘法疊到無限快 (最快約 2.5× 射速)。synergy 倍率另算 (不在此 clamp)。
    const interval = this._playstyleState().effectiveFireIntervalMs(nowMs, this._fireIntervalMs)
      * this._runModifiers().effectiveAutoFireMult()
      * this._runModifiers().synergyFireMult;
    if (nowMs - this.lastFireMs >= interval) {
      this.lastFireMs = nowMs;
      this._spawnBullet();
    }
  }

  /**
   * 換上一把武器後的一次性 scene 設定:歸零雷射過熱視覺,並把 mortar 落點右緣上限
   * 設成實際畫面寬留白(榴彈不飛出畫面外炸不到)。不寫 progress / 不碰修煉值。
   */
  private _onWeaponEquipped(): void {
    this._weaponHeatRatio = 0;
    this._weaponOverheated = false;
    const w = this._equippedWeapon;
    if (w instanceof MortarBehavior) {
      w.setMaxLandingX(GAME.width - 48);
    }
  }

  // ── R-005 P3 (C) 戰場武器箱(單場掉落 + 撿到當場換裝)──────────────────────────

  /** 稀有度 → 顯示色(common 灰 / rare 藍 / epic 紫)。武器箱外框 + 撿取提示用。 */
  private static readonly _RARITY_COLOR: Readonly<Record<WeaponRarity, number>> = {
    common: 0xb0bec5,
    rare: 0x42a5f5,
    epic: 0xab47bc,
  };

  /**
   * 在 (x,y) 落一個武器箱 pickup(箱內 = weaponId)。純局內視覺 + 記錄,玩家碰到即換裝。
   * headless(無 this.add)安全:視覺建失敗仍記座標,collision 仍可判定(e2e/單測友善)。
   */
  private _spawnWeaponBox(x: number, y: number, weaponId: string): void {
    const rarity = weaponRarity(weaponId);
    const color = HorizontalBattleScene._RARITY_COLOR[rarity];
    const emoji = weaponMetaById(weaponId)?.emoji ?? '📦';
    let sprite: Phaser.GameObjects.GameObject | null = null;
    let label: Phaser.GameObjects.GameObject | null = null;
    try {
      // 外框(稀有度色)+ emoji,讓孩子一眼看出「這是武器、什麼稀有度」。
      sprite = this.add.rectangle(x, y, 30, 30, color, 0.28)
        .setStrokeStyle(3, color, 1)
        .setDepth(6);
      label = this.add.text(x, y, emoji, { fontSize: '20px' }).setOrigin(0.5).setDepth(7);
    } catch { /* headless — 無 this.add,純記座標即可 */ }
    this._weaponBoxes.push({ x, y, weaponId, rarity, sprite, label });
  }

  /**
   * 每幀檢查玩家是否碰到武器箱;碰到 → 撿起(當場換裝 + 提示 + 移除箱子)。
   * 鐵則:只改 _equippedWeapon(單場),**不**寫 progress.selectedWeapon / 任何戰力欄位。
   */
  private _updateWeaponBoxes(): void {
    if (this._weaponBoxes.length === 0) return;
    const PICKUP_R = 34; // 玩家中心到箱子中心 ≤ 34px 視為撿到(K-3 友善,寬容半徑)。
    const r2 = PICKUP_R * PICKUP_R;
    const remaining: typeof this._weaponBoxes = [];
    for (const box of this._weaponBoxes) {
      const dx = box.x - this.playerX;
      const dy = box.y - this.playerY;
      if (dx * dx + dy * dy <= r2) {
        this._pickupWeaponBox(box);
      } else {
        remaining.push(box);
      }
    }
    this._weaponBoxes = remaining;
  }

  /** 撿起一個武器箱:當場換裝 + 飄字提示 + 銷毀箱子視覺。 */
  private _pickupWeaponBox(box: { weaponId: string; rarity: WeaponRarity; sprite: Phaser.GameObjects.GameObject | null; label: Phaser.GameObjects.GameObject | null }): void {
    this._equipWeaponForRun(box.weaponId);
    const name = weaponMetaById(box.weaponId)?.displayName ?? '主武器';
    const color = HorizontalBattleScene._RARITY_COLOR[box.rarity];
    try {
      const toast = this.add.text(this.playerX, this.playerY - 40, `🔫 換上 ${name}!`, {
        fontSize: '16px', color: `#${color.toString(16).padStart(6, '0')}`, fontFamily: 'sans-serif', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(30);
      this.tweens?.add({ targets: toast, y: this.playerY - 80, alpha: 0, duration: 900, onComplete: () => { try { toast.destroy(); } catch { /* noop */ } } });
    } catch { /* headless */ }
    try { box.sprite?.destroy(); } catch { /* noop */ }
    try { box.label?.destroy(); } catch { /* noop */ }
  }

  /**
   * R-005 P3 (C):單場換裝一把行為主武器(不寫 progress)。換新 behavior + reset() +
   * 重置武器視覺狀態,等價 __equipWeapon dev hook,但走擊殺掉落觸發。
   * 鐵則:純局內,**絕不**寫 selectedWeapon / unlockedWeapons / 任何戰力欄位。
   */
  private _equipWeaponForRun(weaponId: string): void {
    this._equippedWeapon = resolveWeaponBehavior(weaponId, [weaponId]);
    this._equippedWeapon.reset();
    this._weaponChargeRatio = 0;
    this._bladeAngleRad = 0;
    this._onWeaponEquipped();
  }

  /**
   * 把 scene 的開火能力包成 WeaponTickContext(函式 seam),讓武器 behavior 複用既有
   * 子彈池 / 敵人集合 / RunModifiers,不重造任何系統。武器威力讀武器等級曲線
   * (weaponLevels[id]),**絕不讀修煉值**(鐵則放寬,boss 2026-06-13)。
   */
  private _buildWeaponTickContext(nowMs: number): WeaponTickContext {
    const self = this;
    const ctx: RapidCannonContext = {
      dt: this._lastDtSec,
      nowMs,
      playerX: this.playerX,
      playerY: this.playerY,
      mods: this._runModifiers(),
      weaponLevel: this._equippedWeaponLevel(),
      // 直射一發 → 複用既有 _spawnOneBullet(主彈/散彈共用路徑,含彈跳/穿透 seed)。
      spawnBullet: (dmg, vy, sizeMult) => { self._spawnOneBullet(dmg, vy, sizeMult); },
      baseBulletDamage: () => self._effectiveBulletDamage(nowMs),
      enemies: () => self._weaponTargets(),
      // railgun 光束:對玩家 y ± halfHeight 內全敵人施傷 + 畫一道光束 FX。
      fireBeam: (centerY, halfHeight, damage) => { self._fireWeaponBeam(centerY, halfHeight, damage, nowMs); },
      // laser-beam 光束(boss 2026-06-17 自動鎖定):取第 beamIndex 近的前方敵人當瞄準方向,
      // 從砲口沿該方向射一道光束,對沿線 halfHeight 內全敵人施(低)傷 + 畫指向性光束 FX。
      fireLaserAimed: (beamIndex, halfHeight, damage) => { self._fireWeaponLaserAimed(beamIndex, halfHeight, damage, nowMs); },
      // mortar 榴彈:落地時對落點半徑內全敵人施 AoE 傷 + 畫爆炸 FX。
      fireMortar: (lx, ly, radius, damage) => { self._fireWeaponMortar(lx, ly, radius, damage, nowMs); },
      setBladeAngle: (a) => { self._bladeAngleRad = a; },
      setChargeRatio: (r) => { self._weaponChargeRatio = r; },
      setHeatRatio: (r, overheated) => { self._weaponHeatRatio = r; self._weaponOverheated = overheated; },
      // rapid-cannon 保真 seam:跑一次原本的固定間隔自動直射(行為不變)。
      runDefaultAutoFire: () => { self._handleDefaultAutoFire(nowMs); },
    };
    return ctx;
  }

  /** 本場裝備武器的等級(weaponLevels[id],缺省 Lv1)。讀檔失敗 → 1。不讀修煉值。 */
  private _equippedWeaponLevel(): number {
    try {
      const p = loadProgress();
      const lv = p?.weaponLevels?.[this._equippedWeapon.id];
      return typeof lv === 'number' && lv >= 1 ? Math.floor(lv) : 1;
    } catch {
      return 1;
    }
  }

  /**
   * 武器引擎用的「可命中/可追蹤目標」集合:活著的一般敵人 + 活著的 boss,包成
   * WeaponTargetInfo(x/y/alive/takeDamage)。takeDamage 走各實體既有 API,死亡的
   * 一般敵由 scene 既有 kill flow 在碰撞階段結算(此處只施傷)。
   */
  private _weaponTargets(): WeaponTargetInfo[] {
    const out: WeaponTargetInfo[] = [];
    for (const e of this.enemies) {
      if (!e.entity.alive) continue;
      const ent = e.entity;
      out.push({
        x: ent.x,
        y: ent.y,
        alive: ent.alive,
        // bugfix (boss 2026-06-14「追蹤飛彈都沒有經驗」):homing-missile / spin-blade 等
        // 直接呼 ctx.enemies().takeDamage 殺敵,先前沒走 kill flow → 不進經驗條、升不了級。
        // 改成偵測 alive→dead 時走「與雷射/榴彈/子彈同一個」結算(_onRegularEnemyKilled
        // 內含 _onEnemyKilledXp),冪等不雙計(已死的 wasAlive=false 不重複觸發)。
        takeDamage: (amt) => {
          const wasAlive = ent.alive;
          ent.takeDamage(amt);
          if (wasAlive && !ent.alive) this._onRegularEnemyKilled(e, this.time?.now ?? 0);
        },
      });
    }
    if (this.boss && this.boss.alive && !this._bossShieldActive) {
      const b = this.boss;
      out.push({
        x: b.x,
        y: b.y,
        alive: b.alive,
        takeDamage: (amt) => { b.takeDamage(amt); },
      });
    }
    return out;
  }

  /**
   * railgun 光束施工:對玩家 y ± halfHeight 內所有活敵人(+ boss)施一次高傷,並畫
   * 一道貫穿全寬的光束 FX。複用既有 takeDamage / 死亡結算(一般敵在下一幀碰撞掃描
   * 已死的會被 kill flow 收掉)。鐵則:傷害值來自武器,不碰 progress/修煉值。
   */
  private _fireWeaponBeam(centerY: number, halfHeight: number, damage: number, nowMs: number): void {
    // boss 2026-06-17「軌道砲只有戰車前面才有砲擊跟傷害」:戰車在左、面朝右,只打玩家右側
    // (前方)的敵人;x < playerX 的敵人在戰車後方,前射砲擊不該打到(光束也只從砲口往右畫)。
    for (const e of this.enemies) {
      if (!e.entity.alive) continue;
      if (e.entity.x >= this.playerX && Math.abs(e.entity.y - centerY) <= halfHeight + 16) {
        e.entity.takeDamage(damage);
        try {
          const hitColor = this._playerHitColor();
          this._flashEnemy(e, hitColor);
          spawnDamageNumber(this, e.entity.x, e.entity.y - 20, damage, { kind: 'normal', faction: this._playerFaction });
        } catch { /* test harness */ }
        if (!e.entity.alive) this._onRegularEnemyKilled(e, nowMs);
      }
    }
    if (this.boss && this.boss.alive && !this._bossShieldActive
      && this.boss.x >= this.playerX
      && Math.abs(this.boss.y - centerY) <= halfHeight + (this.boss.def.size ?? 48) / 2) {
      this.boss.takeDamage(damage);
      try {
        const c = this._playerHitColor();
        this._flashBoss(c);
        spawnDamageNumber(this, this.boss.x, this.boss.y - 30, damage, { kind: 'normal', faction: this._playerFaction });
      } catch { /* test harness */ }
    }
    // 軌道砲 FX (boss 2026-06-17「雷射跟軌道砲太像」徹底區隔)。軌道砲 = 電磁瞬發貫穿彈,
    // 跟雷射(青色持續流動光束 + 灼燒接觸點)用三招分家:
    //   ① 紫電配色(雷射是青色)② 極細「啪」一條瞬閃就消(雷射是常駐帶)
    //   ③ 砲口爆閃環 + 放射電花 + 較重震動,像實實在在開了一砲(雷射是溫和持續燒)。
    try {
      const ADD = Phaser.BlendModes.ADD;
      const RG = 0xb388ff;      // 紫電 — 跟雷射青徹底分開的識別色
      const RG_HOT = 0xe1b6ff;
      // ② 紫電軌道線:極細核心 + 薄紫光暈,從砲口往右瞬間出現 → 快速收掉(非持續帶)。
      // boss 2026-06-17「只有戰車前面才有砲擊」:光束起點 = 砲口 mx,只畫到右緣,不畫戰車後方。
      const mx = this.playerX + 24;
      const beamW = Math.max(0, GAME.width - mx);
      const beamCX = mx + beamW / 2;
      const glow = this.add.rectangle(beamCX, centerY, beamW, 10, RG, 0.20).setDepth(7);
      const core = this.add.rectangle(beamCX, centerY, beamW, 2.5, 0xffffff, 0.95).setDepth(8);
      for (const o of [glow, core]) { try { (o as { setBlendMode?: (m: number) => void }).setBlendMode?.(ADD); } catch { /* headless */ } }
      this.tweens.add({ targets: core, alpha: 0, duration: 150, ease: 'Cubic.easeOut', onComplete: () => core.destroy() });
      this.tweens.add({ targets: glow, alpha: 0, scaleY: 0.25, duration: 230, ease: 'Cubic.easeOut', onComplete: () => glow.destroy() });
      // ③ 砲口爆閃:擴張光環 + 白核閃 + 放射電花(扇向右),賣「開了一砲」的重量感。
      const ring = this.add.circle(mx, centerY, 9, RG_HOT, 0.85).setDepth(8);
      const burst = this.add.circle(mx, centerY, 13, 0xffffff, 0.9).setDepth(9);
      const spk = this.add.graphics().setDepth(8);
      for (const o of [ring, burst, spk]) { try { (o as { setBlendMode?: (m: number) => void }).setBlendMode?.(ADD); } catch { /* headless */ } }
      for (let i = 0; i < 6; i++) {
        const ang = (-46 + i * 18) * Math.PI / 180;
        const len = 13 + (i % 3) * 9;
        spk.lineStyle(2, i % 2 ? RG_HOT : 0xffffff, 0.9);
        spk.beginPath(); spk.moveTo(mx, centerY);
        spk.lineTo(mx + Math.cos(ang) * len, centerY + Math.sin(ang) * len); spk.strokePath();
      }
      this.tweens.add({ targets: ring, scale: 2.8, alpha: 0, duration: 220, ease: 'Cubic.easeOut', onComplete: () => ring.destroy() });
      this.tweens.add({ targets: burst, scale: 0.2, alpha: 0, duration: 130, ease: 'Quad.easeOut', onComplete: () => burst.destroy() });
      this.tweens.add({ targets: spk, alpha: 0, duration: 200, ease: 'Cubic.easeOut', onComplete: () => spk.destroy() });
    } catch { /* test harness: no add.rectangle / tweens */ }
    this._juiceLayer?.screenShake(0.016, 120); // 軌道砲震得明顯比雷射重(單發重砲一擊)
  }

  /**
   * laser-beam 自動鎖定光束施工(boss 2026-06-17 重新設計)。取「第 beamIndex 近的前方敵人」
   * 當瞄準方向,從砲口 (playerX+22, playerY) 沿該方向射一道光束(延伸到畫面外),對沿線
   * halfHeight 內所有活敵人(+ boss)施(低)傷,並畫一道指向性青色光束 + 鎖定敵人身上的燒灼球。
   * 跟 railgun(_fireWeaponBeam:橫向蓄力一發貫穿)徹底不同:會瞄準、每幀畫、傷低、燒接觸點。
   * damage=0(過熱前的節流幀)時只畫光束不施傷。鐵則:傷害值來自武器,不碰 progress / 修煉值。
   */
  private _fireWeaponLaserAimed(beamIndex: number, halfHeight: number, damage: number, nowMs: number): void {
    const LEN = 1100; // 夠長,從砲口射穿出畫面(800×600 對角 ~1000)
    const mx = this.playerX + 22; const my = this.playerY;
    // 取前方活敵人按離砲口距離排序,挑第 beamIndex 近的當瞄準方向。
    const sorted = this._laserTargetsInFront(mx, my);
    let dirX = 1; let dirY = 0;                 // 預設:無敵人 → 直射右
    let tgtX = -1; let tgtY = my; let hasTarget = false;
    if (sorted.length > beamIndex) {
      const t = sorted[beamIndex];
      const dx = t.x - mx; const dy = t.y - my; const d = Math.hypot(dx, dy) || 1;
      dirX = dx / d; dirY = dy / d; tgtX = t.x; tgtY = t.y; hasTarget = true;
    } else if (beamIndex > 0) {
      return; // 沒有第 beamIndex 個敵人 → 不畫多餘直射(只有主光束在無敵人時會直射)
    }
    const nDirX = -dirY; const nDirY = dirX;   // 垂直瞄準方向的單位法線(算側向距離)

    // 施傷:沿瞄準射線 proj∈(0,LEN]、側向 |perp| <= halfHeight 的敵人都燒到。
    if (damage > 0) {
      for (const e of this.enemies) {
        if (!e.entity.alive) continue;
        const vx = e.entity.x - mx; const vy = e.entity.y - my;
        const proj = vx * dirX + vy * dirY;
        if (proj <= 0 || proj > LEN) continue;
        if (Math.abs(vx * nDirX + vy * nDirY) > halfHeight) continue;
        e.entity.takeDamage(damage);
        try {
          this._flashEnemy(e, this._playerHitColor());
          spawnDamageNumber(this, e.entity.x, e.entity.y - 20, damage, { kind: 'normal', faction: this._playerFaction });
        } catch { /* test harness */ }
        if (!e.entity.alive) this._onRegularEnemyKilled(e, nowMs);
      }
      if (this.boss && this.boss.alive && !this._bossShieldActive) {
        const vx = this.boss.x - mx; const vy = this.boss.y - my;
        const proj = vx * dirX + vy * dirY;
        if (proj > 0 && proj <= LEN && Math.abs(vx * nDirX + vy * nDirY) <= halfHeight + (this.boss.def.size ?? 48) / 2) {
          this.boss.takeDamage(damage);
          try { this._flashBoss(this._playerHitColor()); } catch { /* test harness */ }
        }
      }
    }

    // 雷射 FX:青色「指向性光束」—— 從砲口沿瞄準角畫一條(旋轉到 angle),核心呼吸脈動,
    // 鎖定的敵人身上冒燒灼球。跟軌道砲(紫電橫向瞬閃 + 砲口爆閃)徹底分家。
    try {
      const ADD = Phaser.BlendModes.ADD;
      const angle = Math.atan2(dirY, dirX);
      const cx = mx + dirX * LEN / 2; const cy = my + dirY * LEN / 2;
      const color = this._weaponOverheated ? 0xff5252 : 0x29ffe3;
      const coreColor = this._weaponOverheated ? 0xffd180 : 0x9bfff3;
      const pulse = 0.72 + 0.28 * Math.sin(nowMs * 0.02);
      const glow = this.add.rectangle(cx, cy, LEN, halfHeight * 2, color, this._weaponOverheated ? 0.15 : 0.18).setDepth(6);
      const core = this.add.rectangle(cx, cy, LEN, 3, coreColor, (this._weaponOverheated ? 0.26 : 0.42) * pulse).setDepth(7);
      for (const o of [glow, core]) {
        try { (o as { setRotation?: (r: number) => void }).setRotation?.(angle); } catch { /* headless */ }
        try { (o as { setBlendMode?: (m: number) => void }).setBlendMode?.(ADD); } catch { /* headless */ }
      }
      this.tweens.add({ targets: [glow, core], alpha: 0, duration: 80, ease: 'Linear', onComplete: () => { glow.destroy(); core.destroy(); } });
      if (beamIndex === 0) {
        // 砲口集束微光(只主光束畫一次,省物件)。
        const muzzle = this.add.circle(mx, my, 6 * pulse, coreColor, 0.85).setDepth(8);
        try { (muzzle as { setBlendMode?: (m: number) => void }).setBlendMode?.(ADD); } catch { /* headless */ }
        this.tweens.add({ targets: muzzle, alpha: 0, duration: 80, ease: 'Linear', onComplete: () => muzzle.destroy() });
      }
      // 燒灼接觸球:鎖定的敵人身上冒高熱光球 + 白火花(只施傷幀畫,自然節流)。
      if (hasTarget && damage > 0) {
        const burn = this.add.circle(tgtX, tgtY, 9 * pulse, this._weaponOverheated ? 0xffb74d : 0xbafff5, 0.85).setDepth(8);
        const spark = this.add.circle(tgtX, tgtY, 4, 0xffffff, 0.9).setDepth(9);
        for (const o of [burn, spark]) { try { (o as { setBlendMode?: (m: number) => void }).setBlendMode?.(ADD); } catch { /* headless */ } }
        this.tweens.add({ targets: [burn, spark], scale: 1.7, alpha: 0, duration: 160, ease: 'Cubic.easeOut', onComplete: () => { burn.destroy(); spark.destroy(); } });
      }
    } catch { /* test harness: no add.rectangle / tweens */ }
  }

  /** 前方(x>=mx)活敵人(含 boss)按離砲口距離由近到遠排序的座標清單(雷射自動瞄準用)。 */
  private _laserTargetsInFront(mx: number, my: number): Array<{ x: number; y: number }> {
    const arr: Array<{ x: number; y: number; d: number }> = [];
    for (const e of this.enemies) {
      if (!e.entity.alive || e.entity.x < mx) continue;
      const dx = e.entity.x - mx; const dy = e.entity.y - my;
      arr.push({ x: e.entity.x, y: e.entity.y, d: dx * dx + dy * dy });
    }
    if (this.boss && this.boss.alive && !this._bossShieldActive && this.boss.x >= mx) {
      const dx = this.boss.x - mx; const dy = this.boss.y - my;
      arr.push({ x: this.boss.x, y: this.boss.y, d: dx * dx + dy * dy });
    }
    arr.sort((a, b) => a.d - b.d);
    return arr;
  }

  /**
   * mortar 榴彈落地 AoE 施工:對落點 (lx,ly) 半徑內所有活敵人(+ boss)施同傷,並畫
   * 爆炸圈 FX。複用既有 takeDamage / 死亡結算。鐵則:傷害值來自武器,不碰 progress / 修煉值。
   */
  private _fireWeaponMortar(lx: number, ly: number, radius: number, damage: number, nowMs: number): void {
    const r2 = radius * radius;
    for (const e of this.enemies) {
      if (!e.entity.alive) continue;
      const dx = e.entity.x - lx;
      const dy = e.entity.y - ly;
      if (dx * dx + dy * dy <= r2) {
        e.entity.takeDamage(damage);
        try {
          const hitColor = this._playerHitColor();
          this._flashEnemy(e, hitColor);
          spawnDamageNumber(this, e.entity.x, e.entity.y - 20, damage, { kind: 'normal', faction: this._playerFaction });
        } catch { /* test harness */ }
        if (!e.entity.alive) this._onRegularEnemyKilled(e, nowMs);
      }
    }
    if (this.boss && this.boss.alive && !this._bossShieldActive) {
      const bdx = this.boss.x - lx;
      const bdy = this.boss.y - ly;
      const reach = radius + (this.boss.def.size ?? 48) / 2;
      if (bdx * bdx + bdy * bdy <= reach * reach) {
        this.boss.takeDamage(damage);
        try {
          this._flashBoss(this._playerHitColor());
          spawnDamageNumber(this, this.boss.x, this.boss.y - 30, damage, { kind: 'normal', faction: this._playerFaction });
        } catch { /* test harness */ }
      }
    }
    // 爆炸 FX:落點一圈擴張光圈(headless / 無 add 時略過)。
    try {
      const blast = this.add.circle(lx, ly, radius, 0xffb300, 0.5).setDepth(6);
      try { blast.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      this.tweens.add({
        targets: blast,
        scale: 1.4,
        alpha: 0,
        duration: 280,
        ease: 'Cubic.easeOut',
        onComplete: () => blast.destroy(),
      });
    } catch { /* test harness: no add.circle / tweens */ }
    this._juiceLayer?.screenShake(0.01, 90);
  }

  /**
   * R-005 主武器視覺。每幀清空一張 Graphics 重畫:
   *   - homing-missile:在飛的每發飛彈畫一個小火箭點 + 短尾。
   *   - spin-blade:玩家周圍兩片旋轉刀刃(用 _bladeAngleRad)。
   *   - railgun:蓄力中在砲口畫蓄力光點 + 蓄力比例的弧(用 _weaponChargeRatio)。
   *   - laser-beam:玩家 y 那條 lane 的掃描導引線 + 過熱條(用 _weaponHeatRatio/_weaponOverheated)。
   *   - mortar:在飛砲彈的拋物軌跡 + 砲彈體 + 落點預警圈(用 behavior.shells()/arcOffsetY)。
   * 純美術,不碰子彈池 / 碰撞 / 傷害;headless(無 add.graphics)整段 try 包起略過。
   */
  private _drawWeaponFx(_nowMs: number): void {
    let g = this._weaponFxGfx;
    try {
      if (!g) { g = this.add.graphics().setDepth(7); this._weaponFxGfx = g; }
      g.clear();
    } catch { return; /* headless: no add.graphics */ }
    const w = this._equippedWeapon;
    try {
      if (w instanceof HomingMissileBehavior) {
        for (const m of w.missiles()) {
          this._drawRocket(g, m.x, m.y, Math.atan2(m.vy, m.vx));
        }
        // 命中爆炸:消費 behavior 本幀記下的命中事件(每筆生一次短命爆炸 FX)。
        // 純美術 — 命中/傷害早已在 behavior tick 完成,這裡只是把它「畫出來」。
        for (const imp of w.drainImpacts()) {
          this._spawnMissileExplosion(imp.x, imp.y, imp.radius);
        }
      } else if (w instanceof SpinBladeBehavior) {
        const a = this._bladeAngleRad;
        for (const off of [0, Math.PI]) {
          const bx = this.playerX + Math.cos(a + off) * SPIN_BLADE_ORBIT_RADIUS;
          const by = this.playerY + Math.sin(a + off) * SPIN_BLADE_ORBIT_RADIUS;
          g.fillStyle(0x90caf9, 0.9);
          g.fillCircle(bx, by, 7);
          g.fillStyle(0xffffff, 1);
          g.fillCircle(bx, by, 2.5);
        }
        // 環帶提示圈(淡)
        g.lineStyle(2, 0x64b5f6, 0.25);
        g.strokeCircle(this.playerX, this.playerY, SPIN_BLADE_ORBIT_RADIUS);
      } else if (w instanceof RailgunBehavior) {
        const ratio = this._weaponChargeRatio;
        if (ratio > 0) {
          const cx = this.playerX + 34;
          const cy = this.playerY;
          // 蓄力光點:越蓄越大越亮。紫電配色(跟雷射青徹底分家,蓄力時就看得出「這是軌道砲」)。
          g.fillStyle(0xb388ff, 0.35 + 0.5 * ratio);
          g.fillCircle(cx, cy, 4 + 8 * ratio);
          g.fillStyle(0xffffff, ratio);
          g.fillCircle(cx, cy, 2 + 3 * ratio);
          // 蓄力弧(0..滿)
          g.lineStyle(3, 0xb388ff, 0.8);
          g.beginPath();
          g.arc(cx, cy, 16, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * ratio);
          g.strokePath();
        }
      } else if (w instanceof LaserBeamBehavior) {
        // 雷射改為自動鎖定光束(光束本體在 _fireWeaponLaserAimed 每幀畫),這裡只畫過熱條。
        const hot = this._weaponOverheated;
        // 過熱條:玩家上方一截橫條,熱量越高越長/越紅(過熱閃紅)。
        const ratio = this._weaponHeatRatio;
        const barW = 40;
        const bx = this.playerX - barW / 2;
        const by = this.playerY - 40;
        g.fillStyle(0x000000, 0.4);
        g.fillRect(bx, by, barW, 5);
        g.fillStyle(hot ? 0xff5252 : 0xffd54f, hot ? 1 : 0.9);
        g.fillRect(bx, by, barW * ratio, 5);
      } else if (w instanceof MortarBehavior) {
        // 飛行中的每發砲彈:沿拋物線畫軌跡虛點 + 砲彈體 + 落點預警圈。
        for (const s of w.shells()) {
          const span = s.landingX - s.originX;
          // 軌跡(沿 t 取數點,加上拱位移)
          g.fillStyle(0xffd54f, 0.35);
          for (let k = 1; k <= 6; k++) {
            const tt = k / 7;
            const px = s.originX + span * tt;
            const py = s.originY + (s.landingY - s.originY) * tt + w.arcOffsetY(tt);
            g.fillCircle(px, py, 2);
          }
          // 砲彈體(目前飛行位置)
          const px = s.originX + span * s.t;
          const py = s.originY + (s.landingY - s.originY) * s.t + w.arcOffsetY(s.t);
          g.fillStyle(0xff9800, 1);
          g.fillCircle(px, py, 5);
          g.fillStyle(0xfff3e0, 1);
          g.fillCircle(px, py, 2);
          // 落點預警圈(快落地時更明顯)
          g.lineStyle(2, 0xff7043, 0.25 + 0.5 * s.t);
          g.strokeCircle(s.landingX, s.landingY, s.radius);
        }
      }
    } catch { /* headless graphics stub lacks some ops — FX is best-effort */ }
  }

  /**
   * 在 (x,y) 依朝向 ang 畫一發「像樣的火箭」(homing-missile 飛行造型)。純美術:
   * 尖頭錐 + 彈體(膠囊) + 兩片小尾翼 + 鼻錐高光 + 後方 3 段漸層尾焰(越後越淡橙紅)。
   * 全在傳入的 _weaponFxGfx(每幀清空重畫)上畫,不新增物件、不碰碰撞/傷害。
   * K-3 一眼「這是飛彈」。座標沿 ang 的「前(+)/後(-)」與「側(法線)」分解,旋轉無關引擎。
   */
  private _drawRocket(g: Phaser.GameObjects.Graphics, x: number, y: number, ang: number): void {
    const ca = Math.cos(ang);
    const sa = Math.sin(ang);
    // 沿飛彈軸的單位向量:fwd=前進方向, side=左側法線。用來把局部座標轉到世界座標。
    const fwd = (f: number, s: number): [number, number] => [
      x + ca * f - sa * s,
      y + sa * f + ca * s,
    ];
    // 幾何尺寸(px,局部):比舊的 r4 小球大得多,看得出長條火箭。
    const NOSE = 13;   // 鼻錐尖端(最前)
    const BODY_F = 6;  // 彈體前緣
    const BODY_B = -7; // 彈體後緣(尾噴口)
    const HW = 4.5;    // 彈體半寬

    // 1. 尾焰:後方 3 段越來越淡的橢圓火舌(有動感,輕微隨機抖動長度)。
    const flick = 1 + Math.sin((this._weaponFxGfx ? performance.now() : 0) * 0.03 + x) * 0.18;
    const flames: Array<[number, number, number, number]> = [
      // [後移距離, 半寬, 色, alpha]
      [9 * flick, 3.4, 0xffe082, 0.9],
      [16 * flick, 4.6, 0xffa726, 0.6],
      [24 * flick, 5.6, 0xff5722, 0.32],
    ];
    for (const [back, w, col, a] of flames) {
      const [fx, fy] = fwd(BODY_B - back, 0);
      g.fillStyle(col, a);
      g.fillCircle(fx, fy, w);
    }

    // 2. 彈體(膠囊):後緣到前緣的矩形 + 圓頭,銀灰金屬色。
    g.fillStyle(0xeceff1, 1);
    const bl = fwd(BODY_F, -HW);
    const br = fwd(BODY_F, HW);
    const cr = fwd(BODY_B, HW);
    const cl = fwd(BODY_B, -HW);
    g.fillPoints(
      [
        new Phaser.Geom.Point(bl[0], bl[1]),
        new Phaser.Geom.Point(br[0], br[1]),
        new Phaser.Geom.Point(cr[0], cr[1]),
        new Phaser.Geom.Point(cl[0], cl[1]),
      ],
      true,
    );

    // 3. 尖頭錐(鼻):彈體前緣兩肩 → 尖端,暖紅警示色(像飛彈彈頭)。
    g.fillStyle(0xef5350, 1);
    const tip = fwd(NOSE, 0);
    g.fillPoints(
      [
        new Phaser.Geom.Point(bl[0], bl[1]),
        new Phaser.Geom.Point(br[0], br[1]),
        new Phaser.Geom.Point(tip[0], tip[1]),
      ],
      true,
    );

    // 4. 兩片後掠尾翼(後緣外擴的小三角),讓側影更像火箭。
    g.fillStyle(0xb0bec5, 1);
    for (const dir of [-1, 1]) {
      const root = fwd(BODY_B + 3, dir * HW);
      const mid = fwd(BODY_B - 2, dir * (HW + 4));
      const tail = fwd(BODY_B, dir * HW);
      g.fillPoints(
        [
          new Phaser.Geom.Point(root[0], root[1]),
          new Phaser.Geom.Point(mid[0], mid[1]),
          new Phaser.Geom.Point(tail[0], tail[1]),
        ],
        true,
      );
    }

    // 5. 鼻錐高光小點(立體感)。
    const hl = fwd(BODY_F + 2, -1.2);
    g.fillStyle(0xffffff, 0.9);
    g.fillCircle(hl[0], hl[1], 1.6);
  }

  /**
   * 命中爆炸 FX:在 (x,y) 生一次短命(~250ms)爆炸。沿用既有 mortar 落地爆炸的暖橙
   * 風格 + 門派色:白閃核心 + 擴張光圈 + explosionParticles 幾點放射火花。純美術 —
   * 命中/傷害已在 behavior tick 完成,這裡只是把它畫出來;headless 整段 try 包略過。
   * 尊重 reduceMotion:減晃時收斂(不抖鏡、光圈擴張幅度收小)。
   * @param radius 視覺基準半徑(來自 impact;爆裂彈=AoE 半徑,單體=預設小爆半徑)。
   */
  private _spawnMissileExplosion(x: number, y: number, radius: number): void {
    const reduced = isReduceMotion();
    const color = this._playerHitColor();
    try {
      // 1. 白閃核心:一瞬間的亮點,快速縮放淡出(命中的「啪」)。
      const core = this.add.circle(x, y, Math.max(6, radius * 0.5), 0xffffff, 0.95).setDepth(8);
      try { core.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      this.tweens.add({
        targets: core,
        scale: reduced ? 1.2 : 1.8,
        alpha: 0,
        duration: 160,
        ease: 'Cubic.easeOut',
        onComplete: () => core.destroy(),
      });
      // 2. 擴張光圈(門派色描邊),由小擴到 radius 外圈,淡出。
      const ring = this.add.circle(x, y, Math.max(8, radius * 0.6), color, 0.45).setDepth(7);
      try { ring.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      this.tweens.add({
        targets: ring,
        scale: reduced ? 1.35 : 2.0,
        alpha: 0,
        duration: 250,
        ease: 'Cubic.easeOut',
        onComplete: () => ring.destroy(),
      });
    } catch { /* headless: no add.circle / tweens */ }
    // 3. 放射火花(複用既有 juice helper;門派色,headless 內部已自保)。
    try { explosionParticles(this, x, y, color); } catch { /* headless */ }
    // 4. 命中微震(JuiceLayer 內建 reduceMotion clamp,減晃時自動收斂/歸零)。
    this._juiceLayer?.screenShake(0.008, 70);
  }

  /**
   * v1.1 + v1.2 effective player bullet damage:
   *   base × 星算門 mastery mult × elemental dmg-buff mult (+) techno scan bonus.
   */
  private _effectiveBulletDamage(nowMs: number): number {
    const ps = this._playstyleState();
    const mods = this._runModifiers();
    // Roguelike upgrades: 「重砲」 flat damage bonus + 「暴擊」 random 2× crit.
    // evoBulletDamageBonus = 場內武器進化 (R-004 包 C) 的當局質變傷害,跨場歸零,
    // 同 bulletDamageBonus 性質 (純當局,不碰修煉值)。
    //
    // 升級單發傷害封頂 (boss 2026-06-14 第二輪「王都被秒殺」,見 DIFFICULTY_PROGRESSION_MODEL.md §7):
    // 「重砲 flat 傷」相對 base 的倍率走 mods.cappedDamageMult() 夾在 RUN_DAMAGE_MULT_CAP,
    // 杜絕 +22 flat 把單發拉到 base 的 ~4-5 倍。**mastery(學習軌)與 elemental/playstyle
    // damageMult(時限 buff)在 cap 之外另乘** — 只封「升級牌的乘法堆疊」,不碰學習戰力。
    const baseDmg = this._bulletDamage > 0 ? this._bulletDamage : 1;
    const flatTotal = this._bulletDamage + mods.bulletDamageBonus + mods.evoBulletDamageBonus;
    const overBaseMult = mods.cappedDamageMult(flatTotal / baseDmg);
    const base = baseDmg * overBaseMult
      * this._masteryDamageMult * ps.damageMult(nowMs);
    let dmg = Math.round(base) + ps.scanBonusDmg(nowMs);
    // synergy 幸運散彈 (散彈+暴擊):暴擊機率 = 基礎 critChance + synergyCritBonus。
    // synergyCritBonus 預設 0 = 無 synergy,行為同今日;湊出組合後散彈也更常暴擊。
    const effCritChance = mods.critChance + mods.synergyCritBonus;
    if (effCritChance > 0 && Math.random() < effCritChance) {
      // 暴擊倍率封頂:讓「期望暴擊加成」不超過 CRIT_AVG_MULT_CAP(boss 2026-06-14 第二輪)。
      // 由 cappedCritAvg() 反推這發實際該用的倍率,避免 35%×2 + 超新星 20%×3 疊出平均 ~1.85×
      // 再與傷害 cap 疊乘二次爆表。中庸 4 抽 (35%×2) 本就 < cap,行為同今日。
      const cappedMult = effCritChance > 0
        ? 1 + (mods.cappedCritAvg() - 1) / Math.min(1, effCritChance)
        : mods.critMult;
      dmg = Math.round(dmg * Math.min(mods.critMult, cappedMult));
    }
    return dmg;
  }

  /**
   * 穿透傷害衰減倍率(boss 2026-06-14「不能打得又廣傷害又高」)。data-driven:讀
   * balance.weaponEvolution.pierceDamageFalloff,缺省退回常數 PIERCE_DAMAGE_FALLOFF。
   * 純當局戰力旋鈕,不碰學習軌。
   */
  private _pierceFalloff(): number {
    // 防呆:測試 harness 可能只塞部分 balance(無 weaponEvolution)→ 退回常數。
    const v = this._balance?.weaponEvolution?.pierceDamageFalloff;
    return Number.isFinite(v) && (v as number) > 0 && (v as number) <= 1 ? (v as number) : PIERCE_DAMAGE_FALLOFF;
  }

  private _spawnBullet(): void {
    // W2: mark "fired this frame" so PlayerFSM.update transitions to firing.
    this._firedThisFrame = true;
    const mods = this._runModifiers();
    const dmg = this._effectiveBulletDamage(this.time.now);
    // Roguelike「巨彈」upgrade: per-run bullet size multiplier (visual + hitbox).
    const sizeMult = mods.bulletSizeMult;
    // Main forward shot.
    this._spawnOneBullet(dmg, 0, sizeMult);
    // Roguelike「散彈強化」upgrade: fire `spreadBulletBonus` extra bullets fanned
    // out symmetrically around the forward axis (±vy). 0 = no extra shots, so
    // existing behaviour is unchanged until the kid picks the upgrade.
    // evoSpreadBonus = 場內武器進化「爆裂散彈」的當局質變額外散彈數 (跨場歸零)。
    // 平衡 (boss 2026-06-13):總散彈數走 effectiveSpreadBullets() cap (含 evo),
    // 杜絕滿屏彈幕無腦輾 (cap = SPREAD_BULLET_CAP 顆)。
    const extra = mods.effectiveSpreadBullets();
    if (extra > 0) {
      const speed = Math.abs(this._bulletVx) || 600;
      // 散彈副作用 (boss 2026-06-17):每發散彈只 SPREAD_DAMAGE_FALLOFF× 傷害,
      // 主前彈仍全傷。散彈=打更廣/打群,而非乘倍單體 DPS(杜絕滿散彈 5× DPS 繞過傷害 cap)。
      const spreadDmg = dmg * SPREAD_DAMAGE_FALLOFF;
      for (let i = 1; i <= extra; i++) {
        // alternate up/down, growing angle: ±0.12rad, ±0.24rad, ...
        const step = Math.ceil(i / 2);
        const sign = i % 2 === 1 ? -1 : 1;
        const vy = sign * step * 0.12 * speed;
        this._spawnOneBullet(spreadDmg, vy, sizeMult);
      }
    }
  }

  /**
   * Spawn a single player bullet with an explicit vertical velocity component
   * and per-run size multiplier. Extracted so 巨彈 (size) + 散彈強化 (extra
   * bullets) both flow through one render/collision-consistent path.
   */
  private _spawnOneBullet(dmg: number, vy: number, sizeMult: number): void {
    const mods = this._runModifiers();
    // 彈跳彈 + 場內武器進化 (彈跳風暴/貫穿彈/爆裂散彈) 的當局質變,seed 到每顆子彈
    // (跨場歸零,0 = 無,行為同今日)。詳見 weapon-evolution.ts。
    const bouncesLeft = mods.bounceCount + mods.evoBounceBonus;
    const pierceLeft = mods.pierceCount;
    const blastRadius = mods.spreadBlastRadius;
    const entity = new HBullet(
      { x: this.playerX + 30, y: this.playerY },
      { vx: this._bulletVx, vy },
      { damage: dmg, sizeMult, bouncesLeft, pierceLeft, blastRadius },
    );
    const sprite = this._makePlayerBulletSprite(sizeMult);
    this.bullets.push({ entity, sprite });
  }

  /**
   * extras-wire:建一發玩家主砲彈的渲染物。優先用對應門派色的火球 sprite Image
   * (`bullet-<faction>`,texture 存在時);texture 缺(stripped / headless / 測試
   * harness)就退回原本的發光彈體 Graphics —— 行為與外觀跟今日完全一致。
   *
   * 純美術:碰撞框只看 `entity`(HBullet)+ `sizeMult`,此處不碰任何傷害/速度。
   * 子彈是熱路徑且無物件池,但 sprite Image 比多層 Graphics fillEllipse 更省(單一
   * texture quad),手機反而更順;tiny 高頻敵彈(bossBullets/enemyBullets)維持
   * Graphics,不在此路徑。
   */
  private _makePlayerBulletSprite(sizeMult: number): Phaser.GameObjects.Graphics | Phaser.GameObjects.Image {
    const bulletKey = bulletKeyFor(this._playerFaction);
    // texture-exists gate — wrapped so the headless test harness (no
    // `this.textures`) safely falls through to the Graphics path below.
    let hasTex = false;
    try { hasTex = !!this.textures?.exists(bulletKey); } catch { hasTex = false; }
    if (hasTex) {
      try {
        const img = this.add.image(this.playerX + 30, this.playerY, bulletKey).setDepth(4);
        // 顯示高 ~20px(× 巨彈倍率);origin 置中讓依速度向量旋轉時樞紐正確。
        const s = this.textures.get(bulletKey).getSourceImage() as { width?: number; height?: number };
        const h = 20;
        const w = s.width && s.height ? h * (s.width / s.height) : h * 1.6;
        img.setDisplaySize(w * sizeMult, h * sizeMult);
        try { img.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
        return img;
      } catch { /* fall through to Graphics */ }
    }
    const sprite = this.add.graphics();
    // bullet-glow (2026-05-31, boss「子彈太醜」): glowing energy bolt — outer glow
    // halo + warm aura + tail streak + bright body + white-hot core, ADD blend.
    try {
      sprite.fillStyle(0xffa726, 0.22); sprite.fillEllipse(0, 0, 26, 18);
      sprite.fillStyle(0xffb74d, 0.5);  sprite.fillEllipse(0, 0, 18, 12);
      sprite.fillStyle(0xffd54f, 0.55); sprite.fillEllipse(-9, 0, 14, 5);
      sprite.fillStyle(0xffeb3b, 1);    sprite.fillEllipse(1, 0, 12, 8);
      sprite.fillStyle(0xffffff, 1);    sprite.fillCircle(2, 0, 2.4);
      try { sprite.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
    } catch {
      try { sprite.fillStyle(0xffeb3b, 1); sprite.fillRect(-4, -3, 12, 6); } catch { /* noop */ }
    }
    sprite.setDepth(4);
    // 巨彈:scale the sprite so a ×2 bullet visibly doubles in size. Hitbox is
    // enlarged to match in _checkCollisions (uses entity.sizeMult), so the
    // bigger bullet really is easier to land — not just cosmetic.
    try { if (sizeMult !== 1) sprite.setScale(sizeMult); } catch { /* headless graphics stub */ }
    return sprite;
  }

  /**
   * v1.1 星算門 burst-fire bullet (slot 1). 星算門紫 + 略大 + mastery 傷害。
   * Reuses the bullet pipeline so existing collision/charge hooks just work.
   */
  private _spawnBurstBullet(): void {
    this._firedThisFrame = true;
    const dmg = this._effectiveBulletDamage(this.time.now);
    const mods = this._runModifiers();
    // 巨彈 applies to burst shots too so the upgrade is consistent across slots.
    const sizeMult = mods.bulletSizeMult;
    // 彈跳彈 also carries to burst shots for consistency. 場內武器進化的當局質變
    // (額外彈跳 / 貫穿 / 爆裂) 同樣帶進 burst 彈,讓進化在所有射擊管道一致。
    const bouncesLeft = mods.bounceCount + mods.evoBounceBonus;
    const pierceLeft = mods.pierceCount;
    const blastRadius = mods.spreadBlastRadius;
    const entity = new HBullet(
      { x: this.playerX + 30, y: this.playerY },
      { vx: this._bulletVx * 1.15, vy: 0 },
      { damage: dmg, sizeMult, bouncesLeft, pierceLeft, blastRadius },
    );
    const sprite = this.add.graphics();
    try {
      // bullet-glow: 星算門 burst bolt — glowing-energy treatment in 星算門紫.
      sprite.fillStyle(0x6a1b9a, 0.25);        sprite.fillEllipse(0, 0, 30, 20);
      sprite.fillStyle(CELESTIAL_PURPLE, 0.55); sprite.fillEllipse(0, 0, 20, 13);
      sprite.fillStyle(0xb388ff, 0.55);        sprite.fillEllipse(-10, 0, 16, 6);
      sprite.fillStyle(CELESTIAL_PURPLE, 1);   sprite.fillEllipse(1, 0, 14, 9);
      sprite.fillStyle(0xffffff, 1);           sprite.fillCircle(2, 0, 2.8);
      sprite.setDepth(5);
      try { sprite.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      if (sizeMult !== 1) sprite.setScale(sizeMult);
    } catch { /* headless graphics stub */ }
    this.bullets.push({ entity, sprite });
  }

  // ── v1.1 active-skill system ──────────────────────────────────────────────

  /**
   * Build per-slot cooldowns, read 星算門 mastery + factionXP from progress,
   * and create the HUD. 學習與遊戲分離鐵則:只讀 progress.studentKPs 數學科
   * box level 換成傷害倍率,絕不在戰鬥中觸發答題。
   */
  private _initSkillSystem(progress: ReturnType<typeof loadProgress>): void {
    // v1.2 主人翁:slot-0 主玩法依「選的英雄」切換。celestial 維持 BURST 的
    // 3s CD;其餘各派 cooldown 由 SLOT0_CONFIG 決定(符文盾/偵察/火柱/轟炸)。
    const slot0 = SLOT0_CONFIG[this._heroId] ?? SLOT0_CONFIG.celestial;
    this._skillSlots = [
      new CooldownSlot(slot0.cooldownMs), // slot 0 = hero 主玩法
      new CooldownSlot(8000),
      new CooldownSlot(8000),
      new CooldownSlot(8000),
    ];
    this._skillSlotUnlocked = [true, false, false, false];
    this._ultimateCharge = new ChargingState();
    this._skillKeyWasDown = [false, false, false, false];
    this._ultimateKeyWasDown = false;
    this._enemyFreezeUntilMs = 0;
    this._burstShotsLeft = 0;
    this._burstNextShotMs = 0;
    this._playstyleState().reset();

    // mastery box → damage multiplier (pilot: math/星算門 only).
    this._masteryBoxLevel = masteryBoxLevelForMath(progress);
    this._masteryDamageMult = masteryDamageMultiplier(progress);
    // factionXP charge multiplier (faster ultimate charge for invested players).
    const celestialXP = progress?.factionXP?.celestial ?? 0;
    this._skillChargeMult = factionMultiplier(celestialXP);

    // Keyboard handles (desktop). Touch HUD writes window.__touchInput.skillN.
    try {
      const KC = Phaser.Input.Keyboard.KeyCodes;
      this._slotKeys = [
        this.input.keyboard!.addKey(KC.ONE),
        this.input.keyboard!.addKey(KC.TWO),
        this.input.keyboard!.addKey(KC.THREE),
        this.input.keyboard!.addKey(KC.FOUR),
      ];
      this._ultimateKey = this.input.keyboard!.addKey(KC.SPACE);
    } catch { this._slotKeys = []; this._ultimateKey = null; }

    // HUD: slot 1 = hero 主玩法 (icon/label 隨英雄), slots 2-4 locked.
    const configs: SkillSlotConfig[] = [
      { icon: slot0.icon, label: slot0.label, unlocked: true },
      { icon: '?', label: '?', unlocked: false },
      { icon: '?', label: '?', unlocked: false },
      { icon: '?', label: '?', unlocked: false },
    ];
    try {
      this._skillHud = new SkillHud(this, configs, GAME.width, GAME.height);
    } catch { this._skillHud = null; }

    // Pre-battle 2s bonus banner — 成就感 (boss memory: 遊戲端可彩光).
    this._showMasteryBonusBanner();
  }

  /**
   * Pre-battle full-screen banner: 「本次加成:⚡ 星算門修煉 Lv{N} → 技能傷害 {x}x」.
   * Fades in, holds, fades out over ~2s. Try-guarded for headless.
   */
  private _showMasteryBonusBanner(): void {
    try {
      const cx = GAME.width / 2;
      const cy = GAME.height / 2;
      const overlay = this.add.rectangle(cx, cy, GAME.width, GAME.height, 0x000000, 0.45).setDepth(60);
      const title = this.add.text(cx, cy - 30, '本次加成', {
        fontSize: '20px', fontFamily: 'sans-serif', color: '#d1b3ff',
      }).setOrigin(0.5).setDepth(61);
      const main = this.add.text(cx, cy + 6, `⚡ 星算門修煉 Lv${this._masteryBoxLevel}`, {
        fontSize: '28px', fontFamily: 'sans-serif', color: '#ffd54f', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(61);
      const sub = this.add.text(cx, cy + 44, `技能傷害 ${this._masteryDamageMult.toFixed(2)}x`, {
        fontSize: '20px', fontFamily: 'sans-serif', color: '#ffffff',
      }).setOrigin(0.5).setDepth(61);
      const items = [overlay, title, main, sub];
      for (const it of items) it.setAlpha(0);
      this.tweens.add({ targets: items, alpha: 1, duration: 350, ease: 'Cubic.easeOut' });
      this._juiceLayer?.scalePunch(main as Phaser.GameObjects.GameObject, 1.25, 500);
      this.time.delayedCall(1500, () => {
        try {
          this.tweens.add({
            targets: items, alpha: 0, duration: 400, ease: 'Cubic.easeIn',
            onComplete: () => { for (const it of items) { try { it.destroy(); } catch { /* noop */ } } },
          });
        } catch { for (const it of items) { try { it.destroy(); } catch { /* noop */ } } }
      });
    } catch { /* headless: no add/tweens */ }
  }

  /**
   * Per-frame skill input + cooldown/charge tick. Called from update().
   * Edge-detect on keys 1-4 (slot fire) + space (ultimate). Hold ≠ repeat.
   */
  private _handleSkills(dtSec: number, nowMs: number): void {
    const touch = (window as unknown as {
      __touchInput?: { skill1: boolean; skill2: boolean; skill3: boolean; skill4: boolean; space: boolean };
    }).__touchInput;

    // ── slots 1-4 ──
    for (let i = 0; i < 4; i++) {
      const keyDown = (this._slotKeys[i]?.isDown ?? false)
        || (touch ? (touch[`skill${i + 1}` as 'skill1'] ?? false) : false);
      const rising = keyDown && !this._skillKeyWasDown[i];
      this._skillKeyWasDown[i] = keyDown;
      if (rising && this._skillSlotUnlocked[i]) {
        this._tryFireSlot(i, nowMs);
      }
    }

    // ── ultimate (space, charge-gated) ──
    const ultDown = (this._ultimateKey?.isDown ?? false) || (touch?.space ?? false);
    const ultRising = ultDown && !this._ultimateKeyWasDown;
    this._ultimateKeyWasDown = ultDown;
    if (ultRising) {
      if (this._ultimateCharge.canActivate()) {
        this._castUltimate(nowMs);
      } else {
        // BUG FIX (boss「空格按不了」): the gauge isn't full yet. Instead of a
        // silent no-op (which read as "broken" to the kid), give immediate
        // feedback — flash the HUD power label + a deny SFX — so it's clear the
        // button works but 戰力 isn't ready.
        this._flashPowerNotReady();
      }
    }

    // ── burst-fire scheduling (5 shots @ 120ms) ──
    if (this._burstShotsLeft > 0 && nowMs >= this._burstNextShotMs) {
      this._spawnBurstBullet();
      this._burstShotsLeft--;
      this._burstNextShotMs = nowMs + BURST.intervalMs;
      try { this._sfx?.play('shoot', 0.4); } catch { /* noop */ }
    }

    // ── charge ultimate over time ──
    // 快充 upgrade folds into _effectiveChargeMult() (chargeMultBonus).
    this._ultimateCharge.onTick(dtSec, this._effectiveChargeMult());

    // ── HUD refresh ──
    if (this._skillHud) {
      const fracs = this._skillSlots.map((s) => s.fraction(nowMs));
      const rems = this._skillSlots.map((s) => s.remainingMs(nowMs));
      this._skillHud.updateCooldowns(fracs, rems);
      this._skillHud.updatePower(this._ultimateCharge.progressFraction());

      // Juice Batch 1 — 技能槽冷卻→可用脈衝。只對已解鎖槽偵測 rising edge,
      // 觸發 glow 脈衝 + 輕「ready」音(複用 xp-popup sparkle)。
      const readyNow = this._skillSlots.map(
        (s, i) => this._skillSlotUnlocked[i] === true && s.isReady(nowMs),
      );
      const justReady = detectJustReady(this._skillReadyPrev, readyNow);
      if (justReady.length > 0) {
        for (const i of justReady) {
          try { this._skillHud.pulseSlotReady(i); } catch { /* headless */ }
        }
        try { this._sfx?.play('xp-popup', 0.3); } catch { /* AudioContext busy */ }
      }
      this._skillReadyPrev = readyNow;
    }
  }

  /**
   * BUG FIX (boss「空格按不了」): immediate feedback when the player taps
   * space/⚡ but the 戰力槽 isn't full. Flashes the HUD power label to
   * 「戰力未滿」 (auto-reverts) and plays a soft deny SFX. Throttled so holding
   * the key doesn't spam. Fully try-guarded for the headless harness.
   */
  private _flashPowerNotReady(): void {
    const now = this.time.now;
    if (now - this._lastPowerDenyMs < 500) return; // throttle
    this._lastPowerDenyMs = now;
    try { this._skillHud?.flashPowerNotReady(); } catch { /* headless / no HUD */ }
    try { this._sfx?.play('hit', 0.2); } catch { /* AudioContext busy */ }
  }

  /** Fire skill slot `i` if its cooldown is ready. Slot 0 = hero 主玩法. */
  private _tryFireSlot(i: number, nowMs: number): void {
    const slot = this._skillSlots[i];
    if (!slot || !slot.tryActivate(nowMs)) return;
    if (i === 0) {
      this._fireHeroSlot0(nowMs);
    }
  }

  /**
   * v1.2 主人翁 slot-0 dispatch — 5 門派各自不同手感:
   *   celestial 星算 → 爆發連射 5 發 (現況)
   *   runic 符文     → 展開符文盾 (applyShield,擋 N 發)
   *   techno 機械    → 偵察提速 (startScan 加傷視窗 + buffAutoFire 射速)
   *   elemental 自然 → 火柱 DoT (spawnFirePillar 前方持續傷害場)
   *   dominion 疆域  → 範圍轟炸 (damageAllEnemies 一次清場)
   * 學習與遊戲分離鐵則:純遊戲機制,0 答題。每派各自 ActivationOutput body
   * 由下方 scene 端方法實作。
   */
  private _fireHeroSlot0(nowMs: number): void {
    let reduce = false;
    try { reduce = isReduceMotion(); } catch { /* default false */ }
    // 念咒→發力 釋放點(= _playHeroCutin 換第二張 frame 的同一時間,務必一致)。
    const releaseMs = reduce ? 1800 : 2000;

    // boss 2026-06-16「立繪出來的時候敵我雙方都暫停」:凍結全場到「發力」釋放點,讓
    // cut-in 是真正的過場演出。Math.max 避免縮短既有(如大絕)更長的凍結。
    try {
      this._enemyFreezeUntilMs = Math.max(this._enemyFreezeUntilMs, nowMs + releaseMs);
      this.playerFrozenUntilMs = Math.max(this.playerFrozenUntilMs, nowMs + releaseMs);
      this.playerSilencedUntilMs = Math.max(this.playerSilencedUntilMs, nowMs + releaseMs);
    } catch { /* noop */ }

    // 立繪(念咒 frame → 發力 frame 自動換)+ 第一張立繪的蓄力光影(boss 2026-06-16
    // 「第一個立繪開始可以加一些光影特效」)。純演出 0 戰力寫入。
    try { this._playHeroCutin(this._heroId); } catch { /* noop */ }
    try { this._playCastChargeGlow(this._heroId, releaseMs); } catch { /* noop */ }

    // boss 2026-06-16「第二張立繪出來才有隕石那些特效跟傷害、這時候才開始可以動」:
    // 招式機制 + 招式特效 + 傷害全部延到「發力」釋放點,凍結同時到期 → 解凍。
    const hero = this._heroId;
    this.time.delayedCall(releaseMs, () => {
      try { this._releaseHeroSlot0(hero, this.time.now); } catch { /* noop */ }
    });
  }

  /**
   * 「發力」釋放點:跑門派技能機制 + 招式專屬粒子特效 + 傷害。凍結(_fireHeroSlot0 設的)
   * 同時到期 → 敵我雙方恢復可動。每招用不同的粒子動畫(skill-particles),不再「同一張
   * 爆炸換色」(boss 2026-06-16「太多一樣了」):
   *   celestial 星辰灑落 / runic 旋轉魔法陣 / techno 雷射齊射 / elemental 真火焰 /
   *   dominion 隕石(維持 explosion-vfx,成為唯一的爆炸系 = 自然區隔)。
   */
  private _releaseHeroSlot0(faction: FactionId, nowMs: number): void {
    const punch = () => {
      try {
        const tgt = (this.playerImage ?? this.playerSprite) as Phaser.GameObjects.GameObject;
        this._juiceLayer?.scalePunch(tgt, 1.2, 250);
        this._juiceLayer?.screenShake(0.006, 60);
      } catch { /* noop */ }
    };
    try { this.triggerSkillCastBurst(faction); } catch { /* noop */ }
    try { this._playSkillFx(faction); } catch { /* noop */ }
    const targets = this._collectEnemyPoints();
    const hooks = this._skillFxHooks();

    switch (faction) {
      case 'runic':
        // 符文盾(吸傷)保留為隱形 bonus + 符文爆 AoE 傷害。特效 = 旋轉魔法陣 + 逐敵符文漩渦。
        this.applyShield(RUNIC_SHIELD.hitsAbsorbed);
        this.damageAllEnemies(RUNIC_BLAST.damageAll);
        punch();
        try { playRunicRunes(this, this.playerX, this.playerY, targets, hooks); } catch { /* noop */ }
        break;
      case 'techno':
        // 偵察(加傷 + 提速)保留為隱形 bonus + 齊射 AoE 傷害。特效 = 雷射射向每個敵人 + 命中火花。
        this.startScan(TECHNO_SCAN.windowMs, TECHNO_SCAN.bonusDmg, 0);
        this.buffAutoFire(TECHNO_SCAN.autoFireIntervalMs, TECHNO_SCAN.windowMs);
        this.damageAllEnemies(TECHNO_BARRAGE.damageAll);
        punch();
        try { playTechnoBarrage(this, this.playerX, this.playerY, targets, hooks); } catch { /* noop */ }
        break;
      case 'elemental': {
        // 火柱 DoT 場(_renderFirePillarFx 持續綠焰)+ 真火焰粒子噴發。
        this.spawnFirePillar(
          ELEMENTAL_FIRE_PILLAR.durationMs,
          ELEMENTAL_FIRE_PILLAR.dmgPerTick,
          ELEMENTAL_FIRE_PILLAR.intervalMs,
        );
        punch();
        const cx = this.playerX + ELEMENTAL_FIRE_PILLAR.offsetX;
        try { playElementalFlames(this, cx, this.playerY, ELEMENTAL_FIRE_PILLAR.height, hooks); } catch { /* noop */ }
        break;
      }
      case 'dominion':
        // 範圍轟炸 = 隕石風暴(唯一的爆炸系)+ AoE 傷害。
        punch();
        try { this._playMeteorStorm(); } catch { /* noop */ }
        try { this.damageAllEnemies(DOMINION_BOMBARD.damageAll); } catch { /* noop */ }
        break;
      case 'celestial':
      default:
        // 星算門 burst fire(5 連發)+ 星辰隕落 AoE 傷害 + 灑落特效。
        this._burstShotsLeft = BURST.shots;
        this._burstNextShotMs = nowMs;
        this.damageAllEnemies(CELESTIAL_STARFALL.damageAll);
        punch();
        try { playCelestialStars(this, this.playerX, this.playerY, targets, hooks); } catch { /* noop */ }
        break;
    }
  }

  /** 收集現存敵人 + 魔王座標(招式特效瞄準用)。 */
  private _collectEnemyPoints(): { x: number; y: number }[] {
    const pts: { x: number; y: number }[] = [];
    try {
      for (const e of this.enemies) { if (e && e.entity && e.entity.alive) pts.push({ x: e.entity.x, y: e.entity.y }); }
      if (this.boss) pts.push({ x: this.boss.x, y: this.boss.y });
    } catch { /* noop */ }
    return pts;
  }

  /** scene 端 juice/sfx/camera 回呼,交給 skill-particles 純演出函式使用。 */
  private _skillFxHooks(): SkillFxHooks {
    return {
      shake: (i, ms) => { try { this._juiceLayer?.screenShake(i, ms); } catch { /* noop */ } },
      hitStop: (ms) => { try { this._juiceLayer?.hitStop(ms); } catch { /* noop */ } },
      sfx: (k, g) => { try { this._sfx?.play(k as SoundKind, g); } catch { /* noop */ } },
      flash: (ms, r, g, b) => { try { this._cameraFlash(ms, r, g, b); } catch { /* noop */ } },
    };
  }

  /**
   * 第一張立繪(念咒)的蓄力光影(boss 2026-06-16):立繪胸口處冒出門派色光暈,由小漸大
   * 漸亮 + 三圈光環由外往內收(能量匯聚),發力時收掉。depth 885 在 cut-in 立繪(880)之
   * 上、發力特效(890+)之下。純演出,texture 缺 / headless 全 no-op。
   */
  private _playCastChargeGlow(faction: FactionId, durationMs: number): void {
    try {
      const color = (SLOT0_CONFIG as Record<string, { color: number }>)[faction as string]?.color ?? 0xffffff;
      const W = this.scale.width || 800; const H = this.scale.height || 600;
      const gx = W * 0.24; const gy = H * 0.62; // 立繪胸口附近
      const DZ = 885;
      // 蓄力光暈:由小漸大 + 漸亮,發力時收掉
      if (this.textures.exists('flare_01')) {
        const glow = this.add.image(gx, gy, 'flare_01').setDepth(DZ).setScale(0.08).setTint(color).setAlpha(0);
        try { glow.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
        this.tweens.add({ targets: glow, scaleX: 0.55, scaleY: 0.55, alpha: 0.7, duration: durationMs * 0.85, ease: 'Quad.easeIn' });
        this.tweens.add({ targets: glow, alpha: 0, delay: durationMs * 0.85, duration: durationMs * 0.18, onComplete: () => { try { glow.destroy(); } catch { /* noop */ } } });
      }
      // 收束光環:3 圈由外往內收(能量匯聚到角色身上)
      if (this.textures.exists('circle_03')) {
        for (let i = 0; i < 3; i++) {
          this.time.delayedCall(i * (durationMs / 4), () => {
            try {
              const ring = this.add.image(gx, gy, 'circle_03').setDepth(DZ).setScale(0.9).setTint(color).setAlpha(0);
              try { ring.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
              this.tweens.add({
                targets: ring, scaleX: 0.12, scaleY: 0.12, alpha: 0.85, duration: durationMs / 3, ease: 'Cubic.easeIn',
                onComplete: () => { this.tweens.add({ targets: ring, alpha: 0, duration: 150, onComplete: () => { try { ring.destroy(); } catch { /* noop */ } } }); },
              });
            } catch { /* noop */ }
          });
        }
      }
    } catch { /* noop */ }
  }

  /**
   * extras-wire:在玩家前方放一發門派技能特效 sprite(隕石/符文盾/冰爆/散彈/閃電)。
   * 短演出 tween:出現(放大+淡入)→ 淡出+略縮,~480ms 後自毀。texture 缺(stripped /
   * headless / 測試 harness)或 faction 未知就直接 no-op —— 純美術,絕不阻擋技能效果。
   *
   * 鐵則:0 戰力寫入。本方法只 add.image + tween + destroy,不碰 HP/傷害/CD/修煉值。
   */
  private _playSkillFx(faction: FactionId): void {
    const key = skillFxKeyForFaction(faction);
    if (!key || !this.textures.exists(key)) return;
    try {
      // 符文盾罩在玩家身上;其餘特效在玩家正前方(砲口前)。
      const inFront = faction !== 'runic';
      const fx = this.add.image(
        this.playerX + (inFront ? 56 : 0),
        this.playerY,
        key,
      ).setDepth(6);
      // 顯示高 ~72px,等比縮放;origin 置中。
      const s = this.textures.get(key).getSourceImage() as { width?: number; height?: number };
      const h = 72;
      const w = s.width && s.height ? h * (s.width / s.height) : h;
      fx.setDisplaySize(w, h);
      fx.setScale(fx.scaleX * 0.6, fx.scaleY * 0.6);
      fx.setAlpha(0.2);
      try { fx.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      const fullX = fx.scaleX / 0.6;
      const fullY = fx.scaleY / 0.6;
      this.tweens.add({
        targets: fx,
        scaleX: fullX,
        scaleY: fullY,
        alpha: 1,
        duration: 140,
        ease: 'Back.easeOut',
        onComplete: () => {
          this.tweens.add({
            targets: fx,
            alpha: 0,
            scaleX: fullX * 1.25,
            scaleY: fullY * 1.25,
            duration: 340,
            ease: 'Cubic.easeIn',
            onComplete: () => { try { fx.destroy(); } catch { /* noop */ } },
          });
        },
      });
    } catch { /* headless / texture race — skip the FX overlay */ }
  }

  /**
   * Ultimate「時空爆裂」— freeze all enemies + their bullets for 2s. Player can
   * still move + fire. Drains the charge gauge. 學習與遊戲分離:純遊戲機制。
   */
  private _castUltimate(nowMs: number): void {
    this._ultimateCharge.drain();
    // Universal base: freeze 全場 (keeps __isFrozen e2e contract intact for all
    // heroes). Per-hero flavor layered on top — 不強制每派不同, 先確保不壞.
    this._enemyFreezeUntilMs = nowMs + ULTIMATE.freezeMs;
    // v1.2 per-hero ultimate flavor:
    //   dominion → 凍結 + 全場轟炸 (清場感);
    //   elemental → 凍結 + 元素強化 (傷害 buff 一段時間);
    //   其餘 → 純凍結。
    if (this._heroId === 'dominion') {
      this.damageAllEnemies(DOMINION_BOMBARD.damageAll * 2);
    } else if (this._heroId === 'elemental') {
      this.buffPlayerDamage(1.5, ULTIMATE.freezeMs + 2000);
    }
    const label = this._heroId === 'dominion' ? '🏰 全境壓制!'
      : this._heroId === 'elemental' ? '🌀 元素爆發!'
      : this._heroId === 'runic' ? '🔮 時空封印!'
      : this._heroId === 'techno' ? '🤖 系統超載!'
      : '⚡ 時空爆裂!';
    // juice: dramatic flash + shake + faction burst.
    try {
      this.triggerSkillCastBurst(this._heroId);
      this._juiceLayer?.screenShake(0.018, 250);
      this._cameraFlash(180, 156, 77, 255); // purple-ish flash
    } catch { /* noop */ }
    try { this._sfx?.play('explode', 0.7); } catch { /* noop */ }
    // visible ultimate label
    try {
      const t = this.add.text(GAME.width / 2, GAME.height / 2 - 80, label, {
        fontSize: '32px', fontFamily: 'sans-serif', color: '#d1b3ff', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(62);
      this.tweens.add({
        targets: t, alpha: 0, y: GAME.height / 2 - 130, duration: 1200, ease: 'Cubic.easeOut',
        onComplete: () => { try { t.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
  }

  // ── v1.2 主人翁 ActivationOutput 方法 (scene-side bodies) ───────────────────
  //
  // These implement the per-faction effects declared in skill-instance.ts's
  // ActivationOutput. The slot-0 dispatch (_fireHeroSlot0) calls them; the
  // transient state lives in `_playstyle` (pure FactionPlaystyleState) and is
  // applied each frame in update()/collision. 學習與遊戲分離:純遊戲機制。

  /** runic 符文盾:吸收接下來 N 發玩家受擊 (consumed in _checkCollisions). */
  applyShield(hitsAbsorbed: number): void {
    this._playstyleState().applyShield(hitsAbsorbed);
    // visible 盾 label + cyan flash.
    try {
      this._cameraFlash(120, 79, 195, 247);
      const t = this.add.text(this.playerX, this.playerY - 44, '🔮 符文盾!', {
        fontSize: '20px', fontFamily: 'sans-serif', color: '#4fc3f7', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(62);
      this.tweens.add({
        targets: t, alpha: 0, y: this.playerY - 80, duration: 900, ease: 'Cubic.easeOut',
        onComplete: () => { try { t.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
  }

  /** techno 偵察:開啟命中加傷視窗。allyBoostPct unused (no ally skills lv1). */
  startScan(durationMs: number, bonusDmg: number, _allyBoostPct: number = 0): void {
    this._playstyleState().startScan(this.time.now, durationMs, bonusDmg);
    try {
      this._cameraFlash(120, 128, 203, 196);
      const t = this.add.text(this.playerX, this.playerY - 44, '🤖 偵察!', {
        fontSize: '20px', fontFamily: 'sans-serif', color: '#80cbc4', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(62);
      this.tweens.add({
        targets: t, alpha: 0, y: this.playerY - 80, duration: 900, ease: 'Cubic.easeOut',
        onComplete: () => { try { t.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
  }

  /** techno 提速:auto-fire 間隔縮短一段時間 (consumed in _handleAutoFire). */
  buffAutoFire(intervalMs: number, durationMs: number): void {
    this._playstyleState().buffAutoFire(this.time.now, intervalMs, durationMs);
  }

  /** elemental 元素強化:玩家子彈傷害 × mult 一段時間 (ultimate 用). */
  buffPlayerDamage(mult: number, durationMs: number): void {
    this._playstyleState().buffPlayerDamage(this.time.now, mult, durationMs);
  }

  /** elemental 火柱:在玩家前方鋪一個 DoT 場 (ticked in _tickFirePillars). */
  spawnFirePillar(durationMs: number, dmgPerTick: number, intervalMs: number): void {
    const cx = this.playerX + ELEMENTAL_FIRE_PILLAR.offsetX;
    const cy = this.playerY;
    const field = this._playstyleState().spawnFirePillar(
      this.time.now, cx, cy,
      ELEMENTAL_FIRE_PILLAR.width, ELEMENTAL_FIRE_PILLAR.height,
      durationMs, dmgPerTick, intervalMs,
    );
    // game-feel: layered flame field (visual only — DoT lives in `field` above).
    this._renderFirePillarFx(cx, cy, field.width, field.height, durationMs);
  }

  /**
   * game-feel: replaces the old flat orange rectangle + green「火柱!」text with a
   * layered, animated flame field. Purely cosmetic — the DoT damage is owned by
   * `_playstyleState().spawnFirePillar(...)` and is untouched. Fully wrapped in
   * try/catch so the headless unit-test harness (no `this.add` / particles) is
   * unaffected.
   */
  private _renderFirePillarFx(cx: number, cy: number, w: number, h: number, durationMs: number): void {
    // Base ember band — low-alpha ground glow that doubles as the DoT footprint
    // hint, fading over the field lifetime.
    try {
      // depth 885:疊到 cut-in 立繪(880)之上 —— 否則持續火焰場被立繪整個蓋住,
      // 只剩立繪退場後的尾巴看得到(boss 2026-06-16「綠色的不明顯」的真兇)。
      const base = this.add.rectangle(cx, cy, w, h, 0x0c2e0c, 0.34).setDepth(885);
      const base2 = this.add.rectangle(cx, cy + h * 0.18, w, h * 0.55, 0x33dd44, 0.22)
        .setDepth(885).setBlendMode(Phaser.BlendModes.ADD);
      this.tweens.add({
        targets: [base, base2], alpha: 0.04, duration: durationMs, ease: 'Linear',
        onComplete: () => { try { base.destroy(); base2.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }

    // Flame tongues — 6~9 stacked ellipses across the field width, tinted
    // red→orange→yellow bottom-to-top, additive blend, each with its own
    // out-of-phase flicker tween (scaleY + alpha) and a tiny x jitter.
    try {
      // 綠色自然焰梯度(深綠 → 翠綠 → 黃綠焰尖),對上 自然門 門派色。
      const FLAME_TINTS = [0x1b7d2e, 0x2e9e3a, 0x43c94d, 0x76dd5a, 0xc6f06a];
      const count = 9;
      const objs: Phaser.GameObjects.GameObject[] = [];
      for (let i = 0; i < count; i++) {
        const fx = cx + (i / (count - 1) - 0.5) * w * 0.92;
        const fw = w * 0.20;
        const fh = h * (0.85 + Math.random() * 0.45);
        const tint = FLAME_TINTS[i % FLAME_TINTS.length];
        const flame = this.add.ellipse(fx, cy + h * 0.1, fw, fh, tint, 0.82)
          .setDepth(886).setBlendMode(Phaser.BlendModes.ADD);
        flame.setOrigin(0.5, 1);
        objs.push(flame);
        const phase = Math.random() * 120;
        this.time.delayedCall(phase, () => {
          try {
            this.tweens.add({
              targets: flame, scaleY: 1.4, alpha: 0.9,
              duration: 150 + Math.random() * 150, ease: 'Sine.easeInOut',
              yoyo: true, repeat: -1,
            });
            this.tweens.add({
              targets: flame, x: fx + (Math.random() < 0.5 ? -3 : 3),
              duration: 120 + Math.random() * 120, ease: 'Sine.easeInOut',
              yoyo: true, repeat: -1,
            });
          } catch { /* noop */ }
        });
      }
      // Lifetime: fade the whole flame stack out, then destroy.
      this.time.delayedCall(Math.max(0, durationMs - 250), () => {
        try {
          this.tweens.add({
            targets: objs, alpha: 0, duration: 250, ease: 'Cubic.easeOut',
            onComplete: () => { for (const o of objs) { try { o.destroy(); } catch { /* noop */ } } },
          });
        } catch { for (const o of objs) { try { o.destroy(); } catch { /* noop */ } } }
      });
    } catch { /* headless */ }

    // Optional rising sparks — a soft radial particle texture (built once) that
    // drifts upward. Wrapped in try/catch: headless has no particles factory.
    try {
      if (!this.textures.exists('flameparticle')) {
        const g = this.make.graphics({ x: 0, y: 0 }, false);
        g.fillStyle(0xffffff, 1);
        g.fillCircle(8, 8, 4);
        g.fillStyle(0xffffff, 0.4);
        g.fillCircle(8, 8, 8);
        g.generateTexture('flameparticle', 16, 16);
        g.destroy();
      }
      // Spread spawn x across the field width via speedX (instead of an
      // emitZone — Phaser's RandomZoneSource typing is fussy and an x spread
      // reads identically for a short-lived upward spark). speedX range scales
      // with field width so wider pillars get a proportionally wider spark fan.
      const sparkSpreadX = Math.max(20, w * 0.4);
      const emitter = this.add.particles(cx, cy + h * 0.3, 'flameparticle', {
        lifespan: 600,
        speedY: { min: -120, max: -40 },
        speedX: { min: -sparkSpreadX, max: sparkSpreadX },
        scale: { start: 0.6, end: 0 },
        alpha: { start: 0.8, end: 0 },
        tint: [0x43c94d, 0x76dd5a, 0xc6f06a],
        blendMode: 'ADD',
        frequency: 32,
        quantity: 3,
      });
      emitter.setDepth(887);
      this.time.delayedCall(durationMs, () => { try { emitter.destroy(); } catch { /* noop */ } });
    } catch { /* headless / no particles */ }

    // Brief warm「🔥」accent that fades fast (was the green「🌀 火柱!」text).
    try {
      const t = this.add.text(cx, cy - h / 2 - 10, '🔥', {
        fontSize: '30px', fontFamily: 'sans-serif',
      }).setOrigin(0.5).setDepth(888);
      this.tweens.add({
        targets: t, alpha: 0, y: cy - h / 2 - 40, duration: 800, ease: 'Cubic.easeOut',
        onComplete: () => { try { t.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
  }

  /** dominion 範圍轟炸:對全場敵人各造成 amount 傷害 (含 mastery 倍率). */
  damageAllEnemies(amount: number): void {
    const dmg = Math.max(1, Math.round(amount * this._masteryDamageMult));
    for (const e of this.enemies) {
      if (!e.entity.alive) continue;
      e.entity.takeDamage(dmg);
      try {
        spawnDamageNumber(this, e.entity.x, e.entity.y - 20, dmg, {
          kind: 'normal', faction: this._heroId,
        });
        this._flashEnemy(e);
      } catch { /* headless */ }
      if (!e.entity.alive) {
        this.killedThisWave++; this.totalKilled++;
        this._onEnemyKilledXp(this._xpMultForEntry(e));
        const elite = e.entity.affinity != null;
        this._ultimateCharge.onEnemyKilled(this._skillChargeMult, elite);
        this._emitEnemyDeathFx(e.entity.x, e.entity.y);
      }
    }
    // Boss also takes the bombard (respecting castedShield like normal bullets).
    if (this.boss && this.boss.alive && !this._bossShieldActive) {
      this.boss.takeDamage(dmg);
      try {
        this._flashBoss();
        spawnDamageNumber(this, this.boss.x, this.boss.y - 30, dmg, {
          kind: 'normal', faction: this._heroId,
        });
      } catch { /* headless */ }
    }
    try {
      const t = this.add.text(GAME.width / 2, GAME.height / 2 - 80, '🏰 範圍轟炸!', {
        fontSize: '30px', fontFamily: 'sans-serif', color: '#ef9a9a', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(62);
      this.tweens.add({
        targets: t, alpha: 0, y: GAME.height / 2 - 130, duration: 1100, ease: 'Cubic.easeOut',
        onComplete: () => { try { t.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
  }

  /**
   * elemental 火柱 DoT tick — called each frame from update(). Applies
   * field.dmgPerTick to every enemy inside an active fire-pillar AABB on each
   * due tick, prunes expired fields. Pure-logic ticking lives in _playstyle.
   */
  private _tickFirePillars(nowMs: number): void {
    const fields = this._playstyleState().firePillars(nowMs);
    for (const field of fields) {
      const ticks = this._playstyleState().consumeFirePillarTicks(field, nowMs);
      if (ticks <= 0) continue;
      const dmg = Math.max(1, Math.round(field.dmgPerTick * ticks * this._masteryDamageMult));
      for (const e of this.enemies) {
        if (!e.entity.alive) continue;
        if (!FactionPlaystyleState.pointInField(field, e.entity.x, e.entity.y)) continue;
        e.entity.takeDamage(dmg);
        try {
          spawnDamageNumber(this, e.entity.x, e.entity.y - 16, dmg, {
            kind: 'normal', faction: 'elemental',
          });
        } catch { /* headless */ }
        if (!e.entity.alive) {
          this.killedThisWave++; this.totalKilled++;
        this._onEnemyKilledXp(this._xpMultForEntry(e));
          const elite = e.entity.affinity != null;
          this._ultimateCharge.onEnemyKilled(this._skillChargeMult, elite);
          this._emitEnemyDeathFx(e.entity.x, e.entity.y);
        }
      }
    }
    this._playstyleState().pruneFirePillars(nowMs);
  }

  private _updateBullets(dtSec: number): void {
    for (const b of this.bullets) { b.entity.update(dtSec); }
    for (const b of this.bullets.filter((b) => !b.entity.alive)) { b.sprite.destroy(); }
    this.bullets = this.bullets.filter((b) => b.entity.alive);
  }

  private _handleWaveSpawning(nowMs: number): void {
    if (this.waveState === 'done' || this.waveState === 'boss') return;
    if (this._merchantPaused) return;
    if (this._shopPaused) return;

    if (this.waveState === 'waiting') {
      if (nowMs - this.waveGapStartMs >= this._waveGapMs) {
        this.currentWave++;
        if (this.currentWave >= this._totalWaves) {
          // Phase 2 §4-1:魔王前先彈金幣商店 (一次)。_showCoinShop 設 _shopPaused
          // (update 早退凍結戰場);onClose 清旗標 + spawn boss。
          if (!this._preBossShopShown) {
            this._preBossShopShown = true;
            this._showCoinShop(() => { this._spawnBoss(); });
            return;
          }
          // All waves cleared — spawn boss
          this._spawnBoss();
          return;
        }
        if (this.currentWave % 3 === 0) { this._showMerchantOverlay(); return; }
        // Roguelike 三選一強化:before each non-final, non-merchant wave begins,
        // pause the battle and let the kid pick 1 of 3 random upgrades (純遊戲
        // 機制,不彈題目). The picker freezes the scene via _pickPaused; its
        // onResume callback starts the next wave exactly like the no-pick path.
        // roguelite-xp (2026-05-31, boss 指定): the 三選一 picker is NO LONGER
        // triggered by clearing a wave. It now fires on LEVEL-UP (see
        // _onEnemyKilledXp). Wave-clear just advances to the next wave directly,
        // keeping the existing 清波推進 flow intact.
        this._beginNextWave(this.time.now);
        return;
      }
      return;
    }

    const waveSize = this._waveSizes[this.currentWave] ?? this._waveSizes[this._waveSizes.length - 1];
    // mini-boss: on a 小魔王 wave, spawn it once mid-wave as the 壓軸.
    if (
      this._isMiniBossWave(this.currentWave) &&
      !this._miniBossSpawnedThisWave &&
      this.spawnedThisWave >= Math.floor(waveSize / 2)
    ) {
      this._spawnMiniBoss();
      this._miniBossSpawnedThisWave = true;
    }
    if (this.spawnedThisWave < waveSize) {
      if (nowMs - this.lastSpawnMs >= this._spawnIntervalMs) {
        this.lastSpawnMs = nowMs;
        this._spawnEnemy();
        this.spawnedThisWave++;
      }
    }
    if (this.spawnedThisWave >= waveSize && this.enemies.length === 0) {
      this.waveState = 'waiting';
      this.waveGapStartMs = nowMs;
    }
  }

  /**
   * Roguelike 三選一強化:start the next wave's spawning. Extracted from the
   * inline 'waiting' → 'spawning' transition so the upgrade-picker's onResume
   * callback can advance the wave AFTER the kid picks (the picker's onPick fires
   * asynchronously on click, so the advance can't stay inline). Resets the
   * per-wave spawn counters and updates the wave label — identical to the legacy
   * inline path.
   */
  private _beginNextWave(nowMs: number): void {
    this.spawnedThisWave = 0;
    this.killedThisWave = 0;
    this.waveState = 'spawning';
    this.lastSpawnMs = nowMs;
    // mini-boss: fresh per-wave guard so the new wave can field its own 小魔王.
    this._miniBossSpawnedThisWave = false;
    this.waveText.setText('Wave ' + (this.currentWave + 1) + ' / ' + this._totalWaves);
    // boss 2026-06-16「每一波漸進變換場景」:每進一波 crossfade 背景到下一個門派 realm。
    try { this._battleParallax?.setSceneForWave(this.currentWave); } catch { /* noop */ }
  }

  /**
   * mini-boss (2026-05-31): is the current wave a 小魔王 wave? Every
   * MINI_BOSS_EVERY_N_WAVES-th wave (1-based). Wave 0 excluded (easy on-ramp).
   */
  _isMiniBossWave(waveIndex: number): boolean {
    if (waveIndex <= 0) return false;
    return (waveIndex + 1) % HorizontalBattleScene.MINI_BOSS_EVERY_N_WAVES === 0;
  }

  private _showMerchantOverlay(): void {
    if (this._merchantPaused) return;
    this._merchantPaused = true;
    // MERCHANT-BUFF FIX: seed `ps` with the current accumulated buff values so
    // the cap math inside MerchantOverlay.applyMerchantOption (which clamps
    // skillCharge at 1.0) correctly accounts for prior picks across merchants.
    const ps = {
      hp: this.playerHp,
      maxHp: this._balance.player.initial_hp,
      skillCharge: this._skillChargeBonus,
      waveXpMultiplier: this._waveXpMult,
    };
    // MERCHANT-FAKE-BUFF FIX (kid feedback 2026-05-21 01:09, 🔥🔥🔥):
    // The 「技能槽充能 +30%」 option used to be an "empty promise" — `ps.skillCharge`
    // was mutated but no game system consumed `_skillChargeBonus`, so the kid
    // saw zero effect from picking it. The wire-up (`_consumeSkillChargeBonus`)
    // is retained as a test hook for future W2 skill-fire code, but the option
    // is REMOVED from the visible merchant pool until a real consumer exists.
    // Heal (immediate HP bar bump) and XP (visible on result screen) BOTH have
    // observable kid-facing feedback, so they stay.
    this._merchantOverlay = new MerchantOverlay(
      this as unknown as OverlayScene,
      {
        options: [
          { kind: "heal", label: "回血 50" },
          { kind: "xp",   label: "本波修煉值 x2" },
        ],
      },
      ps,
      {
        onPick: (_: number) => {
          // MERCHANT-BUFF FIX: read back ALL three mutated fields, not just hp.
          // heal:   ps.hp grows  → write to playerHp (clamped to maxHp).
          // charge: ps.skillCharge grows → store on scene (cap 1.0).
          // xp:     ps.waveXpMultiplier grows → store on scene (cap 5×).
          if (ps.hp > this.playerHp) {
            this.playerHp = Math.min(this._maxHp(), ps.hp);
          }
          this._skillChargeBonus = Math.min(
            HorizontalBattleScene.MAX_SKILL_CHARGE_BONUS,
            ps.skillCharge,
          );
          this._waveXpMult = Math.min(
            HorizontalBattleScene.MAX_WAVE_XP_MULT,
            ps.waveXpMultiplier,
          );
          this._resumeFromMerchant();
        },
        onDismiss: () => { this._resumeFromMerchant(); },
      },
    );
    this._merchantOverlay.show();
  }

  /**
   * MERCHANT-BUFF FIX: consume the accumulated skill-charge bonus.
   * Future skill-fire code (W2) will call this when a skill activates so the
   * 「充能 +30%」 merchant pick actually shortens the next skill's wind-up.
   * Returns the bonus that was consumed (0–1) and resets the field to 0.
   */
  _consumeSkillChargeBonus(): number {
    const v = this._skillChargeBonus;
    this._skillChargeBonus = 0;
    return v;
  }
  _resumeFromMerchant(): void {
    this._merchantPaused = false; this._merchantOverlay = null;
    const n = this.time.now; this.spawnedThisWave = 0; this.killedThisWave = 0; this.waveState = "spawning"; this.lastSpawnMs = n;
    this.waveText.setText("Wave " + (this.currentWave + 1) + " / " + this._totalWaves);
  }
  /**
   * enemy-diversity (2026-05-31, boss 回饋「不只單一直線、有些特別快、成群結隊、
   * 至少多 2 倍」): the wave loop calls this once per spawn tick. Instead of
   * always emitting a single straight-line grunt it now:
   *   (a) rolls a richer variant mix with MORE 'fast' so the field feels speedy;
   *   (b) picks one of 4 movement patterns (straight / sine / diagonal / chaseY);
   *   (c) ~28% of the time spawns a small CLUSTER (2–4) of the SAME variant in a
   *       tight vertical stack so enemies arrive '成群結隊' rather than drip-fed.
   * Each emitted enemy still counts as one spawn for the wave-size accounting
   * (the wave loop increments spawnedThisWave once per _spawnEnemy call), so a
   * cluster is a free '熱鬧' bonus on top of the doubled wave_sizes.
   *
   * Difficulty stays K-3-friendly: clusters share one variant (predictable),
   * chaseY only homes on the vertical axis (never reverses X), and the doubled
   * counts are offset by the same low per-enemy HP (1, tank 3).
   */
  private _spawnEnemy(): void {
    // bestiary-enemy-wire (2026-06-17): pick one of the stage faction's 20 enemies
    // (wave-weighted) and drive art + move + hp from its def. Falls back to the
    // legacy variant/pattern roll when no faction / def (texture-exists gates art).
    // 與 preload 的門派來源一致:hubFactionId → 選的英雄門派(_heroId)→ _playerFaction。
    const faction = this._initHubFactionId ?? this._heroId ?? this._playerFaction;
    const def = faction
      ? (pickEnemyForWave(faction, this.currentWave, this._totalWaves, Math.random()) ?? null)
      : null;
    const pattern = def ? movePatternForDef(def) : this._pickMovePattern();
    const variant = def ? variantForDef(def) : this._pickVariant();
    // swarm:N → 強制成群(N 隻);否則沿用 ~28% cluster roll。
    const isCluster = def && def.swarm > 0 ? true : Math.random() < HorizontalBattleScene.CLUSTER_CHANCE;
    const count = def && def.swarm > 0
      ? Math.min(def.swarm, 6)
      : isCluster
      ? HorizontalBattleScene.CLUSTER_MIN +
        Math.floor(Math.random() *
          (HorizontalBattleScene.CLUSTER_MAX - HorizontalBattleScene.CLUSTER_MIN + 1))
      : 1;
    // Anchor Y for the (possibly stacked) group.
    const anchorY = ENEMY_Y_MIN + Math.random() * (ENEMY_Y_MAX - ENEMY_Y_MIN);
    for (let i = 0; i < count; i++) {
      // Stack members are offset vertically by CLUSTER_GAP_PX, wrapped into the
      // playable band so a cluster near the edge still reads as a group.
      const rawY = anchorY + i * HorizontalBattleScene.CLUSTER_GAP_PX;
      const spawnY = ENEMY_Y_MIN +
        ((rawY - ENEMY_Y_MIN) % (ENEMY_Y_MAX - ENEMY_Y_MIN));
      // Stagger X slightly so cluster members don't perfectly overlap on entry.
      const spawnX = GAME.width + 32 + i * 26;
      this._spawnOneEnemy(variant, pattern, spawnX, spawnY, def);
    }
  }

  /**
   * R-004 D3:敵種組成走「章節波次配方」(衝鋒/坦克/混編),用既有敵種做差異,
   * 讓不同章/不同波玩起來感覺不同。配方由 waveCompositionFor(stage, wave) 取,
   * pickVariantFromComposition 依權重挑 normal/fast/heavy,再映回場景的
   * basic/fast/tank。資料驅動 — scene 不 inline 比例。
   *
   * (legacy 註:enemy-diversity 原為固定 ~45/40/15;改為每章配方後,第 1 章
   *  仍是「漸進引入快敵/坦克」的入門曲線,後段章節衝鋒/坦克波明顯不同。)
   */
  private _pickVariant(): EnemyTankType {
    const recipe = waveCompositionFor(this._stage, this.currentWave);
    const variant = pickVariantFromComposition(recipe, Math.random());
    // EnemyVariant(normal/fast/heavy)→ 場景 EnemyTankType(basic/fast/tank)。
    return variant === 'normal' ? 'basic' : variant === 'heavy' ? 'tank' : 'fast';
  }

  /**
   * enemy-diversity: movement-pattern mix. Half stay 'straight' (readable
   * baseline), the other half split across sine / diagonal / chaseY so the
   * field has visual variety without overwhelming a 9-year-old.
   */
  private _pickMovePattern(): HEnemyMovePattern {
    // boss 2026-06-15「小兵一直追著主角有點恐怖」: 小兵 HP 拉高後追逐兵活更久 = 太壓迫。
    // 調低追逐比例、拉高可預測的 straight:straight 50% / sine 15% / diagonal 13% /
    // chaseY 10% / 真追逐 12%(原 40/15/12/13/20 → 追逐 33%→22%)。仍保留追逐讓近戰有用。
    const r = Math.random();
    if (r < 0.5) return 'straight';
    if (r < 0.65) return 'sine';
    if (r < 0.78) return 'diagonal';
    if (r < 0.88) return 'chaseY';
    return 'chase'; // 12% 真追逐(X+Y 貼上來),讓近戰武器有用(老闆 2026-06-06)
  }

  /**
   * enemy-diversity: create ONE enemy with the given variant + movement pattern
   * at (spawnX, spawnY). Extracted from the legacy _spawnEnemy body so the
   * cluster orchestrator can emit several at once. All rendering / affinity /
   * tutorial wiring is unchanged from the pre-diversity single-spawn path.
   */
  private _spawnOneEnemy(
    variant: EnemyTankType,
    pattern: HEnemyMovePattern,
    spawnX: number,
    spawnY: number,
    def: EnemyDef | null = null,
  ): void {
    // Pull fire_interval_ms from balance config (per variant); HEnemy will
    // fall back to its hardcoded per-variant default if undefined.
    const balanceEntry = this._balance.enemies.types.find(t => t.id === variant);

    // HENEMY-AFFINITY (2026-05-21): pick affinity per HALO_NONE_WEIGHT split.
    // null = no halo (basic grunt), else one of 5 factions evenly weighted.
    const affinity = this._pickAffinityForSpawn();

    // enemy-diversity: per-variant horizontal speed. 'fast' uses its own (faster)
    // balance speedVx so the '特別快' variant is visibly quicker; others keep the
    // basic _enemyVx. 'diagonal' gets a non-zero vy so it slides across.
    // R-004 D2:章節難度曲線 — 移速 × enemySpeedMult(章越後越快;|vx| 放大,保留方向)。
    // bestiary def 帶自己的速度(vxFor by speed slow/med/fast),否則沿用 balance variant 速度。
    const baseVx = def ? vxFor(def) : (balanceEntry?.speedVx ?? this._enemyVx);
    const variantVx = baseVx * this._difficulty.enemySpeedMult;
    const diagVy = pattern === 'diagonal'
      ? (Math.random() < 0.5 ? -1 : 1) * 70
      : 0;

    // enemy-toughness (2026-05-31, boss「敵人太弱」): per-variant HP from balance
    // when present, else HEnemy's DEFAULT_HP (basic/fast 2, tank 6).
    // R-004 D2:章節難度曲線 — HP × enemyHpMult(章越後越硬,向上取整,至少 1)。
    // R-004 D2:章難度 enemyHpMult × 波內逐波強化 waveStrengthHpMult(boss 2026-06-17
    // 「每一波小兵要變強」)→ 同關 wave 越後越硬,封頂避免末波海綿。
    // bestiary def 帶自己的基礎 HP(hp_tier),否則沿用 balance variant hp。再乘章難度×逐波。
    const baseHp = def ? baseHpFor(def) : (balanceEntry as unknown as { hp?: number } | undefined)?.hp;
    const balanceHp = typeof baseHp === 'number'
      ? Math.max(1, Math.ceil(baseHp * this._difficulty.enemyHpMult * waveStrengthHpMult(this.currentWave)))
      : undefined;
    const entity = new HEnemy(
      { x: spawnX, y: spawnY },
      { vx: variantVx, vy: diagVy },
      {
        variant: variant as HEnemyVariant,
        fireIntervalMs: balanceEntry?.fire_interval_ms,
        affinity,
        movePattern: pattern,
        hp: balanceHp,
      },
    );

    // HENEMY-AFFINITY: halo Graphics layer sits BELOW the sprite (depth 0,
    // sprite/image at depth 1). Created lazily — only when the enemy has an
    // affinity (null = no halo allocation, keeps the GC footprint identical
    // to the pre-fix path for the ~50% of grunts that stay un-haloed).
    let halo: Phaser.GameObjects.Graphics | null = null;
    if (affinity) {
      halo = this.add.graphics().setDepth(0);
    }

    const sprite = this.add.graphics();
    sprite.fillStyle(0xef5350, 1);
    sprite.fillRect(-16, -16, 32, 32);
    sprite.setRotation(-Math.PI / 2);

    // ansimuz-sprite-skin (2026-06-05): overlay the ansimuz CC0 enemy sprite
    // when its texture loaded. If the texture is absent we keep the red
    // Graphics square as a fallback. P01: prefer atlas-frame so wave spawns
    // batch into 1 draw call.
    let image: Phaser.GameObjects.Image | null = null;
    try {
      // roster-wire (2026-06-13): pick the cartoon roster enemy art for THIS
      // stage's faction (celestial level → celestial war-machine, etc). Falls
      // back to the generic basic/fast/tank frame when the faction has no
      // roster art (enemyTankKeyForFaction handles the fallback internally).
      const initFaction = this._initHubFactionId ?? this._playerFaction;
      // bestiary-enemy-wire (2026-06-17): prefer this enemy's own move-frame art.
      // 關鍵:新敵人圖是獨立 load.image(不在 tanks atlas)→ 必須走 image 模式。
      // resolveTankRender 只要 atlas texture 存在就回 atlas frame,會把不在 atlas 的新
      // key 當成不存在的 frame → render 壞掉/退回(這是「怪物沒看到變」的真兇)。
      const moveKey = def ? enemyFrameKey(def.id, 'move') : '';
      const render: ReturnType<typeof resolveTankRender> =
        def && this.textures.exists(moveKey)
          ? { mode: 'image', key: moveKey }
          : resolveTankRender(this.textures, enemyTankKeyForFaction(initFaction, variant));
      if (render) {
        image = (render.mode === 'atlas'
          ? this.add.image(entity.x, entity.y, render.atlasKey, render.frame)
          : this.add.image(entity.x, entity.y, render.key))
          .setRotation(0)
          .setDepth(1);
        // tank-direction (2026-06-13, TANK_ART_SPEC §1 + visual verify): every
        // roster enemy chassis is authored facing LEFT (barrel + face point
        // left). The player is on the LEFT, so enemies face the player with
        // flipX:false — same contract as the prior ansimuz frames.
        try { image.setFlipX(false); } catch { /* headless */ }
        // tank-size: target TANK_SPRITE_H tall(同 player,boss 2026-06-13 降 72→56). Keep the
        // source aspect ratio so the mixed-source ansimuz sprites (biped /
        // flyer / walker, non-square) don't squash — read real dims from the
        // atlas FRAME when atlased, or the source image otherwise. Fall back
        // to square when dims are unavailable (headless/test).
        let sized = false;
        try {
          let w = 0;
          let h = 0;
          if (render.mode === 'atlas') {
            const fr = this.textures.get(render.atlasKey).get(render.frame) as
              { realWidth?: number; realHeight?: number; width?: number; height?: number } | undefined;
            w = fr?.realWidth ?? fr?.width ?? 0;
            h = fr?.realHeight ?? fr?.height ?? 0;
          } else {
            const src = this.textures.get(render.key).getSourceImage() as { width?: number; height?: number };
            w = src?.width ?? 0;
            h = src?.height ?? 0;
          }
          if (w > 0 && h > 0) { image.setDisplaySize(TANK_SPRITE_H * (w / h), TANK_SPRITE_H); sized = true; }
        } catch { /* headless: fall through to square */ }
        if (!sized) { try { image.setDisplaySize(TANK_SPRITE_H, TANK_SPRITE_H); } catch { /* headless */ } }
        sprite.setVisible(false);
      }
    } catch { /* defensive: keep Graphics fallback */ }

    this.enemies.push({
      entity, sprite, image, halo, def,
      moveKey: def ? enemyFrameKey(def.id, 'move') : undefined,
      attackKey: def ? enemyFrameKey(def.id, 'attack') : undefined,
      damagedKey: def ? enemyFrameKey(def.id, 'damaged') : undefined,
      curFrame: def ? 'move' : undefined,
    });

    // HENEMY-AFFINITY: first haloed grunt of the run triggers the same
    // affinity tutorial overlay the boss uses (kid needs the legend BEFORE
    // they meet a haloed grunt, not only when the boss arrives 30 wonders later).
    if (affinity && !this._firstHaloedEnemySeen && !this._affinityTutorialSeen) {
      this._firstHaloedEnemySeen = true;
      this._affinityTutorialSeen = true;
      showAffinityTutorial(
        this as unknown as import('../ui/affinity-tutorial-overlay').ATOverlayScene,
        affinity,
        entity.x,
        entity.y,
        () => {
          const p = loadProgress();
          if (p) {
            const ps2 = p.parentSettings as unknown as Record<string, unknown>;
            ps2['seenAffinityTutorial'] = true;
            saveProgress(p);
          }
        },
      );
    }
  }

  /**
   * HENEMY-AFFINITY: pick an affinity for a fresh spawn.
   * null with probability HALO_NONE_WEIGHT (default 0.5), otherwise an even
   * roll across the 5 factions. Pure (apart from Math.random) so it can be
   * unit-tested via stubbed RNG if needed.
   */
  /**
   * mini-boss (2026-05-31): spawn a 小魔王 — big, tough (hp 40 ≫ 雜兵 2-6),
   * magenta-tinted, 120px, with its own fan-shot attack. Reuses the HEnemy
   * entity (movement/collision/death-FX flow through the grunt pipeline) but
   * flags the EnemyEntry miniBoss. 鐵則:純遊戲機制,不彈題目、不寫修煉值。
   */
  private _spawnMiniBoss(): void {
    const spawnX = GAME.width + 48;
    const spawnY = ENEMY_Y_MIN + Math.random() * (ENEMY_Y_MAX - ENEMY_Y_MIN);
    const entity = new HEnemy(
      { x: spawnX, y: spawnY },
      { vx: HorizontalBattleScene.MINI_BOSS_VX, vy: 0 },
      { variant: 'tank', movePattern: 'straight', affinity: null, hp: HorizontalBattleScene.MINI_BOSS_HP },
    );
    const sprite = this.add.graphics();
    const half = HorizontalBattleScene.MINI_BOSS_SIZE / 2;
    sprite.fillStyle(HorizontalBattleScene.MINI_BOSS_TINT, 1);
    sprite.fillRect(-half, -half, HorizontalBattleScene.MINI_BOSS_SIZE, HorizontalBattleScene.MINI_BOSS_SIZE);
    let image: Phaser.GameObjects.Image | null = null;
    let skinned = false;
    try {
      // miniboss-roster-wire (2026-06-13, boss「那個魔王很棒,多產生一些類似的」):
      // rotate through the 6 bespoke cartoon mini-boss skins by a monotonic spawn
      // counter (deterministic). These are FULL-COLOUR PNGs → NO setTint (tinting
      // would muddy them, same as the boss roster). Sized by source aspect ratio,
      // anchored on MINI_BOSS_SIZE height. Authored facing LEFT → flipX:false.
      // The skins are STANDALONE images (not in the tanks atlas), so gate on the
      // image texture key directly — NOT resolveTankRender (which is atlas-first
      // and would mis-resolve a non-atlas key to a bogus atlas frame).
      const skinKey = miniBossSkinKey(this._miniBossSpawnIndex);
      if (this.textures.exists(skinKey)) {
        image = this.add.image(entity.x, entity.y, skinKey)
          .setRotation(0)
          .setDepth(1);
        try { image.setFlipX(false); } catch { /* headless */ }
        // size by source aspect ratio so the non-square cartoon skins don't squash.
        let sized = false;
        try {
          const src = this.textures.get(skinKey).getSourceImage() as { width?: number; height?: number };
          const w = src?.width ?? 0;
          const h = src?.height ?? 0;
          if (w > 0 && h > 0) {
            image.setDisplaySize(HorizontalBattleScene.MINI_BOSS_SIZE * (w / h), HorizontalBattleScene.MINI_BOSS_SIZE);
            sized = true;
          }
        } catch { /* headless: fall through to square */ }
        if (!sized) {
          try {
            image.setDisplaySize(HorizontalBattleScene.MINI_BOSS_SIZE, HorizontalBattleScene.MINI_BOSS_SIZE);
          } catch { /* headless */ }
        }
        sprite.setVisible(false);
        skinned = true;
      } else {
        // no bespoke skin texture (stripped deploy) → reuse the stage faction's
        // heavy/tank chassis, tinted MINI_BOSS_TINT so it still reads as an elite.
        const initFaction = this._initHubFactionId ?? this._playerFaction;
        const key = enemyTankKeyForFaction(initFaction, 'tank');
        const render = resolveTankRender(this.textures, key);
        if (render) {
          image = (render.mode === 'atlas'
            ? this.add.image(entity.x, entity.y, render.atlasKey, render.frame)
            : this.add.image(entity.x, entity.y, render.key))
            .setRotation(0)
            .setDisplaySize(HorizontalBattleScene.MINI_BOSS_SIZE, HorizontalBattleScene.MINI_BOSS_SIZE)
            .setDepth(1);
          try { image.setTint(HorizontalBattleScene.MINI_BOSS_TINT); } catch { /* headless */ }
          sprite.setVisible(false);
        }
      }
    } catch { /* keep Graphics box fallback */ }
    // advance the rotation for the next 小魔王 (only after a successful spawn).
    this._miniBossSpawnIndex++;
    let hpBar: Phaser.GameObjects.Graphics | null = null;
    try { hpBar = this.add.graphics().setDepth(6); } catch { hpBar = null; }
    this.enemies.push({
      entity, sprite, image, halo: null, hpBar, miniBoss: true, miniBossSkinned: skinned,
      nextAttackMs: this.time.now + HorizontalBattleScene.MINI_BOSS_ATTACK_INTERVAL_MS,
    });
  }

  /**
   * mini-boss: per-frame attack tick. Each alive 小魔王 fires a fan toward the
   * player every MINI_BOSS_ATTACK_INTERVAL_MS. Frozen during the ultimate.
   */
  private _tickMiniBossAttacks(nowMs: number): void {
    if (nowMs < this._enemyFreezeUntilMs) return;
    for (const e of this.enemies) {
      if (!e.miniBoss || !e.entity.alive) continue;
      if (e.entity.x > GAME.width) continue;
      if (nowMs < (e.nextAttackMs ?? 0)) continue;
      e.nextAttackMs = nowMs + HorizontalBattleScene.MINI_BOSS_ATTACK_INTERVAL_MS;
      this._fireMiniBossFan(e.entity.x, e.entity.y);
      // game-feel (動的感覺): mini-boss fire recoil — same scale punch as grunts
      // but a touch bigger (1.22) to match its size. Visual-only, try-guarded.
      const tgt = (e.image ?? e.sprite) as Phaser.GameObjects.GameObject;
      try { this._juiceLayer?.scalePunch(tgt, 1.22, 140); } catch { /* headless */ }
    }
  }

  /**
   * mini-boss: emit a symmetric fan of bullets aimed at the player. Spawns into
   * enemyBullets (same collision path as grunt shots). Public for tests.
   */
  _fireMiniBossFan(originX: number, originY: number): void {
    const baseAngle = Math.atan2(this.playerY - originY, this.playerX - originX);
    const count = HorizontalBattleScene.MINI_BOSS_FAN_COUNT as number;
    const spread = 1.0;
    const speed = HorizontalBattleScene.MINI_BOSS_BULLET_SPEED;
    const dmg = HorizontalBattleScene.MINI_BOSS_BULLET_DAMAGE;
    for (let i = 0; i < count; i++) {
      const t = count === 1 ? 0.5 : i / (count - 1);
      const angle = baseAngle - spread / 2 + spread * t;
      this._spawnEnemyBullet(originX, originY, Math.cos(angle) * speed, Math.sin(angle) * speed, dmg);
    }
    try { this._sfx?.play('shoot', 0.3); } catch { /* AudioContext busy */ }
  }

  /**
   * enemy-toughness / mini-boss (2026-05-31): floating HP bar above an enemy
   * whose maxHp > 2 (tanks + 小魔王). Fragile 2-HP grunts get no bar.
   */
  private _drawEnemyHpBar(e: EnemyEntry): void {
    const ent = e.entity;
    if (!e.miniBoss && ent.maxHp <= 2) return;
    if (!ent.alive) return;
    try {
      if (!e.hpBar) { e.hpBar = this.add.graphics().setDepth(6); }
      const g = e.hpBar;
      g.clear();
      const size = e.miniBoss ? HorizontalBattleScene.MINI_BOSS_SIZE : 36;
      const w = e.miniBoss ? 96 : 36;
      const h = e.miniBoss ? 7 : 4;
      const x = ent.x - w / 2;
      const y = ent.y - size / 2 - (e.miniBoss ? 14 : 10);
      const ratio = Math.max(0, Math.min(1, ent.hp / Math.max(1, ent.maxHp)));
      g.fillStyle(0x000000, 0.55);
      g.fillRect(x - 1, y - 1, w + 2, h + 2);
      const fillColor = ratio > 0.5 ? 0x66bb6a : ratio > 0.25 ? 0xffa726 : 0xef5350;
      g.fillStyle(fillColor, 1);
      g.fillRect(x, y, w * ratio, h);
    } catch { /* headless graphics stub */ }
  }

  private _pickAffinityForSpawn(): FactionId | null {
    const roll = Math.random();
    if (roll < HALO_NONE_WEIGHT) return null;
    const idx = Math.floor((roll - HALO_NONE_WEIGHT) / ((1 - HALO_NONE_WEIGHT) / ALL_FACTIONS_FOR_HALO.length));
    return ALL_FACTIONS_FOR_HALO[Math.min(idx, ALL_FACTIONS_FOR_HALO.length - 1)];
  }

  // ── Boss lifecycle ──────────────────────────────────────────────────────

  private _spawnBoss(): void {
    // C05-wire: in tutorial mode, ALWAYS use BOSS_TUTORIAL_POOL[0]
    // (the "練習魔王・木靶王" must-win training-dummy). NEVER call
    // pickRandomBoss() here — that would expose the kid to the real 9-boss
    // pool inside what's advertised as a 必勝 tutorial, breaking the C05
    // hook promise. The pool has exactly one entry today; the [0] index is
    // intentional so a future second-tier tutorial boss can be added without
    // randomising into the existing 必勝 commitment.
    // tutorial-boss mode keeps the strict ternary form so the C05-wire
    // contract test (tutorial-boss-wire) locks `pickRandomBoss()` as the
    // ONLY non-tutorial path — that guard prevents accidental fallthrough
    // back to random picks while in tutorial mode.
    let def = this._tutorialBossMode ? BOSS_TUTORIAL_POOL[0] : pickRandomBoss();
    // W7 hub→battle wiring — override post-process: when scene was started
    // from HubScene.onBuildingClick with an init(data).bossId, swap the
    // random pick for the requested def. Tutorial mode is untouched (the
    // 必勝 contract takes priority). Lookup miss → keep the random pick.
    if (!this._tutorialBossMode && this._initBossIdOverride) {
      const override = pickBossById(this._initBossIdOverride);
      if (override) def = override;
    }
    // boss 2026-06-16「大魔王血應該 *10 倍」:大魔王(非教學木靶王 必勝)HP ×10。相位
    // 門檻 phase2HpThreshold 是「絕對 HP 值」,必須同步 ×10 才能維持相位切換比例;
    // phaseThresholds(若有)是比例制、不受影響。不改共用 pool 物件(複製一份 def)。
    if (!this._tutorialBossMode) {
      const MULT = 10;
      def = { ...def, hp: def.hp * MULT, phase2HpThreshold: def.phase2HpThreshold * MULT };
    }
    this.boss = new Boss(def, { x: BOSS_SPAWN_X, y: GAME.height / 2 });
    this.bossBobBaseY = GAME.height / 2;
    this.bossArrived = false;
    this.bossArrivalTimeMs = 0;
    this.waveState = 'boss';
    this.waveText.setText('BOSS: ' + def.displayName);

    const factionKey = FACTION_STAGE_KEY[def.faction] ?? ASSETS.hStageCelestial;
    const bgKey = this.textures.exists(factionKey) ? factionKey : ASSETS.hStageCelestial;
    if (this.stageBg) { this.stageBg.destroy(); this.stageBg = null; }
    if (this.textures.exists(bgKey)) {
      this.stageBg = this.add.tileSprite(0, 0, GAME.width, GAME.height, bgKey)
        .setOrigin(0, 0).setDepth(-10);
      // W1 戰場可讀性:boss 換景同樣壓暗背景星星(與 create() 的 tint 一致)。
      this.stageBg.setTint?.(BACKGROUND_READABILITY.dimTint);
    }

    this.bossSprite = this.add.graphics().setDepth(5);
    this._drawBossSprite();

    // boss-sprite-wire: overlay AI-gen boss PNG on top of the Graphics box +
    // halo (the Graphics layer stays for the affinity halo / HP-tint flash).
    // If the texture failed to load (e.g. stripped deployment) or the boss
    // has no registered sprite (some tier-2 cross-faction bosses), bossImage
    // remains null and the Graphics-only render stays visible.
    // boss-roster-wire (2026-06-13): fresh spawn → boss starts in phase-1 art;
    // the phase-2 暴怒變身 swap fires once later from _updateBoss.
    this._bossPhase2Swapped = false;
    this._bossImageDisplayH = def.size ?? 96;
    this.bossImage = null;
    try {
      const key = bossSpriteKey(def.id);
      if (key && this.textures.exists(key)) {
        this.bossImage = this.add.image(this.boss.x, this.boss.y, key)
          .setDepth(6); // above Graphics box (5), below warning ring (7)
        // tank-direction (TANK_ART_SPEC §1 + visual verify): every bespoke boss
        // PNG is drawn FACING LEFT (face/barrel point left). The boss sits on
        // the RIGHT facing the left-side player, so NO flipX.
        try { this.bossImage.setFlipX(false); } catch { /* headless */ }
        this._sizeBossImage(key, this._bossImageDisplayH);
        // boss-roster-wire: bosses are now FULL-COLOUR bespoke PNGs — the old
        // per-faction `setTint(def.color)` muddied the art, so it's dropped here
        // (and in _drawBossSprite). Faction identity comes from the bespoke
        // silhouette/palette, not a flat tint.
      }
    } catch { /* defensive: keep Graphics fallback if anything goes wrong */ }

    if (this._warningRingGfx) { this._warningRingGfx.destroy(); }
    this._warningRingGfx = this.add.graphics().setDepth(7);

    // learnable-boss-proto: telegraph layer sits just under the warning ring
    // (depth 6 < 7) so the aim-line / charge / breach cue draws over the boss
    // sprite (depth 5) but under the countdown ring.
    if (this._telegraphGfx) { this._telegraphGfx.destroy(); }
    this._telegraphGfx = this.add.graphics().setDepth(6);

    this.bossHpBar = this.add.graphics().setDepth(10);
    this.bossHpText = this.add.text(
      GAME.width - 10, 30,
      def.displayName + ' HP: ' + def.hp,
      { fontSize: '12px', color: '#ef9a9a', fontFamily: 'sans-serif' },
    ).setOrigin(1, 0).setDepth(10);

    // Task #11: switch to boss BGM. Faction preset (pitch + filter) is
    // preserved by switchToBossTheme. No-op if _bgm couldn't be constructed.
    try { this._bgm?.switchToBossTheme(); } catch { /* noop */ }

    // W8 PR-1 — fresh per-spawn mechanic state + reset shield.
    this._factionMechanicState = makeFactionMechanicState();
    // boss-teleport-gimmick — fresh per-spawn 瞬移狀態 (HP<50% 後每 5s 觸發)。
    this._teleportGimmickState = makeTeleportGimmickState();
    this._teleportPendingX = null;
    this._clearTeleportMarker();
    this._bossShieldActive = false;
    this._bossShieldEndMs = 0;
  }

  /**
   * boss-roster-wire: size this.bossImage to `displayH` px tall, preserving the
   * texture's source aspect ratio (boss PNGs are non-square — a flat square
   * size squashes them). Headless/test-safe: square-sizes when the source dims
   * are unavailable. Shared by _spawnBoss and the phase-2 swap so both phases
   * size by the same rule.
   */
  private _sizeBossImage(key: string, displayH: number): void {
    if (!this.bossImage) return;
    try {
      const src = this.textures.get(key).getSourceImage() as { width?: number; height?: number };
      const w = src?.width ?? 0;
      const h = src?.height ?? 0;
      if (w > 0 && h > 0) {
        this.bossImage.setDisplaySize(displayH * (w / h), displayH);
      } else {
        this.bossImage.setDisplaySize(displayH, displayH);
      }
    } catch {
      try { this.bossImage.setDisplaySize(displayH, displayH); } catch { /* headless */ }
    }
  }

  /**
   * boss-roster-wire (2026-06-13, boss「二階段照型要變」): swap the boss sprite to
   * its phase-2 暴怒變身 art (裂開露核心/增刺/紅光) + play a 變身演出 (閃白+震動).
   * Idempotent via the `_bossPhase2Swapped` one-shot guard. Returns true if a
   * swap actually happened (for tests / call-site clarity).
   *
   * 鐵則:純美術換圖 + 演出,絕不改 HP/傷害/數值/冷卻。
   */
  private _swapBossToPhase2(): boolean {
    const boss = this.boss;
    if (!boss || this._bossPhase2Swapped || !this.bossImage) return false;
    const p2key = bossPhase2SpriteKey(boss.def.id);
    if (!p2key || !this.textures.exists(p2key)) return false;
    this._bossPhase2Swapped = true;
    try {
      this.bossImage.setTexture(p2key);
      // re-assert direction + size for the (different-aspect) 變身 PNG.
      try { this.bossImage.setFlipX(false); } catch { /* headless */ }
      // 變身型略大 (×1.12) 讓「進化壓迫感」更明顯,但仍 cap 在 boss 階級內。
      this._sizeBossImage(p2key, this._bossImageDisplayH * 1.12);
    } catch { /* headless: texture swap unavailable */ }
    // 變身演出:閃白 + 震動 (既有 bossExplode preset — 重量級閃黃/震屏/粒子,
    // 已存在於 feedback-presets.json,過 coverage 守門)。純演出,不發傷害。
    try {
      this._flashBoss();
      this._juice?.playFeedback('bossPhase2', { x: boss.x, y: boss.y, flashTarget: this.bossImage });
    } catch { /* headless */ }
    return true;
  }

  private _updateBoss(dtSec: number, nowMs: number): void {
    if (!this.boss) return;

    // BOSS-END FIX (2026-05-21, 2nd kid complaint「打完還是不會結束」):
    // Fire the defeat transition BEFORE the alive-gate. Root cause: a previous
    // hit (last frame's _checkCollisions) calls boss.takeDamage(d) which sets
    // both `hp = 0` AND `alive = false` synchronously. The old guard
    // `if (!this.boss.alive) return;` then short-circuited _updateBoss on the
    // next frame and the `if (this.boss.hp <= 0) { _bossDefeated() }` branch
    // below NEVER ran → the kid was stuck on a dead-boss screen with no
    // ResultScene transition. Fix: check hp first; the _bossDefeatedFired
    // guard makes this idempotent (fires exactly once even if hp stays ≤ 0
    // across many frames).
    if (this.boss.hp <= 0 && !this._bossDefeatedFired) {
      this._bossDefeated();
      return;
    }
    if (!this.boss.alive) return;

    // boss-roster-wire (2026-06-13, boss「二階段照型要變」): the moment the boss
    // crosses into phase ≥ 2 / berserk, swap its sprite to the 暴怒變身 art +
    // play a one-shot 變身演出. `shouldSwapToPhase2` (pure, unit-tested) decides;
    // `_swapBossToPhase2`'s own guard makes the swap idempotent. 鐵則:純美術,
    // 不碰任何 HP/傷害 (we只 READ boss.phase / boss.berserkMode here).
    if (
      shouldSwapToPhase2({
        phase: this.boss.phase,
        berserk: this.boss.berserkMode,
        alreadySwapped: this._bossPhase2Swapped,
      })
    ) {
      this._swapBossToPhase2();
    }

    // Fly-in phase: move left toward BOSS_TARGET_X
    if (!this.bossArrived) {
      this.boss.x -= BOSS_FLY_SPEED * dtSec;
      if (this.boss.x <= BOSS_TARGET_X) {
        this.boss.x = BOSS_TARGET_X;
        this.bossArrived = true;
        this.bossArrivalTimeMs = nowMs;
        // W5: boss-enter camera punch — 1.2x zoom + warm orange flash so the
        // boss arrival reads as "something big landed". Per spec §4.1.
        this._cameraZoomPunch(1.2, 300);
        this._cameraFlash(150, 255, 200, 100);
      }
    } else {
      // Idle bob: ±BOSS_BOB_AMP sinusoidal up/down
      const elapsed = (nowMs - this.bossArrivalTimeMs) / 1000;
      this.boss.y = this.bossBobBaseY + Math.sin((elapsed / BOSS_BOB_PERIOD_SEC) * Math.PI * 2) * BOSS_BOB_AMP;
      if (!this._affinityTutorialSeen) {
        this._affinityTutorialSeen = true;
        const faction = this.boss.def.affinityFaction;
        showAffinityTutorial(this as unknown as import("../ui/affinity-tutorial-overlay").ATOverlayScene, faction, this.boss.x, this.boss.y, () => {
          const p = loadProgress(); if (p) { const ps2 = p.parentSettings as unknown as Record<string, unknown>; ps2["seenAffinityTutorial"] = true; saveProgress(p); }
        });
      }
    }

    // Task #31: detect rising edge (warning ring just turned on) → boss-warning SFX once.
    const ringActiveNow = this.boss.warningRing.active;
    if (ringActiveNow && !this._warningRingWasActive) {
      try { this._sfx?.play('boss-warning', 0.45); } catch { /* AudioContext busy */ }
    }
    this._warningRingWasActive = ringActiveNow;

    if (ringActiveNow) { this.boss.tickWarningRing(nowMs); }
    if (this._warningRingGfx) { drawChargeRing(this._warningRingGfx, this.boss.getWarningRingDescriptor(), 56, 6); }

    // Tick boss attacks — pass playerY as the "playerX" argument.
    // The boss-pool tickAttacks signature is tickAttacks(self, nowMs, playerX).
    // In horizontal mode, we pass playerY here and re-derive angles in _executeBossAttack,
    // so the pool's own angle computation is ignored for single-shot/beam-cast/fan-shot.
    const attacks = this.boss.def.tickAttacks(this.boss, nowMs, this.playerY);
    for (const atk of attacks) {
      this._executeBossAttack(atk);
    }
    // game-feel (動的感覺): boss fire recoil — ONE scale punch per attack volley
    // (not per bullet) so the boss "kicks" when it shoots without strobing on
    // multi-bullet patterns. Visual-only, try-guarded headless.
    if (attacks.length > 0 && this.bossImage) {
      try { this._juiceLayer?.scalePunch(this.bossImage as Phaser.GameObjects.GameObject, 1.08, 160); } catch { /* headless */ }
    }

    // learnable-boss-proto (老闆 2026-06「魔王要可學習、可精通、有攻防節奏」):
    // tickAttacks 剛更新了 boss.patternTelegraph (目前只有 SHU_MO 會驅動)。
    // 依招式畫「專屬、可區分」的預警 (laser=紅瞄準線預演 / sniper=砲口黃蓄光),
    // 並在 RECOVER 破綻窗畫青色弱點環 + 讓 boss 微暗 → 玩家學會「看預警閃 / 趁破綻打」。
    if (this._telegraphGfx) {
      const tel = this.boss.getTelegraphDescriptor();
      const plan = computeTelegraphPlan(tel);
      drawBossTelegraph(
        this._telegraphGfx,
        plan,
        this.boss.x,
        this.boss.y,
        this.playerX,
        this.playerY,
        this.boss.def.size / 2,
      );
      // 破綻微暗:RECOVER 期間把 boss 主視覺降到 ~0.6 alpha (恢復時拉回 1)。
      // 純視覺提示「現在 boss 露破綻」,不改任何命中 / 傷害邏輯。
      const dimTarget = (this.bossImage ?? this.bossSprite) as { setAlpha?: (a: number) => unknown } | null;
      if (dimTarget?.setAlpha) {
        try { dimTarget.setAlpha(tel.vulnerable ? 0.6 : 1); } catch { /* headless */ }
      }
    }

    // W8 PR-1 — faction-specific mechanic tick (additive after stock A/B).
    // Strategy fn may mutate boss.x (techno teleport) and may push extra
    // BossAttack entries (celestial fan / runic shield / techno beam). Missing
    // table entry = silent no-op, so hybrid/tier-2 bosses without a unique
    // mechanic keep stock behaviour.
    if (this._factionMechanicState) {
      const mechFn = FACTION_MECHANIC[this.boss.def.faction];
      if (mechFn) {
        const extra = mechFn(this.boss, nowMs, this._factionMechanicState, this.playerY);
        for (const atk of extra) this._executeBossAttack(atk);
      }
    }

    // boss-teleport-gimmick — uniqueGimmick:'teleport' 的 boss (任意門派) 在 HP<50%
    // 後每 5s 真的瞬移 (文件定義的行為,先前只有 techno 門派透過 FACTION_MECHANIC 生效)。
    // 落點限制在競技場右半邊 [width*0.5, width-80] → 仍在玩家視野、不會貼到左側玩家,
    // 純讓 boss「閃位、難被逼角」(老闆「魔王應具備瞬間移動」+「躲得掉」)。瞬移成功播一下
    // teleport-in 視覺殘影 (複用相機 flash,純美術)。只在 bossArrived 之後才允許瞬移
    // (fly-in / bob 期間不動位置)。學習與遊戲分離:純遊戲機制。
    // boss 2026-06-16「瞬移前 1-2 秒在目的地要有明顯記號」:拆兩段——
    //   Phase 1(到點):決定落點 + 在落點顯示明顯預警記號(脈動傳送門 + 警告環 + 倒數),
    //                   但 boss 先不動;
    //   Phase 2(1.5s 後):boss 真的瞬移到落點 + 清記號 + teleport-in 閃光/音。
    if (this._teleportGimmickState && this.bossArrived) {
      const st = this._teleportGimmickState;
      if (this._teleportPendingX !== null && nowMs >= this._teleportFireAtMs) {
        // Phase 2:真的瞬移
        this.boss.x = this._teleportPendingX;
        this._teleportPendingX = null;
        st.lastTeleportMs = nowMs; // 5s 間隔從「實際抵達」起算
        this._clearTeleportMarker();
        try { this.bossImage && (this.bossImage.x = this.boss.x); } catch { /* headless */ }
        try { this._cameraFlash(120, 120, 200, 255); } catch { /* headless */ }
        try { this._sfx?.play('boss-warning', 0.4); } catch { /* AudioContext busy */ }
      } else if (this._teleportPendingX === null && bossTeleportDue(this.boss, nowMs, st)) {
        // Phase 1:決定落點 + 預警記號(1.5s)
        const dest = pickBossTeleportTarget(this.playerX, GAME.width * 0.5, GAME.width - 80);
        this._teleportPendingX = dest;
        this._teleportFireAtMs = nowMs + BOSS_TELEPORT_TELEGRAPH_MS;
        try { this._showTeleportMarker(dest, this.boss.y); } catch { /* headless */ }
        try { this._sfx?.play('boss-warning', 0.25); } catch { /* AudioContext busy */ }
      }
    }

    // W8 PR-1 — castedShield decay: flip shield off once its window lapses
    // so subsequent player hits register damage again.
    if (this._bossShieldActive && nowMs > this._bossShieldEndMs) {
      this._bossShieldActive = false;
    }

    // Belt-and-suspenders: also fire on same-frame death (e.g. boss took the
    // killing blow earlier in this frame via a future direct call). Guarded
    // by _bossDefeatedFired so it only ever runs once.
    if (this.boss.hp <= 0 && !this._bossDefeatedFired) {
      this._bossDefeated();
    }
  }

  /**
   * boss 2026-06-16「瞬移前 1-2 秒在目的地要有明顯記號」:在落點顯示明顯預警 —— 垂直
   * 光柱(落點欄)+ 地面光暈 + 脈動警告環 + 連續收束的「傳送進來」環 + ⚠。boss 色。
   * 1.5s 後 boss 才真的瞬移過來(見 _updateBoss),抵達時 _clearTeleportMarker 清掉。
   * 純演出,headless 全 no-op。
   */
  private _showTeleportMarker(x: number, y: number): void {
    this._clearTeleportMarker();
    const objs: Phaser.GameObjects.GameObject[] = [];
    const col = this.boss?.def.color ?? 0xc8b0ff;
    const H = GAME.height;
    try {
      // 垂直光柱(落點欄)
      const beam = this.add.rectangle(x, H / 2, 30, H, col, 0.14).setDepth(8);
      try { beam.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      this.tweens.add({ targets: beam, alpha: 0.30, duration: 360, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      objs.push(beam);
      // 落點地面光暈
      const glow = this.add.circle(x, y, 48, col, 0.18).setDepth(8);
      try { glow.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      this.tweens.add({ targets: glow, scale: 1.18, alpha: 0.34, duration: 360, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      objs.push(glow);
      // 脈動警告環
      const ring = this.add.circle(x, y, 56, 0x000000, 0).setStrokeStyle(4, col, 0.95).setDepth(9);
      try { ring.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      this.tweens.add({ targets: ring, scale: 1.28, duration: 420, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      objs.push(ring);
      // 連續收束「傳送進來」環(由大縮到點,告訴你 boss 要出現在這)
      for (let i = 0; i < 2; i++) {
        const c = this.add.circle(x, y, 100, 0x000000, 0).setStrokeStyle(3, col, 0.85).setDepth(9).setScale(1.4).setAlpha(0);
        try { c.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
        this.tweens.add({ targets: c, scale: 0.18, alpha: 0.9, duration: 760, delay: i * 380, repeat: -1, ease: 'Cubic.easeIn' });
        objs.push(c);
      }
      // ⚠ 警告圖示
      const warn = this.add.text(x, y - 86, '⚠', { fontSize: '34px', fontFamily: 'sans-serif' }).setOrigin(0.5).setDepth(10);
      this.tweens.add({ targets: warn, alpha: 0.3, duration: 300, yoyo: true, repeat: -1 });
      objs.push(warn);
    } catch { /* headless */ }
    this._teleportMarker = objs.length ? objs : null;
  }

  /** 清掉瞬移落點預警記號(瞬移完成 / boss 死亡 / 換場)。 */
  private _clearTeleportMarker(): void {
    if (this._teleportMarker) {
      for (const o of this._teleportMarker) {
        try { this.tweens.killTweensOf(o); } catch { /* noop */ }
        try { o.destroy(); } catch { /* noop */ }
      }
      this._teleportMarker = null;
    }
  }

  /**
   * Execute a boss attack with horizontal-mode angle transforms.
   *
   * The boss-pool defs compute angles assuming the boss descends from above (top-down).
   * In horizontal mode the boss sits to the right of the player, so we must re-orient:
   *
   *  single-shot / beam-cast:
   *    intermediateAngle = atan2(playerY - bossY, bossX - playerX)
   *      -- gives angle from rightward axis aimed toward player
   *    finalAngle = π + intermediateAngle  (π flips to leftward base)
   *    vx = cos(finalAngle) * speed  (negative = leftward)
   *    vy = sin(finalAngle) * speed
   *
   *  fan-shot:
   *    Pool offset `a` is relative to "straight down" in top-down.
   *    In horizontal mode, base direction is left (π).
   *    finalAngle = π + a  → bullets spread left with ±vy spread
   *
   *  spawn-minions:
   *    Minion spawns at boss.x, random y within [ENEMY_Y_MIN, ENEMY_Y_MAX], vx = -150 (left).
   *
   *  silence-player / freeze-player / silence-wave:
   *    No directional component — applied directly to player timers.
   */
  _executeBossAttack(atk: BossAttack): void {
    if (!this.boss) return;
    const boss = this.boss;
    // G07: pick faction-themed particle preset for the boss's attack visuals.
    const burstPreset = this._particlesPresetForBoss('burst');
    const fanPreset = this._particlesPresetForBoss('fan');

    switch (atk.kind) {
      case 'single-shot': {
        // Re-compute aimed angle toward player from right side, then flip to left.
        const intermediateAngle = Math.atan2(this.playerY - boss.y, boss.x - this.playerX);
        this._spawnBossBullet(boss.x, boss.y, Math.PI + intermediateAngle, atk.speedPxPerSec, atk.damage);
        // G07: JSON-tunable muzzle particles (replaces hardcoded emitter config).
        try { this._juice?.spawnParticles(burstPreset, boss.x, boss.y); } catch { /* test harness */ }
        break;
      }

      case 'beam-cast': {
        // Same re-orientation as single-shot (high-speed beam variant)
        const intermediateAngle = Math.atan2(this.playerY - boss.y, boss.x - this.playerX);
        this._spawnBossBullet(boss.x, boss.y, Math.PI + intermediateAngle, atk.speedPxPerSec, atk.damage);
        try { this._juice?.spawnParticles(burstPreset, boss.x, boss.y); } catch { /* test harness */ }
        break;
      }

      case 'fan-shot': {
        // Pool angles are offsets from "down". Remap to "left" (π base).
        //
        // boss-behavior: 多砲管 — 大魔王身上沿身體高度分 3 個 Y 偏移原點射出
        // (模擬多砲管),讓「砲管多」在彈幕上看得出來. 偏移幅度依 boss 大小縮放,
        // 但每點只射「子集」角度以免子彈總數爆炸(效能/可讀性).
        const barrels = boss.def.size >= 150 ? 3 : 1;
        const halfH = boss.def.size * 0.28; // 砲口分布在身體高度約 ±28%
        if (barrels === 1) {
          for (const a of atk.angles) {
            this._spawnBossBullet(boss.x, boss.y, Math.PI + a, atk.speedPxPerSec, atk.damage);
          }
        } else {
          // 3 砲管: 上/中/下各射全部角度的 ~1/3 子集(交錯),總彈數 ≈ 原本.
          const offsets = [-halfH, 0, halfH];
          for (let bi = 0; bi < barrels; bi++) {
            const oy = boss.y + offsets[bi];
            for (let i = bi; i < atk.angles.length; i += barrels) {
              this._spawnBossBullet(boss.x, oy, Math.PI + atk.angles[i], atk.speedPxPerSec, atk.damage);
            }
          }
        }
        // JUICE (task #19): fan-shot = boss attack-B. Heavier shake than
        // player-hit so the kid feels the boss "winding up". 70ms 0.012.
        try { this.cameras?.main?.shake?.(SHAKE_BOSS_FAN_MS, SHAKE_BOSS_FAN_INTENSITY); }
        catch { /* test harness */ }
        // G07: bigger fan-shot burst — uses the "fan" variant (nova/rally/overload).
        try { this._juice?.spawnParticles(fanPreset, boss.x, boss.y); } catch { /* test harness */ }
        break;
      }

      case 'homing-shot': {
        // boss-behavior: 追蹤彈. 初始向量朝玩家略微散開,之後每幀各自導引.
        // 數量受 atk.count 控制,且 _spawnHomingBullet 內部會 enforce 全域上限.
        // 學習與遊戲分離:純遊戲機制.
        const baseAngle = Math.atan2(this.playerY - boss.y, this.playerX - boss.x);
        const n = Math.max(1, atk.count);
        const spread = Math.min(0.9, 0.18 * (n - 1)); // 初始散開總弧度
        for (let i = 0; i < n; i++) {
          const t = n === 1 ? 0.5 : i / (n - 1);
          const angle = baseAngle - spread / 2 + spread * t;
          this._spawnHomingBullet(boss.x, boss.y, angle, atk.speedPxPerSec, atk.turnRatePerSec, atk.damage);
        }
        // 與 fan 一樣給點震屏回饋(追蹤彈是高威脅 attack).
        try { this.cameras?.main?.shake?.(SHAKE_BOSS_FAN_MS, SHAKE_BOSS_FAN_INTENSITY); }
        catch { /* test harness */ }
        try { this._juice?.spawnParticles(fanPreset, boss.x, boss.y); } catch { /* test harness */ }
        break;
      }

      case 'ring-burst': {
        // boss-bullet-hell (老闆:「子彈很多一次發個 20 顆」): 360° 環形均勻彈幕.
        // 不只朝玩家 → 比扇形更有「彈幕」壓迫感. 子彈速度由 atk 控制(刻意偏慢),
        // K-3 可往環的縫隙 / 擴散外圈閃. 學習與遊戲分離:純遊戲機制.
        this._fireBossRing(atk.count, atk.speedPxPerSec, atk.damage, {
          layers: atk.layers,
          gapRad: atk.gapRad,
          rotateDps: atk.rotateDps,
        });
        // 環形大招 = 重量級回饋,給較重震屏.
        try { this.cameras?.main?.shake?.(SHAKE_BOSS_FAN_MS, SHAKE_BOSS_FAN_INTENSITY); }
        catch { /* test harness */ }
        try { this._juice?.spawnParticles(fanPreset, boss.x, boss.y); } catch { /* test harness */ }
        break;
      }

      case 'spawn-minions': {
        // boss 回饋「叫出正方形很醜」: 召喚物原本自己畫紅橘 fillRect 方塊(沒走
        // 圖載入路徑 → 醜方塊)。改走 _spawnOneEnemy,跟一般小兵一樣套敵人 PNG
        // (現指向魔王圖)+ 翻轉 + 保持長寬比。bonusSpeed>0 → 用 fast variant 近似
        // 加速召喚。學習與遊戲分離:純遊戲機制。
        const variant: HEnemyVariant = (atk.bonusSpeed ?? 0) > 0 ? 'fast' : 'basic';
        for (let i = 0; i < atk.count; i++) {
          const spawnY = ENEMY_Y_MIN + Math.random() * (ENEMY_Y_MAX - ENEMY_Y_MIN);
          this._spawnOneEnemy(variant, 'straight', boss.x, spawnY);
        }
        break;
      }

      case 'silence-player': {
        const nowMs = this.time.now;
        // W8 PR-1 — for a runic boss the `silence-player` kind is repurposed
        // as the boss's own castedShield (FactionBossAI runicMechanic returns
        // this kind every 30 s). Other factions keep stock behaviour — the
        // player is silenced. Routing on boss.def.faction keeps the BossAttack
        // union shape stable; introducing a dedicated kind would force every
        // attack-handling site to learn it.
        if (this.boss?.def.faction === 'runic') {
          this._bossShieldActive = true;
          this._bossShieldEndMs = nowMs + atk.durationMs;
        } else {
          this.playerSilencedUntilMs = Math.max(this.playerSilencedUntilMs, nowMs + atk.durationMs);
        }
        break;
      }

      case 'freeze-player': {
        const nowMs = this.time.now;
        this.playerFrozenUntilMs = Math.max(this.playerFrozenUntilMs, nowMs + atk.durationMs);
        break;
      }

      case 'silence-wave': {
        // Treat as player silence (no radial logic needed for MVP)
        const nowMs = this.time.now;
        this.playerSilencedUntilMs = Math.max(this.playerSilencedUntilMs, nowMs + atk.durationMs);
        break;
      }

      case 'laser-beam': {
        // boss-movesets (老闆「多加雷射光」): SHU_MO 數魔招牌。一條持續貫穿光束。
        // 角度在「現在」鎖定朝玩家 → 預警期間玩家看得到方向、移過去就能閃 (不追)。
        // warn (細線預警) → fire (粗光束命中) → 消失。學習與遊戲分離:純遊戲機制。
        const angle = Math.atan2(this.playerY - boss.y, this.playerX - boss.x);
        this._spawnBossLaser(boss.x, boss.y, angle, atk.thickness, atk.damage, atk.warnMs, atk.fireMs);
        try { this.cameras?.main?.shake?.(SHAKE_BOSS_FAN_MS, SHAKE_BOSS_FAN_INTENSITY); }
        catch { /* test harness */ }
        try { this._juice?.spawnParticles(burstPreset, boss.x, boss.y); } catch { /* test harness */ }
        break;
      }

      case 'magic-bolt': {
        // boss-movesets (老闆「多加魔法元素」): FU_HUN/YUAN_SU 用。波浪前進的魔法彈,
        // 紫藍視覺、sine 擺動 → 跟直線彈明顯不同。朝玩家略散開,速度適中可預判。
        // 學習與遊戲分離:純遊戲機制。
        const baseAngle = Math.atan2(this.playerY - boss.y, this.playerX - boss.x);
        const n = Math.max(1, atk.count);
        const spread = Math.min(0.8, 0.16 * (n - 1));
        for (let i = 0; i < n; i++) {
          const t = n === 1 ? 0.5 : i / (n - 1);
          const angle = baseAngle - spread / 2 + spread * t;
          this._spawnMagicBolt(boss.x, boss.y, angle, atk.speedPxPerSec, atk.amplitudePx, atk.periodMs, atk.damage);
        }
        try { this.cameras?.main?.shake?.(SHAKE_BOSS_FAN_MS, SHAKE_BOSS_FAN_INTENSITY); }
        catch { /* test harness */ }
        try { this._juice?.spawnParticles(fanPreset, boss.x, boss.y); } catch { /* test harness */ }
        break;
      }

      case 'slow-player': {
        // boss-movesets (老闆「多加緩速元素」): FU_HUN 符魂招牌。玩家移速暫時變慢
        // (×slowMult),非定身 (仍能移動避免挫折)。有畫面藍光提示 (_drawSlowOverlay)。
        // 學習與遊戲分離:純遊戲機制。
        const nowMs = this.time.now;
        this.playerSlowedUntilMs = Math.max(this.playerSlowedUntilMs, nowMs + atk.durationMs);
        this.playerSlowMult = atk.slowMult;
        try { this._juice?.spawnParticles(fanPreset, this.playerX, this.playerY); } catch { /* test harness */ }
        break;
      }

      case 'shockwave': {
        // boss-aoe (老闆「大面積傷害」+「要有地方可以躲」): 從 boss 位置鎖定圓心,
        // 預警期間畫擴張危險圈 (warnMs),玩家走出圈外即安全;預警結束「轟」一下只在
        // 圈內判定傷害。warnMs 夠長 → K-3 一定來得及走出去 (可躲)。圓心鎖在 spawn 當下
        // (不追玩家),所以「現在站圈邊往外走」一定能離開。複用 ring/explode 視覺。
        this._spawnShockwave(boss.x, boss.y, atk.radiusPx, atk.warnMs, atk.damage);
        // 大招預警 = 重量級回饋:震屏 + 召喚式粒子。
        try { this.cameras?.main?.shake?.(SHAKE_BOSS_FAN_MS, SHAKE_BOSS_FAN_INTENSITY); }
        catch { /* test harness */ }
        try { this._sfx?.play('boss-warning', 0.5); } catch { /* AudioContext busy */ }
        break;
      }
    }
  }

  /**
   * boss-aoe: 生成一個大面積衝擊波 (預警圈 → 引爆判定 → 殘留消失)。圓心 (x,y) 在此
   * 鎖定 (不追玩家);warnMs 預警期間玩家看得到危險圈、走出去即安全;預警結束只在
   * 圈內判定一次傷害。命中判定在 _updateShockwaves、渲染在 _drawShockwaves。
   * 學習與遊戲分離:純遊戲機制。
   *
   * @param radiusPx  危險圈/判定半徑 (px)
   * @param warnMs    預警時間 (ms) — 夠長才可躲 (指派處強制 ≥ SHOCKWAVE_MIN_SAFE_WARN_MS)
   */
  _spawnShockwave(x: number, y: number, radiusPx: number, warnMs: number, damage: number): void {
    const nowMs = this.time.now;
    const sprite = this.add.graphics();
    // 危險圈要畫在「boss / 場景之上、HUD 之下」(depth 8):boss 變身圖 depth≈1,
    // HUD depth≥10 — 不設 depth 會被巨大的 boss 圖蓋住,小孩看不到危險範圍 (可躲鐵則
    // 依賴看得見)。headless graphics stub 沒有 setDepth → try 包住。
    try { sprite.setDepth(8); } catch { /* headless */ }
    this.bossShockwaves.push({
      x, y, radiusPx, damage,
      detonateMs: nowMs + warnMs,
      // 引爆後殘留 260ms 的衝擊環視覺 (純美術),然後整體回收。
      expireMs: nowMs + warnMs + 260,
      detonated: false,
      alive: true,
      sprite,
    });
  }

  /**
   * boss-aoe: tick 衝擊波 — 預警 → 到 detonateMs「引爆」一次判定 (僅圈內玩家受傷,
   * 圈外 0 傷) → expireMs 後回收。回傳本幀是否有任一衝擊波「引爆且命中玩家」。
   * 渲染在 _drawShockwaves (render loop)。學習與遊戲分離:純遊戲機制。
   */
  private _updateShockwaves(): boolean {
    const nowMs = this.time.now;
    let hit = false;
    for (const w of this.bossShockwaves) {
      if (!w.alive) continue;
      if (nowMs >= w.expireMs) { w.alive = false; continue; }
      // 引爆瞬間 (跨過 detonateMs 的第一幀) 做一次圈內判定。
      if (!w.detonated && nowMs >= w.detonateMs) {
        w.detonated = true;
        if (shockwaveHitsPlayer(w.x, w.y, w.radiusPx, this.playerX, this.playerY)) {
          hit = true;
          if (!this._absorbPlayerHit()) {
            this.playerHp = Math.max(0, this.playerHp - w.damage);
            this._onPlayerHit(w.damage);
            if (this.playerHp <= 0 && !this.gameOver) { this._gameOver(nowMs); }
          }
        }
      }
    }
    for (const w of this.bossShockwaves.filter((w) => !w.alive)) { try { w.sprite.destroy(); } catch { /* headless */ } }
    this.bossShockwaves = this.bossShockwaves.filter((w) => w.alive);
    return hit;
  }

  /**
   * boss-aoe: 畫衝擊波。預警階段 = 一個從小擴張到 radiusPx 的危險圈 (紅虛線環 + 半透
   * 紅填充,讓 K-3 看清「圈內危險、圈外安全」);引爆瞬間 = 亮白衝擊環一閃後淡出。
   * render loop 每幀 clear + 重畫。學習與遊戲分離:純遊戲機制。
   */
  private _drawShockwaves(): void {
    const nowMs = this.time.now;
    for (const w of this.bossShockwaves) {
      if (!w.alive) continue;
      const g = w.sprite;
      try {
        g.clear();
        if (nowMs < w.detonateMs) {
          // 預警:整段都把「最終危險範圍」以最大半徑顯示 (玩家看最終圈最保險 —— 站圈外
          // 即安全),半透紅填充 + 脈動紅環邊框讓危險區醒目;內圈黃環標記圓心提示「往外離開」。
          const r = w.radiusPx;
          const pulse = 0.12 + 0.10 * Math.abs(Math.sin(nowMs / 140));
          g.fillStyle(0xff3030, pulse);
          g.fillCircle(w.x, w.y, r);
          g.lineStyle(4, 0xff5252, 0.85);
          g.strokeCircle(w.x, w.y, r);
          g.lineStyle(2, 0xffeb3b, 0.7);
          g.strokeCircle(w.x, w.y, Math.max(8, r * 0.18));
        } else {
          // 引爆:亮白衝擊環一閃,沿殘留期淡出 + 略外擴 (純美術)。
          const t = Math.min(1, (nowMs - w.detonateMs) / 260);
          const alpha = 1 - t;
          const r = w.radiusPx * (1 + 0.12 * t);
          g.lineStyle(10, 0xffffff, 0.85 * alpha);
          g.strokeCircle(w.x, w.y, r);
          g.lineStyle(5, 0xff5252, 0.7 * alpha);
          g.strokeCircle(w.x, w.y, r);
        }
        try { g.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      } catch { /* headless graphics stub */ }
    }
  }

  /**
   * G07: pick particle preset for current boss faction × attack variant.
   *   variant='burst' → single-shot / beam-cast muzzle FX  (subtle)
   *   variant='fan'   → fan-shot wide release              (heavier nova)
   *
   * Falls back to a celestial preset if boss has unknown faction (defensive).
   * Caller wraps in try/catch so test harness can't crash on missing scene state.
   */
  private _particlesPresetForBoss(variant: 'burst' | 'fan'): string {
    const faction = this.boss?.def.faction ?? 'celestial';
    // Map (faction, variant) → preset name in particles-presets.json.
    const map: Record<string, { burst: string; fan: string }> = {
      celestial: { burst: 'fire-burst', fan: 'fire-nova' },
      runic: { burst: 'ice-shatter', fan: 'ice-crystal-burst' },
      elemental: { burst: 'plant-bloom', fan: 'plant-thorn-burst' },
      techno: { burst: 'electric-arc', fan: 'electric-overload' },
      dominion: { burst: 'dominion-wave', fan: 'dominion-rally' },
    };
    return (map[faction] ?? map.celestial)[variant];
  }

  /**
   * W8 PR-1 — spawn one minion entity at the given world coordinates.
   * Shares the same HEnemy 'basic' template the in-attack `spawn-minions`
   * case uses, but lets callers pin x/y precisely (used by elemental
   * death-respawn so minions appear AT the boss death point, not at random
   * y-jitter in the spawn band).
   */
  _spawnBossMinionAt(x: number, y: number): void {
    const baseVx = this._enemyVx;
    const entity = new HEnemy({ x, y }, { vx: baseVx, vy: 0 });
    const sprite = this.add.graphics();
    sprite.fillStyle(0xff7043, 1);
    sprite.fillRect(-16, -16, 32, 32);
    sprite.setRotation(-Math.PI / 2);
    this.enemies.push({ entity, sprite });
  }

  /**
   * Spawn a boss bullet using standard angle convention (0=right, π=left).
   * @param x       spawn x
   * @param y       spawn y
   * @param angle   final angle — π = straight left, π±offset = spread
   * @param speed   px/sec magnitude
   * @param damage  hp damage on hit
   */
  _spawnBossBullet(x: number, y: number, angle: number, speed: number, damage: number): void {
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;
    const sprite = this.add.graphics();
    // boss-bullet-glow (2026-05-31, boss「魔王子彈也要畫好看」): 扇/環主力彈 =
    // 橘紅火焰能量球(外發光+暖核+白熱中心,ADD blend)。圓形利各方向一致;
    // 碰撞箱不變(glow 純視覺)。headless graphics stub 走 fallback。
    try {
      sprite.fillStyle(0xff5722, 0.22); sprite.fillCircle(0, 0, 11); // outer glow
      sprite.fillStyle(0xff7043, 0.55); sprite.fillCircle(0, 0, 8);  // warm aura
      sprite.fillStyle(0xff8a65, 0.95); sprite.fillCircle(0, 0, 6);  // body
      sprite.fillStyle(0xffccbc, 1);    sprite.fillCircle(0, 0, 3.2);// hot core
      sprite.fillStyle(0xffffff, 1);    sprite.fillCircle(0, 0, 1.6);// white center
      try { sprite.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
    } catch {
      try { sprite.fillStyle(0xff5252, 1); sprite.fillRect(-6, -6, 12, 12); } catch { /* noop */ }
    }
    this.bossBullets.push({ x, y, vx, vy, damage, alive: true, sprite });
  }

  /**
   * boss-bullet-hell (老闆:「子彈很多一次發個 20 顆」): 從 boss 中心 360° 均勻
   * 發射 count 顆一般 boss 彈 (預設 20). 與 _fireBossFan / fan-shot 不同 — 這個
   * 不朝玩家收斂,而是平均鋪滿全圓,製造「環形彈幕」壓迫感. 走既有 _spawnBossBullet
   * (同一條碰撞/更新路徑),不需新增彈種. 學習與遊戲分離:純遊戲機制.
   *
   * @param count  環上子彈數 (≥1;老闆要的 20)
   * @param speed  px/秒 (刻意偏慢讓 K-3 能從縫隙閃)
   * @param damage 命中傷害
   */
  _fireBossRing(
    count: number,
    speed: number,
    damage: number,
    opts?: { layers?: number; gapRad?: number; rotateDps?: number },
  ): void {
    if (!this.boss) return;
    const boss = this.boss;
    // combat-polish: 環彈幾何 (雙層/缺口/旋轉) 抽成 computeRingShots 純函式 (有單元測試)。
    // 場景只負責把每顆 spawn 出去 → 邏輯與渲染分離,structure 由測試保證。
    const shots = computeRingShots(count, speed, opts);
    for (const s of shots) {
      this._spawnBossBulletVel(boss.x, boss.y, s.vx, s.vy, damage);
    }
  }

  /**
   * combat-polish: 以「直接給速度向量」生成一顆一般 boss 彈 (旋轉環需要切向分量,
   * 不能只用單一 angle)。複用 _spawnBossBullet 的視覺/碰撞路徑:先算出等效 angle+speed
   * 再轉呼叫,確保子彈外觀與碰撞跟其他環彈完全一致 (同一路徑,不新增彈種)。
   */
  _spawnBossBulletVel(x: number, y: number, vx: number, vy: number, damage: number): void {
    const speed = Math.hypot(vx, vy);
    const angle = Math.atan2(vy, vx);
    this._spawnBossBullet(x, y, angle, speed, damage);
  }

  /**
   * boss-behavior: 生成一顆追蹤彈 (homing). 初始角度由 caller 給(朝玩家略散開),
   * 之後 _updateBossBullets 每幀以 stepHomingBullet 限速轉向(可閃,非必中).
   * 視覺:紫紅色稍大方塊以區別一般 boss 彈. 同時存在數量超過 MAX_HOMING_BULLETS
   * 時回收最舊的一顆(效能 + 畫面清晰). 學習與遊戲分離:純遊戲機制.
   *
   * @param angle           初始飛行角度(標準座標,0=右,π=左)
   * @param speed           px/秒
   * @param turnRatePerSec  度/秒 最大轉向(90~150 = 可閃)
   */
  _spawnHomingBullet(
    x: number,
    y: number,
    angle: number,
    speed: number,
    turnRatePerSec: number,
    damage: number,
  ): void {
    // 回收最舊的 homing 彈,維持上限.
    let homingCount = 0;
    for (const b of this.bossBullets) { if (b.alive && b.homing) homingCount++; }
    if (homingCount >= MAX_HOMING_BULLETS) {
      const oldest = this.bossBullets.find((b) => b.alive && b.homing);
      if (oldest) { oldest.alive = false; }
    }
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;
    const sprite = this.add.graphics();
    // boss-bullet-glow: 追蹤飛彈 = 紫紅電漿球(色系跟一般橘紅彈區別,稍大更醒目)。
    // ADD blend 發光;碰撞箱不變。headless 走 fallback。
    try {
      sprite.fillStyle(0xff2bd0, 0.25); sprite.fillCircle(0, 0, 13); // outer glow
      sprite.fillStyle(0xe040fb, 0.55); sprite.fillCircle(0, 0, 9);  // violet aura
      sprite.fillStyle(0xff80ab, 0.95); sprite.fillCircle(0, 0, 6.5);// body
      sprite.fillStyle(0xffe0f0, 1);    sprite.fillCircle(0, 0, 3.4);// hot core
      sprite.fillStyle(0xffffff, 1);    sprite.fillCircle(0, 0, 1.7);// white center
      try { sprite.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
    } catch {
      try { sprite.fillStyle(0xff2bd0, 1); sprite.fillRect(-7, -7, 14, 14); } catch { /* noop */ }
    }
    this.bossBullets.push({
      x, y, vx, vy, damage, alive: true, sprite,
      homing: true, speedPxPerSec: speed, turnRatePerSec,
      homingUntilMs: this.time.now + HOMING_DURATION_MS,
    });
  }

  /**
   * boss-movesets (老闆「多加緩速元素」): 玩家被緩速時畫畫面邊緣藍光提示,讓小孩
   * 知道「我變慢了」(非定身,只是慢)。緩速結束自動清除。render loop 每幀呼叫。
   */
  private _drawSlowOverlay(): void {
    const active = this.time && this.time.now < this.playerSlowedUntilMs;
    // 懶建立一個全螢幕邊框 Graphics (只在第一次需要時)。
    const self = this as unknown as { _slowOverlayGfx?: Phaser.GameObjects.Graphics };
    if (active && !self._slowOverlayGfx) {
      try { self._slowOverlayGfx = this.add.graphics(); self._slowOverlayGfx.setDepth(50); } catch { /* headless */ }
    }
    const g = self._slowOverlayGfx;
    if (!g) return;
    try {
      g.clear();
      if (active) {
        // 玩家身上的藍光環 + 畫面四角藍暈 (簡單、明顯、不擋視線)。
        const pulse = 0.3 + 0.2 * Math.abs(Math.sin(this.time.now / 200));
        g.fillStyle(0x40c4ff, pulse * 0.5);
        g.fillCircle(this.playerX, this.playerY, 26);
        g.lineStyle(4, 0x40c4ff, pulse);
        g.strokeRect(2, 2, GAME.width - 4, GAME.height - 4);
        try { g.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      }
    } catch { /* headless graphics stub */ }
  }

  /**
   * boss-movesets (老闆「多加雷射光」): 生成一條雷射光束 (SHU_MO 招牌)。
   * 角度由 caller 鎖定 (spawn 當下朝玩家,之後不追 → 可閃)。先細線預警 warnMs,
   * 再粗光束開火 fireMs (期間命中判定),然後整條消失。學習與遊戲分離:純遊戲機制。
   *
   * @param angle      光束方向 (rad, 標準座標 0=右)
   * @param thickness  開火時光束全寬 (px)
   * @param warnMs     預警 (細線) 持續時間
   * @param fireMs     開火 (粗光束 + 命中) 持續時間
   */
  _spawnBossLaser(
    x: number,
    y: number,
    angle: number,
    thickness: number,
    damage: number,
    warnMs: number,
    fireMs: number,
  ): void {
    const nowMs = this.time.now;
    const sprite = this.add.graphics();
    this.bossLasers.push({
      x, y, angleRad: angle, thickness, damage,
      warnEndMs: nowMs + warnMs,
      fireEndMs: nowMs + warnMs + fireMs,
      alive: true, sprite,
    });
  }

  /**
   * boss-movesets (老闆「多加魔法元素」): 生成一顆波浪 (sine) 前進的魔法彈
   * (FU_HUN/YUAN_SU 用)。主行進向量 = base(vx,vy);_updateBossBullets 每幀沿
   * 「行進方向的垂直軸」加 sine 偏移 → 視覺波浪。紫藍發光球以區別直線橘紅彈。
   * 學習與遊戲分離:純遊戲機制。
   *
   * @param angle        主行進角度 (rad, 標準座標 0=右)
   * @param speed        主行進速度 px/秒
   * @param amplitudePx  波幅 px
   * @param periodMs     波動週期 ms
   */
  _spawnMagicBolt(
    x: number,
    y: number,
    angle: number,
    speed: number,
    amplitudePx: number,
    periodMs: number,
    damage: number,
  ): void {
    const baseVx = Math.cos(angle) * speed;
    const baseVy = Math.sin(angle) * speed;
    const sprite = this.add.graphics();
    // boss-bullet-glow: 魔法彈 = 紫藍魔法能量球 (跟橘紅直線彈、紫紅追蹤彈都不同色)。
    // ADD blend 發光;碰撞箱不變。headless 走 fallback。
    try {
      sprite.fillStyle(0x3d5afe, 0.22); sprite.fillCircle(0, 0, 12); // outer glow (blue)
      sprite.fillStyle(0x536dfe, 0.55); sprite.fillCircle(0, 0, 8.5);// azure aura
      sprite.fillStyle(0x8c9eff, 0.95); sprite.fillCircle(0, 0, 6);  // body
      sprite.fillStyle(0xb388ff, 1);    sprite.fillCircle(0, 0, 3.4);// violet core
      sprite.fillStyle(0xffffff, 1);    sprite.fillCircle(0, 0, 1.7);// white center
      try { sprite.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
    } catch {
      try { sprite.fillStyle(0x536dfe, 1); sprite.fillRect(-7, -7, 14, 14); } catch { /* noop */ }
    }
    this.bossBullets.push({
      x, y, vx: baseVx, vy: baseVy, damage, alive: true, sprite,
      magic: true, baseVx, baseVy,
      magicAmp: amplitudePx, magicPeriodMs: periodMs, magicBornMs: this.time.now,
    });
  }

  /**
   * boss-movesets: tick 雷射光束 — 推進 warn→fire→死亡,並在 fire 階段做命中判定。
   * 回傳本幀是否有任一雷射「正在開火且命中玩家」(供測試/碰撞使用)。
   * 渲染在 _drawBossLasers (render loop)。學習與遊戲分離:純遊戲機制。
   */
  private _updateBossLasers(): boolean {
    const nowMs = this.time.now;
    let hit = false;
    for (const l of this.bossLasers) {
      if (!l.alive) continue;
      if (nowMs >= l.fireEndMs) { l.alive = false; continue; }
      const firing = nowMs >= l.warnEndMs;
      if (firing && this._laserHitsPlayer(l)) {
        hit = true;
        if (!this._absorbPlayerHit()) {
          // 每幀最多扣一次:用 warnEnd 之後的「已扣過」旗避免 fire 期間每幀爆血。
          if (!(l as BLaserEntry & { _struck?: boolean })._struck) {
            (l as BLaserEntry & { _struck?: boolean })._struck = true;
            this.playerHp = Math.max(0, this.playerHp - l.damage);
            this._onPlayerHit(l.damage);
            if (this.playerHp <= 0 && !this.gameOver) { this._gameOver(nowMs); }
          }
        }
      }
    }
    for (const l of this.bossLasers.filter((l) => !l.alive)) { try { l.sprite.destroy(); } catch { /* headless */ } }
    this.bossLasers = this.bossLasers.filter((l) => l.alive);
    return hit;
  }

  /**
   * boss-movesets: 點到「雷射線段」的垂直距離 < 半寬 ⇒ 命中。雷射視為從 (x,y) 沿
   * angleRad 的半無限射線 (只取正向投影)。純幾何、可單測。
   */
  private _laserHitsPlayer(l: BLaserEntry): boolean {
    const dx = Math.cos(l.angleRad);
    const dy = Math.sin(l.angleRad);
    const px = this.playerX - l.x;
    const py = this.playerY - l.y;
    const proj = px * dx + py * dy; // 沿光束方向的投影
    if (proj < 0) return false;     // 玩家在 boss 後方,不算命中
    // 垂直距離 = |叉積|
    const perp = Math.abs(px * dy - py * dx);
    const pHalf = 16; // 玩家半徑近似
    return perp < l.thickness / 2 + pHalf;
  }

  /**
   * boss-movesets: 畫雷射光束。warn 階段 = 細亮預警線 (閃爍),fire 階段 = 粗發光
   * 光束。從 (x,y) 沿 angleRad 畫到畫面外。render loop 每幀 clear + 重畫。
   */
  private _drawBossLasers(): void {
    const nowMs = this.time.now;
    const reach = GAME.width + GAME.height; // 夠長畫出畫面
    for (const l of this.bossLasers) {
      if (!l.alive) continue;
      const g = l.sprite;
      try {
        g.clear();
        const ex = l.x + Math.cos(l.angleRad) * reach;
        const ey = l.y + Math.sin(l.angleRad) * reach;
        const firing = nowMs >= l.warnEndMs;
        if (!firing) {
          // 預警:細閃爍亮線 (讓 K-3 看清方向先閃)。
          const blink = 0.35 + 0.35 * Math.abs(Math.sin(nowMs / 80));
          g.lineStyle(3, 0xffeb3b, blink);
          g.beginPath(); g.moveTo(l.x, l.y); g.lineTo(ex, ey); g.strokePath();
        } else {
          // 開火:粗發光光束 (外光暈 + 亮核)。
          g.lineStyle(l.thickness, 0xff1744, 0.35);
          g.beginPath(); g.moveTo(l.x, l.y); g.lineTo(ex, ey); g.strokePath();
          g.lineStyle(l.thickness * 0.5, 0xff5252, 0.7);
          g.beginPath(); g.moveTo(l.x, l.y); g.lineTo(ex, ey); g.strokePath();
          g.lineStyle(3, 0xffffff, 1);
          g.beginPath(); g.moveTo(l.x, l.y); g.lineTo(ex, ey); g.strokePath();
        }
        try { g.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      } catch { /* headless graphics stub */ }
    }
  }

  private _updateBossBullets(dtSec: number): void {
    // boss-movesets: 雷射光束與 boss 彈同一更新節奏 tick (預警→開火→命中→消失)。
    this._updateBossLasers();
    // boss-aoe: 衝擊波同節奏 tick (預警→引爆判定→殘留消失)。
    this._updateShockwaves();
    const frozen = this.time.now < this._enemyFreezeUntilMs; // v1.1 ultimate (含 homing)
    const deltaMs = dtSec * 1000;
    for (const b of this.bossBullets) {
      if (!b.alive) continue;
      if (!frozen) {
        const stillHoming = b.homing
          && (b.homingUntilMs === undefined || this.time.now < b.homingUntilMs);
        if (stillHoming) {
          // boss-behavior: 限速轉向朝玩家,然後位移(stepHomingBullet mutate x/y/vx/vy).
          // 凍結時(frozen)整段跳過 → 大招凍結也凍 homing 彈.
          stepHomingBullet(b, this.playerX, this.playerY, deltaMs);
        } else if (b.magic && b.baseVx !== undefined && b.baseVy !== undefined) {
          // boss-movesets: 魔法彈波浪前進。沿主行進方向直線推進,再沿「垂直軸」
          // 加 sine 偏移 → 視覺波浪 (跟直線彈明顯不同)。命中盒用 b.x/b.y 中心。
          const elapsed = this.time.now - (b.magicBornMs ?? this.time.now);
          const period = b.magicPeriodMs ?? 900;
          const amp = b.magicAmp ?? 40;
          const speed = Math.hypot(b.baseVx, b.baseVy) || 1;
          const dirx = b.baseVx / speed;
          const diry = b.baseVy / speed;
          // 垂直單位向量
          const perpx = -diry;
          const perpy = dirx;
          // 上一幀與這一幀的 sine 位移差 → 疊加到位置 (沿主向量 + 垂直擺動)。
          const prevWave = Math.sin((elapsed - dtSec * 1000) / period * Math.PI * 2) * amp;
          const wave = Math.sin(elapsed / period * Math.PI * 2) * amp;
          const dWave = wave - prevWave;
          b.x += b.baseVx * dtSec + perpx * dWave;
          b.y += b.baseVy * dtSec + perpy * dWave;
        } else {
          // 直線飛行 (一般彈,或導引期限已過的 homing 彈 → 飛出畫面後回收).
          b.x += b.vx * dtSec;
          b.y += b.vy * dtSec;
        }
      }
      if (b.x < -32 || b.x > GAME.width + 32 || b.y < -32 || b.y > GAME.height + 32) {
        b.alive = false;
      }
    }
    for (const b of this.bossBullets.filter((b) => !b.alive)) { b.sprite.destroy(); }
    this.bossBullets = this.bossBullets.filter((b) => b.alive);
  }

  // ── Enemy bullets (task #2) ──────────────────────────────────────────────

  /**
   * ENEMY-BULLETS (task #2): tick each alive enemy's fire cooldown. When
   * tryFire returns a spec we spawn a small red bullet that travels leftward.
   * Boss minions also fire — they're stored in `enemies[]` so they participate
   * naturally with no extra wiring.
   */
  private _tickEnemyFire(nowMs: number): void {
    if (nowMs < this._enemyFreezeUntilMs) return; // v1.1 ultimate freeze
    for (const e of this.enemies) {
      if (!e.entity.alive) continue;
      // Don't fire if enemy is offscreen-right (still entering) — looks weird.
      if (e.entity.x > GAME.width) continue;
      const spec = e.entity.tryFire(nowMs);
      if (spec) {
        this._spawnEnemyBullet(spec.x, spec.y, spec.vx, spec.vy, spec.damage);
        // game-feel (動的感覺): brief fire recoil — a small scale punch on the
        // enemy sprite that springs back in ~120ms so static art "kicks" when it
        // shoots. Visual-only; entity transform untouched. Try-guarded headless.
        const tgt = (e.image ?? e.sprite) as Phaser.GameObjects.GameObject;
        try { this._juiceLayer?.scalePunch(tgt, 1.18, 120); } catch { /* headless */ }
      }
    }
  }

  /**
   * Spawn one enemy bullet: 6×6 red filled circle + subtle 1-frame trail.
   * Trail Graphics is destroyed alongside the bullet in _updateEnemyBullets.
   */
  private _spawnEnemyBullet(x: number, y: number, vx: number, vy: number, damage: number): void {
    const sprite = this.add.graphics();
    // boss-bullet-glow: 小兵彈 = 小型紅色能量球(比魔王彈小,維持威脅層級可讀)。
    // ADD blend 發光;碰撞箱不變。headless 走 fallback。
    try {
      sprite.fillStyle(0xff3030, 0.25); sprite.fillCircle(0, 0, 7);  // outer glow
      sprite.fillStyle(0xff5252, 0.6);  sprite.fillCircle(0, 0, 4.5);// aura
      sprite.fillStyle(0xff8a80, 1);    sprite.fillCircle(0, 0, 2.6);// body
      sprite.fillStyle(0xffffff, 1);    sprite.fillCircle(0, 0, 1.2);// white center
      try { sprite.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
    } catch {
      try { sprite.fillStyle(0xff3030, 1); sprite.fillCircle(0, 0, 3); } catch { /* noop */ }
    }
    sprite.setDepth(4);
    const trail = this.add.graphics();
    // JUICE (task #19): tighter, more opaque trail — 0.4 → 0.7 alpha so the
    // pixel reads as a hard streak, not a haze. Same 2px disc, keeps cost flat.
    trail.fillStyle(0xff8080, 0.7);
    trail.fillCircle(0, 0, 2);
    trail.setDepth(3);
    this.enemyBullets.push({ x, y, vx, vy, damage, alive: true, sprite, trail });
  }

  private _updateEnemyBullets(dtSec: number): void {
    const frozen = this.time.now < this._enemyFreezeUntilMs; // v1.1 ultimate
    for (const b of this.enemyBullets) {
      if (!b.alive) continue;
      if (!frozen) {
        b.x += b.vx * dtSec;
        b.y += b.vy * dtSec;
      }
      // Sync visuals: trail sits ~8px behind the bullet (right side since vx<0)
      b.sprite.x = b.x;
      b.sprite.y = b.y;
      b.trail.x = b.x + 8;
      b.trail.y = b.y;
      if (b.x < -32 || b.x > GAME.width + 32) {
        b.alive = false;
      }
    }
    for (const b of this.enemyBullets.filter((b) => !b.alive)) {
      b.sprite.destroy();
      b.trail.destroy();
    }
    this.enemyBullets = this.enemyBullets.filter((b) => b.alive);
  }

  private _bossDefeated(): void {
    // BOSS-END FIX: one-shot guard — never run twice (would double-grant XP +
    // restart ResultScene mid-transition, leaving the player in a broken
    // limbo). Idempotent return is safe; the early-return in _updateBoss
    // backs this up so we don't waste cycles on a dead-boss frame.
    if (this._bossDefeatedFired) return;
    this._bossDefeatedFired = true;
    // 瞬移預警記號隨 boss 死亡清掉(避免殘留 + 死後再瞬移)。
    this._teleportPendingX = null;
    this._clearTeleportMarker();

    // ── BULLETPROOF RESULT TRANSITION (boss「最後卡在最下面不會動」2026-05-30) ──
    // Root cause: anything below (FX / XP grant on null-ish progress / skill-
    // unlock popup tween) throwing would abort _bossDefeated BEFORE the result
    // transition was scheduled → gameOver stays true, update() early-returns,
    // the kid freezes on a dead battlefield forever. Fix: set gameOver + define
    // an IDEMPOTENT goToResult + a wall-clock scheduler, and queue a GUARANTEED
    // 2.5s net RIGHT NOW, before any throw-prone work. The result screen then
    // appears no matter what fails afterwards. The richer popup-gated path below
    // still runs first when healthy; the idempotent guard makes the double safe.
    this.gameOver = true;
    // 雙軌進度(spec §3.2.1 / §3.4):帶 commander 所需 stats(wave/killed/boss/
    // stageCleared),result-scene 以 computeCommanderXp 算「指揮官經驗」。boss 場
    // = 通關該 stage → stageCleared:true。freeXP 欄位語意已改為「本場指揮官經驗
    // fallback」(見 result-scene ResultData);此處續寫 §3.2.1 公式重算的值。
    const resultPayload: Record<string, unknown> = {
      outcome: 'victory', freeXP: 50, bossDefeated: true,
      bossName: this.boss?.def.displayName ?? 'Boss', stage: this._stage,
      // 穩定 boss id → result-scene 查「打贏這隻該解鎖哪種武器類型」(玩遊戲軌廣度)。
      // 對應表 boss-weapon-unlocks.ts;tutorial-boss / 未知 id → 不解鎖(查無 → null)。
      bossId: this.boss?.def.id,
      stageCleared: true,
      battleStats: {
        wave: this.currentWave + 1,
        killed: this.totalKilled,
        survivedSec: Math.round((this.time.now - this.startTimeMs) / 1000),
      },
      hubFactionId: this._initHubFactionId ?? undefined,
      // 闖關評星用:勝利當下剩餘血量比例(0..1)。result-scene 以此 computeStars
      // (>=0.8→3★ / >=0.4→2★ / 其餘→1★)。純表現度量,不影響任何戰力。
      hpRatioAtWin: Math.max(0, Math.min(1, this.playerHp / Math.max(1, this._maxHp()))),
    };
    let _resultDispatched = false;
    const goToResult = () => {
      if (_resultDispatched) return;
      _resultDispatched = true;
      try { document.querySelector('.touch-controls')?.remove(); } catch { /* noop */ }
      try { this.scene.start('ResultScene', resultPayload); } catch { /* noop */ }
    };
    const win = (typeof window !== 'undefined') ? window : null;
    const schedule = (ms: number, fn: () => void) => {
      if (win && typeof win.setTimeout === 'function') {
        win.setTimeout(() => { try { this.time.timeScale = 1; } catch { /* noop */ } fn(); }, ms);
      } else {
        try { this.time.delayedCall(ms, fn); } catch { fn(); }
      }
    };
    schedule(2500, goToResult); // guaranteed net — fires even if everything below throws

    // G06-WIRE: boss is dead — wipe every grunt's pathfinder route so they
    // immediately revert to straight-line vx motion. Without this, leftover
    // minions would keep walking the last computed curve toward where the
    // boss used to be (jarring "ghost obstacle" behaviour).
    clearAllEnemyPaths(this.enemies.map((e) => e.entity));

    // W8 PR-1 — elemental boss death-respawn: spawn 2 minions ±40 px around
    // the death point so the kid sees「再生 2 隻」 effect. One-shot via the
    // state.flag; non-elemental bosses no-op inside the helper.
    if (this.boss && this._factionMechanicState) {
      const res = applyElementalDeathRespawn(this.boss, this._factionMechanicState);
      for (const pos of res.shouldSpawnMinionAt) {
        this._spawnBossMinionAt(pos.x, pos.y);
      }
    }

    try { this._bgm?.switchToBattleTheme(); } catch { /* noop */ }

    const bossName = this.boss?.def.displayName ?? 'Boss';
    const bossX = this.boss?.x ?? GAME.width - 120;
    const bossY = this.boss?.y ?? GAME.height / 2;
    if (this.boss) this.boss.alive = false;
    if (this.bossSprite) { this.bossSprite.destroy(); this.bossSprite = null; }
    // boss-sprite-wire: clean up the AI-gen image overlay if it was created.
    if (this.bossImage) { this.bossImage.destroy(); this.bossImage = null; }
    if (this.bossHpBar) { this.bossHpBar.destroy(); this.bossHpBar = null; }
    if (this.bossHpText) { this.bossHpText.destroy(); this.bossHpText = null; }
    if (this._warningRingGfx) { this._warningRingGfx.destroy(); this._warningRingGfx = null; }
    if (this._telegraphGfx) { this._telegraphGfx.destroy(); this._telegraphGfx = null; }

    // BOSS-END FIX (kid earlier complaint「魔王徒還是爛的」, death FX missing):
    // Big explosion VFX + camera shake + boom SFX so the kill actually FEELS
    // climactic. Wrapped in try/catch — test harness lacks add.circle/tweens/
    // cameras.main / AudioContext.
    // G04: bossExplode preset (trauma 0.6 + yellow flash + explosion particles + 80ms pause + explode sfx).
    // Replaces flat 300ms/0.015 shake — now has decay tail so the boom feels meatier.
    if (this._juice) {
      this._juice.playFeedback('bossExplode', { x: bossX, y: bossY });
    } else {
      try { this.cameras?.main?.shake?.(300, 0.015); } catch { /* test harness */ }
      try { this._sfx?.play('explode', 0.9); } catch { /* AudioContext busy */ }
    }
    // W3 hook #4 — boss die: heavy screen shake + long hit-stop on top of
    // legacy bossExplode preset. Per spec §4.4. Additive — does not gate
    // on _juice (different concern: preset bundle vs per-effect granular).
    if (this._juiceLayer) {
      this._juiceLayer.screenShake(0.02, 300);
      this._juiceLayer.hitStop(80);
    }
    // W5: boss-defeat camera punch — 1.3x dramatic zoom + bright white victory
    // flash on top of the existing JuiceLayer shake/hitStop. Per spec §4.3.
    this._cameraZoomPunch(1.3, 400);
    this._cameraFlash(200, 255, 255, 255);
    // W8 PR-2: faction-color boss burst — replaces generic yellow burst with
    // 門派色 flashSprite + 200px graphics ring. Per spec docs/game-pr-specs/
    // W8-boss-polish.md §3.3. Reads boss.def.faction; undefined → fallback gold.
    this._emitBossBurst(this.boss?.def.faction, bossX, bossY);
    // Asset Pipeline B: large CC0 ring-explosion spritesheet at the boss death
    // point, tinted to the boss's 門派 color (fallback gold). No-op when the
    // sheet texture is absent — the 24-particle burst below still fires.
    try {
      const fid = this.boss?.def.faction;
      const bossTint = (fid != null && FACTION_BURST_COLOR[fid] != null)
        ? FACTION_BURST_COLOR[fid]!
        : FACTION_BURST_FALLBACK_COLOR;
      playBossExplosion(this, bossX, bossY, bossTint);
    } catch { /* headless */ }
    // 24-particle radial burst (4× the enemy-death burst) — counted for test
    // visibility via _particleCount.
    this._particleCount += 24;
    try {
      for (let i = 0; i < 24; i++) {
        const angle = (Math.PI * 2 * i) / 24 + Math.random() * 0.3;
        const dist = 40 + Math.random() * 50;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        const size = 4 + Math.random() * 4;
        const color = Math.random() < 0.5 ? 0xffeb3b : 0xff5722; // yellow/orange mix
        const p = this.add.circle(bossX, bossY, size, color, 0.95).setDepth(8);
        this.tweens.add({
          targets: p,
          x: bossX + dx,
          y: bossY + dy,
          alpha: 0,
          scale: 0.2,
          duration: 600,
          ease: 'Cubic.easeOut',
          onComplete: () => { p.destroy(); },
        });
      }
      // Central white flash that fades to black
      const flash = this.add.circle(bossX, bossY, 8, 0xffffff, 1).setDepth(9);
      this.tweens.add({
        targets: flash,
        scale: 8,
        alpha: 0,
        duration: 400,
        ease: 'Cubic.easeOut',
        onComplete: () => { flash.destroy(); },
      });
    } catch { /* test harness lacks add.circle / tweens */ }

    this.waveState = 'done';
    this.gameOver = true;

    // C05-wire (2026-05-23): tutorial-boss kill → broadcast completion event.
    // main.ts listens and calls dayone-hook.completeTutorialBoss(progress) to
    // flip `progress.dailyHook.tutorialBossCompleted = true` and grant
    // TUTORIAL_BOSS_XP. Wrapped in try/catch so a window without
    // dispatchEvent (test harness edge case) doesn't break the result flow.
    // Guarded by _tutorialBossMode so regular bosses never fire this event.
    if (this._tutorialBossMode && typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('ef:tutorial-boss-completed', {
          detail: { bossId: TUTORIAL_BOSS_ID },
        }));
      } catch { /* test harness: no CustomEvent */ }
    }

    // MERCHANT-BUFF FIX: apply accumulated wave-XP multiplier from the
    // 「本波修煉值 x2」 merchant pick. Cap is already enforced at pick time
    // (MAX_WAVE_XP_MULT = 5), so straight multiplication is safe here.
    // BOSS-END FIX: also fold in a fixed boss-defeat bonus (matches the
    // ResultScene `_defaultBreakdown` which splits freeXP into base + boss
    // bonus). Boss-kill should pay out meaningfully more than a normal wave
    // clear (30 → 50 base, ×_waveXpMult on top).
    const baseFreeXP = 50;
    // Roguelike「雙倍戰利品」upgrade: freeXPMult (×2) stacks on top of the
    // merchant wave-XP multiplier so the kid sees the loot bonus on the result
    // screen. Defaults to 1 → unchanged until picked.
    const freeXP = Math.round(baseFreeXP * this._waveXpMult * this._runModifiers().freeXPMult);
    // W7 hub→battle wiring — derive hubFactionId from either the explicit
    // init data (HubScene path) or the boss's own faction (legacy random path,
    // so even random-pick bosses still light up the right hub building when
    // the kid happens to be in the hub→battle→result loop).
    const hubFactionId = this._initHubFactionId ?? (this.boss?.def.faction as FactionId | undefined) ?? null;
    // Populate the real result payload with full computed values. goToResult,
    // `schedule`, `win` and the guaranteed 2.5s net were all set up at the TOP
    // of this method (bulletproof transition) — do NOT redeclare them here.
    resultPayload.freeXP = freeXP;
    resultPayload.bossName = bossName;
    resultPayload.hubFactionId = hubFactionId ?? undefined;

    let firstUnlock = false;
    try {
      const p = loadProgress();
      if (p && !p.unlockedSkills.includes(CELESTIAL_L2_SKILL_ID)) {
        p.unlockedSkills.push(CELESTIAL_L2_SKILL_ID);
        saveProgress(p);
        firstUnlock = true;
      }
    } catch { firstUnlock = false; }

    // Healthy path: first-unlock shows the skill popup then result; otherwise
    // straight to result. The idempotent goToResult guard makes these overlap
    // safely with the guaranteed 2.5s net scheduled at the top of the method.
    if (firstUnlock) {
      schedule(700, () => this._showSkillUnlockPopup(goToResult));
    } else {
      schedule(500, goToResult);
    }
  }

  /**
   * v1.1 項目 5: full-screen「⚡ 星算門 Lv2 技能解鎖!」popup with a skill card
   * flying in. Tap / wait → onDone() (→ ResultScene). Try-guarded; headless or
   * tween failure calls onDone() immediately so the run never gets stuck.
   */
  private _showSkillUnlockPopup(onDone: () => void): void {
    let done = false;
    const finish = () => { if (done) return; done = true; try { onDone(); } catch { /* noop */ } };
    try {
      const cx = GAME.width / 2;
      const cy = GAME.height / 2;
      const overlay = this.add.rectangle(cx, cy, GAME.width, GAME.height, 0x05010f, 0.82)
        .setDepth(70).setInteractive();
      const title = this.add.text(cx, cy - 110, '⚡ 技能解鎖!', {
        fontSize: '30px', fontFamily: 'sans-serif', color: '#ffd54f', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(72);
      // skill card (flies in from the left)
      const card = this.add.rectangle(cx, cy, 220, 150, 0x1a1030, 0.96)
        .setStrokeStyle(3, CELESTIAL_PURPLE).setDepth(71);
      const cardIcon = this.add.text(cx, cy - 36, '⚡', { fontSize: '46px', fontFamily: 'sans-serif' })
        .setOrigin(0.5).setDepth(72);
      const cardName = this.add.text(cx, cy + 18, '星算門 Lv2', {
        fontSize: '22px', fontFamily: 'sans-serif', color: '#d1b3ff', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(72);
      const cardDesc = this.add.text(cx, cy + 48, '連發威力提升', {
        fontSize: '14px', fontFamily: 'sans-serif', color: '#ffffff',
      }).setOrigin(0.5).setDepth(72);
      const hint = this.add.text(cx, cy + 110, '點一下繼續', {
        fontSize: '14px', fontFamily: 'sans-serif', color: '#aaaaaa',
      }).setOrigin(0.5).setDepth(72);
      const cardItems = [card, cardIcon, cardName, cardDesc];
      // fly-in: start off to the left + transparent, slide to center.
      for (const it of cardItems) { it.x = cx - 320; it.setAlpha(0); }
      this.tweens.add({
        targets: cardItems, x: cx, alpha: 1, duration: 450, ease: 'Back.easeOut',
      });
      this._juiceLayer?.scalePunch(card as Phaser.GameObjects.GameObject, 1.15, 500);
      try { this._sfx?.play('explode', 0.5); } catch { /* noop */ }

      const all = [overlay, title, hint, ...cardItems];
      const close = () => {
        try {
          this.tweens.add({
            targets: all, alpha: 0, duration: 250,
            onComplete: () => { for (const it of all) { try { it.destroy(); } catch { /* noop */ } } finish(); },
          });
        } catch { for (const it of all) { try { it.destroy(); } catch { /* noop */ } } finish(); }
      };
      overlay.on('pointerup', close);
      // auto-advance after 3.5s if the kid doesn't tap.
      this.time.delayedCall(3500, close);
    } catch {
      finish();
    }
  }

  private _updateEnemies(dtSec: number, nowMs: number = 0): void {
    // 冰光環:while the aura is active, enemies advance at 70% (−30%) speed. We
    // scale the per-frame dt passed to entity.update rather than mutating vx so
    // the slow auto-expires cleanly and never permanently alters the enemy.
    // boss 2026-06-16「敵人跟敵人的子彈也要凍結啊」:大絕 / 立繪施法凍結時,敵人「移動」
    // 也要停(原本只凍了開火 + 子彈,移動沒凍 → 立繪過場時敵人還在前進)。frozen 時把
    // effDt 設 0:entity.update 照跑(tint/HP bar 等不壞)但位移為 0。
    const frozen = nowMs < this._enemyFreezeUntilMs;
    const slow = this._slowAuraActive(nowMs);
    const effDt = frozen ? 0 : (slow ? dtSec * HorizontalBattleScene.SLOW_AURA_FACTOR : dtSec);
    for (const e of this.enemies) {
      // enemy-diversity: refresh the chase target so 'chaseY' grunts ease toward
      // the player's current Y this frame. No-op for other patterns.
      if (e.entity.movePattern === 'chaseY' || e.entity.movePattern === 'chase') {
        e.entity.chaseTargetY = this.playerY;
        e.entity.chaseTargetX = this.playerX; // 'chase' 真追逐需 X 目標(chaseY 用不到,設了無害)
      }
      e.entity.update(effDt);
      // Visible cue: tint slowed enemies icy blue; clear tint when aura ends.
      try {
        const tgt = (e.image ?? e.sprite) as unknown as {
          setTint?: (c: number) => void; clearTint?: () => void;
        };
        if (slow) {
          tgt.setTint?.(0x7fd4ff);
        } else if (e.miniBoss && !e.miniBossSkinned) {
          // only the tinted faction-chassis fallback gets the magenta elite tint;
          // a bespoke full-colour skin must NOT be tinted (would muddy the art).
          tgt.setTint?.(HorizontalBattleScene.MINI_BOSS_TINT);
        } else {
          tgt.clearTint?.();
        }
      } catch { /* headless graphics stub */ }
      // enemy-toughness / mini-boss: floating HP bar above tougher units.
      this._drawEnemyHpBar(e);
    }
    for (const e of this.enemies.filter((e) => !e.entity.alive)) {
      e.sprite.destroy();
      // art-gen-1: clean up AI-gen image overlay when the enemy dies/leaves.
      if (e.image) { e.image.destroy(); e.image = null; }
      // HENEMY-AFFINITY: tear down halo Graphics on death/exit.
      if (e.halo) { e.halo.destroy(); e.halo = null; }
      // enemy-toughness / mini-boss: tear down HP bar Graphics on death/exit.
      if (e.hpBar) { e.hpBar.destroy(); e.hpBar = null; }
    }
    this.enemies = this.enemies.filter((e) => e.entity.alive);
  }

  /**
   * 冰光環:draw a brief expanding blue ground ring centred on the player when
   * the aura (re)triggers, so the kid sees the slow kick in. Purely cosmetic;
   * fully try-guarded for the headless test harness.
   */
  private _spawnSlowAuraFx(): void {
    try {
      const ring = this.add.circle(this.playerX, this.playerY, 30, 0x7fd4ff, 0.25)
        .setDepth(1).setStrokeStyle(3, 0x29b6f6, 0.9);
      this.tweens.add({
        targets: ring, scale: 4, alpha: 0, duration: 600, ease: 'Cubic.easeOut',
        onComplete: () => { try { ring.destroy(); } catch { /* noop */ } },
      });
      const t = this.add.text(this.playerX, this.playerY - 50, '❄️ 冰光環!', {
        fontSize: '18px', fontFamily: 'sans-serif', color: '#7fd4ff', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(62);
      this.tweens.add({
        targets: t, alpha: 0, y: this.playerY - 84, duration: 800, ease: 'Cubic.easeOut',
        onComplete: () => { try { t.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless: no add/tweens */ }
  }

  /**
   * G06-WIRE: every BATTLE_REPATH_INTERVAL_MS (500ms), rebuild the pathfinder
   * grid with the current boss bbox as the only obstacle and ask A* to route
   * every alive grunt around it toward the player.
   *
   * When the boss is null / dead / hasn't arrived yet, we still tick: we clear
   * obstacles (so any stale grid from the previous frame doesn't haunt us)
   * AND clear every enemy's stored `path` so they fall back to straight-line
   * `vx, vy` motion. That's the "boss dies → leftover grunts walk straight
   * again" behaviour the test asserts.
   *
   * Called every frame from `update()`; throttled by `shouldRepath()`.
   *
   * Public for tests so they can drive it without time-of-day fakes — pass
   * `now` directly. Production callers always pass `this.time.now`.
   */
  _maybeRepath(nowMs: number): void {
    if (!this._pathfinder) return;
    if (!shouldRepath(nowMs, this._lastRepathMs, BATTLE_REPATH_INTERVAL_MS)) return;
    this._lastRepathMs = nowMs;

    const bossObstacles = computeBossObstacles(this.boss, { arrived: this.bossArrived });
    // P02: when TileMap mode is active, the static map obstacles are ALWAYS
    // part of the grid — they don't disappear when the boss dies. So we
    // build the full obstacle list as `mapObstacles + bossObstacles`. When
    // TileMap mode is off, `mapObstacles` is empty and behaviour is identical
    // to pre-P02.
    const mapObstacles = this._tileMapObjects?.obstacles ?? [];
    const obstacles = mapObstacles.concat(bossObstacles);
    if (obstacles.length === 0) {
      // Boss gone AND no map obstacles — clear obstacle grid AND wipe stale
      // enemy paths so grunts revert to straight-line motion. We still call
      // setObstacles([]) so the grid is freshly all-walkable on the next
      // attack-with-boss spawn.
      this._pathfinder.setObstacles(GAME.width, GAME.height, []);
      clearAllEnemyPaths(this.enemies.map((e) => e.entity));
      return;
    }

    this._pathfinder.setObstacles(GAME.width, GAME.height, obstacles);
    // Target = player current position. We route enemies toward the player
    // because the grunts are already moving leftward via vx — the path
    // detour is what we care about; once around the boss they re-engage
    // straight-line via `path: null` (popped at end-of-path in followPathStep).
    const target = { x: this.playerX, y: this.playerY };
    const issued = repathEnemiesToward(
      this._pathfinder,
      this.enemies.map((e) => e.entity),
      target,
    );
    this._repathCallCount += issued;
  }

  /**
   * Bookkeeping that fires when a regular enemy dies (XP, 吸血, 冰光環, ultimate
   * charge, death fx, juice). Extracted from the inline bullet-hit path so the
   * 爆炸彈 AoE can reuse the EXACT same kill flow for enemies it finishes off —
   * no double-counting, no divergence.
   */
  private _onRegularEnemyKilled(e: EnemyEntry, nowMs: number): void {
    this.killedThisWave++; this.totalKilled++;
    this._onEnemyKilledXp(this._xpMultForEntry(e));
    // Roguelike「吸血」upgrade: heal 1 HP every 1/lifestealPerKill kills.
    {
      const mods = this._runModifiers();
      if (mods.lifestealPerKill > 0) {
        this._runKillCount++;
        const threshold = Math.max(1, Math.round(1 / mods.lifestealPerKill));
        if (this._runKillCount % threshold === 0) {
          this.playerHp = Math.min(this._maxHp(), this.playerHp + 1);
        }
      }
    }
    // 冰光環 upgrade: advance the 5-kill streak; on each boundary the aura
    // (re)triggers (−30% enemy speed 5s) + spawns a visible ring.
    if (this._slowAuraOnKill(nowMs)) {
      this._spawnSlowAuraFx();
    }
    // v1.1: feed the ultimate charge gauge (kill充能). elite = affinity.
    // 快充 upgrade folds into _effectiveChargeMult() (chargeMultBonus).
    const elite = e.entity.affinity != null;
    this._ultimateCharge.onEnemyKilled(this._effectiveChargeMult(), elite);
    // Phase 2 §4-1:掉局內金幣 (常數 × coinDropMult;mini-boss/elite ×3)。
    // 鐵則:coins 純局內、打完即丟、不寫回 progress;與 freeXP/修煉值完全無關。
    {
      const mods = this._runModifiers();
      const eliteMult = (e.miniBoss || elite) ? HorizontalBattleScene.COIN_ELITE_MULT : 1;
      mods.addCoins(HorizontalBattleScene.COIN_PER_KILL * mods.coinDropMult * eliteMult);
    }
    // R-005 P3 (C):機率掉「武器箱」(精英/小魔王掉率更高)。撿到當場換武器(單場)。
    // 鐵則:純局內,只改 _equippedWeapon、不寫 progress.selectedWeapon。
    if (rollWeaponBoxDrop(e.miniBoss || elite)) {
      this._spawnWeaponBox(e.entity.x, e.entity.y, pickWeaponBoxContents());
    }
    // R3 FIX: play explode SFX + 6-particle burst on enemy death.
    this._emitEnemyDeathFx(e.entity.x, e.entity.y);
    // W4: consecutive-hit pitch ladder stub.
    try { this._audioBus?.pitchLadder('hit'); } catch { /* noop */ }
    // W3 hook #3 — enemy kill (scalePunch + stronger shake + longer hitStop).
    if (this._juiceLayer) {
      const tgt = e.image ?? e.sprite;
      this._juiceLayer.scalePunch(tgt as Phaser.GameObjects.GameObject, 1.3, 250);
      this._juiceLayer.screenShake(0.008, 60);
      this._juiceLayer.hitStop(50);
    }
  }

  /**
   * 爆炸彈 質變. If RunModifiers.explodeOnHitRadius > 0, deal one AoE damage tick
   * (`dmg`) to every OTHER alive enemy within the radius of the impact point and
   * spawn a visible blast ring. Enemies finished off by the blast run the normal
   * kill flow. No-op when the flag is 0, so default behaviour is unchanged.
   *
   * @param hitX/hitY  impact point (explosion center)
   * @param dmg        damage already dealt to the directly-hit enemy; reused for AoE
   * @param directIndex index of the directly-hit enemy (excluded from AoE)
   */
  private _explodeOnHit(hitX: number, hitY: number, dmg: number, directIndex: number, nowMs: number): void {
    const mods = this._runModifiers();
    // synergy 連鎖爆炸 (彈跳+爆炸):湊出組合 → 爆炸範圍 ×1.5,連鎖炸得更大更猛。
    // 沒組合時倍率 = 1,行為同今日。
    const radius = mods.explodeOnHitRadius * chainBlastRadiusMult(mods.synergyChainExplode);
    if (!(radius > 0)) return;
    const center = { x: hitX, y: hitY };
    const points = this.enemies.map((e) => ({ x: e.entity.x, y: e.entity.y, alive: e.entity.alive }));
    const caught = enemiesInBlastRadius(center, radius, points, directIndex);
    // AoE damage scales with stack count (bigger radius = more 爆炸彈 picks =
    // harder hit), so picking 爆炸彈 a second time feels genuinely stronger
    // instead of just covering a wider area for the same per-target damage.
    const aoeDmg = explodeDamageForRadius(dmg, radius);
    for (const idx of caught) {
      const e = this.enemies[idx];
      if (!e.entity.alive) continue;
      e.entity.takeDamage(aoeDmg);
      try { this._flashEnemy(e); } catch { /* headless */ }
      if (!e.entity.alive) {
        this._onRegularEnemyKilled(e, nowMs);
      }
    }
    this._spawnBlastRingFx(hitX, hitY, radius);
  }

  /**
   * 場內武器進化「爆裂散彈」AoE — 以「明確半徑」對範圍內其他敵人造一次 AoE 傷害 +
   * 畫 blast ring。與 _explodeOnHit 共用同一套幾何 / 傷害縮放 (explodeDamageForRadius)
   * + 同款 ring,差別只在半徑來自「子彈自身 blastRadius」而非 run-wide 旗標,讓進化
   * 的散彈即使沒選「爆炸彈」卡也會爆。純當局效果,不碰 progress/修煉值。
   */
  private _blastAt(hitX: number, hitY: number, radius: number, dmg: number, directIndex: number, nowMs: number): void {
    if (!(radius > 0)) return;
    const center = { x: hitX, y: hitY };
    const points = this.enemies.map((e) => ({ x: e.entity.x, y: e.entity.y, alive: e.entity.alive }));
    const caught = enemiesInBlastRadius(center, radius, points, directIndex);
    const aoeDmg = explodeDamageForRadius(dmg, radius);
    for (const idx of caught) {
      const e = this.enemies[idx];
      if (!e.entity.alive) continue;
      e.entity.takeDamage(aoeDmg);
      try { this._flashEnemy(e); } catch { /* headless */ }
      if (!e.entity.alive) {
        this._onRegularEnemyKilled(e, nowMs);
      }
    }
    this._spawnBlastRingFx(hitX, hitY, radius);
  }

  /** Visible AoE blast ring for 爆炸彈 (expanding orange circle). Headless-safe. */
  private _spawnBlastRingFx(x: number, y: number, radius: number): void {
    try {
      const ring = this.add.circle(x, y, Math.max(8, radius * 0.4), 0xff7043, 0.30)
        .setDepth(3).setStrokeStyle(3, 0xffab40, 0.95);
      try { ring.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
      this.tweens.add({
        targets: ring,
        scale: Math.max(2, (radius / Math.max(8, radius * 0.4))),
        alpha: 0,
        duration: 320,
        ease: 'Cubic.easeOut',
        onComplete: () => { try { ring.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless: no add/tweens */ }
  }

  /**
   * 彈跳彈 質變. Redirect `bullet` toward the nearest OTHER alive enemy, preserving
   * its speed. If no other enemy exists, reflect off the top/bottom wall so the
   * bounce still "feels" like a bounce. Returns true if a redirect happened (so
   * the caller decrements the bounce budget); false means "let it die normally".
   */
  private _tryBounceBullet(bullet: HBullet, directIndex: number): boolean {
    const from = { x: bullet.x, y: bullet.y };
    const points = this.enemies.map((e) => ({ x: e.entity.x, y: e.entity.y, alive: e.entity.alive }));
    const targetIdx = nearestOtherEnemyIndex(from, points, directIndex);
    if (targetIdx >= 0) {
      const t = points[targetIdx];
      const v = redirectVelocityToward(from, t, bullet.vx, bullet.vy, Math.abs(this._bulletVx) || 600);
      bullet.vx = v.vx;
      bullet.vy = v.vy;
      return true;
    }
    // No other enemy: reflect off the top/bottom wall (flip vy). Only meaningful
    // if the bullet has some vertical component or we give it one so the bounce
    // is visible; otherwise let it die normally (return false).
    if (bullet.vy !== 0) {
      const v = reflectVerticalWall(bullet.vx, bullet.vy);
      bullet.vx = v.vx;
      bullet.vy = v.vy;
      return true;
    }
    return false;
  }

  private _checkCollisions(nowMs: number): void {
    const half = 16;
    const pHalf = 20;

    // Player bullets vs regular enemies
    for (const b of this.bullets) {
      if (!b.entity.alive) continue;
      for (let ei = 0; ei < this.enemies.length; ei++) {
        const e = this.enemies[ei];
        if (!e.entity.alive) continue;
        if (
          Math.abs(b.entity.x - e.entity.x) < half + 4 &&
          Math.abs(b.entity.y - e.entity.y) < half + 3
        ) {
          // HENEMY-AFFINITY: matching skill faction → 1.5× damage. Player bullets
          // carry no per-shot faction (skill = W2), so use _playerFaction.
          // 穿透傷害衰減(boss 2026-06-14):pierceHits=0=滿傷,第 N 個被穿目標 ×falloff^N。
          const pierceAdjusted = pierceDamageFalloff(b.entity.damage, b.entity.pierceHits, this._pierceFalloff());
          const dmg = e.entity.effectiveDamageFor(this._playerFaction, pierceAdjusted);
          // Remember the impact point BEFORE any redirect (explosion centers here).
          const hitX = b.entity.x;
          const hitY = b.entity.y;
          e.entity.takeDamage(dmg);
          // JUICE: 門派色貫穿命中回饋(Juice Batch 1)。多層:染色閃 + 傷害數字
          // + 門派色粒子 + 小震 + hitstop。玩家門派色 = _playerHitColor()。
          const hitColor = this._playerHitColor();
          try {
            this._flashEnemy(e, hitColor);
            const isAffinity = e.entity.affinity != null && e.entity.affinity === this._playerFaction;
            spawnDamageNumber(this, e.entity.x, e.entity.y - 20, dmg, {
              kind: isAffinity ? 'affinity' : 'normal',
              faction: this._playerFaction,
            });
            explosionParticles(this, hitX, hitY, hitColor);
          } catch { /* test harness lacks add/tweens */ }
          // W3 hook #1 — 小震 + hitstop。spec docs/game-pr-specs/W3-juice-layer.md §4.1.
          if (this._juiceLayer) {
            this._juiceLayer.screenShake(0.005, 50);
            this._juiceLayer.hitStop(30);
          }
          if (!e.entity.alive) {
            this._onRegularEnemyKilled(e, nowMs);
          }
          // 爆炸彈 質變:on hit, if explodeOnHitRadius>0 deal a single AoE damage
          // tick to every OTHER enemy within radius of the impact point, plus a
          // visible blast ring. Reads the flag live (same model as slow-aura).
          this._explodeOnHit(hitX, hitY, dmg, ei, nowMs);
          // 爆裂散彈 (場內武器進化):若此子彈帶 per-bullet blastRadius (來自進化),
          // 即使沒有「爆炸彈」卡也要爆 — 用既有 AoE 機制 (同傷害、同 ring)。
          if (b.entity.blastRadius > 0) {
            this._blastAt(hitX, hitY, b.entity.blastRadius, dmg, ei, nowMs);
          }
          // 場內武器進化「貫穿彈」:pierce 優先於彈跳 — 子彈穿過敵人不消失,直到
          // pierceLeft 用完。穿透中不轉向 (穿一整排),用完才走彈跳/消失邏輯。
          // pierceHits++ → 下一個被穿目標吃更低傷(pierceDamageFalloff),「廣」不滿傷。
          if (b.entity.pierceLeft > 0) {
            b.entity.pierceLeft -= 1;
            b.entity.pierceHits += 1;
            break; // 子彈續飛 (不 kill、不 bounce),只命中一隻就結束本次碰撞掃描。
          }
          // 彈跳彈 質變:if the bullet still has bounces left, redirect it toward
          // the nearest OTHER enemy (or reflect off the top/bottom wall when none
          // remain) instead of dying. Otherwise it dies on hit as before.
          if (b.entity.bouncesLeft > 0 && this._tryBounceBullet(b.entity, ei)) {
            b.entity.bouncesLeft -= 1;
          } else {
            b.entity.kill();
          }
          break;
        }
      }
    }

    // Player bullets vs boss
    if (this.boss && this.boss.alive) {
      const bossHalf = (this.boss.def.size ?? 48) / 2;
      for (const b of this.bullets) {
        if (!b.entity.alive) continue;
        const bHalfX = 4 * b.entity.sizeMult;  // 巨彈:half-extent ×sizeMult vs boss
        const bHalfY = 3 * b.entity.sizeMult;
        if (
          Math.abs(b.entity.x - this.boss.x) < bossHalf + bHalfX &&
          Math.abs(b.entity.y - this.boss.y) < bossHalf + bHalfY
        ) {
          const bDmg = pierceDamageFalloff(b.entity.damage, b.entity.pierceHits, this._pierceFalloff());
          const hitX = b.entity.x;
          const hitY = b.entity.y;
          // W8 PR-1 — runic castedShield: bullet still consumed (so kid sees
          // the impact and shoot rhythm isn't broken) but damage is absorbed.
          if (!this._bossShieldActive) {
            this.boss.takeDamage(bDmg);
          }
          // Juice Batch 1 — 門派色貫穿 boss 命中回饋(多層:染色閃 + 傷害數字 +
          // 門派色粒子 + 小震)。boss 不上 hitstop(維持原節奏,只在死亡 burst 用)。
          const bossHitColor = this._playerHitColor();
          try {
            this._flashBoss(bossHitColor);
            spawnDamageNumber(this, this.boss.x, this.boss.y - 30, bDmg, {
              kind: 'normal',
              faction: this._playerFaction,
            });
            explosionParticles(this, hitX, hitY, bossHitColor);
          } catch { /* test harness lacks add/tweens */ }
          this._juiceLayer?.screenShake(0.005, 40);
          // 爆炸彈 質變 vs 魔王:if the shot dealt damage (not shielded) and
          // explodeOnHitRadius>0, the boss eats an EXTRA scaled blast tick and we
          // draw the ring. Bigger radius (more 爆炸彈 picks) → more bonus damage →
          // picking 爆炸彈 again is clearly stronger against the boss too. Runs
          // through the boss's own takeDamage (berserk/death flow intact).
          if (!this._bossShieldActive) {
            const radius = this._runModifiers().explodeOnHitRadius;
            if (radius > 0 && this.boss.alive) {
              this.boss.takeDamage(explodeDamageForRadius(bDmg, radius));
              this._spawnBlastRingFx(hitX, hitY, radius);
            }
          }
          // 穿透彈 vs 魔王:pierce 優先於彈跳 — 子彈穿過魔王不消失(續飛找下一個目標),
          // pierceHits++ 讓後續命中吃 falloff。把子彈推出魔王框避免同幀重觸發。
          if (b.entity.pierceLeft > 0) {
            b.entity.pierceLeft -= 1;
            b.entity.pierceHits += 1;
            b.entity.x += Math.sign(b.entity.vx || 1) * (bossHalf + bHalfX + 2);
            break;
          }
          // 彈跳彈 質變 vs 魔王:if the bullet still has bounces left, DON'T kill
          // it — ricochet it off the boss so it can come back for another hit.
          // bounceCount 2→4 = +2 extra boss hits, so picking 彈跳彈 again is
          // clearly stronger here too. Nudge the bullet off the boss to avoid a
          // same-frame re-trigger.
          if (b.entity.bouncesLeft > 0) {
            const v = ricochetOffBoss(b.entity.vx, b.entity.vy);
            b.entity.vx = v.vx;
            b.entity.vy = v.vy;
            b.entity.bouncesLeft -= 1;
            // Push the bullet outside the boss hitbox along its new heading so
            // it doesn't immediately collide again this frame.
            b.entity.x += Math.sign(v.vx || -1) * (bossHalf + bHalfX + 2);
          } else {
            b.entity.kill();
          }
          break;
        }
      }
    }

    // Regular enemies vs player
    for (const e of this.enemies) {
      if (!e.entity.alive) continue;
      if (
        Math.abs(e.entity.x - this.playerX) < half + pHalf &&
        Math.abs(e.entity.y - this.playerY) < half + pHalf
      ) {
        e.entity.alive = false;
        // R3 FIX: collision-kill counts too — explode FX + count.
        this.totalKilled++;
        this._onEnemyKilledXp(this._xpMultForEntry(e));
        this._emitEnemyDeathFx(e.entity.x, e.entity.y);
        const colDmg = this._balance.enemies.types.find(t => t.id === 'basic')?.collisionDamage ?? 10;
        // v1.2 runic 符文盾:吸收一發 → 玩家受 0 傷,否則正常扣血。
        if (this._absorbPlayerHit()) { continue; }
        this.playerHp = Math.max(0, this.playerHp - colDmg);
        if (this._sfx) { try { this._sfx.play('hit', 0.4); } catch { /* AudioContext busy */ } }
        this._onPlayerHit(colDmg);
        if (this.playerHp <= 0 && !this.gameOver) { this._gameOver(nowMs); }
      }
    }

    // cover-rework: 戰術掩體擋彈 — 在「魔王彈/敵彈 vs 玩家」之前先讓掩體攔截。
    // 被掩體 AABB 擋到的彈在這裡 alive=false,下方玩家受擊判定就掃不到它 → 躲在
    // 掩體後真的能擋彈幕。lazy spawn 守衛確保第一幀也有掩體可擋。純遊戲機制。
    this._ensureDestructiblesSpawned();
    try { this._checkCoverBlocks(); } catch { /* noop */ }

    // Boss bullets vs player
    for (const b of this.bossBullets) {
      if (!b.alive) continue;
      if (
        Math.abs(b.x - this.playerX) < pHalf + 8 &&
        Math.abs(b.y - this.playerY) < pHalf + 8
      ) {
        b.alive = false;
        if (this._absorbPlayerHit()) { continue; } // 符文盾
        this.playerHp = Math.max(0, this.playerHp - b.damage);
        this._onPlayerHit(b.damage);
        if (this.playerHp <= 0 && !this.gameOver) { this._gameOver(nowMs); }
      }
    }

    // ENEMY-BULLETS (task #2): enemy bullets vs player. Smaller hitbox than
    // boss bullets (3px radius circle vs 6px square) → use pHalf + 3.
    for (const b of this.enemyBullets) {
      if (!b.alive) continue;
      if (
        Math.abs(b.x - this.playerX) < pHalf + 3 &&
        Math.abs(b.y - this.playerY) < pHalf + 3
      ) {
        b.alive = false;
        if (this._absorbPlayerHit()) { continue; } // 符文盾
        this.playerHp = Math.max(0, this.playerHp - b.damage);
        if (this._sfx) { try { this._sfx.play('hit', 0.4); } catch { /* AudioContext busy */ } }
        this._onPlayerHit(b.damage);
        if (this.playerHp <= 0 && !this.gameOver) { this._gameOver(nowMs); }
      }
    }
  }

  /**
   * v1.2 runic 符文盾:try to absorb one incoming player hit. Returns true when
   * a shield charge was consumed (caller skips damage), false otherwise.
   * Spawns a small「擋下!」FX on absorb. Pure-logic decrement in _playstyle.
   */
  private _absorbPlayerHit(): boolean {
    if (!this._playstyleState().absorbHit()) return false;
    try {
      const t = this.add.text(this.playerX, this.playerY - 30, '🛡 擋下!', {
        fontSize: '16px', fontFamily: 'sans-serif', color: '#4fc3f7', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(62);
      this.tweens.add({
        targets: t, alpha: 0, y: this.playerY - 60, duration: 600, ease: 'Cubic.easeOut',
        onComplete: () => { try { t.destroy(); } catch { /* noop */ } },
      });
      const tgt = (this.playerImage ?? this.playerSprite) as Phaser.GameObjects.GameObject;
      this._juiceLayer?.flashSprite(tgt, 0x4fc3f7, 120);
    } catch { /* headless */ }
    return true;
  }

  /**
   * JUICE (task #19): unified hook for "player took damage this frame" →
   * camera-shake + floating damage number above the player. Centralised so
   * all 3 damage sources (collision / boss-bullet / enemy-bullet) FEEL
   * identical to the kid. Try-guarded so test harness without cameras stub
   * still runs.
   */
  /**
   * W2: forward this-frame damage to PlayerFSM so it transitions to 'hit'
   * (or 'dead' if fatal). Scene-side playerHp remains source-of-truth.
   */
  private _driveFSMOnHit(amount: number): void {
    if (!this.playerFSM) return;
    this.playerFSM.hp = this.playerHp + amount;
    try { this.playerFSM.takeDamage(amount, this.time.now); } catch { /* test harness */ }
    this.playerFSM.hp = this.playerHp;
  }

  private _onPlayerHit(amount: number): void {
    this._driveFSMOnHit(amount);
    // G04: playerHit preset (trauma 0.3 + red flash + spark particles + 50ms pause + hit sfx).
    // Falls back to legacy flat shake if juice system isn't ready (eg. test harness).
    if (this._juice) {
      this._juice.playFeedback('playerHit', { x: this.playerX, y: this.playerY });
    } else {
      try { this.cameras?.main?.shake?.(SHAKE_PLAYER_HIT_MS, SHAKE_PLAYER_HIT_INTENSITY); }
      catch { /* test harness lacks shake */ }
    }
    try {
      spawnDamageNumber(this, this.playerX, this.playerY - 24, amount, {
        kind: 'normal',
      });
    } catch { /* test harness lacks add/tweens */ }
    // W3 hook #2 — additive red hurt flash on player sprite. Per spec §4.2.
    // Kept AFTER spawnDamageNumber so prior fx-regex tests (which check the
    // first 600 chars of _onPlayerHit for SHAKE_PLAYER_HIT_* constants) keep
    // matching despite this additive code.
    if (this._juiceLayer) {
      const tgt = (this.playerImage ?? this.playerSprite) as Phaser.GameObjects.GameObject;
      this._juiceLayer.flashSprite(tgt, 0xff0000, 120);
    }
  }

  /**
   * JUICE: 80ms white flash on the regular enemy. Prefers the AI-gen Image
   * overlay (which has setTint); falls back to the Graphics underlay otherwise.
   * Halo (separate Graphics) is intentionally NOT flashed — it's an affinity
   * cue, not a hit cue.
   */
  /**
   * Juice Batch 1 — 升級兌現浮字。選完三選一後在戰車上方浮出
   * 「升級! {emoji}{displayName}」並彈跳(scale punch)+ 一聲 upgrade-pick。
   * 讓「升級看不出效果」立刻有可見兌現。純呈現,不碰數值(數值已由 caller
   * 的 mods.applyUpgrade 寫好)。全 try-guarded。
   */
  private _showUpgradeCommit(chosen: Upgrade): void {
    const x = this.playerX;
    const y = this.playerY - 60;
    // 主彈跳字:「升級!」金色,scale punch。
    try {
      const head = this.add.text(x, y, '升級!', {
        fontSize: '26px', color: '#ffd54f', fontFamily: 'sans-serif', fontStyle: 'bold',
      });
      try { head.setOrigin?.(0.5); } catch { /* headless */ }
      try { head.setDepth?.(70); } catch { /* headless */ }
      this._juiceLayer?.scalePunch(head as Phaser.GameObjects.GameObject, 1.4, 400);
      this.tweens.add({
        targets: head, y: y - 36, alpha: 0,
        duration: 1100, delay: 250, ease: 'Cubic.easeOut',
        onComplete: () => { try { head.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
    // 副字:升級名稱(emoji + displayName)。
    try {
      const sub = this.add.text(x, y + 22, `${chosen.emoji ?? '⬆'} ${chosen.displayName ?? ''}`.trim(), {
        fontSize: '16px', color: '#fff59d', fontFamily: 'sans-serif',
      });
      try { sub.setOrigin?.(0.5); } catch { /* headless */ }
      try { sub.setDepth?.(70); } catch { /* headless */ }
      this.tweens.add({
        targets: sub, y: y - 8, alpha: 0,
        duration: 1100, delay: 450, ease: 'Cubic.easeOut',
        onComplete: () => { try { sub.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
    // 兌現音 + 門派色 cast burst 收尾。
    try { this._sfx?.play('upgrade-pick', 0.5); } catch { /* AudioContext busy */ }
    try { skillCastBurst(this, this.playerX, this.playerY, this._heroId); } catch { /* headless */ }
  }

  /**
   * build synergy 成立時的純 FX 提示 — 在戰車上方彈出大字「COMBO!」+ synergy 名稱
   * + 因果說明,讓 K-3 一眼看到「我湊出 combo 了」。純視覺,完全不碰任何戰力/
   * progress;synergy 的實際效果由 RunModifiers 旗標在施工點生效 (見 synergies.ts)。
   */
  private _showSynergyFx(s: SynergyDef): void {
    const x = this.playerX;
    const y = this.playerY - 88;
    // 主字:「COMBO!」橘金大字 + scale punch,比「升級!」更醒目 (湊到組合是大事)。
    try {
      const head = this.add.text(x, y, 'COMBO!', {
        fontSize: '30px', color: '#ff7043', fontFamily: 'sans-serif', fontStyle: 'bold',
      });
      try { head.setOrigin?.(0.5); } catch { /* headless */ }
      try { head.setDepth?.(72); } catch { /* headless */ }
      try { head.setStroke?.('#fff3e0', 4); } catch { /* headless */ }
      this._juiceLayer?.scalePunch(head as Phaser.GameObjects.GameObject, 1.6, 450);
      this.tweens.add({
        targets: head, y: y - 40, alpha: 0,
        duration: 1300, delay: 350, ease: 'Cubic.easeOut',
        onComplete: () => { try { head.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
    // 副字:emoji + synergy 名 + 因果 blurb (K-3 看得懂在 combo 什麼)。
    try {
      const sub = this.add.text(x, y + 26, `${s.emoji} ${s.displayName}`, {
        fontSize: '18px', color: '#ffd54f', fontFamily: 'sans-serif', fontStyle: 'bold',
      });
      try { sub.setOrigin?.(0.5); } catch { /* headless */ }
      try { sub.setDepth?.(72); } catch { /* headless */ }
      this.tweens.add({
        targets: sub, y: y - 6, alpha: 0,
        duration: 1300, delay: 550, ease: 'Cubic.easeOut',
        onComplete: () => { try { sub.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
    // 額外慶祝:玩家門派色 cast burst + 升級音 (復用既有資源,不新增 asset)。
    try { this._sfx?.play('upgrade-pick', 0.6); } catch { /* AudioContext busy */ }
    try { skillCastBurst(this, this.playerX, this.playerY, this._heroId); } catch { /* headless */ }
  }

  /**
   * 場內武器進化成立時的明顯演出 (R-004 包 C2)。比 synergy COMBO 更隆重:
   * 「武器進化!」大字 + 進化名/因果 + 既有 juice.playFeedback(recipe.feedbackPreset)
   * (preset 名來自 weapon-evolution-recipes.json,必存在於 feedback-presets.json —
   * coverage 守門會抓)。純視覺 + 當局數值質變,完全不碰 progress/修煉值。
   */
  private _showWeaponEvolutionFx(recipe: EvolutionRecipe): void {
    const x = this.playerX;
    const y = this.playerY - 96;
    // 主字:「武器進化!」金紅大字 + 大 scale punch (進化是這場最大的質變)。
    try {
      const head = this.add.text(x, y, '武器進化!', {
        fontSize: '32px', color: '#ffca28', fontFamily: 'sans-serif', fontStyle: 'bold',
      });
      try { head.setOrigin?.(0.5); } catch { /* headless */ }
      try { head.setDepth?.(74); } catch { /* headless */ }
      try { head.setStroke?.('#bf360c', 5); } catch { /* headless */ }
      this._juiceLayer?.scalePunch(head as Phaser.GameObjects.GameObject, 1.7, 480);
      this.tweens.add({
        targets: head, y: y - 44, alpha: 0,
        duration: 1400, delay: 400, ease: 'Cubic.easeOut',
        onComplete: () => { try { head.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
    // 副字:emoji + 進化名 + 因果 blurb (K-3 看得懂進化成什麼)。
    try {
      const sub = this.add.text(x, y + 30, `${recipe.emoji} ${recipe.displayName} — ${recipe.blurb}`, {
        fontSize: '17px', color: '#fff8e1', fontFamily: 'sans-serif', fontStyle: 'bold',
      });
      try { sub.setOrigin?.(0.5); } catch { /* headless */ }
      try { sub.setDepth?.(74); } catch { /* headless */ }
      this.tweens.add({
        targets: sub, y: y - 4, alpha: 0,
        duration: 1400, delay: 600, ease: 'Cubic.easeOut',
        onComplete: () => { try { sub.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
    // C2 明顯演出:用既有 juice/playFeedback 系統 (震屏/閃光/粒子/音效) 在玩家位置
    // 放進化爆。preset 名來自配方 (資料驅動),守門測試保證它存在。
    try { this._juice?.playFeedback(recipe.feedbackPreset, { x: this.playerX, y: this.playerY }); } catch { /* headless */ }
    try { skillCastBurst(this, this.playerX, this.playerY, this._heroId); } catch { /* headless */ }
  }

  /**
   * Juice Batch 1 — 玩家門派色(命中染色用)。複用 factionColors.ts 的
   * FACTION_BURST_COLOR(5 派鮮明識別色:紫/藍/綠/金/紅),未知派回退通用金。
   * 讓「玩家子彈命中」的回饋帶玩家門派識別感(取代純白)。
   */
  private _playerHitColor(): number {
    return FACTION_BURST_COLOR[this._heroId] ?? FACTION_BURST_FALLBACK_COLOR;
  }

  /**
   * @param color 可選:flash 染色。未給時維持原本白閃(向後相容)。
   */
  private _flashEnemy(e: EnemyEntry, color?: number): void {
    const target = (e.image ?? e.sprite) as
      Phaser.GameObjects.Sprite
      | Phaser.GameObjects.Image
      | Phaser.GameObjects.Graphics;
    if (typeof color === 'number' && this._juiceLayer) {
      try { this._juiceLayer.flashSprite(target as Phaser.GameObjects.GameObject, color, 80); return; }
      catch { /* fall through to white flash */ }
    }
    try { flashWhite(this, target as never); } catch { /* test harness */ }
  }

  /**
   * JUICE: 80ms white flash on the boss. Prefers the AI-gen Image overlay
   * (setTint path); falls back to the Graphics underlay (alpha-pulse path).
   * Both are handled by `flashWhite`.
   */
  private _flashBoss(color?: number): void {
    const target = (this.bossImage ?? this.bossSprite);
    if (!target) return;
    if (typeof color === 'number' && this._juiceLayer) {
      try { this._juiceLayer.flashSprite(target as unknown as Phaser.GameObjects.GameObject, color, 90); return; }
      catch { /* fall through to white flash */ }
    }
    // Image is structurally tint-capable so flashWhite's setTint branch works;
    // cast through Sprite to satisfy the helper's narrower union type.
    try { flashWhite(this, target as unknown as Phaser.GameObjects.Sprite); }
    catch { /* test harness */ }
  }

  /**
   * W5: Zoom punch — camera rapidly zooms to `zoom` then lerps back to 1.0
   * over `ms` milliseconds. Used for dramatic moments (boss enter / skill /
   * defeat). Two-phase: ease-in for ms/2 to `zoom`, then ease-out for ms/2
   * back to 1.0 via the zoomTo onComplete callback.
   *
   * @param zoom  Target zoom level. 1.05–1.2 subtle; 1.3+ dramatic.
   * @param ms    Total duration in ms. Default 200ms.
   */
  private _cameraZoomPunch(zoom: number = 1.15, ms: number = 200): void {
    // BUG FIX (boss「放完大招畫面放大回不去」2026-05-30): the old zoom-back
    // relied solely on the zoomTo onComplete callback firing with progress===1.
    // Phaser's float progress often never lands exactly on 1, and the callback
    // can also be skipped if a freeze / hitStop / timeScale=0 stalls the tween
    // mid-flight — so the camera stayed stuck zoomed-in. Robust fix:
    //   1. progress >= 1 (not ===1) so the in-tween zoom-back still fires normally.
    //   2. A wall-clock setTimeout safety net (window.setTimeout — unaffected by
    //      timeScale/freeze) that FORCES zoom back to 1.0 after the full punch
    //      duration, guaranteeing the camera always returns even if the tween
    //      callback never completes. `_zoomReturning` guards against the two
    //      paths double-firing a competing zoom-out tween.
    this._zoomReturning = false;
    const restore = () => {
      if (this._zoomReturning) return;
      this._zoomReturning = true;
      try { this.cameras.main.zoomTo(1.0, ms / 2, 'Sine.easeOut'); } catch { /* noop */ }
    };
    try {
      this.cameras.main.zoomTo(zoom, ms / 2, 'Sine.easeIn', false, (_cam: unknown, progress: number) => {
        if (progress >= 1) restore();
      });
    } catch { /* headless test harness */ }
    // Wall-clock safety net: forcibly snap zoom back to 1.0 well after the punch
    // should have finished. Uses window.setTimeout (real time) so a frozen
    // scaled clock can never strand the camera.
    //
    // CRITICAL (2nd-pass fix): a bare setZoom(1.0) is NOT enough. Phaser's
    // zoomTo runs a `cameras.main.zoomEffect` that writes camera.zoom every
    // frame until it completes. If the zoom-IN effect (or a stalled zoom-back)
    // is still running when we setZoom, the effect keeps overwriting our value
    // on the next frame and the camera settles at the effect's value (observed
    // ~1.036, not 1.0). So we FIRST `zoomEffect.reset()` to kill any in-flight
    // zoom tween, THEN setZoom(1.0) as the authoritative final state.
    if (typeof window !== 'undefined' && window.setTimeout) {
      window.setTimeout(() => {
        try {
          this._zoomReturning = true;
          const cam = this.cameras.main as unknown as {
            zoomEffect?: { isRunning?: boolean; reset?: () => void };
            setZoom: (z: number) => void;
          };
          // Kill any running zoom effect so it can't overwrite our snap-back.
          try { cam.zoomEffect?.reset?.(); } catch { /* older Phaser / headless */ }
          cam.setZoom(1.0);
        } catch { /* scene torn down / headless */ }
      }, ms + 120);
    }
  }

  /**
   * W5: Full-screen color flash via cameras.main.flash. Used for correct-
   * answer / skill release (white), boss enter (warm orange), boss defeat
   * (white victory), player death (red).
   *
   * @param ms  Duration. Default 100ms.
   * @param r   Red 0–255. Default 255.
   * @param g   Green 0–255. Default 255.
   * @param b   Blue 0–255. Default 255.
   */
  private _cameraFlash(ms: number = 100, r: number = 255, g: number = 255, b: number = 255): void {
    try {
      this.cameras.main.flash(ms, r, g, b);
    } catch { /* headless test harness */ }
  }

  /**
   * W8 PR-2 — Faction-color boss burst FX。spec docs/game-pr-specs/
   * W8-boss-polish.md §3.3。
   *
   * 在 `_bossDefeated` 中緊接 `_cameraFlash` 被呼叫。讀 `FACTION_BURST_COLOR`
   * 對照表挑門派色,做兩層視覺共鳴:
   *   1. `JuiceLayer.flashSprite(player, color, 160)` — 此時 boss sprite 已
   *      destroy,改 flash player sprite 作「擊殺回響」共鳴,150–200ms 短
   *      pulse 不喧賓奪主。 + 0.018 / 300ms screenShake 補一層手感(獨立於
   *      `_bossDefeated` 自身的 0.02/300ms 大震,疊加但低強度)。
   *   2. 200px 漸層圓圈 — alpha 0.6 → 0 tween 400ms Quad.easeOut,模擬色光暈
   *      擴散,搭配既有 W5 24-particle yellow/orange burst 形成「中央黃焰
   *      + 外圈門派色光暈」雙層。
   *
   * factionId === undefined / 未知 key → fallback 通用金 `0xffd54f`(與 W5
   * yellow/orange burst 風格一致,不破壞死亡 FX 一致性)。
   *
   * 全程 try-guarded:test harness 沒 add.graphics / tweens.add 不會 throw
   * (headless 安全是 scene FX 一致鐵則 — 見 JuiceLayer / _cameraFlash 同款
   * try/catch 收尾)。
   *
   * @param factionId  Boss 的 `BossDef.faction`(string,可為 undefined)。
   * @param x          Boss 死亡時的 world X(`bossX`)。
   * @param y          Boss 死亡時的 world Y(`bossY`)。
   */
  private _emitBossBurst(factionId: string | undefined, x: number, y: number): void {
    const color = (factionId != null && FACTION_BURST_COLOR[factionId] != null)
      ? FACTION_BURST_COLOR[factionId]!
      : FACTION_BURST_FALLBACK_COLOR;
    // 1. JuiceLayer flashSprite + screenShake — boss sprite 已 destroy 改打玩家
    //    sprite 作擊殺共鳴。`_juiceLayer` 在 headless test 可能未 init(see
    //    line ~574 try-guard),所以額外判 null。
    if (this._juiceLayer) {
      try {
        const tgt = (this.playerImage ?? this.playerSprite) as unknown as Phaser.GameObjects.GameObject;
        if (tgt) this._juiceLayer.flashSprite(tgt, color, 160);
      } catch { /* test harness */ }
      try { this._juiceLayer.screenShake(0.018, 300); } catch { /* no cameras */ }
    }
    // 2. 200px 漸層色光圓圈 — alpha 0.6 → 0 tween 400ms。
    try {
      const gfx = this.add.graphics();
      gfx.fillStyle(color, 0.6);
      gfx.fillCircle(x, y, 200);
      gfx.setDepth(20);
      this.tweens.add({
        targets: gfx,
        alpha: 0,
        duration: 400,
        ease: 'Quad.easeOut',
        onComplete: () => { try { gfx.destroy(); } catch { /* headless */ } },
      });
    } catch { /* headless: no add.graphics / tweens */ }
  }

  /**
   * JUICE (task #19): public hook for the W2 skill system to fire a radial
   * particle burst (10 small circles, faction-colored) at the player's current
   * position the moment a skill casts. Exposed as a method so we don't have
   * to touch src/game/skills/* in this PR (per task scope).
   *
   * @param faction — player's strongest faction; null = neutral white burst.
   * @returns number of particles actually spawned (for tests).
   */
  triggerSkillCastBurst(faction: FactionId | null = this._playerFaction): number {
    // W4: correct-answer / skill-cast trigger → duck BGM -12dB for 800ms
    // (Godot takeaway 4). Stub for W4; real ramp validated by AudioBus tests.
    try { this._audioBus?.duck(-12, 800); } catch { /* noop */ }
    // W5: skill-release camera punch — subtle 1.1x zoom, no flash so it doesn't
    // wash out the faction-colored burst particles. Per spec §4.2.
    this._cameraZoomPunch(1.1, 180);
    return skillCastBurst(this, this.playerX, this.playerY, faction);
  }

  /** hero-cutin-wire:目前的主角立牌 cut-in 容器(新一次施放會先收掉舊的)。純演出。 */
  private _heroCutin?: Phaser.GameObjects.Container | null;

  /**
   * boss 2026-06-16「立繪出現時不要跳三選一,等結束才跳」:大招 cut-in 立繪播放期間若
   * 觸發升級三選一,先把 onResume 存這裡延後,等 cut-in finish() 再補開 picker。
   * null = 沒有延後中的三選一。
   */
  private _pendingUpgradeResume: (() => void) | null = null;

  /**
   * 放技能時叫出「主角立牌」cut-in(boss 2026-06-16「增氣勢、稍微有動」)。立繪從
   * 左側滑入 + 門派色斜帶背景 + 輕微 punch/bob,停留約 0.4s 後滑出淡出。純演出 ——
   * 0 戰力寫入;texture 缺檔 / 動畫例外都 no-op;reduceMotion 時改溫和靜態淡入淡出。
   * 性別目前用無性別預設立繪(戰鬥端未追蹤 garage 的 scene-local 性別選擇)。
   */
  private _playHeroCutin(faction: FactionId | string | null | undefined): void {
    try {
      // 優先用動態大招立繪(hero-skill-cutin);缺檔回退車庫 portrait。
      const cutinKey = (HERO_CUTIN_KEYS as Record<string, string>)[faction as string];
      const key = (cutinKey && this.textures.exists(cutinKey))
        ? cutinKey
        : ((HERO_PORTRAIT_KEYS as Record<string, string>)[faction as string] ?? HERO_PORTRAIT_KEYS.celestial);
      if (!key || !this.textures.exists(key)) return;
      const W = this.scale.width || 800;
      const H = this.scale.height || 600;
      let reduce = false;
      try { reduce = isReduceMotion(); } catch { /* default false */ }
      const color = (SLOT0_CONFIG as Record<string, { color: number }>)[faction as string]?.color ?? 0xffffff;

      // 既有 cut-in 先收掉(技能 CD≥3s 通常不重疊,保險)。
      try { this._heroCutin?.destroy(); } catch { /* noop */ }

      const cont = this.add.container(0, 0).setDepth(880).setScrollFactor(0);
      this._heroCutin = cont;
      const finish = () => {
        try { cont.destroy(); } catch { /* noop */ }
        if (this._heroCutin === cont) this._heroCutin = null;
        // boss 2026-06-16:cut-in 結束 → 若期間有被延後的升級三選一,現在補開。
        if (!this._heroCutin && this._pendingUpgradeResume) {
          const resume = this._pendingUpgradeResume;
          this._pendingUpgradeResume = null;
          try { this._showUpgradePicker(resume); } catch { /* noop */ }
        }
      };

      const restX = W * 0.24;
      const restY = H + 6; // 微出底邊,像從畫面下緣站起來

      // 暗底襯托(boss 2026-06-16「分不出來」):立繪後方鋪半透明深色,讓角色不論
      // 在哪種門派場景色上都能跳出來。左側面板 + 右緣 3 段漸淡(避免硬切)。
      const dim = this.add.graphics();
      dim.fillStyle(0x0a0613, 0.62); dim.fillRect(0, 0, W * 0.44, H);
      dim.fillStyle(0x0a0613, 0.42); dim.fillRect(W * 0.44, 0, W * 0.06, H);
      dim.fillStyle(0x0a0613, 0.22); dim.fillRect(W * 0.50, 0, W * 0.06, H);
      dim.fillStyle(0x0a0613, 0.08); dim.fillRect(W * 0.56, 0, W * 0.05, H);
      dim.setAlpha(0);
      this.tweens.add({ targets: dim, alpha: 1, duration: reduce ? 140 : 180, ease: 'Quad.easeOut' });
      cont.add(dim);

      // 門派色斜帶(氣勢背景),立繪後方。
      const band = this.add.graphics();
      band.fillStyle(color, 0.3);
      band.fillRect(-W, -68, W * 2, 136);
      band.fillStyle(0xffffff, 0.5);
      band.fillRect(-W, -8, W * 2, 16);
      band.setAngle(-18).setPosition(restX, H * 0.5);
      cont.add(band);

      // 立繪:錨底置中,縮到約螢幕高 0.6(reduceMotion 略小)。
      const img = this.add.image(restX, restY, key).setOrigin(0.5, 1);
      const ih = img.height || 1;
      const scale = (H * (reduce ? 0.5 : 0.6)) / ih;
      img.setScale(scale);
      cont.add(img);

      // 2-frame「念咒→發力」(boss 2026-06-16):有第二格的門派,在技能觸發點把立繪
      // 換成「發力 pose」+ 一震,串成 念咒→發力 序列。沒第二格的門派維持單格。純演出。
      const cutin2Key = (HERO_CUTIN2_KEYS as Record<string, string>)[faction as string];
      if (cutin2Key && this.textures.exists(cutin2Key)) {
        // boss 2026-06-16:第一張(念咒)撐 ~2 秒(喊聲在這放)→ 喊完才換發力 frame。
        // 精確「喊完才換」之後接上配音時改成 = 喊聲長度;現用 2000ms 佔位。
        this.time.delayedCall(reduce ? 1800 : 2000, () => {
          try {
            img.setTexture(cutin2Key);
            const sc2 = (H * (reduce ? 0.5 : 0.6)) / (img.height || 1);
            img.setScale(sc2);
            if (!reduce) {
              this.tweens.add({ targets: img, scaleX: sc2 * 1.08, scaleY: sc2 * 1.08, duration: 110, yoyo: true, ease: 'Quad.easeOut' });
              try { this._juiceLayer?.screenShake(0.008, 80); } catch { /* noop */ }
            }
          } catch { /* noop */ }
        });
      }

      if (reduce) {
        cont.setAlpha(0);
        this.tweens.add({ targets: cont, alpha: 1, duration: 140, ease: 'Quad.easeOut' });
        this.tweens.add({ targets: cont, alpha: 0, delay: 2400, duration: 220, ease: 'Quad.easeIn', onComplete: finish });
        return;
      }

      // 動態:立繪從左滑入 + punch;斜帶淡入;站定後輕微 bob;停留後滑出淡出。
      img.setX(-img.displayWidth);
      band.setAlpha(0);
      this.tweens.add({ targets: img, x: restX, duration: 240, ease: 'Back.easeOut' });
      this.tweens.add({ targets: band, alpha: 1, duration: 160, ease: 'Quad.easeOut' });
      this.tweens.add({ targets: img, y: restY - 10, delay: 240, duration: 520, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      this.tweens.add({ targets: img, scaleX: scale * 1.04, scaleY: scale * 1.04, delay: 240, duration: 120, yoyo: true, ease: 'Quad.easeOut' });
      this.tweens.add({ targets: img, x: restX - 80, delay: 2700, duration: 240, ease: 'Quad.easeIn' });
      this.tweens.add({ targets: cont, alpha: 0, delay: 2700, duration: 240, ease: 'Quad.easeIn', onComplete: finish });
    } catch { /* headless / 任意失敗都不影響技能機制 */ }
  }

  /**
   * 疆域「隕石風暴」大絕特效(boss 2026-06-16「招式要真效果、不要幾何圖形」)。天降
   * 一批隕石(skill-fx-meteor 貼圖斜射)→ 落地播 CC0 動畫爆炸序列(playExplosion /
   * RING_EXPLOSION,非幾何)+ 連環震 + 紅閃。純演出 0 戰力寫入;texture 缺 / headless
   * 全程 no-op,不影響傷害結算。
   */
  private _playMeteorStorm(tint: number = 0xff5a2a): void {
    try {
      const W = this.scale.width || 800;
      const H = this.scale.height || 600;
      // 瞄準敵人(boss 2026-06-16):隕石砸在實際敵人/魔王位置上,不是亂落。
      const targets: { x: number; y: number }[] = [];
      try {
        for (const e of this.enemies) {
          if (e && e.entity) targets.push({ x: e.entity.x, y: e.entity.y });
        }
        if (this.boss) targets.push({ x: this.boss.x, y: this.boss.y });
      } catch { /* noop */ }
      const count = Math.min(10, Math.max(targets.length, 6));
      for (let i = 0; i < count; i++) {
        this.time.delayedCall(i * 95 + Math.random() * 60, () => {
          try {
            let ix: number; let iy: number;
            if (targets.length > 0) {
              const t = targets[i % targets.length];
              ix = t.x + (Math.random() - 0.5) * 40;
              iy = t.y + (Math.random() - 0.5) * 30;
            } else {
              ix = W * (0.32 + 0.6 * Math.random());
              iy = H * (0.30 + 0.4 * Math.random());
            }
            const boom = () => {
              try { playExplosion(this, RING_EXPLOSION, ix, iy, { scale: 1.3, tint, depth: 9 }); } catch { /* noop */ }
              try { this._juiceLayer?.screenShake(0.011, 90); } catch { /* noop */ }
              try { this._sfx?.play('big-boom', 0.4); } catch { /* AudioContext busy */ }
            };
            if (this.textures.exists('skill-fx-meteor')) {
              const m = this.add.image(ix + 220, iy - 260, 'skill-fx-meteor')
                .setDepth(8).setScale(0.45).setRotation(Math.PI * 0.7).setTint(tint);
              try { m.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
              this.tweens.add({
                targets: m, x: ix, y: iy, duration: 230, ease: 'Quad.easeIn',
                onComplete: () => { try { m.destroy(); } catch { /* noop */ } boom(); },
              });
            } else {
              boom();
            }
          } catch { /* noop */ }
        });
      }
      // 法術出來的瞬間(boss 2026-06-16「法術出來要震動加音效」):重震 + 發招音效 +
      // 短頓,讓「大絕出來」有重量感。
      try { this._juiceLayer?.screenShake(0.024, 300); } catch { /* noop */ }
      try { this._juiceLayer?.hitStop(70); } catch { /* noop */ }
      // 大絕重音 = 預烤的 meteor-boom.ogg(boss 2026-06-16 選的 #5:explosionCrunch_004
      // 降頻 + 兩層 lowFrequency_explosion 混音 q5)。降頻/疊層都已烤進檔,直接 rate 1.0 播。
      try { this._sfx?.play('big-boom', 1.0); } catch { /* AudioContext busy */ }
      try { this._cameraFlash(160, 255, 110, 60); } catch { /* noop */ }
    } catch { /* noop */ }
  }

  private _syncSprites(nowMs: number = 0): void {
    this.playerSprite.x = this.playerX;
    this.playerSprite.y = this.playerY;
    this._drawPlayer();
    // game-feel: engine-idle bob — a tiny sinusoidal vertical wobble applied
    // ONLY to the visual image (never this.playerX/Y, so movement clamps and
    // collision stay exact). Amplitude kept small (±3px) so it reads as an
    // idling engine, not floating. Rotation is intentionally NOT touched here —
    // horizontal-battle-scene-orient.test.ts locks the player image rotation at
    // 0 (cannon-facing contract), so a roll wobble would break that test.
    const bob = Math.sin(nowMs / 220) * 3;
    // art-gen-1: keep the AI-gen player sprite glued to the same coords.
    if (this.playerImage) {
      this.playerImage.x = this.playerX;
      this.playerImage.y = this.playerY + bob;
    }
    // P1:配件 overlay 跟車身(含 bob)一起浮動。
    if (this.tankAccessories && this.playerImage) {
      this.tankAccessories.x = this.playerImage.x;
      this.tankAccessories.y = this.playerImage.y;
    }
    // W1 戰場可讀性:底光跟車(含 bob),主角永遠帶一圈柔光。
    if (this._playerGlow) {
      this._playerGlow.x = this.playerX;
      this._playerGlow.y = this.playerY + bob;
    }
    if (this._playerGlowInner) {
      this._playerGlowInner.x = this.playerX;
      this._playerGlowInner.y = this.playerY + bob;
    }
    // P3:僚機繞行+自動射擊、近戰旋刃旋轉+近距判傷(皆射 HBullet 走現有碰撞)。
    this._updateCompanions(nowMs);
    for (const b of this.bullets) {
      b.sprite.x = b.entity.x;
      b.sprite.y = b.entity.y;
      // 彈跳/散彈質變:子彈視覺(發光彈體+拖尾)是朝右(rotation 0)畫的,反彈或斜射
      // 後若不轉向,拖尾仍指右 → 看起來「方向錯」(老闆 2026-06-05)。每幀依速度向量
      // 轉向(直射 vy=0 → atan2=0 不變,行為相容)。
      if (b.entity.vx !== 0 || b.entity.vy !== 0) {
        try { b.sprite.rotation = Math.atan2(b.entity.vy, b.entity.vx); } catch { /* headless graphics */ }
      }
    }
    for (const e of this.enemies) {
      e.sprite.x = e.entity.x;
      e.sprite.y = e.entity.y;
      if (e.image) {
        e.image.x = e.entity.x;
        // game-feel: per-enemy idle bob, phase-seeded by x so they don't all
        // wobble in lockstep. Visual-only (entity.x/y untouched).
        const ph = e.entity.x * 0.03;
        e.image.y = e.entity.y + Math.sin(nowMs / 200 + ph) * 2.5;
      }
      // HENEMY-AFFINITY: re-draw halo each frame (clear + fillCircle) so the
      // alpha pulse animates. Halo Graphics is only present when affinity is
      // non-null (see _spawnEnemy), so no per-frame cost for un-haloed grunts.
      if (e.halo) {
        e.halo.clear();
        e.entity.drawHalo(e.halo, e.entity.x, e.entity.y, nowMs);
      }
    }
    if (this.boss && this.bossSprite) { this._drawBossSprite(); }
    // boss-sprite-wire: keep the AI-gen boss image glued to boss x/y so it
    // tracks the fly-in + idle bob motion.
    if (this.boss && this.bossImage) {
      this.bossImage.x = this.boss.x;
      this.bossImage.y = this.boss.y;
    }
    for (const b of this.bossBullets) { b.sprite.x = b.x; b.sprite.y = b.y; }
    // boss-movesets: 重畫雷射光束 (預警/開火) + 玩家被緩速時的藍光提示。
    this._drawBossLasers();
    // boss-aoe: 重畫衝擊波 (預警危險圈 / 引爆衝擊環)。
    this._drawShockwaves();
    this._drawSlowOverlay();
  }

  private _drawPlayer(): void {
    // Draw around local origin (0,0). The scene applies setRotation(+Math.PI/2)
    // to the player Graphics; under Phaser's screen-coord rotation (y-down)
    // a local -y vector rotates to world +x. So the cannon must be drawn in
    // the local -y direction (UP in pre-rotation frame) for it to end up
    // facing RIGHT in world space, matching the player bullet vector (vx>0)
    // and the player image PNG (also rotated +PI/2 from native UP-facing).
    //
    // tank-direction fix: prior version drew barrel at +x with rotation +PI/2,
    // which left the visible barrel pointing DOWN. Re-aligned to face RIGHT.
    this.playerSprite.clear();
    this.playerSprite.fillStyle(0x4caf50, 1);
    this.playerSprite.fillRect(-15, -20, 30, 40);
    this.playerSprite.fillStyle(0x66bb6a, 1);
    this.playerSprite.fillRect(-4, -40, 8, 22);
  }

  private _drawBossSprite(): void {
    if (!this.boss || !this.bossSprite) return;
    const s = (this.boss.def.size ?? 96) / 2;
    this.bossSprite.clear();
    // boss-transparency-fix (2026-05-31, boss 回饋「魔王底色沒透明」): when a real
    // boss PNG is overlaid (this.bossImage non-null), the PNG already has its own
    // transparent silhouette. Painting a SOLID def.color square + white barrel
    // beneath it showed through the PNG's transparent pixels, so the boss looked
    // like it sat on an opaque colour block (no 去背). With a PNG present we now
    // draw ONLY additive overlay effects (the HP<50% red glow) and skip the solid
    // body + barrel entirely. Without a PNG we keep the full Graphics fallback so
    // PNG-less bosses (stripped build / tier-2 cross-faction) still render a body.
    const hasPng = this.bossImage != null;
    if (!hasPng) {
      this.bossSprite.fillStyle(this.boss.def.color, 1);
      this.bossSprite.fillRect(this.boss.x - s, this.boss.y - s, s * 2, s * 2);
      // Barrel on LEFT side (boss faces left; Graphics has no setFlipX)
      this.bossSprite.fillStyle(0xffffff, 0.4);
      this.bossSprite.fillRect(this.boss.x - s - 16, this.boss.y - 6, 16, 12);
    }
    // HP<50% 受傷回饋。
    // boss-roster-wire (2026-06-13): bosses are now FULL-COLOUR bespoke PNGs, and
    // the HP<50% (berserk) moment is conveyed by the phase-2 暴怒變身 sprite swap
    // (_swapBossToPhase2) — far stronger than a flat tint. So for PNG bosses we
    // do NOT persistently tint the image (any tint muddies the bespoke art).
    // Transient 受擊 flash still happens via _flashBoss on each hit. PNG-less
    // fallback bosses keep the Graphics red glow square (it IS their body).
    const lowHp = this.boss.hp / this.boss.def.hp < 0.5;
    if (!hasPng && lowHp) {
      this.bossSprite.fillStyle(0xff0000, 0.25);
      this.bossSprite.fillRect(this.boss.x - s, this.boss.y - s, s * 2, s * 2);
    }
  }

  /**
   * 不同敵人不同經驗(boss 2026-06-13):依該敵的 variant + elite/mini-boss 算單場 XP 倍率。
   * 純委派給 enemy-xp.enemyKillXpMult(資料驅動可單測);mini-boss 維持較高的 scene 常數。
   * elite = 有 affinity 光環的菁英雜兵。鐵則:只影響 in-run XP 漲速,0 修煉值寫入。
   */
  private _xpMultForEntry(e: EnemyEntry): number {
    return enemyKillXpMult(
      { variant: e.entity.variant, elite: e.entity.affinity != null, miniBoss: !!e.miniBoss },
      HorizontalBattleScene.MINI_BOSS_XP_MULT,
    );
  }

  /**
   * roguelite-xp: grant XP for one enemy kill and, if the bar fills, LEVEL UP
   * and open the existing 三選一 picker (once per level-up). Pure game mechanic
   * — _runXp is in-run only and is NEVER written to 修煉值/progress (鐵則:
   * 學習與遊戲分離). The picker freezes the battle via _pickPaused; its onResume
   * is a no-op here because the wave loop keeps running afterwards.
   */
  private _onEnemyKilledXp(xpMult: number = 1): void {
    // mini-boss: 擊殺小魔王給較多 XP. 鐵則:純 in-run XP,never persisted.
    const res = this._runXp.add(XP_PER_KILL * xpMult);
    if (res.leveledUp && !this._pickPaused) {
      // Level-up → fire the SAME 三選一 picker the merchant/wave flow used.
      this._showUpgradePicker(() => { /* battle resumes; wave loop unaffected */ });
    }
  }

  /**
   * roguelite-xp: draw the bottom-edge XP bar. Dark track + amber fill scaled by
   * _runXp.progress01, with a 'Lv N' label. Try-guarded for the headless test
   * harness (no add/graphics). Mirrors _drawHpBar's defensive style.
   */
  /**
   * cover-rework: 生成本場戰術掩體 (玩家右前方 x45-55%,垂直散開擋得到魔王彈)。
   * 優先用卡通 Image 貼圖 (cover-*.png);若該貼圖缺失 (stripped / 載入失敗) →
   * fallback 用 Graphics 改良卡通畫法;完全 headless (test 無 this.add) → sprite=null,
   * 純擋彈/扣血邏輯仍在。純遊戲機制 (學習與遊戲分離)。預設 3 面。
   */
  /** cover-rework: 一次性 lazy spawn 守衛 (擋彈判定與重畫兩條路徑都可呼叫,只生成一次)。 */
  private _ensureDestructiblesSpawned(): void {
    if (this._destructiblesSpawned) return;
    this._destructiblesSpawned = true;
    try { this._spawnDestructibles(); } catch { /* headless / no add */ }
  }

  private _spawnDestructibles(count = 2): void {
    const layout = makeDestructibleLayout(count, GAME.width, GAME.height);
    for (const init of layout) {
      const entity = new Destructible(init);
      let sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Graphics | null = null;
      const texKey = COVER_TEXTURE_KEYS[entity.kind];
      try {
        const hasTex = this.textures?.exists?.(texKey);
        if (hasTex) {
          // 卡通 Image:縮放到掩體 AABB 大小 (牆 = 窄高;略放大 2.2x 讓視覺蓋過判定框)。
          const img = this.add.image(entity.x, entity.y, texKey);
          img.setDepth(3);
          img.setDisplaySize(entity.halfW * 2.2, entity.halfH * 2.2);
          sprite = img;
        } else {
          const g = this.add.graphics();
          g.setDepth(3);
          sprite = g;
        }
      } catch { sprite = null; }
      this.destructibles.push({ entity, sprite });
    }
    this._drawDestructibles();
  }

  /**
   * cover-rework: 每幀更新掩體視覺。
   *   - Image sprite (卡通貼圖):血量越低 alpha 越淡 (破損感);命中後 120ms 內
   *     setTint 白閃,否則 clearTint。同步位置 (掩體不移動但保險)。
   *   - Graphics sprite (fallback / 缺貼圖):用「改良卡通牆」畫法 (圓角 + 高光 +
   *     深色描邊 + 血量越低越暗的破損裂痕),比舊版裸幾何精緻很多。
   * try-guard headless 不炸。純視覺 (不影響擋彈/扣血數值)。
   */
  private _drawDestructibles(): void {
    const nowMs = this.time?.now ?? 0;
    for (const d of this.destructibles) {
      const s = d.sprite;
      if (!s) continue;
      const e = d.entity;
      const flash = nowMs - e.lastHitMs < 120;
      // 血量越低越淡/越暗 (0.5~1.0),做出「快破」的破損視覺。
      const dim = 0.5 + 0.5 * e.hpFrac;

      // Image 分支:用 tint/alpha 表現命中閃白與破損,不重畫形狀。
      const asImg = s as unknown as {
        setTint?: (c: number) => unknown;
        clearTint?: () => unknown;
        setAlpha?: (a: number) => unknown;
        setPosition?: (x: number, y: number) => unknown;
        setVisible?: (v: boolean) => unknown;
      };
      if (typeof asImg.setTint === 'function') {
        try {
          if (!e.alive) { asImg.setVisible?.(false); continue; }
          asImg.setPosition?.(e.x, e.y);
          asImg.setAlpha?.(0.7 + 0.3 * e.hpFrac); // 越破越透
          if (flash) asImg.setTint?.(0xffffff);
          else if (e.hpFrac < 0.5) asImg.setTint?.(0xff9e80); // 半血以下泛紅警示
          else asImg.clearTint?.();
        } catch { /* headless image stub */ }
        continue;
      }

      // Graphics fallback 分支:改良卡通牆 (圓角 + 高光 + 描邊 + 破損裂痕)。
      const g = s as Phaser.GameObjects.Graphics;
      try {
        g.clear();
        if (!e.alive) continue;
        const hw = e.halfW, hh = e.halfH;
        const x0 = e.x - hw, y0 = e.y - hh, w = hw * 2, h = hh * 2;
        const shade = (c: number) => {
          if (flash) return 0xffffff;
          const rr = Math.round(((c >> 16) & 0xff) * dim);
          const gg = Math.round(((c >> 8) & 0xff) * dim);
          const bb = Math.round((c & 0xff) * dim);
          return (rr << 16) | (gg << 8) | bb;
        };
        // 各 kind 主色 / 高光 / 描邊。
        const palette: Record<string, [number, number, number]> = {
          sandbag: [0xc9a36a, 0xe6c691, 0x8a6d3b],
          shield:  [0x29b6f6, 0x81d4fa, 0x0277bd],
          stone:   [0x9e9e9e, 0xcfcfcf, 0x5f5f5f],
        };
        const [base, hi, edge] = palette[e.kind] ?? palette.stone;
        const fillRR = (g as unknown as { fillRoundedRect?: (a: number, b: number, c: number, d: number, e: number) => void }).fillRoundedRect;
        const strokeRR = (g as unknown as { strokeRoundedRect?: (a: number, b: number, c: number, d: number, e: number) => void }).strokeRoundedRect;
        if (typeof fillRR === 'function') {
          g.fillStyle(shade(base), 1); fillRR.call(g, x0, y0, w, h, 7);
          g.fillStyle(shade(hi), 0.6); fillRR.call(g, x0 + 2, y0 + 2, w - 4, h * 0.4, 6); // 上半高光
        } else {
          g.fillStyle(shade(base), 1); g.fillRect(x0, y0, w, h);
          g.fillStyle(shade(hi), 0.6); g.fillRect(x0 + 2, y0 + 2, w - 4, h * 0.4);
        }
        g.lineStyle(3, shade(edge), 1);
        if (typeof strokeRR === 'function') strokeRR.call(g, x0, y0, w, h, 7);
        else g.strokeRect(x0, y0, w, h);
        // 破損裂痕:血量越低裂越多 (>1 條時畫斜裂)。
        if (e.hpFrac < 0.66) {
          g.lineStyle(2, shade(edge), 0.9);
          g.lineBetween(e.x, y0 + 4, e.x - 6, e.y);
          if (e.hpFrac < 0.33) g.lineBetween(e.x + 4, e.y, e.x - 4, y0 + h - 4);
        }
      } catch { /* headless graphics stub */ }
    }
  }

  /**
   * cover-rework 核心:魔王彈 / 敵彈 vs 戰術掩體 (擋彈)。
   *
   * 「有意義」的關鍵:此法在 _checkCollisions 的「Boss bullets vs player」與
   * 「enemy bullets vs player」判定『之前』呼叫 → 凡是被存活掩體 AABB 擋到的
   * 魔王彈/敵彈 → 該彈 alive=false (消失) + 掩體扣血,『不會再走到傷玩家的判定』。
   * 於是「躲在掩體後」真的擋得下飛來的彈幕 = 戰術價值。掩體被打多了破 (HP=0) →
   * 安全區消失,逼玩家換位。
   *
   * 玩家子彈 (this.bullets) 完全不在此處理 → 穿過自己的掩體不互動,玩家照樣打魔王。
   *
   * 純遊戲機制:不彈題、不寫回修煉值;擊毀只接戰鬥內 run-xp (加分非主誘因)。
   * 每幀於 _checkCollisions 開頭呼叫 (在玩家受擊判定之前)。
   */
  private _checkCoverBlocks(): void {
    if (this.destructibles.length === 0) return;
    const nowMs = this.time?.now ?? 0;
    // 魔王彈 (較大) + 敵彈 (較小) 都會被掩體擋。容差略放大讓擋彈手感寬鬆 (K-3)。
    const streams: { bullets: { x: number; y: number; alive: boolean; damage: number }[]; tol: number }[] = [
      { bullets: this.bossBullets as unknown as { x: number; y: number; alive: boolean; damage: number }[], tol: 8 },
      { bullets: this.enemyBullets as unknown as { x: number; y: number; alive: boolean; damage: number }[], tol: 5 },
    ];
    for (const { bullets, tol } of streams) {
      for (const b of bullets) {
        if (!b.alive) continue;
        for (const d of this.destructibles) {
          const e = d.entity;
          if (!e.alive) continue;
          if (!e.blocks(b.x, b.y, tol)) continue;
          // 擋下:彈消失 + 掩體扣血 (不走玩家受擊)。
          b.alive = false;
          const destroyed = e.takeDamage(b.damage ?? 10, nowMs);
          this._coverHitFx(d, e, destroyed);
          break; // 一顆彈只打一面掩體
        }
      }
    }
    // 清掉已破掩體 (sprite 已銷毀)。
    this.destructibles = this.destructibles.filter((d) => d.entity.alive);
  }

  /**
   * cover-rework: 掩體被命中 / 被打爆的共用 juice + XP + sprite 清理。
   * headless / 無 _juice 全程 try-guard。
   */
  private _coverHitFx(
    d: { entity: Destructible; sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Graphics | null },
    e: Destructible,
    destroyed: boolean,
  ): void {
    const juice = this._juice as unknown as
      { playFeedback?: (preset: string, pos: { x: number; y: number }) => void } | undefined;
    // preset 名必須存在於 feedback-presets.json — 'hit' 不存在曾讓最高頻的
    // 打擊回饋在 prod 靜默 no-op(2026-06-12 體感體檢抓到;守門測試見
    // tests/game/systems/feedback-presets-coverage.test.ts)。
    try { juice?.playFeedback?.('normalHit', { x: e.x, y: e.y }); } catch { /* noop */ }
    if (destroyed) {
      try { juice?.playFeedback?.('enemyExplode', { x: e.x, y: e.y }); } catch { /* noop */ }
      try { this._onEnemyKilledXp(DESTRUCTIBLE_XP_MULT); } catch { /* noop */ }
      try {
        const sp = d.sprite as unknown as { clear?: () => void; destroy?: () => void } | null;
        sp?.clear?.(); sp?.destroy?.();
      } catch { /* noop */ }
      d.sprite = null;
    }
  }

  /**
   * combat-polish (老闆 2026-06「整體 UI 太粗糙」): 共用「精緻圓角條」繪製。
   * 一條 = 暗底軌道(圓角)+ 雙色漸層填充(亮頂 highlight + 主色)+ 細邊框。
   * 全部 try-guard:headless graphics stub 沒有 fillRoundedRect 也不會炸。
   * 卡通風:圓角 + 高光 + 邊框,比裸色塊精緻很多。純視覺,數值映射不變。
   */
  private _drawFancyBar(
    g: Phaser.GameObjects.Graphics,
    x: number, y: number, w: number, h: number,
    pct: number, fillColor: number, highlightColor: number,
  ): void {
    const p = Math.max(0, Math.min(1, pct));
    const r = Math.min(h / 2, 6);
    try {
      g.clear();
      // 軌道:暗底圓角 + 內陰影感
      g.fillStyle(0x10121a, 0.92);
      g.fillRoundedRect(x - 1, y - 1, w + 2, h + 2, r + 1);
      g.fillStyle(0x2a2f3d, 1);
      g.fillRoundedRect(x, y, w, h, r);
      // 填充:主色 (圓角) + 上半高光帶 (雙色,做出漸層/立體感)
      const fw = Math.max(0, w * p);
      if (fw > 0) {
        g.fillStyle(fillColor, 1);
        g.fillRoundedRect(x, y, fw, h, r);
        g.fillStyle(highlightColor, 0.55);
        g.fillRoundedRect(x, y + 1, fw, Math.max(1, h * 0.42), r);
      }
      // 邊框
      g.lineStyle(2, 0x0a0c12, 0.9);
      g.strokeRoundedRect(x, y, w, h, r);
    } catch {
      // headless fallback:仍呼叫 fillRect/fillStyle 讓 call-count 測試成立。
      try {
        g.clear();
        g.fillStyle(0x2a2f3d, 1); g.fillRect(x, y, w, h);
        g.fillStyle(fillColor, 1); g.fillRect(x, y, w * p, h);
      } catch { /* noop */ }
    }
  }

  private _drawXpBar(): void {
    // cover-rework: 戰術掩體的「每幀視覺驅動」掛在這裡 (此法每幀由 render loop 呼叫)。
    // 一次性 lazy spawn 守衛 (也可能已在 _checkCollisions 先生成);這裡只負責重畫。
    // 擋彈判定 (_checkCoverBlocks) 改掛在 _checkCollisions「玩家受擊之前」,確保彈
    // 在傷玩家之前先被掩體攔下。純遊戲機制 (學習與遊戲分離)。
    this._ensureDestructiblesSpawned();
    try { this._drawDestructibles(); } catch { /* noop */ }

    if (!this._xpBar) return;
    // combat-polish: 經驗條加高 (8→14) + 圓角 + 雙色金色漸層 + 邊框,精緻可讀。
    const h = 14;
    const y = GAME.height - h - 2;
    const x = 4;
    const w = GAME.width - 8;
    this._drawFancyBar(this._xpBar, x, y, w, h, this._runXp.progress01, 0xffb300, 0xffe082);
    // 大字 + 描邊,K-3 友善;星星 icon 表「修煉等級」(不出現學科名)。
    try {
      this._xpText?.setFontSize?.(15);
      this._xpText?.setColor?.('#fff8e1');
      this._xpText?.setStroke?.('#5d3a00', 4);
    } catch { /* headless */ }
    this._xpText?.setText('⭐ 等級 ' + this._runXp.level);
  }

  private _drawHpBar(): void {
    const bx = 12;
    const by = GAME.height - 34;
    const w = 132;
    const h = 16;
    const pct = Math.max(0, Math.min(1, this.playerHp / this._maxHp()));
    // combat-polish: 綠/黃/紅三段 + 對應亮色高光,圓角邊框。
    const fill = pct > 0.5 ? 0x43a047 : pct > 0.25 ? 0xfbc02d : 0xe53935;
    const hi   = pct > 0.5 ? 0x81c784 : pct > 0.25 ? 0xfff176 : 0xff8a80;
    this._drawFancyBar(this.hpBar, bx, by, w, h, pct, fill, hi);
    try {
      // W1 戰場可讀性:HP 數字 15 → HUD_TEXT.fontPxHp(手機縮放後仍可讀)。
      this.hpText.setFontSize?.(HUD_TEXT.fontPxHp);
      this.hpText.setColor?.('#ffffff');
      this.hpText.setStroke?.(HUD_TEXT.strokeColor, HUD_TEXT.strokePx);
    } catch { /* headless */ }
    // 愛心 icon + 大字。
    this.hpText.setText('❤️ ' + this.playerHp);
    // Phase 2:金幣 HUD 跟著每幀更新 (純局內顯示)。
    try { this._coinText?.setText('💰 ' + this._runModifiers().coins); } catch { /* headless */ }
  }

  private _drawBossHpBar(): void {
    if (!this.boss || !this.bossHpBar || !this.bossHpText) return;
    const w = 220;
    const h = 16;
    const bx = GAME.width - w - 12;
    const by = GAME.height - 34;
    // game-feel: clamp to 1 so stage-scaled boss HP (hp may exceed def.hp at
    // stage>1) never draws the bar past 100% width.
    const pct = Math.max(0, Math.min(1, this.boss.hp / this.boss.def.hp));
    // combat-polish: 魔王血條 = 深紅 + 亮紅高光,圓角邊框,跟玩家綠條色系區隔。
    this._drawFancyBar(this.bossHpBar, bx, by, w, h, pct, 0xd32f2f, 0xff6f60);
    try {
      this.bossHpText.setFontSize?.(15);
      this.bossHpText.setColor?.('#fff5f5');
      this.bossHpText.setStroke?.('#3a0000', 4);
    } catch { /* headless */ }
    // 骷髏 icon + 魔王名 + HP。
    this.bossHpText.setText('💀 ' + this.boss.def.displayName + ' ' + this.boss.hp);
  }

  private _gameOver(nowMs: number): void {
    this.gameOver = true;
    // G04: playerDeath preset — max trauma (1.0) + red flash + explosion + 200ms hit-pause.
    // Kid feedback: death should FEEL final, not silent.
    try {
      this._juice?.playFeedback('playerDeath', { x: this.playerX, y: this.playerY });
    } catch { /* test harness */ }
    const survivedSec = Math.round((nowMs - this.startTimeMs) / 1000);
    // v1.1 battle-flow P0 (boss:「死掉就卡住了」): the playerDeath preset triggers
    // a hit-pause that sets time.timeScale = 0. A Phaser `time.delayedCall` is a
    // SCALED timer → its countdown freezes while timeScale is 0, so the
    // ResultScene transition NEVER fired and the kid was stuck on the death
    // frame. Fix: drive the transition off wall-clock `window.setTimeout`
    // (immune to Phaser timeScale) AND restore timeScale defensively before
    // starting the next scene. Touch-HUD removal moved up so it can never be
    // left blocking the defeat-screen CTAs.
    const goToDefeat = () => {
      try { document.querySelector('.touch-controls')?.remove(); } catch { /* noop */ }
      try { this.time.timeScale = 1; } catch { /* noop */ }
      try {
        this.scene.start('ResultScene', {
          outcome: 'defeat',
          freeXP: 0,
          battleStats: { wave: this.currentWave + 1, killed: this.totalKilled, survivedSec },
          // W7 hub->battle wiring — re-enter the originating hub building.
          hubFactionId: this._initHubFactionId ?? undefined,
        });
      } catch { /* noop */ }
    };
    // Remove the touch HUD immediately too (belt-and-suspenders) so even if the
    // timer is delayed the death overlay never has the joystick on top of it.
    try { document.querySelector('.touch-controls')?.remove(); } catch { /* noop */ }
    if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
      window.setTimeout(goToDefeat, 600);
    } else {
      // headless / no-DOM fallback: fire synchronously so tests still observe it.
      goToDefeat();
    }
  }

  /** Show the daily time-limit block screen and stop the scene. */
  private _showTimeLimitScreen(): void {
    this.gameOver = true;
    this._timeLimitBlocked = true;
    this.cameras.main.setBackgroundColor('#0a0a1a');

    this.add.text(GAME.width / 2, GAME.height / 2 - 80, '⏰ 今日遊戲時間已到', {
      fontSize: '40px', color: '#ffd54f', fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.add.text(GAME.width / 2, GAME.height / 2 - 20, '家長設定的每日遊戲時間已用完。', {
      fontSize: '22px', color: '#ccc', fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.add.text(GAME.width / 2, GAME.height / 2 + 30, '繼續修煉提升實力，明天再戰！', {
      fontSize: '20px', color: '#80cbc4', fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    // Button: go to learn
    const btnBg = this.add.rectangle(GAME.width / 2, GAME.height / 2 + 100, 240, 52, 0x2e7d32)
      .setDepth(10)
      .setInteractive();
    this.add.text(GAME.width / 2, GAME.height / 2 + 100, '📘 去修煉', {
      fontSize: '22px', color: '#ffffff', fontFamily: 'sans-serif',
    }).setOrigin(0.5).setDepth(11);
    btnBg.on('pointerdown', () => { window.dispatchEvent(new CustomEvent('ef:return-home')); });
    void btnBg;
  }

  /** Expose test hooks (dev/E2E only — not used by production code). */
  private _exposeTestHooks(): void {
    const self = this;

    // window.__hbattle — live scene state for E2E assertions
    (window as unknown as { __hbattle?: unknown }).__hbattle = {
      get enemies()     { return self.enemies; },
      get boss()        { return self.boss; },
      get bossBullets() { return self.bossBullets; },
      get enemyBullets() { return self.enemyBullets; },
      get paused()      { return self.paused; },
      get gameRunning() { return !self.paused && !self.gameOver; },
      // game-feel e2e: expose live player coords so movement tests can assert
      // the tank actually moves (esp. after a 下一關 stage restart).
      get playerX()     { return self.playerX; },
      get playerY()     { return self.playerY; },
      // dev/E2E: 直接設玩家 y(laser 掃描自驗用,模擬上下移動把光束掃過不同 lane)。
      setPlayerY: (y: number) => { if (Number.isFinite(y)) self.playerY = y; },
      // dev/E2E: 取目前裝備武器 behavior(讀 laser 過熱 / mortar shells 等狀態)。
      get weapon()      { return self._equippedWeapon; },
      get stage()       { return self._stage; },
      pause:  () => self._pauseGame(),
      resume: () => self._resumeGame(),
      get player() {
        return {
          get hp()         { return self.playerHp; },
          set hp(v: number){ self.playerHp = v; },
        };
      },
    };

    // __skipToBoss: set currentWave = _totalWaves (past last wave) + kill enemies
    // so _handleWaveSpawning() sees currentWave >= _totalWaves and calls _spawnBoss().
    (window as unknown as { __skipToBoss?: () => void }).__skipToBoss = () => {
      // Kill all living enemies so enemies.length === 0
      for (const e of self.enemies) { e.entity.alive = false; }
      self.enemies = [];
      // Set currentWave to _totalWaves so the next 'waiting' → advance check spawns boss
      const lastWaveIdx = self._totalWaves - 1;
      self.currentWave = lastWaveIdx;
      self.spawnedThisWave = self._waveSizes[lastWaveIdx] ?? self._waveSizes[self._waveSizes.length - 1];
      self.killedThisWave = self.spawnedThisWave;
      // Put in 'waiting' state with waveGapStartMs far in the past so gap expires immediately
      self.waveState = 'waiting';
      self.waveGapStartMs = 0;
    };

    // miniboss-roster-wire: spawn a 小魔王 on demand (visual QA of the rotating
    // cartoon skins) — same affordance level as __skipToBoss / __fireSkillSlot.
    // Returns the skin texture key so the harness can label the screenshot.
    (window as unknown as { __spawnMiniBoss?: () => string | null }).__spawnMiniBoss = () => {
      self._spawnMiniBoss();
      const last = self.enemies[self.enemies.length - 1];
      const img = last?.image as { texture?: { key?: string } } | null | undefined;
      return img?.texture?.key ?? null;
    };

    // R-005 主武器:dev/E2E 用,直接換上指定主武器 behavior(視覺自驗用,不寫 progress)。
    // 回傳實際裝上的武器 id(未知 id → 回退 rapid-cannon)。
    (window as unknown as { __equipWeapon?: (id: string) => string }).__equipWeapon = (id: string) => {
      self._equipWeaponForRun(id);
      return self._equippedWeapon.id;
    };

    // R-005 P3 (C):dev/E2E 用,在玩家附近落一個武器箱(視覺自驗 + 模擬撿取換裝)。
    // 未指定 id → 依稀有度權重隨機抽一把。回傳箱內武器 id。
    (window as unknown as { __spawnWeaponBox?: (id?: string) => string }).__spawnWeaponBox = (id?: string) => {
      const weaponId = id ?? pickWeaponBoxContents();
      self._spawnWeaponBox(self.playerX + 50, self.playerY, weaponId);
      return weaponId;
    };
    // R-005 P3 (C):dev/E2E 用,目前場上武器箱數量(掉落驗證)。
    (window as unknown as { __getWeaponBoxCount?: () => number }).__getWeaponBoxCount = () => self._weaponBoxes.length;
    // R-005 P3 (C):dev/E2E 用,目前裝備武器 id(撿箱換裝驗證)。
    (window as unknown as { __getEquippedWeaponId?: () => string }).__getEquippedWeaponId = () => self._equippedWeapon.id;

    // ── v1.1 active-skill hooks (Playwright / vitest) ──
    const w = window as unknown as Record<string, unknown>;
    w['__fireSkillSlot'] = (i: number) => self._tryFireSlot(i, self.time.now);
    w['__getMasteryDamageMult'] = () => self._masteryDamageMult;
    w['__getMasteryBoxLevel'] = () => self._masteryBoxLevel;
    w['__getUltimateCharge'] = () => self._ultimateCharge.progressFraction();
    w['__fillUltimateCharge'] = () => { let guard = 0; while (!self._ultimateCharge.canActivate() && guard++ < 100) self._ultimateCharge.onEnemyKilled(10, true); };
    w['__castUltimate'] = () => { if (self._ultimateCharge.canActivate()) self._castUltimate(self.time.now); };
    w['__isFrozen'] = () => self.time.now < self._enemyFreezeUntilMs;
    w['__getBurstShotsLeft'] = () => self._burstShotsLeft;
    w['__getSlotReady'] = (i: number) => self._skillSlots[i]?.isReady(self.time.now) ?? false;
    w['__getBulletCount'] = () => self.bullets.length;
    // v1.1 battle-flow P0: backdrop present (solid faction rect OR stage PNG) —
    // proves the screen is never a bare grey ParallaxSystem placeholder.
    w['__hasBattleBackdrop'] = () => !!self._stageBackdrop || !!self.stageBg;
    // ── v1.2 主人翁 / 英雄玩法 hooks (Playwright / vitest) ──
    w['__getHeroId'] = () => self._heroId;
    w['__getShieldHits'] = () => self._playstyle.shieldHits();
    w['__isScanning'] = () => self._playstyle.isScanning(self.time.now);
    w['__hasAutoFireBuff'] = () => self._playstyle.hasAutoFireBuff(self.time.now);
    w['__getFirePillarCount'] = () => self._playstyle.firePillars(self.time.now).length;
    w['__getEnemyCount'] = () => self.enemies.filter((e) => e.entity.alive).length;
    // ── Roguelike 三選一強化 hooks (Playwright / vitest) ──
    // __pickThree: return a fresh random trio (re-enables the parked
    // upgrade-picker.spec.ts shuffle-distribution tests).
    w['__pickThree'] = () => pickThree();
    // __isPickPaused: true while the DOM upgrade picker overlay is showing.
    w['__isPickPaused'] = () => self._pickPaused;
    // __getRunMods: snapshot of the per-run upgrade modifiers (speed/damage/…).
    w['__getRunMods'] = () => ({ ...self._runMods });
    // __showUpgradePicker: drive the real in-scene 三選一 picker (freezes battle
    // via _pickPaused + mounts the DOM overlay). Used by upgrade-picker.spec.ts
    // to verify the picker shows → choose → modifier applied → battle resumes.
    w['__showUpgradePicker'] = () => self._showUpgradePicker(() => { /* resume noop for e2e */ });
    // ── Phase 2 局內金幣 / 商店 hooks (Playwright / vitest) ──
    // __getCoins: 當前局內金幣 (純局內,不持久化)。
    w['__getCoins'] = () => self._runModifiers().coins;
    // __addCoins: 測試用直接加金幣 (Playwright 驗證商店買得起 gating)。
    w['__addCoins'] = (n: number) => self._runModifiers().addCoins(n);
    // __showCoinShop: 直接彈金幣商店 (freezes battle via _shopPaused + 掛 DOM)。
    w['__showCoinShop'] = () => self._showCoinShop(() => { /* resume noop for e2e */ });
    // __isShopPaused: true while the coin shop overlay is showing.
    w['__isShopPaused'] = () => self._shopPaused;
    // __getZoom: current camera zoom. Used by camera-zoom-restore.spec.ts to
    // assert the ultimate's _cameraZoomPunch returns to 1.0 (boss「放完大招畫面
    // 放大回不去」). Returns 1 when cameras are absent (headless safety).
    w['__getZoom'] = () => {
      try { return self.cameras.main.zoom; } catch { return 1; }
    };
  }
}
